/* Turn Tracker — shared reactive store.
 *
 * Domain model
 * ------------
 * services  configurable: name, an OPTIONAL shortcut key (the character you
 *           press — a salon that never uses the keyboard leaves it empty), a
 *           separate abbreviation of up to two characters (what gets drawn on
 *           the square and every badge), and turn weight (1 = full, 0.5 = half).
 * staff     the roster. Global, not per-day.
 * dayStaff  per-day presence for the current business date. `missedTurns` is
 *           frozen when a tech is marked in: the full turns the floor had
 *           already finished. Those squares render slashed, and they count
 *           toward the tech's turn credit so a late arrival does not leapfrog
 *           everyone who has been working since open.
 * turns     one per booked service, carrying a snapshot of the service name,
 *           letter and weight so renaming a service never rewrites history.
 *
 * Queue order
 * -----------
 * 1. turn credit ascending — anyone with zero turns sorts first
 * 2. then first-in-first-out on who has been free longest
 * 3. then column order
 * A tech is skipped entirely while a service of theirs is still running.
 *
 * Local state is the source of truth for rendering and is applied immediately;
 * sync.js carries it to Supabase in the background.
 */
import * as Vue from 'vue';
import { plugins } from './plugins.js';
import { prefs } from './prefs.js';
import { buildSlots, buildRows, creditFrom } from './rules/turns.js';
// Namespaced: `reorderTurns` and `sameOrder` read as though they were about
// the roster in a file that also reorders columns, and the move sheet's
// arithmetic is worth being able to point at.
import * as moveRules from './rules/move.js';
import { shouldReloadBoard, shouldReportLoadFailure } from './rules/session.js';
import { queueOrder, headOfQueue } from './rules/rotation.js';
// Namespaced: half of these read as though they were about the salon's hours
// in a file that has those too, and "whose day ends when" against "when the
// doors shut" is exactly the distinction worth keeping visible at the call.
import * as shiftRules from './rules/shift.js';
import * as workRules from './rules/work-hours.js';
import { supportBanner } from './rules/support.js';
// Namespaced rather than picked apart: `checkinOffer` here wraps the one in
// there and adds the words, and two functions of that name in one file with
// only an import to tell them apart is a trap.
import { licensing } from './licensing.js';
import * as waitlistRules from './rules/waitlist.js';
// Namespaced for the same reason: `resolveLook` and `cleanColor` are generic
// enough names that a bare import into a 6,000-line file would be a coin toss
// about which of them is meant.
import * as look from './rules/checkin-look.js';
// And again: `cleanServiceRef` and `bookingUrl` say nothing about which of the
// several ideas of a service id in this file they belong to until the namespace
// is in front of them.
import * as booking from './rules/booking.js';
import * as conflictRules from './rules/conflict.js';
import * as nextRules from './rules/next-up.js';
import * as writeUpRules from './rules/write-up.js';
import { takerFor, followBookFor } from './rules/taker.js';
import { findSwap, bestStart, passedOverNote, mayTake } from './rules/swap.js';
import { checkoutEnd, bookedServiceEnd as bookedServiceEndFromClose } from './rules/checkout.js';
import { bookedRequest, requestStillHolds } from './rules/booked-request.js';
import * as sameTimeRules from './rules/same-time.js';
import { optionTurnType } from './rules/option-type.js';
import { describeVisitChange } from './rules/booking-diff.js';
import { addOnHost, relinkExtras } from './rules/add-on.js';
import * as partyWaitRules from './rules/party-wait.js';
import * as partyRules from './rules/party-avail.js';
import { earlierFirst } from './rules/visit-order.js';
import { sameTimeLines, busyChoices, waitsForDesk, earlyCheckIn } from './rules/auto-start.js';
import { visitKey, clientFromBooking } from './rules/visit-key.js';
import { finishedSwapLine } from './rules/finished-swap.js';
import { visitWeights, cleanCutoff, lineIsFullTurn } from './rules/price-weight.js';
import * as ownRules from './rules/own-clients.js';
import * as missedFloorRules from './rules/missed-floor.js';
import * as notesRules from './rules/client-notes.js';
import * as deskNoteRules from './rules/notes.js';
import * as skipRules from './rules/skip-marks.js';
import { turnIdForBooking } from './rules/turn-id.js';
import { shouldCatchUp, staleNote } from './rules/catch-up.js';
// Namespaced: `lastFinished` and `lateStart` are exactly the kind of names this
// file already has half a dozen near-misses for, and which "last" and which
// "start" is meant should not depend on remembering an import line.
import * as overrun from './rules/overrun.js';
import * as offsetRules from './rules/start-offset.js';
import { followsBook as followsBookRule } from './rules/booked-clients.js';
import { planRoom } from './rules/turn-order.js';
import {
  shouldBringBookInLine, pendingFromResults, pendingRow, pendingLine, stillOpen,
  noteNamingStaff, shortClient, localStart, clockOf,
  bookRowFromVisit, namedOnLine, squareOf, squareAction, assignAgainstOpen, mergeLastResult,
  unitAtSameTime, bookMoveAfterFailure, onFirstTry
} from './rules/pending-moves.js';
import { optimizerApi } from './optimizer-api.js';
import * as seatRules from './rules/seated-start.js';
// The hotkey is optional and the square's label is not, which is one rule with
// two halves — shared with js/sync.js so a service built from the Settings form
// and the same service read back out of the database draw identically.
import { cleanShortcut, serviceAbbrev } from './rules/services.js';
import { sync } from './sync.js';
// Only for the one-time adoption of the old localStorage close marker — see
// adoptLegacyDayClosed. Nothing else in the store touches localStorage keys by
// name any more.
import { tenant } from './tenant.js';
// Aliased, because `theme` in this file is the salon's current scheme — the
// reactive state a few hundred lines down — and this is the module that loads,
// resolves and saves it.
import { theme as themeApi } from './theme.js';

var reactive = Vue.reactive;
var computed = Vue.computed;
var ref = Vue.ref;


// The scheduling plugin slot: where appointments, the service catalogue and
// checkout times come from, or nothing at all when it is switched off. Every
// read below goes through `scheduling.provider`; nothing here knows the name
// of the system behind it.
var scheduling = plugins.scheduling;

// Rows are sized to the window rather than to a fixed number: the board should
// fill the screen it is on and grow past it as the day fills up. MIN_ROWS is
// only the guess used before the board has been measured; MIN_VISIBLE_ROWS
// stops a very short window collapsing the grid to nothing.
var MIN_ROWS = 10;
var MIN_VISIBLE_ROWS = 3;

// How far ahead Due now looks for bookings that are about to start.
// Ten minutes out of the box, but it is the one number here that depends on
// the salon rather than on the board: a desk that seats people the moment they
// walk in wants two, a desk that gets the paperwork out first wants ten. The
// three offered are the ones worth a button; anything else is typed in.
var DUE_SOON_DEFAULT_MIN = 10;
var DUE_SOON_CHOICES = [2, 5, 10];
var DUE_SOON_LIMITS = { min: 1, max: 120 };

// Where the Next availability *Next* line stops counting down and starts naming a
// time. Half an hour is about as far ahead as "in forty minutes" is worth
// doing in your head; past it, what the desk repeats to the client is a time
// to come back at.
var ETA_CLOCK_AFTER_MIN = 30;

var PALETTE = [
  '#C5A059', '#7A9E7E', '#C98B8B', '#6E86B8',
  '#A98BC4', '#5FA8A0', '#B98A5E', '#8C93A8'
];

// What a turn is drawn in when its service has been deleted out from under it,
// and the neutral fill for an unmapped booking. It was written out by hand in
// eight files, so a palette change quietly left some of them behind.
var DEFAULT_SERVICE_COLOR = '#8C93A8';

/* ---------- helpers ---------- */

function todayKey(d) {
  var dt = d ? new Date(d) : new Date();
  var m = String(dt.getMonth() + 1).padStart(2, '0');
  var day = String(dt.getDate()).padStart(2, '0');
  return dt.getFullYear() + '-' + m + '-' + day;
}

function formatDuration(msVal) {
  if (msVal == null || msVal < 0) msVal = 0;
  var total = Math.floor(msVal / 1000);
  var h = Math.floor(total / 3600);
  var m = Math.floor((total % 3600) / 60);
  var s = total % 60;
  if (h > 0) return h + ':' + String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  return m + ':' + String(s).padStart(2, '0');
}

function formatClock(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatCredit(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// Coarser than formatDuration: the column headers have room for "23m", not
// "23:41", and a second-by-second idle clock is noise.
function formatMins(msVal) {
  var mins = Math.max(0, Math.round((msVal || 0) / 60000));
  if (mins < 60) return mins + 'm';
  var h = Math.floor(mins / 60);
  var m = mins % 60;
  return m ? h + 'h' + String(m).padStart(2, '0') : h + 'h';
}

function pluralTurns(n) {
  return formatCredit(n) + (n === 1 ? ' turn' : ' turns');
}

/* ---------- state ---------- */

var state = reactive({
  businessDate: todayKey(),
  services: [],
  staff: [],
  /* The tt_days row for `businessDate`, or null where there is none — see the
   * day section near the bottom of this file. Null is not a missing value to be
   * filled in: it is "nobody has opened today", the state that makes the board
   * ask who is in. Both facts on it belong to the salon rather than to a screen,
   * which is why they are here and not in localStorage. */
  day: null,      // { businessDate, startedAt, closedAt } | null
  dayStaff: {},   // staffId -> { present, presentSince, missedTurns }
  turns: [],
  // Today's self check-ins, waiting and dealt with alike — see the check-in
  // section near the bottom of this file, and js/rules/waitlist.js.
  checkins: []
});

var ui = reactive({
  view: 'board',            // 'board' | 'settings' | 'history'
  configTab: 'staff',       // 'staff' | 'services' | 'scheduling' | 'board' | 'theme'
  showNextUp: true,         // per-service "up next" line on the service chips
  partySize: 1,             // Next availability answers for this many, side by side
  checkinWaits: true,       // the lobby check-in screen quotes a wait
  autoStop: true,           // stop a turn when the booking system closes a ticket
  autoStart: true,          // start turns off the book when a client goes in service
  lockOrder: false,         // column order pinned: no dragging names on the board
  hideDoneRows: false,      // fold away turn rows the whole floor has finished
  hideDoneStaff: false,     // fold away the columns of whoever is done for today
  /* Which way round the grid is drawn. 'columns' is the board this console has
   * always had — a column per technician, turns counting down the screen.
   * 'rows' is the same board transposed: a row per technician, turns counting
   * across. Nothing about the day changes with it; it is the same squares read
   * along the other axis, for a salon whose screen is wider than it is tall or
   * whose desk reads a technician's day left to right.
   *
   * Salon-level (prefs, not localStorage) — see the note in js/prefs.js. */
  boardLayout: 'columns',   // 'columns' | 'rows'
  /* How a running square shows that it is running.
   *
   * 'light' is the board this console has always had: a gold light blinking on
   * the left of the timer strip for as long as the service is live. It says
   * *that* something is running and nothing more, which is all a square with no
   * booked length can honestly say.
   *
   * 'bar' fills that same strip left to right as the booked length runs down,
   * so a glance across the board reads how far through everybody is rather than
   * only who is busy. It needs a length to measure against, so a turn with
   * neither a booking nor a usual length falls back to the light — see
   * `turnPct` in js/components/turn-board.js.
   *
   * Salon-level (prefs, not localStorage) — see the note in js/prefs.js. */
  turnProgress: 'light',    // 'light' | 'bar'
  /* ---- the screen this is being read on ----
   *
   * `phone` is measured, not chosen: a hand-held screen gets the compact
   * chrome — one line of top bar, the nav on a bottom bar under the thumb,
   * the service menu behind a button — because the desktop layout spends the
   * whole of a 660px viewport before the first square. See setPhone.
   *
   * `boardMode` is the one part of that the reader overrides. On a phone the
   * grid becomes a list of technicians, one row each, because ten columns do
   * not fit across four inches and a column is what the desk asks about. The
   * grid is still the day's fairness written down, so it stays one tap away —
   * and `auto` brings it back by itself once the screen is turned sideways.
   */
  phone: false,             // hand-held: compact chrome, sheets, bottom bar
  gridFits: false,          // wide enough for the condensed grid (landscape)
  boardMode: 'auto',        // 'auto' | 'list' | 'grid' — phone board shape
  listOrder: 'queue',       // 'queue' | 'board' — how the phone list is sorted
  serviceSheet: false,      // the phone's ＋ Start a turn sheet
  phoneMore: false,         // the phone's ⋯ overflow menu
  phoneList: null,          // { kind: 'due' | 'waiting' } expanded count strip
  endDay: null,             // { step: 'confirm' | 'summary' } — the day's books
  serviceWarning: null,     // { serviceName, reason, names } blocking a booking
  armedServiceId: null,
  // Client requests, off out of the box. Nothing the booking system sends says
  // whether a client asked for the technician by name — Mangomint puts exactly
  // one assigned tech on every service either way — so the board can only ever
  // guess it, and everything downstream of the guess was making claims it could
  // not back up. Off, the whole idea disappears: no stars, no request mode, no
  // "requests" columns. On, it is the board's own record, marked by hand.
  requestsOn: false,
  requestMode: false,       // next booking is a client request — a star, not a weight
  cellMenuTurnId: null,
  staffMenu: null,          // { staffId, x, y } context menu on a column header
  addStaff: null,           // { ids } picking who else is in today
  // { turnId, toStaffId, anchorId } awaiting keep/reset/cancel. `anchorId` is
  // the square it goes in front of — null for the end of the column.
  moveConfirm: null,
  // A square in the air over the board: { turnId, staffId, row }. The column
  // under it is redrawn as it would read if the square landed there, which is
  // the only honest way to show a drop that pushes other work down — see
  // `turnDropPreview`. Written only when the pointer crosses into a different
  // square, never per pixel.
  turnDrag: null,
  swapConfirm: null,        // { turnId, otherId } two squares the book has crossed
  pluginPrompt: null,       // { name, mapped } confirming a plugin switch-off
  missedWalkin: null,       // { partySize, serviceIds } logging a turned-away party
  // { checkinId, clientName, serviceId, serviceName, waited } — the question
  // asked after somebody is taken off the waiting list without being seated:
  // did they leave without being seen? See `dropCheckin`.
  checkinMissed: null,
  skipRemove: null,         // { staffId, ordinal } — taking a red square off
  assign: null,             // assigning a booked service to a tech
  apptUnassignedOnly: true, // hide booked services already on the board
  apptHideCancelled: true,  // keep cancelled visits out of the book pane
  apptRiskOpen: true,       // the Potential conflicts list above the book
  apptDrag: null,           // dragging a booked service onto a running turn
  // A finished booking dropped on a square that already holds a turn, waiting
  // on merge-or-separate: { drag, staffId, turnId, weight }. `drag` is a copy
  // of the card that landed, not the live one — the drag is over by the time
  // the sheet opens. `weight` is what the desk has the combined square counting
  // for, and only means anything to the merge.
  dropChoice: null,
  attachPick: null,         // { turnId } picking an appointment for a turn
  bookClash: null,          // a walk-in that would run into a booking
  orderAsk: null,           // a later service of a visit going up before an earlier one
  autoStartAsk: null,       // { lines: [...] } auto-start found a technician on the visit busy
  apptMenu: null,           // { apptId, svcId, x, y } right-click on a booking
  turnMenu: null,           // { turnId, x, y } right-click on a square
  emptyPick: null,          // { staffId } starting a turn from an empty square
  toast: null,
  dateRolled: false,        // wall clock crossed midnight while open
  loading: true,
  loadError: null
});

var history = reactive({
  from: '', to: '', rows: [], missed: [], dayStaff: [],
  loading: false, error: null, loaded: false
});

// Live mirror of today's book, as the scheduling plugin reports it. Empty and
// untouched whenever that plugin is switched off — everything downstream keys
// off `rows` being empty, so "no plugin" and "nothing booked" render the same.
var appts = reactive({
  open: true,
  rows: [],
  // Which day `rows` is the book for. The board's date can change under it,
  // and a book from another day measured against this day's board is worse
  // than no book at all: everything in it has finished and nothing in it has
  // a square. Anything that compares the two checks this first.
  bookDate: null,
  loading: false,
  error: null,
  lastSync: null,
  serviceMap: {},        // external service id -> tt service id
  optionRules: {},       // external option id -> what it does to a turn
  optionCatalogue: [],   // every option the outside system offers
  optionCatalogueLoaded: false,
  optionFilter: '',
  catalogue: [],         // the outside system's service list, for the mapping tab
  catalogueLoaded: false,
  mapFilter: '',
  unmappedOnly: false,
  ticketClosedAt: {},    // external appointment id -> when its ticket closed
  // external appointment id -> [{ serviceId, staffId, label }], who the settled
  // sale has on each service. Only with a plugin that declares `saleStaff`;
  // see "Writing up the day as it is checked out".
  saleStaff: {},
  // external appointment id -> when the client went in service, as reported by
  // the provider. Stays empty unless the plugin declares `inService`; see the
  // auto-start section for what the board falls back to.
  inServiceAt: {}
});

// Bumped on every ticket refresh so the auto-stop watcher re-runs even when
// the same appointments come back — a close only changes a value, not a key.
var ticketsVersion = ref(0);

/* ---------- settings ----------
 *
 * Two kinds, kept apart on purpose.
 *
 * The SALON's settings — what the board is called, what it looks like, which
 * booking system it talks to, whether it tracks requests, what it does at
 * checkout — live in `tt_settings` and are the same on every station. They are
 * read through `prefs`, which boots from a localStorage mirror so the board
 * comes up with yesterday's answers when the wifi is down, then takes the
 * server's when it arrives. See js/prefs.js.
 *
 * This SCREEN's state — which pane is open, whether the columns are locked,
 * whether finished rows are folded away, whether this station has closed the
 * day — stays local, because it is a fact about a screen. The desk tablet and
 * the wall monitor want different answers, and syncing the column lock would
 * mean one station pinning every other station's board.
 */


try {
  var storedPane = window.localStorage.getItem('dreamspa.dash.apptPane');
  if (storedPane === 'closed') appts.open = false;
  if (window.localStorage.getItem('dreamspa.dash.lockOrder') === 'on') ui.lockOrder = true;
  if (window.localStorage.getItem('dreamspa.dash.hideDoneRows') === 'on') ui.hideDoneRows = true;
  if (window.localStorage.getItem('dreamspa.dash.hideDoneStaff') === 'on') ui.hideDoneStaff = true;
  if (window.localStorage.getItem('dreamspa.dash.apptUnassigned') === 'off') {
    ui.apptUnassignedOnly = false;
  }
  if (window.localStorage.getItem('dreamspa.dash.apptHideCancelled') === 'off') {
    ui.apptHideCancelled = false;
  }
  if (window.localStorage.getItem('dreamspa.dash.apptRisk') === 'closed') {
    ui.apptRiskOpen = false;
  }
  var storedMode = window.localStorage.getItem('dreamspa.dash.boardMode');
  if (storedMode === 'list' || storedMode === 'grid') ui.boardMode = storedMode;
  if (window.localStorage.getItem('dreamspa.dash.listOrder') === 'board') {
    ui.listOrder = 'board';
  }
} catch (e) { /* noop */ }

/* Which way round the grid is drawn, taken from the device mirror here rather
 * than waiting for `applyPrefs` and the server's copy. The board is laid out on
 * the first paint, and coming up in columns for a moment before the salon's
 * real setting lands would turn the whole day over in front of whoever is
 * reading it — the same reason the theme is read before the first paint.
 * `applyPrefs` puts the server's answer over the top of this when it arrives. */
applyBoardLayout(prefs.get('boardLayout'));
applyTurnProgress(prefs.get('turnProgress'));

function setShowNextUp(on) {
  ui.showNextUp = !!on;
  prefs.set('showNextUp', ui.showNextUp);
}

/* How many people Next availability is answering for. Not a pref: it is about
 * whoever is standing at the desk, and tomorrow's first walk-in is one person. */
function setPartySize(n) {
  ui.partySize = partyRules.cleanPartySize(n);
}

/* Whether the lobby's check-in screen quotes a wait.
 *
 * Kept apart from `showNextUp` above even though both are "show the estimate",
 * because they are shown to different people and only one of them is a promise.
 * The desk reads *Next* as a working estimate and knows what it is worth; a
 * client reads "about 25 minutes" as something the salon said. A salon that is
 * happy for its staff to see the arithmetic and not its clients is not being
 * inconsistent, so it gets its own switch — and turning this off does not stop
 * anybody joining the queue, it only stops the screen naming a number.
 */
function setCheckinWaits(on) {
  ui.checkinWaits = !!on;
  prefs.set('checkinWaits', ui.checkinWaits);
}

/* ---------- booking instead of waiting ----------
 *
 * Past some length of wait a waiting list is not a kindness, and a salon would
 * rather have that person booked on a day it can take them. So the lobby screen
 * gets a third answer — see `book` in js/rules/waitlist.js — and this is what
 * governs it: an address, and a number of minutes.
 *
 * Both read through computeds rather than mirrored into `ui`, like the look and
 * the wording and for the same reason: they are read by one screen, which has to
 * redraw when the desk changes them, and a lobby tablet on a wall bracket is the
 * one screen nobody can reload.
 *
 * The link is CLEANED ON THE WAY OUT as well as on the way in. It is written into
 * an `href` on that tablet, and the value comes from a text box on one machine
 * through a database row to another — so the http(s) rule is enforced where it is
 * read, not only where it was typed. See `cleanBookingUrl`.
 */
var checkinBookingUrl = computed(function () {
  return booking.cleanBookingUrl(prefs.get('checkinBookingUrl'));
});

// Whether the link is per-service at all. A plain booking page with no
// placeholder in it is a legitimate setup — every service goes to the same
// place — and it is the only one available to a salon with no booking system
// wired up, so nothing treats the placeholder as required.
var checkinBookingPerService = computed(function () {
  return booking.hasPlaceholder(checkinBookingUrl.value);
});

// The cutoff, or null for a salon that wants everybody offered the queue however
// long the wait is. See `cleanCutoff` for why the absence is storable.
var checkinBookAfter = computed(function () {
  return booking.cleanCutoff(prefs.get('checkinBookAfter'));
});

/* The address for one service: the salon's template with that service's booking
 * id in it. '' whenever there is nowhere to send anybody, which is what makes the
 * cutoff inert until a link is set.
 *
 * Every screen that needs a booking link goes through this — the tablet, and the
 * example line on the settings screen — so what the salon is shown while setting
 * it up is the same string the client will be sent to.
 */
function checkinBookingLink(serviceId) {
  var svc = serviceById.value[serviceId];
  return booking.bookingUrl(checkinBookingUrl.value, svc ? svc.bookingServiceId : '');
}

// Cleaned on the way in as well, so the box reads back the link as it will
// actually be used — a paste with no scheme comes back with one on it, and a
// paste that is not a link at all comes back empty rather than being stored to
// fail silently on a tablet later.
function setCheckinBookingUrl(value) {
  prefs.set('checkinBookingUrl', booking.cleanBookingUrl(value));
}

function setCheckinBookAfter(value) {
  prefs.set('checkinBookAfter', booking.cleanCutoff(value));
}

/* ---------- is this salon sold the lobby screen at all ----------
 *
 * The licence id, in one place, because three screens ask about it — the lobby
 * page itself, its Settings tab, and the desk's waiting list — and a feature
 * behind three separately-typed string literals is a feature that is behind two
 * of them after the first rename.
 *
 * Everything js/rules/licence.js says applies here and is worth repeating,
 * because this is the first gate that is not a module in the switcher: it FAILS
 * OPEN. A project with no licensing schema, a tablet with no internet, a station
 * nobody has signed in — all of those show the check-in screen. Only the
 * database saying, explicitly, that this salon's plan does not include it turns
 * it off. The cost of getting that backwards is a lobby screen that stops
 * working on a bad-wifi Saturday, which is worse than a salon briefly using
 * something they have not bought.
 */
var CHECKIN_FEATURE = 'self-checkin';

// A computed rather than a function, so the answer arriving a moment after the
// first paint redraws what is on screen. `licensing.state` is reactive and the
// entitlement lands a round trip after the session does — without this the
// lobby tablet would show whichever answer it happened to boot with until
// somebody reloaded it, which on a wall mount is nobody.
var checkinLicensed = computed(function () {
  return licensing.licensed(CHECKIN_FEATURE);
});

/* ---------- how the lobby screen looks, and what it says ----------
 *
 * Read straight through `prefs` rather than mirrored into `ui` the way the
 * switches above are, and the difference is where it is read. Those are read on
 * every station forty times a minute; this is read by one screen, which redraws
 * when it changes. Going through the computed means the lobby tablet takes a
 * colour changed at the desk without a reload, rather than keeping whatever it
 * booted with — and a wall-mounted tablet nobody can reach the address bar of is
 * precisely the screen that must not need reloading.
 *
 * The salon's board theme is the second argument: every colour without an
 * override falls back to it, so a salon that sets nothing has a lobby screen
 * already in their brand. See js/rules/checkin-look.js.
 */
var checkinLook = computed(function () {
  return look.resolveLook(prefs.get('checkinLook'), themeApi.resolve(theme));
});

// What has actually been overridden, as stored — what the settings boxes show,
// which is not the same as what the screen draws. A box showing the resolved
// value would look like the salon had set every colour on it.
var checkinLookRaw = computed(function () {
  var v = prefs.get('checkinLook');
  return {
    colors: (v && v.colors) || {},
    text: (v && v.text) || {}
  };
});

var checkinCustomised = computed(function () {
  return look.isCustomised(prefs.get('checkinLook'));
});

/* One field at a time, merged into the stored object.
 *
 * An emptied box deletes its key rather than storing "", and that is the whole
 * of how "back to the theme" works — see `resolveLook`, where an absent value
 * and an empty one both fall through, but only an absent one leaves the row
 * reading as unset on the next station to open Settings.
 */
function setCheckinLook(part, key, value) {
  if (part !== 'colors' && part !== 'text') return;
  var next = {
    colors: Object.assign({}, checkinLookRaw.value.colors),
    text: Object.assign({}, checkinLookRaw.value.text)
  };
  var clean = part === 'colors' ? look.cleanColor(value) : look.cleanText(value);
  if (clean) next[part][key] = clean;
  else delete next[part][key];
  // Nothing set at all goes back to null, so the row reads the same as a salon
  // that never touched it — `checkinCustomised` is what the Reset button hangs
  // off, and an empty-but-present object would leave it offering itself for ever.
  prefs.set('checkinLook', look.isCustomised(next) ? next : null);
}

function resetCheckinLook() {
  prefs.set('checkinLook', null);
  toast('Check-in screen back to the salon’s theme and wording.', 'ok');
}

/* Turning requests off leaves every star already recorded exactly where it is.
 * The flag governs what the board *shows* and what it will let anybody mark
 * from here on; it does not rewrite the day's history, so switching it back on
 * brings last month's stars back with it. Nothing is ever written to clear
 * them — a turn's `requested` only changes when somebody changes it. */
function setRequestsOn(on) {
  ui.requestsOn = !!on;
  if (!ui.requestsOn) ui.requestMode = false;
  prefs.set('requestsOn', ui.requestsOn);
}

/* How long before its start time a booking joins *Due now*.
 *
 * Read through a computed rather than copied into `ui`, so the number the bar
 * uses is always the stored one — a station that has just been told about
 * another station's change re-renders the list rather than keeping the lead it
 * booted with. Anything unreadable falls back to the default: this is a lead
 * time, and a NaN horizon would empty the bar rather than fail loudly.
 */
var dueSoonMinutes = computed(function () {
  var n = Number(prefs.get('dueSoonMinutes'));
  if (!isFinite(n)) return DUE_SOON_DEFAULT_MIN;
  return Math.min(DUE_SOON_LIMITS.max, Math.max(DUE_SOON_LIMITS.min, Math.round(n)));
});

function setDueSoonMinutes(value) {
  var n = Number(value);
  if (!isFinite(n)) return;
  prefs.set('dueSoonMinutes',
    Math.min(DUE_SOON_LIMITS.max, Math.max(DUE_SOON_LIMITS.min, Math.round(n))));
}

/* How far behind a technician can be before the conflicts list says so — see
 * js/rules/conflict.js. Through a computed for the same reason as the lead
 * above: another station's change lands here without a reload. */
var conflictBufferMinutes = computed(function () {
  return conflictRules.cleanBuffer(prefs.get('conflictBufferMinutes'));
});

/* What a sitting has to cost to be a full turn — see js/rules/price-weight.js.
 * Set in the admin app; a computed so another station's change lands here, and
 * re-weighs today's squares, without a reload. */
var fullTurnPrice = computed(function () {
  return cleanCutoff(prefs.get('fullTurnPrice'));
});
Vue.watch(fullTurnPrice, function () { applyPriceWeights(); });

function setConflictBufferMinutes(value) {
  if (String(value).trim() === '' || !isFinite(Number(value))) return;
  prefs.set('conflictBufferMinutes', conflictRules.cleanBuffer(value));
}

/* ---------- theme ---------- */

var theme = reactive(themeApi.load());

function applyTheme() {
  themeApi.apply(theme);
  themeApi.save(theme);
}

function setThemePreset(id) {
  theme.preset = id;
  applyTheme();
}

// An empty or unparseable box clears the override rather than writing a broken
// value, so a half-typed hex never blanks the board mid-keystroke.
function setThemeColor(key, value) {
  var hex = themeApi.clean(value);
  if (hex) theme.custom[key] = hex;
  else delete theme.custom[key];
  applyTheme();
}

function clearThemeCustom() {
  Object.keys(theme.custom).forEach(function (k) { delete theme.custom[k]; });
  applyTheme();
}

// What is actually on screen for a knob, whether it came from the preset or an
// override — so the colour inputs always show the live value.
function themeValue(key) { return themeApi.resolve(theme)[key]; }

var logo = reactive({ value: themeApi.loadLogo(), error: null, busy: false });

async function setLogo(file) {
  logo.busy = true;
  logo.error = null;
  try {
    var read = await themeApi.readLogo(file);
    var saveError = themeApi.saveLogo(read);
    if (saveError) { logo.error = saveError; return false; }
    logo.value = read;
    toast('Logo updated.', 'ok');
    return true;
  } catch (err) {
    logo.error = String(err && err.message ? err.message : err);
    return false;
  } finally {
    logo.busy = false;
  }
}

function clearLogo() {
  themeApi.saveLogo(null);
  logo.value = null;
  logo.error = null;
  toast('Logo removed.', 'ok');
}

// The stored value is left empty until someone sets one, so an unnamed install
// reads as unnamed rather than as somebody else's salon. Everything on screen
// goes through businessName, which supplies the fallback.
var brand = reactive({ name: themeApi.loadBrand() });

var businessName = computed(function () {
  return brand.name || themeApi.BRAND.fallback;
});

/* ---------- the salon's settings, from the server ----------
 * Everything above reads from `prefs`, which starts out holding this device's
 * mirror. These two put the server's copy over the top of it once it lands. */

// Push whatever prefs now holds onto the things that watch their own copy of
// it: the ui switches, the theme (which has to be re-applied to the document),
// the logo and the name in the top bar.
function applyPrefs() {
  ui.showNextUp = prefs.get('showNextUp') !== false;
  ui.checkinWaits = prefs.get('checkinWaits') !== false;
  applyBoardLayout(prefs.get('boardLayout'));
  applyTurnProgress(prefs.get('turnProgress'));
  ui.autoStop = prefs.get('autoStop') !== false;
  ui.autoStart = prefs.get('autoStart') !== false;
  ui.requestsOn = prefs.get('requestsOn') === true;
  // A station left armed for a request when another one switches requests off.
  if (!ui.requestsOn) ui.requestMode = false;
  brand.name = themeApi.cleanBrand(prefs.get('brand'));
  logo.value = themeApi.loadLogo();
  var t = themeApi.load();
  theme.preset = t.preset;
  theme.custom = t.custom;
  themeApi.apply(theme);
}

// Whether a booked client goes to the booked technician rather than to the next
// in turn order. Read from prefs each time, so a change on another station is
// seen without anything having to be reapplied.
function followsBook() {
  return followsBookRule(prefs.get('bookedClientsGo'));
}

// Called once at start-up and again whenever any station changes a setting, so
// a switch flipped at the desk reaches the wall screen without a reload.
// Failure is not worth a toast: the mirror is already on screen, and the next
// realtime event or reload tries again.
async function loadSettings() {
  try {
    prefs.hydrate(await sync.fetchSettings());
    applyPrefs();
    plugins.reload();
  } catch (e) { /* offline, or signed out — the device mirror stands */ }
}

function setBusinessName(value) {
  var v = themeApi.saveBrand(value);
  if (v === brand.name) return v;
  brand.name = v;
  toast(v ? 'Business name saved.' : 'Business name cleared.', 'ok');
  return v;
}

function setAutoStop(on) {
  ui.autoStop = !!on;
  prefs.set('autoStop', ui.autoStop);
  // Turning it back on settles anything that closed while it was off.
  if (ui.autoStop) applyClosedTickets();
}

// No catch-up when this one is switched back on: auto-start acts on things
// happening now, and starting a turn for a client who was seated an hour ago
// would put a service on the board that nobody is performing.
function setAutoStart(on) {
  ui.autoStart = !!on;
  prefs.set('autoStart', ui.autoStart);
}

// Dragging a header is the only way to reorder, so the lock is the whole of
// it: a board set for the day cannot be rearranged by somebody leaning on the
// screen, and the padlock beside the + is how it comes back off.
function setLockOrder(on, quiet) {
  ui.lockOrder = !!on;
  try {
    window.localStorage.setItem('dreamspa.dash.lockOrder', ui.lockOrder ? 'on' : 'off');
  } catch (e) { /* noop */ }
  if (quiet) return;
  toast(ui.lockOrder
    ? 'Board order locked — names cannot be dragged.'
    : 'Board order unlocked — drag a name to move it.', 'ok');
}

function toggleLockOrder() { setLockOrder(!ui.lockOrder); }

/* Which way round the grid is drawn.
 *
 * A view, not a change to the day: the same turns, the same order, the same
 * rotation — read down the screen or across it. Everything that decides what
 * goes in a square is untouched by this, which is why it lives here as one
 * string and nothing below it in this file ever asks about it. Only the board
 * component's geometry and the stylesheet care, and they both read `ui`.
 *
 * The board's list mode on a phone is unaffected: four inches has room for one
 * axis whichever way round the salon has set this, so the list stands.
 */
// Anything that is not the transposed layout is the original one: an unknown
// value written by a later build must not leave the board with no axis.
//
// `board-rows` on <html> alongside it, the same way `is-phone` is set, because
// two things outside the board line up with its first staff column — the
// service chips and the bar's hint line — and they are nowhere near it in the
// document. See setPhone.
function applyBoardLayout(mode) {
  ui.boardLayout = mode === 'rows' ? 'rows' : 'columns';
  try {
    document.documentElement.classList.toggle('board-rows', ui.boardLayout === 'rows');
  } catch (e) { /* noop */ }
}

function setBoardLayout(mode) {
  var v = mode === 'rows' ? 'rows' : 'columns';
  if (v === ui.boardLayout) return;
  applyBoardLayout(v);
  prefs.set('boardLayout', v);
  toast(v === 'rows'
    ? 'Board transposed — a row for each technician.'
    : 'Board back to a column for each technician.', 'ok');
}

/* What a running square does with its timer strip: blink, or fill.
 *
 * A look, not a change to the day — the same rule as the layout above, and for
 * the same reason it lives here as one string that nothing else in this file
 * reads. Only the squares care, and they read `ui`.
 *
 * Anything that is not the bar is the light: an unknown value written by a
 * later build must not leave a live square with no sign of life on it at all.
 */
function applyTurnProgress(mode) {
  ui.turnProgress = mode === 'bar' ? 'bar' : 'light';
}

function setTurnProgress(mode) {
  var v = mode === 'bar' ? 'bar' : 'light';
  if (v === ui.turnProgress) return;
  applyTurnProgress(v);
  prefs.set('turnProgress', v);
  toast(v === 'bar'
    ? 'Running squares now fill as the booked time runs down.'
    : 'Running squares back to a blinking light.', 'ok');
}

// Folding finished rows away is a view, not a change to the day: the turn
// numbers keep their real values, so the gutter reads 4, 5, 9 and the gap
// itself says work is hidden.
function setHideDoneRows(on) {
  ui.hideDoneRows = !!on;
  try {
    window.localStorage.setItem('dreamspa.dash.hideDoneRows', ui.hideDoneRows ? 'on' : 'off');
  } catch (e) { /* noop */ }
}

function toggleHideDoneRows() { setHideDoneRows(!ui.hideDoneRows); }

// The same for the columns of whoever is done for today — a view as well:
// they are still on the day, and marking them back in brings the column back.
function setHideDoneStaff(on) {
  ui.hideDoneStaff = !!on;
  try {
    window.localStorage.setItem('dreamspa.dash.hideDoneStaff', ui.hideDoneStaff ? 'on' : 'off');
  } catch (e) { /* noop */ }
}

function toggleHideDoneStaff() { setHideDoneStaff(!ui.hideDoneStaff); }

/* ---------- which screen this is ----------
 *
 * Two questions, and they are not the same one.
 *
 * PHONE decides the chrome: is there room for the top bar, the service menu
 * and the board, or does the menu have to go behind a button and the nav under
 * the thumb? A landscape phone is still a phone — 390px of height with a
 * browser toolbar in it — so the height clause catches it, qualified by a
 * coarse pointer so a short desktop window is left alone.
 *
 * GRIDFITS decides the board's shape inside that chrome: turned sideways there
 * is room for four or five condensed columns, which is a real board, and the
 * list is the answer to not having that room rather than a thing wanted for
 * its own sake. Hence `auto`.
 *
 * The class on <html> is how the stylesheet is told the same answer. Measuring
 * it twice — once here, once in a media query — is how the two drift apart,
 * and the failure is a bottom bar over a board that has not made room for it.
 */
var PHONE_Q = '(max-width: 719px), (max-height: 560px) and (pointer: coarse)';
var GRID_Q = '(min-width: 700px)';

function setPhone(on, gridFits) {
  ui.phone = !!on;
  ui.gridFits = !!gridFits;
  try {
    document.documentElement.classList.toggle('is-phone', ui.phone);
  } catch (e) { /* noop */ }
  if (ui.phone) closePhoneExtras();
  // The book is a whole screen of its own on a phone and a pane beside the
  // board everywhere else, so `book` is a view only a phone has. Left set, a
  // window dragged wide would render a main with nothing in it.
  else if (ui.view === 'book') ui.view = 'board';
}

// The phone's own transient surfaces, all of which are modal in feel: the
// service sheet, the overflow menu and an expanded count strip. Closed as one
// because Escape, a rotation and a tap outside all mean the same thing.
function closePhoneExtras() {
  ui.serviceSheet = false;
  ui.phoneMore = false;
  ui.phoneList = null;
}

function watchViewport() {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  var phoneQ = window.matchMedia(PHONE_Q);
  var gridQ = window.matchMedia(GRID_Q);
  var read = function () { setPhone(phoneQ.matches, gridQ.matches); };
  read();
  // addListener is the deprecated spelling, and the only one Safari understood
  // until 14 — which is exactly the vintage of tablet that ends up in a lobby.
  [phoneQ, gridQ].forEach(function (q) {
    if (q.addEventListener) q.addEventListener('change', read);
    else if (q.addListener) q.addListener(read);
  });
}

/* The phone board: a list of technicians, or the grid squeezed down.
 *
 * Only ever asked on a phone. Everywhere else the grid is the board and there
 * is nothing to choose between — the toggle is not on screen, so the stored
 * preference cannot be read as an instruction the reader has no way to undo.
 */
var boardIsList = computed(function () {
  if (!ui.phone) return false;
  if (ui.boardMode === 'list') return true;
  if (ui.boardMode === 'grid') return false;
  return !ui.gridFits;
});

function setBoardMode(mode) {
  ui.boardMode = mode;
  try {
    window.localStorage.setItem('dreamspa.dash.boardMode', mode);
  } catch (e) { /* noop */ }
}

// The toggle is a switch between two shapes, not a cycle through three: `auto`
// is where it starts and what a rotation re-reads, but pressing it is somebody
// saying which one they want now, and offering them "decide for me" as the
// third press would put the board back where they just moved it from.
function toggleBoardMode() {
  setBoardMode(boardIsList.value ? 'grid' : 'list');
}

function setListOrder(order) {
  ui.listOrder = order === 'board' ? 'board' : 'queue';
  try {
    window.localStorage.setItem('dreamspa.dash.listOrder', ui.listOrder);
  } catch (e) { /* noop */ }
}

function toggleListOrder() {
  setListOrder(ui.listOrder === 'queue' ? 'board' : 'queue');
}

/* ---------- the two clocks ----------
 *
 * `now` ticks every second because a running square counts in seconds — that
 * is the only thing on screen with a second hand.
 *
 * Everything else moves at the pace of a wall clock: what is due in the next
 * ten minutes, whose booking is next, who the book says is working. Those
 * read `minuteNow`, which only changes as the clock crosses a minute, so a
 * second passing no longer rebuilds the whole book. Pinning them to `now` was
 * costing a full pass over every appointment, every service and every column
 * once a second to produce the same answer sixty times over.
 */
var now = ref(Date.now());
var minuteNow = ref(now.value);

function minuteOf(ms) { return Math.floor(ms / 60000); }

setInterval(function () {
  now.value = Date.now();
  if (minuteOf(now.value) === minuteOf(minuteNow.value)) return;
  minuteNow.value = now.value;
  // Only worth asking once a minute: midnight is a minute boundary, and
  // todayKey() builds a Date and three padded strings every time.
  if (!ui.dateRolled && todayKey() !== state.businessDate) ui.dateRolled = true;
  // A station can miss a note's realtime event (sleeping tablet, dropped socket);
  // asking again every couple of minutes bounds how long that can last.
  if (++notesPollMinutes >= 2) { notesPollMinutes = 0; loadNotes(); }
}, 1000);
var notesPollMinutes = 0;

/* ---------- the support banner ----------
 *
 * Reads `now` rather than `minuteNow`, and it is the only thing besides a
 * running square that does. The rule above is that a value moving at the pace of
 * a wall clock should not rebuild itself sixty times a minute — but this one is
 * a single string, and the minute it is wrong about is the last one, where a
 * label reading "1m" after the session has stopped resolving is exactly the
 * stale reassurance the banner exists to prevent.
 */
var supportState = computed(function () {
  return supportBanner(sync.support, now.value);
});

var toastTimer = null;
function toast(text, kind) {
  ui.toast = { text: text, kind: kind || 'info' };
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(function () { ui.toast = null; }, 2600);
}

/* ---------- business hours ----------
 *
 * When the doors open and shut, one pair of times per weekday. It is the only
 * thing in the console that knows the shape of a day, and two very different
 * parts of the board read it.
 *
 * The MORNING. The board sets itself up off the book — see `openDayFromBook` —
 * and used to do it the instant the book arrived, which on a station left
 * running overnight is a minute past midnight: everyone marked in and the
 * add-staff picker open over an empty salon at 12:01 AM. With hours set it
 * waits until three quarters of an hour before opening, which is when the
 * first technician walks in and the sheet is worth having ready.
 *
 * The EVENING. A walk-in is not free — it runs for as long as the work takes,
 * and Next availability was happy to start a ninety-minute set at twenty to close. So
 * the offer is measured against the closing time: a little over is a question
 * for the technician, and well over is not offered at all. See
 * `closingOverrun`.
 *
 * Nothing is set out of the box, and with nothing set both of those go quiet —
 * the morning behaves exactly as it did, and the evening asks nothing. A day
 * with no hours on it is a day the salon is closed, which is also the answer
 * to "why did the board not set itself up on Sunday".
 */

var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
                 'Friday', 'Saturday'];

// How long before opening the board sets itself up, and how far past closing a
// walk-in may still be offered. Both are deliberately fixed: they are about
// how a salon opens and closes rather than about this salon, and a setting
// nobody would ever change is a setting to read past on the way to the times.
var OPEN_LEAD_MIN = 45;
var CLOSING_GRACE_MIN = 30;

var DEFAULT_OPEN = '09:00';
var DEFAULT_CLOSE = '19:00';

// "9:30" and "09:30" are the same time; anything else is not a time at all.
function cleanClock(v) {
  var m = /^(\d{1,2}):(\d{2})$/.exec(String(v == null ? '' : v).trim());
  if (!m) return null;
  var h = Number(m[1]);
  if (h > 23 || Number(m[2]) > 59) return null;
  return String(h).padStart(2, '0') + ':' + m[2];
}

/* The seven days as the board reads them: null for a day the salon is shut,
 * and null throughout for hours nobody has set. A day needs both ends to
 * count — an opening time with no closing time says nothing about when the
 * last walk-in can start, which is most of what this is for. */
var businessHours = computed(function () {
  var raw = prefs.get('hours');
  var days = raw && raw.days;
  if (!Array.isArray(days)) return null;
  var out = [], any = false;
  for (var i = 0; i < 7; i++) {
    var d = days[i];
    var open = d && cleanClock(d.open);
    var close = d && cleanClock(d.close);
    if (open && close) { out.push({ open: open, close: close }); any = true; }
    else out.push(null);
  }
  return any ? out : null;
});

var hoursSet = computed(function () { return !!businessHours.value; });

/* Today's two instants, or null — which covers both "nobody has said" and
 * "closed today", because neither gives the board a closing time to measure
 * against. Whichever it is, the callers do the same thing: nothing.
 *
 * A closing time at or before the opening time is the small hours of the next
 * morning, not a negative day. */
var todayHours = computed(function () {
  var h = businessHours.value;
  if (!h) return null;
  var key = state.businessDate;
  var p = String(key).split('-');
  var dow = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2])).getDay();
  var d = h[dow];
  if (!d) return null;
  var openAt = clockToMs(key, d.open);
  var closeAt = clockToMs(key, d.close);
  if (openAt == null || closeAt == null) return null;
  if (closeAt <= openAt) closeAt += 86400000;
  return {
    dow: dow, open: d.open, close: d.close,
    openAt: openAt, closeAt: closeAt,
    startAt: openAt - OPEN_LEAD_MIN * 60000
  };
});

// Whether the board may set the day up yet. With no hours it always may,
// which is what it did before there were any. On a day the salon is closed it
// never does — marking a Sunday crew in off a book that should be empty is a
// guess, and the + button is right there for the Sunday that is not.
var dayStartDue = computed(function () {
  if (!hoursSet.value) return true;
  var h = todayHours.value;
  if (!h) return false;
  return minuteNow.value >= h.startAt;
});

/* The seven rows the settings screen edits, padded out of whatever is stored.
 * Raw rather than cleaned: the screen shows what was typed, and cleaning is
 * `businessHours`' job on the way out. */
var hoursRows = computed(function () {
  var raw = prefs.get('hours');
  var days = (raw && Array.isArray(raw.days)) ? raw.days : [];
  var out = [];
  for (var i = 0; i < 7; i++) {
    var d = days[i];
    out.push({
      dow: i, name: DAY_NAMES[i],
      open: (d && d.open) || '', close: (d && d.close) || '',
      closed: !d
    });
  }
  return out;
});

/* One day changed, the other six carried through. A day switched on with no
 * times yet gets the usual ones rather than an empty pair the board would
 * throw away — the point of the switch is to get a row to fill in.
 *
 * Every day closed is not "closed all week", it is hours nobody has set, and
 * it is stored as such: the salon that never fills this in and the salon that
 * fills it in and empties it again both end up where they started. */
function setDayHours(dow, patch) {
  var days = hoursRows.value.map(function (r, i) {
    var open = r.open, close = r.close, closed = r.closed;
    if (i === dow) {
      if ('open' in patch) open = patch.open;
      if ('close' in patch) close = patch.close;
      if ('closed' in patch) closed = !!patch.closed;
    }
    if (closed) return null;
    return { open: open || DEFAULT_OPEN, close: close || DEFAULT_CLOSE };
  });
  prefs.set('hours', days.some(Boolean) ? { days: days } : null);
}

// Copies one day's times onto every day that is open. The salon that keeps the
// same hours all week is the common one, and seven identical rows typed by
// hand is seven chances to typo one of them.
function copyHoursToOpenDays(dow) {
  var rows = hoursRows.value;
  var from = rows[dow];
  if (!from || from.closed) return false;
  prefs.set('hours', {
    days: rows.map(function (r) {
      return r.closed ? null : { open: from.open, close: from.close };
    })
  });
  toast('Those hours are now on every open day.', 'ok');
  return true;
}

function clearBusinessHours() {
  prefs.set('hours', null);
  toast('Business hours cleared.', 'ok');
}

/* ---------- row builders (camel -> snake) ---------- */

function serviceRow(s) {
  return {
    id: s.id, name: s.name,
    // The store spells "no key" as the empty string and the column spells it as
    // null — `char_length(shortcut) = 1` would refuse `''`. This is the one
    // place the two meet.
    shortcut: s.shortcut || null,
    abbrev: s.abbrev,
    turn_weight: s.turnWeight, quick_add: s.quickAdd !== false,
    self_checkin: s.selfCheckin !== false,
    // Empty is stored as null rather than as '': the column means "the booking
    // system's id for this", and a row full of empty strings reads as one that
    // has been filled in with nothing.
    booking_service_id: s.bookingServiceId || null,
    default_minutes: s.defaultMinutes == null ? null : s.defaultMinutes,
    same_time: s.sameTime || null,
    color: s.color, sort_order: s.sortOrder, active: s.active
  };
}

function staffRow(s) {
  return {
    id: s.id, name: s.name, service_ids: s.serviceIds,
    sort_order: s.sortOrder, active: s.active,
    own_clients_only: !!s.ownClientsOnly
  };
}

/* One of the other services folded into a square, on its way to the column.
 *
 * Times go out as epoch numbers rather than as ISO strings, unlike every other
 * timestamp in this file. They are inside a jsonb value, so nothing on the
 * server reads them as timestamps — Postgres would keep whatever it was handed
 * and hand it back, and a string that only some rows have would be one more
 * thing for `mapTurn` to guess at. A number means the same thing everywhere.
 */
function mergedRow(m) {
  return {
    appointmentId: m.appointmentId || null,
    appointmentServiceId: m.appointmentServiceId || null,
    sourceServiceId: m.sourceServiceId == null ? null : m.sourceServiceId,
    clientId: m.clientId == null ? null : m.clientId,
    clientName: m.clientName || '',
    sourceServiceName: m.sourceServiceName || '',
    serviceId: m.serviceId || null,
    serviceName: m.serviceName || '',
    serviceLetter: m.serviceLetter || '',
    turnWeight: m.turnWeight == null ? 0 : m.turnWeight,
    startedAt: m.startedAt || null,
    endedAt: m.endedAt || null,
    options: (m.options || []).map(function (o) { return { id: o.id, name: o.name }; }),
    // Folded on automatically as an add-on, rather than merged by the desk with
    // a weight of its own. Only these count toward the square's price.
    addOn: !!m.addOn
  };
}

function turnRow(t) {
  return {
    id: t.id, business_date: t.businessDate, staff_id: t.staffId,
    staff_name: t.staffName,
    service_id: t.serviceId, service_name: t.serviceName,
    service_letter: t.serviceLetter, turn_weight: t.turnWeight,
    requested: t.requested,
    waiting: !!t.waiting,
    started_at: new Date(t.startedAt).toISOString(),
    ended_at: t.endedAt ? new Date(t.endedAt).toISOString() : null,
    sort_at: new Date(t.sortAt).toISOString(),
    appointment_id: t.appointmentId || null,
    appointment_service_id: t.appointmentServiceId || null,
    source_service_id: t.sourceServiceId == null ? null : t.sourceServiceId,
    client_id: t.clientId == null ? null : t.clientId,
    client_name: t.clientName || null,
    source_service_name: t.sourceServiceName || null,
    options: (t.options || []).map(function (o) {
      return { id: o.id, name: o.name };
    }),
    // The other booked services sharing this square. Written out field by field
    // rather than sent as-is so a stray key picked up in the browser cannot
    // reach the column; see `mergedRow`.
    extras: (t.extras || []).map(mergedRow),
    scheduled_minutes: t.scheduledMinutes == null ? null : t.scheduledMinutes,
    scheduled_end_at: t.scheduledEndAt ? new Date(t.scheduledEndAt).toISOString() : null,
    // Where the clock was told to start, on the rare square somebody was asked
    // about. Null on both for everything else; see the columns' own note in
    // server/schema/02-console-tables.sql.
    start_choice: t.startChoice || null,
    start_booked_at: t.startBookedAt ? new Date(t.startBookedAt).toISOString() : null
  };
}

function dayStaffRow(staffId, d) {
  return {
    business_date: state.businessDate, staff_id: staffId, present: d.present,
    present_since: new Date(d.presentSince).toISOString(), missed_turns: d.missedTurns,
    skipped: !!d.skipped, skip_reason: d.skipReason || null,
    skips: (d.skips || []).slice(),
    skip_why: (d.skipWhy || []).slice(0, (d.skips || []).length),
    // Null for either end nobody has set: the defaults are worked out from the
    // salon's hours and the sheet, and storing them would freeze this
    // morning's guess into the row and quietly outlive the hours it came from.
    shift_start_at: d.shiftStartAt == null ? null : new Date(d.shiftStartAt).toISOString(),
    shift_end_at: d.shiftEndAt == null ? null : new Date(d.shiftEndAt).toISOString(),
    skip_spans: (d.skipSpans || []).map(function (s) {
      return {
        from: new Date(s.from).toISOString(),
        to: s.to == null ? null : new Date(s.to).toISOString()
      };
    }),
    // The sweep's own note of which shift end it has acted on, and the desk's
    // dismissal of the booked-but-not-in notice. Both were per-station; see
    // `sweepDoneForDay` and `dismissMissingBooked`.
    done_swept_at: d.doneSweptAt == null ? null : new Date(d.doneSweptAt).toISOString(),
    missing_dismissed: !!d.missingDismissed,
    // The desk's word on own-clients-only for today, or null for "as usual" —
    // see js/rules/own-clients.js.
    own_clients_only: d.ownClientsOnly == null ? null : !!d.ownClientsOnly,
    // What the board has done with Mangomint's day for them — see
    // js/rules/work-hours.js and schema part 17.
    work_hours: workHoursRow(d.workHours)
  };
}

/* One line of the console's memory of the book. Small enough that the whole row
 * goes every time, like every other upsert here — a patch would need the row to
 * exist already, and the first time the board sees a booking it does not. */
function bookSeenRow(svcId, m) {
  var row = {
    business_date: state.businessDate,
    appointment_service_id: Number(svcId),
    appointment_id: m.appointmentId,
    seen_start_at: m.seenStartAt == null ? null : new Date(m.seenStartAt).toISOString(),
    in_service: !!m.inService,
    late_arrival: !!m.lateArrival
  };
  /* Only ever written, never cleared. This table has no realtime, so another
   * station's copy of the row may not know the service was written up — and a
   * whole-row upsert from there carrying a null would unsay it. Leaving the
   * column out of the row leaves the server's value alone. */
  if (m.writtenUpAt) row.written_up_at = new Date(m.writtenUpAt).toISOString();
  return row;
}

function pushService(s) { sync.enqueue({ table: 'tt_services', type: 'upsert', row: serviceRow(s) }); }
function pushStaff(s) { sync.enqueue({ table: 'tt_staff', type: 'upsert', row: staffRow(s) }); }
function pushTurn(t) { sync.enqueue({ table: 'tt_turns', type: 'upsert', row: turnRow(t) }); }
function pushBookSeen(svcId) {
  var m = bookSeen[svcId];
  if (!m) return;
  sync.enqueue({
    table: 'tt_book_seen', type: 'upsert', row: bookSeenRow(svcId, m),
    onConflict: 'tenant_id,business_date,appointment_service_id'
  });
}

function pushDayStaff(id) {
  var d = state.dayStaff[id];
  if (d) {
    sync.enqueue({
      table: 'tt_day_staff', type: 'upsert', row: dayStaffRow(id, d),
      onConflict: 'tenant_id,business_date,staff_id'
    });
  }
}

/* The whole row every time, both columns, rather than a patch of the one that
 * moved. An upsert is what makes opening the day and closing it the same write
 * — the first station of the morning has no row to patch — and sending
 * `closed_at: null` explicitly is what makes reopening stick, where omitting it
 * would leave yesterday evening's close in place. */
function dayRow(d) {
  return {
    business_date: d.businessDate,
    started_at: new Date(d.startedAt).toISOString(),
    closed_at: d.closedAt == null ? null : new Date(d.closedAt).toISOString()
  };
}

function pushDay() {
  if (!state.day) return;
  sync.enqueue({
    table: 'tt_days', type: 'upsert', row: dayRow(state.day),
    onConflict: 'tenant_id,business_date'
  });
}

/* ---------- load ---------- */

// Only the parts that came back. A partial reload leaves the other arrays
// alone, which matters as much for rendering as for the network: replacing
// an array hands every computed that reads it a new identity and redraws the
// board, even when the rows are identical to the ones already there.
// The date whose squares this station has actually read from the server. A pending
// item judges "the square has gone" by these, permanently and for every station, so
// nothing weaker will do: `ui.loading` only says no load is running, a failed first
// load leaves no squares at all, and a cached snapshot may be hours old.
var turnsLoadedFor = ref(null);
function squaresAreRead() { return turnsLoadedFor.value === state.businessDate; }

// `serverDate` is the date a server read was for. Left out for a cached snapshot.
function applySnapshot(data, serverDate) {
  if (data.services) state.services = data.services;
  if (data.staff) state.staff = data.staff;
  if (data.dayStaff) state.dayStaff = data.dayStaff;
  if (data.turns) {
    state.turns = data.turns;
    turnsLoadedFor.value = serverDate || null;
  }
  if (data.checkins) state.checkins = data.checkins;
  // Replaced wholesale rather than merged: it is a mirror of one table for one
  // date, and a merge would keep yesterday's lines alive across a date roll.
  if (data.bookSeen) replaceBookSeen(data.bookSeen);
  // Tested for presence rather than truth, alone among these. The others are
  // an array or an object either way, so a falsy value means "this part was
  // not fetched"; `day` is legitimately null on every date nobody has opened,
  // and reading that as "not fetched" would leave a stale day on the board
  // across a date change — the one case where it matters most.
  if ('day' in data) state.day = data.day;
}

function cacheSnapshot() {
  sync.saveCache({
    businessDate: state.businessDate,
    data: {
      services: state.services, staff: state.staff,
      // Offline, a station that has closed the books should still show them
      // closed, and one that has already asked who is in should not ask again.
      day: state.day,
      dayStaff: state.dayStaff, turns: state.turns,
      // In the offline snapshot with the rest of the day: the people standing
      // in the waiting area do not stop standing there because the wifi did.
      checkins: state.checkins,
      // And so is what the board has already seen in the book. A station that
      // comes back offline still knows which bookings it has reacted to, which
      // is the difference between a quiet reconnection and one that treats the
      // whole morning as news.
      bookSeen: bookSeen
    }
  });
}

// Who the station is, as js/rules/session.js means it — see the note there for
// why 'checking' is a third answer rather than a kind of signed out.
function authKey() {
  if (sync.auth.checking) return 'checking';
  return sync.auth.user ? 'user:' + sync.auth.user.id : 'anon';
}

/* What the last full load ran as.
 *
 * The board's own tables are authenticated-only, so a load that went out
 * without a session came back with nothing — legitimately, and indistinguishably
 * from a salon that has not set up. Recording who asked is what lets the auth
 * watcher tell "empty because nobody is signed in" from "empty because that is
 * the answer", and ask again when the first stops being true.
 */
var loadedAs = null;

/* A new square's id. One for a booked service is worked out from the booking,
 * so every screen signed in as this station writes the same row when they all
 * put it up at once — see js/rules/turn-id.js. Random otherwise, and random too
 * if that id is already on the board: a square detached from its booking keeps
 * the id it was made with, and a new square for the booking must not land on
 * it. */
function turnIdFor(link) {
  if (!link || link.appointmentServiceId == null) return sync.uuid();
  var id = turnIdForBooking(tenant.get(), link.appointmentServiceId);
  return turnOf(id) ? sync.uuid() : id;
}

/* Another screen put up the square for a booking first, and this one's was
 * refused as a duplicate. The server's square is the one that stands, so the
 * day's squares are read back and this screen's copy gives way to it. */
sync.onTurnRaceLost(function () { load(false, ['turns']); });

async function load(showSpinner, parts) {
  if (showSpinner) ui.loading = true;
  // Captured before the request, not after: this is the identity the rows come
  // back under, and awaiting the fetch is exactly when a sign-in can land.
  //
  // `ranAs` is this one request; `loadedAs` is the last FULL load and is what
  // the auth watcher compares against. They are not the same question — a
  // partial refetch must not overwrite the second — but "did this request go
  // out with a session" has to be asked of the request that actually failed.
  var ranAs = authKey();
  if (!parts) loadedAs = ranAs;
  // A full read is a catch-up, whoever asked for it — see js/rules/catch-up.js.
  if (!parts) lastCatchUpAt = Date.now();
  try {
    var readFor = state.businessDate;
    var data = await sync.fetchParts(readFor, parts);
    applySnapshot(data, readFor);
    heard.board = Date.now();
    // Between the server's answer and the cache, so a close adopted off the
    // old marker goes into the snapshot with the rest of the day.
    if (!parts) adoptLegacyDayClosed();
    cacheSnapshot();
    sync.status.online = true;
    ui.loadError = null;
  } catch (err) {
    /* A signed-out load did not fail, it was refused — see
     * shouldReportLoadFailure. Nothing on screen should mention it: the gate is
     * already up, and it says why better than a banner behind it could. */
    if (!shouldReportLoadFailure(ranAs)) return;

    // Fall back to the last good snapshot so the floor still has a board —
    // but only when this was a full load. A failed partial refetch must not
    // drag three other tables back to whatever the cache last held; what is
    // already in memory is fresher than that.
    var cached = !parts && sync.loadCache();
    if (cached && cached.businessDate === state.businessDate) {
      applySnapshot(cached.data);
      ui.loadError = 'Offline — showing the last synced board.';
    } else if (!parts) {
      ui.loadError = 'Could not reach Supabase: ' + (err.message || err);
    }
    // Only a real failure says anything about the connection. A refusal is the
    // server answering, so calling it offline would put the sync pill on
    // Offline on a station whose network is perfect.
    sync.status.online = false;
  } finally {
    ui.loading = false;
  }
}

// Which slice of the day each table is. A turn changing says nothing about
// the roster or the service list, so there is no reason to pull those back.
var PART_OF_TABLE = {
  tt_turns: 'turns',
  tt_day_staff: 'dayStaff',
  tt_days: 'day',
  tt_staff: 'staff',
  tt_services: 'services',
  tt_checkins: 'checkins'
};

var refetchTimer = null;
var refetchParts = {};

var settingsRefetchTimer = null;

function scheduleRefetch(table) {
  /* The settings are not a slice of the day: they have their own fetch, and
     unlike the day's tables they are safe to reload with writes still in the
     queue — hydrate leaves any key this station has not managed to send yet
     exactly where it is. So this one goes first, before the guard below. */
  if (table === 'tt_appt_requests') { loadApptRequests(); return; }
  if (table === 'tt_settings') {
    if (settingsRefetchTimer) clearTimeout(settingsRefetchTimer);
    settingsRefetchTimer = setTimeout(function () {
      settingsRefetchTimer = null;
      loadSettings();
    }, 400);
    return;
  }
  // Ignore remote echoes while our own writes are still in flight, otherwise
  // a refetch could briefly roll back an optimistic change.
  if (sync.hasPendingWrites()) return;
  // An unrecognised table means we cannot say what moved, so reload the lot.
  var part = PART_OF_TABLE[table];
  if (part) refetchParts[part] = true;
  else {
    refetchParts = {
      services: true, staff: true, day: true, dayStaff: true, turns: true,
      checkins: true
    };
  }

  if (refetchTimer) clearTimeout(refetchTimer);
  refetchTimer = setTimeout(function () {
    refetchTimer = null;
    var parts = Object.keys(refetchParts);
    refetchParts = {};
    load(false, parts);
  }, 400);
}

var apptRefetchTimer = null;
// `kind` is semantic, not a table: providers translate their own tables into
// 'book' or 'checkout' before calling this. A checkout only moves ticket
// state, so reloading the whole book for it would be wasteful.
function scheduleApptRefetch(kind) {
  if (kind === 'checkout') { scheduleTicketRefetch(); return; }
  if (kind === 'requests') { scheduleBookedRequestsRefetch(); return; }
  if (kind === 'status') { scheduleBookStatusRefetch(); return; }
  if (kind === 'shifts') { scheduleWorkHoursRefetch(); return; }
  if (apptRefetchTimer) clearTimeout(apptRefetchTimer);
  apptRefetchTimer = setTimeout(function () {
    apptRefetchTimer = null;
    // The book, and who is in a chair. Not the tickets: editing a booking
    // cannot settle one, and a settlement arrives as its own 'checkout'
    // event with its own refetch. Asking for them here doubled the queries
    // behind every booking change in the salon.
    loadAppointments();
    loadInService();
  }, 500);
}

// The poll replaces a day's rows a few at a time, so one change is several
// events; the timer folds them into a single read.
var bookedRequestsRefetchTimer = null;
function scheduleBookedRequestsRefetch() {
  if (bookedRequestsRefetchTimer) clearTimeout(bookedRequestsRefetchTimer);
  bookedRequestsRefetchTimer = setTimeout(function () {
    bookedRequestsRefetchTimer = null;
    loadBookedRequests();
  }, 500);
}

// The poll rewrites a provider's shift rows together, so one change is
// several events; folded into one read the same way.
var workHoursRefetchTimer = null;
function scheduleWorkHoursRefetch() {
  if (workHoursRefetchTimer) clearTimeout(workHoursRefetchTimer);
  workHoursRefetchTimer = setTimeout(function () {
    workHoursRefetchTimer = null;
    loadWorkHours();
  }, 500);
}

var ticketRefetchTimer = null;
function scheduleTicketRefetch() {
  if (ticketRefetchTimer) clearTimeout(ticketRefetchTimer);
  ticketRefetchTimer = setTimeout(function () {
    ticketRefetchTimer = null;
    loadTickets();
  }, 300);
}

var boardChannel = null;
var notesChannel = null;
var apptChannel = null;
var pendingChannel = null;

function unsubscribeScheduling() {
  if (!apptChannel) return;
  // Through the provider that opened it: the handle is whatever that plugin
  // wanted it to be, and only it knows how to close one.
  var p = scheduling.provider.value;
  if (p && p.unsubscribe) p.unsubscribe(apptChannel);
  else sync.removeChannel(apptChannel);
  apptChannel = null;
}

function subscribeScheduling() {
  unsubscribeScheduling();
  if (!scheduling.can('realtime')) return;
  var p = scheduling.provider.value;
  if (p && p.subscribe) {
    apptChannel = p.subscribe(scheduleApptRefetch, function () { catchUp('rejoin'); });
  }
}

function resubscribe() {
  if (boardChannel) { sync.removeChannel(boardChannel); boardChannel = null; }
  boardChannel = sync.subscribe(scheduleRefetch, function () { catchUp('rejoin'); });
  if (pendingChannel) { sync.removeChannel(pendingChannel); pendingChannel = null; }
  pendingChannel = sync.subscribePendingMoves(schedulePendingRefetch);
  if (notesChannel) { sync.removeChannel(notesChannel); notesChannel = null; }
  notesChannel = sync.subscribeNotes(loadNotes, loadNotes);
  subscribeScheduling();
}

/* ---------- catching up without being told ----------
 *
 * Realtime only says what moved while the socket is up. A rejoin, a tab coming
 * back, the network returning, or simply a long quiet spell all mean this
 * station may have missed something, so it reads the board and the book again.
 * When, and how often, is js/rules/catch-up.js; this is the plumbing.
 *
 * Seen live on 2026-10-31: the Mac front desk's last read was 16:17 UTC and it
 * went on seating clients until 17:36 against a book that had since been
 * re-made, putting up two squares for Allison's party that the Windows station
 * had already put up on the right lines.
 */

// When a read last came back: the board's tables, and the book.
var heard = reactive({ board: 0 });
var lastCatchUpAt = 0;

// The older of the two, since either one being stale is a stale board. The
// book only counts when there is one to read.
function lastHeardAt() {
  var at = heard.board || 0;
  if (scheduling.enabled.value && appts.lastSync) at = Math.min(at || appts.lastSync, appts.lastSync);
  return at || null;
}

function catchUp(reason) {
  if (!sync.auth.user) return;
  var now = Date.now();
  if (!shouldCatchUp({ reason: reason, now: now, lastCatchUpAt: lastCatchUpAt,
                       lastHeardAt: lastHeardAt() })) return;
  lastCatchUpAt = now;
  // Same rule as a realtime echo: a reload with this station's own writes still
  // queued could roll them back on screen. Send them; the next pass reads.
  if (sync.hasPendingWrites()) sync.flush();
  else load(false);
  loadApptRequests();
  // The book is read-only here, so it is always safe to read again.
  if (scheduling.enabled.value) {
    loadAppointments();
    loadInService();
    loadTickets();
    loadBookStatus();
    loadBookedRequests();
    // Lost events include the desk's pending items, and nothing else re-reads them.
    // Desk notes keep their own poll and rejoin.
    loadPendingMoves();
  }
}

var CATCH_UP_TICK_MS = 15 * 1000;
var catchUpWired = false;
function wireCatchUp() {
  if (catchUpWired) return;
  catchUpWired = true;
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) catchUp('visible');
  });
  window.addEventListener('online', function () { catchUp('online'); });
  setInterval(function () { catchUp('tick'); }, CATCH_UP_TICK_MS);
}

// The notice for a board that has not heard from the server in a while.
var staleBoardNote = computed(function () {
  return sync.auth.user ? staleNote(minuteNow.value, lastHeardAt()) : null;
});

// Guarded: the tracker page mounts again every time the console switches back
// to it, and a second init would re-subscribe and re-watch.
var inited = false;

async function init() {
  if (inited) return;
  inited = true;
  var cached = sync.loadCache();
  if (cached && cached.businessDate === state.businessDate) applySnapshot(cached.data);
  await load(true);
  sync.flush();
  wireCatchUp();

  // Realtime bindings are registered server-side when the channel joins and
  // are evaluated against the token the socket holds at that moment. Joining
  // before the stored session has been restored registers them as anon, and
  // RLS then silently filters out every event on these tables — the channel
  // still reports "joined". So wait for auth to settle, and re-subscribe on
  // every sign-in or sign-out.
  Vue.watch(
    authKey,
    function (key) {
      if (key === 'checking') return;
      resubscribe();

      /* The board's own tables — the roster, the services, today's turns — are
       * authenticated-only too, so a load that ran before there was a session
       * fetched an empty day. Signing in has to ask again, or the station sits
       * in front of an empty board having just watched itself sign in.
       *
       * Which transitions count is in js/rules/session.js, with a test per
       * case: notably NOT sign-out, which leaves the last snapshot up, and NOT
       * the ordinary boot, where the session is already back before init()
       * loads and a second fetch would be for nothing. */
      if (shouldReloadBoard(key, loadedAs)) load(true);

      if (sync.auth.user) {
        // tt_settings is authenticated-only like everything else, so the
        // salon's copy can only be asked for once there is a session. Until
        // then — and for ever, on a station with no internet — the device
        // mirror read at boot is what the board is running on.
        loadSettings();
        loadScheduling();
        loadApptRequests();
        loadNotes();
        loadBookedRequests();
        loadBookStatus();
        loadWorkHours();
        loadMissedWalkins();
      } else {
        clearSchedulingData();
        missed.rows = [];
      }
    },
    { immediate: true }
  );

  /* Which salon this is arrives AFTER the session does, and the channels above
   * were opened without it.
   *
   * `authKey` changes the moment there is a user; the tenant comes from a
   * second round trip to tenant_members and lands some time later. So the
   * subscribe that just ran used whatever tenant.js had cached — null on a
   * station's very first sign-in, and the PREVIOUS salon on a station that has
   * been used by another one.
   *
   * Neither announces itself. A channel filtered `tenant_id=eq.<the wrong
   * salon>` joins perfectly happily and then delivers nothing at all, so the
   * board and the appointments pane go quietly stale until somebody reloads the
   * page — the exact failure the filter was added to avoid, arrived at from the
   * other side. Rebind when the answer comes in.
   *
   * Not folded into `authKey`: the user and the tenant settle at different
   * moments, and the board should have a channel from the first of them rather
   * than nothing until the second. */
  Vue.watch(
    function () { return sync.auth.tenantId; },
    function () {
      // Same reason the watcher above sits out 'checking': bindings are
      // registered against the token the socket holds as it joins, and joining
      // before the stored session is back registers them as anon.
      if (sync.auth.checking) return;
      resubscribe();
      /* The closed-day marker used to be re-read here: it was per tenant in
       * localStorage, and the namespace it lived under only became known at
       * this point. It is a row on tt_days now, so the load that follows a
       * tenant change brings it back with the rest of the day and there is
       * nothing left to re-read. */
    }
  );

  // Switching the plugin on or off mid-session takes effect immediately:
  // subscribe and load, or drop the channel and forget the book.
  Vue.watch(
    function () { return scheduling.enabled.value ? (scheduling.plugin.value.id || 'on') : ''; },
    function (on) {
      subscribeScheduling();
      if (on) loadScheduling();
      else clearSchedulingData();
    }
  );

  // Open the day off the book once both the board and the book have arrived,
  // and the clock has reached three quarters of an hour before opening.
  // Watches the date too, so a station left running overnight opens the new
  // day the same way — at the hour it should, rather than at one past
  // midnight when the book for it first lands.
  Vue.watch(
    function () {
      return state.businessDate + '|' + (ui.loading ? 'loading' : 'ready') +
        '|' + appts.rows.length + '|' + (dayStartDue.value ? 'due' : 'early') +
        '|' + Object.keys(mmDayByStaff.value).length + '|' + workHours.loadedFor;
    },
    openDayFromBook,
    { immediate: true }
  );

  /* The other thing the clock does to the board by itself: marking people done
   * when their day ends. See `sweepDoneForDay`.
   *
   * Keyed rather than watching `minuteNow` alone, because the minute is only
   * one of the three things that can make it true. The other two are a square
   * finishing — the second condition, and the one that usually fires it, since
   * an out time passes while somebody is mid-service far more often than it
   * passes on an empty column — and the desk typing a different out time. A
   * plain minute watcher would sit on its hands for up to fifty-nine seconds
   * with a technician standing at a finished square. */
  Vue.watch(
    function () {
      return minuteNow.value + '|' + (ui.loading ? 'loading' : 'ready') +
        '|' + presentStaff.value.map(function (s) {
          var d = state.dayStaff[s.id];
          return s.id + ':' + (d && d.shiftEndAt) + ':' + (shiftOf(s.id) || {}).endAt + ':' +
            (isBusy(s.id) ? 1 : 0) +
            ':' + (isSkipped(s.id) ? 1 : 0);
        }).join(',');
    },
    sweepDoneForDay,
    { immediate: true }
  );

  /* Mangomint's day: somebody with a shift and no place on the board goes on
   * it, and a start that has come — or moved — has its not-in count worked out.
   * See `sweepWorkHours`. Keyed on the starts as well as the minute, so a
   * shift moved in Mangomint is acted on as it lands. */
  Vue.watch(
    function () {
      var days = mmDayByStaff.value;
      return minuteNow.value + '|' + (ui.loading ? 'loading' : 'ready') + '|' +
        (dayOpened.value ? 1 : 0) + '|' + Object.keys(days).map(function (id) {
          var sh = shiftOf(id);
          return id + ':' + (sh ? sh.startAt : '') + ':' + (state.dayStaff[id] ? 1 : 0);
        }).join(',');
    },
    sweepWorkHours,
    { immediate: true }
  );

  /* A new business date starts with nothing dismissed and nothing swept:
   * yesterday's "not today" says nothing about who is in this morning, and
   * yesterday's five o'clock finish says nothing about today's.
   *
   * Nothing to clear for either any more. Both live on the day-staff row, which
   * is keyed by business date — the new day simply has no rows yet, and the
   * load that follows brings the right ones. The same goes for what the console
   * has seen in the book. That is the point of moving them: a fact about a day
   * cannot leak into the next one, and no station has to remember to forget.
   */
  Vue.watch(
    function () { return state.businessDate; },
    function () {
      // Only the one-poll snapshot and the toast notes; see `resetAutoStart`.
      resetAutoStart();
      missed.rows = [];
      loadMissedWalkins();
      // Yesterday's requests were about yesterday's clients.
      apptRequests.rows = {};
      loadApptRequests();
      // Notes are not about a day, but a note that ended overnight is still
      // in the state until something asks again.
      loadNotes();
      bookedRequests.byKey = {};
      loadBookedRequests();
      bookStatus.byAppt = {};
      loadBookStatus();
      // Yesterday's shifts were yesterday's.
      workHours.byExternal = {};
      loadWorkHours();
      // And so is the book. The date can move without a page reload — a
      // station left running overnight rolls over at the banner — and nothing
      // used to refetch it, so the console went on holding yesterday's
      // bookings against this morning's empty board. Every service in them
      // had finished and none of them had a square, which is how a day that
      // has not started yet offers to write up fifty-three of them.
      appts.rows = [];
      appts.bookDate = null;
      appts.ticketClosedAt = {};
      appts.saleStaff = {};
      appts.inServiceAt = {};
      loadAppointments();
      loadTickets();
      loadInService();
    }
  );

  // A square dragged or taken off changes no book and no ticket, so nothing else
  // would notice. The minute is for the grace running out.
  Vue.watch(
    function () {
      if (!pendingMoves.value.length) return '';
      return minuteNow.value + '|' + state.turns.map(function (t) { return t.staffId; }).join();
    },
    reconcilePendingMoves
  );

  // Re-check the closed tickets whenever they refresh, and whenever the set of
  // running turns that could be settled by one changes — so a turn linked to
  // an already-closed appointment stops the moment it is linked.
  Vue.watch(
    function () {
      return ticketsVersion.value + '|' + state.turns
        .filter(function (t) {
          return t.endedAt == null && (t.appointmentId || t.appointmentServiceId);
        })
        .map(function (t) { return t.id; })
        .join(',');
    },
    applyClosedTickets
  );

  // Writing up what was checked out: on every ticket refresh, every new book,
  // whenever a booked square comes or goes, and whenever somebody comes on to
  // the board — a sale naming a technician marked in late is written up the
  // moment they are. See `autoWriteUp`.
  Vue.watch(
    function () {
      if (!scheduling.enabled.value) return '';
      return [
        ticketsVersion.value, appts.lastSync, appts.bookDate, ui.loading,
        dayClosed.value,
        Object.keys(turnByApptService.value).length,
        Object.keys(state.dayStaff).filter(function (id) {
          return state.dayStaff[id] && state.dayStaff[id].present;
        }).sort().join(',')
      ].join('|');
    },
    autoWriteUp
  );

  // Auto-start watches which appointments are in service and what the book
  // says each service starts at. The clock comes in for one thing only: a
  // client checked in a few minutes early, whose booked time arriving is the
  // seating (js/rules/auto-start.js, `earlyCheckIn`). Otherwise time passing
  // on its own never starts a turn.
  Vue.watch(
    function () {
      if (!scheduling.enabled.value) return '';
      var seated = Object.keys(inServiceAt.value).sort().join(',');
      var t = minuteNow.value;
      var due = [];
      var book = appts.rows.map(function (a) {
        var checkedInAt = checkedInAtFor(a.id);
        return a.id + ':' + a.services.map(function (s) {
          if (earlyCheckIn(s.startsAt, checkedInAt, t) === 'due') due.push(s.id);
          return s.id + '@' + s.startsAt;
        }).join(',');
      }).join('|');
      return seated + '#' + book + '#' + due.join(',');
    },
    autoStartFromBook,
    { immediate: true }
  );
}

/* ---------- derived ---------- */

var activeServices = computed(function () {
  return state.services.filter(function (s) { return s.active; })
    .slice().sort(function (a, b) { return a.sortOrder - b.sortOrder; });
});

// What the Next availability bar shows. Everything else — the empty-square modal, the
// assign sheet, change-service, the hotkeys — still offers every active
// service; this only thins out the bar.
var quickAddServices = computed(function () {
  return activeServices.value.filter(function (s) { return s.quickAdd !== false; });
});

/* What the lobby tablet offers. A different list from the one above, off a
 * different flag — see `selfCheckin` in sync.js for why the two come apart.
 *
 * It thins out one screen and nothing else, exactly as `quickAddServices` does:
 * a service kept off the tablet is still bookable everywhere the desk can reach,
 * and a check-in the desk seats off the waiting list goes through `bookTurn`
 * like any other, so this list is never consulted after the tap that made the
 * entry. Which matters: a salon that takes a service off the tablet at four
 * o'clock must not strand the person who checked in for it at half three.
 */
var checkinServices = computed(function () {
  return activeServices.value.filter(function (s) { return s.selfCheckin !== false; });
});

var serviceById = computed(function () {
  var map = {};
  state.services.forEach(function (s) { map[s.id] = s; });
  return map;
});

// Everything below used to reach for a row with `state.staff.find(...)` or
// `state.turns.find(...)`, a linear scan per call and dozens of calls per
// render. Both collections are small, but the scans were in the hot path —
// one per square, per second.
var staffById = computed(function () {
  var map = {};
  state.staff.forEach(function (s) { map[s.id] = s; });
  return map;
});

var turnById = computed(function () {
  var map = {};
  state.turns.forEach(function (t) { map[t.id] = t; });
  return map;
});

function staffOf(staffId) { return staffById.value[staffId] || null; }

/* ---------- the notes on a client's file ----------
 *
 * Read from Mangomint by the server poll (schema part 20) and carried on each
 * of today's appointments as `clientNotes`. Kevin, 2026-10-25: a note icon
 * beside the client's name wherever the board writes it except on a square,
 * and a warning wherever the board offers to hand them to a technician their
 * notes name — "Do not book with Zenny". See js/rules/client-notes.js.
 */
var clientNotesById = computed(function () {
  var out = {};
  appts.rows.forEach(function (a) {
    if (a.clientId != null && a.clientNotes) out[a.clientId] = a.clientNotes;
  });
  return out;
});

function clientNotesFor(clientId) {
  return clientId == null ? '' : clientNotesById.value[clientId] || '';
}

// The client's notes when they name this technician, else ''.
function noteWarningFor(clientId, staffId) {
  var notes = clientNotesFor(clientId);
  var s = staffId == null ? null : staffOf(staffId);
  return notes && s && notesRules.notesMention(notes, s.name) ? notes : '';
}
function turnOf(turnId) { return turnById.value[turnId] || null; }

// The colour a turn draws in. Four components carried their own copy of this,
// each with the fallback hex written out again.
function colorOfTurn(turn) {
  var svc = turn && serviceById.value[turn.serviceId];
  return svc ? svc.color : DEFAULT_SERVICE_COLOR;
}

var rosterStaff = computed(function () {
  return state.staff.filter(function (s) { return s.active; })
    .slice().sort(function (a, b) { return a.sortOrder - b.sortOrder; });
});

var archivedStaff = computed(function () {
  return state.staff.filter(function (s) { return !s.active; })
    .slice().sort(function (a, b) { return a.name.localeCompare(b.name); });
});

var presentStaff = computed(function () {
  return rosterStaff.value.filter(function (s) {
    var d = state.dayStaff[s.id];
    return d && d.present;
  });
});

/* Whether this technician is only taking clients who ask for them today — the
 * roster's default, or the desk's word for the day. See js/rules/own-clients.js. */
function isOwnClients(staffId) {
  return ownRules.ownClientsOnly(staffOf(staffId), state.dayStaff[staffId]);
}

/* Everybody on the board the rotation is about: in today and not on their own
 * clients. Who is next, who ranks where, who stands in for a booking or trades
 * one in a swap, who is charged a skip, and what the floor has done — all of it
 * reads this rather than `presentStaff`. Their column still draws, off
 * `presentStaff`, and a client who asks for them still goes to them. */
var rotationStaff = computed(function () {
  return ownRules.inRotation(presentStaff.value, isOwnClients);
});

/* ---------- the day as Mangomint has it ----------
 *
 * Each technician's shift and the time blocked off on Mangomint's calendar,
 * kept by the server's minute poll (schema part 17) and handed over by the
 * scheduling plugin as `fetchWorkHours`. The rules — who is on the board, who
 * is not in yet, what a time block does, who wins between Mangomint and the
 * desk, and booked work past an out time — are Kevin's, 2026-10-22, and live
 * in js/rules/work-hours.js. What is here is the reactive half.
 *
 * Empty for a provider without the `workHours` ability, a salon without
 * `features.shifts`, or a technician with no Mangomint id — and empty is the
 * board as it was: nobody is held out, and Hours today is the desk's alone.
 */
var workHours = reactive({ byExternal: {}, loadedFor: null });   // Mangomint staff id -> { spans, blocks, changedAt }

// Board staff id -> mangomintDay(), for everybody Mangomint has working today.
var mmDayByStaff = computed(function () {
  var out = {};
  var raw = workHours.byExternal;
  state.staff.forEach(function (s) {
    if (s.externalStaffId == null || s.externalStaffId === '') return;
    var w = raw[String(s.externalStaffId)];
    if (!w) return;
    var day = workRules.mangomintDay(w.spans, w.blocks, w.changedAt);
    if (day) out[s.id] = day;
  });
  return out;
});

function mmDayOf(staffId) { return mmDayByStaff.value[staffId] || null; }

function workHoursOf(d) {
  var w = d && d.workHours;
  return {
    typedAt: w && w.typedAt != null ? w.typedAt : null,
    missedAt: w && w.missedAt != null ? w.missedAt : null,
    released: (w && w.released) || [],
    // How the missed count was last worked out — see `missedRecord`.
    missedWhy: w && w.missedWhy ? w.missedWhy : null
  };
}

function workHoursRow(w) {
  var x = workHoursOf({ workHours: w });
  return { typedAt: x.typedAt, missedAt: x.missedAt, released: x.released.slice(),
           missedWhy: x.missedWhy };
}

/* What a missed count was worked out from, kept on the day row beside it
 * (Kevin, 2026-10-25): the start it was for, the floor as this station saw it
 * (work-hours.js `floorTurnsDetail`), and which station and build did the
 * sum. Two stations work it out in the same minute; this says which one the
 * row ended up with. */
/* global __BUILD_ID__ */
var RUNNING_BUILD = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

function missedRecord(at, detail, by) {
  return {
    at: at,
    workedAt: Date.now(),
    by: by,
    station: typeof location !== 'undefined' ? location.host : null,
    build: RUNNING_BUILD,
    missed: detail.count,
    floor: detail.members
  };
}

/* Who Mangomint's day has out of the rotation right now: not in yet, or on a
 * break. Through a string key for the reason `spentKey` is — the minute clock
 * is behind it, and the board must not be rebuilt sixty times an hour to say
 * the same thing. */
var mmOutKey = computed(function () {
  var t = minuteNow.value;
  var out = [];
  presentStaff.value.forEach(function (s) {
    var mm = mmDayOf(s.id);
    if (!mm) return;
    var sh = shiftOf(s.id);
    var o = workRules.outOfRotationAt(sh ? sh.startAt : null, mm.breaks,
      workHoursOf(state.dayStaff[s.id]).released, t);
    if (o) out.push(JSON.stringify([s.id, o.reason, o.key, o.until, o.label]));
  });
  return out.join('\n');
});

var mmOutStaff = computed(function () {
  var out = {};
  mmOutKey.value.split('\n').forEach(function (line) {
    if (!line) return;
    var x = JSON.parse(line);
    out[x[0]] = { reason: x[1], key: x[2], until: x[3], label: x[4] };
  });
  return out;
});

function mmOutOf(staffId) { return mmOutStaff.value[staffId] || null; }

// Their time blocks still to count: every one the desk has not brought them
// back from.
function mmBreaksFor(staffId) {
  var mm = mmDayOf(staffId);
  if (!mm) return [];
  var released = workHoursOf(state.dayStaff[staffId]).released;
  return mm.breaks.filter(function (b) { return released.indexOf(b.id) === -1; });
}

// The time block that work from `from` to `to` would run into — a cutoff for
// new work, the same as an out time (work-hours.js, BLOCK AHEAD).
function mmBreakInWay(staffId, from, to) {
  return workRules.breakInWay(mmBreaksFor(staffId), [], from, to);
}

// The services the book has on them that have not gone up yet, as their ends.
// What `homeAt` waits for: nobody is sent home with a client still due.
function bookedEndsFor(staffId) {
  return (pinnedBlocksByStaff.value[staffId] || []).map(function (b) { return b.to; });
}

function bookedOnStaff(staffId, apptServiceId) {
  if (apptServiceId == null) return false;
  return (pinnedBlocksByStaff.value[staffId] || []).some(function (b) {
    return b.svc.id === apptServiceId;
  });
}

var turnsByStaff = computed(function () {
  var map = {};
  state.staff.forEach(function (s) { map[s.id] = []; });
  state.turns
    .slice()
    .sort(function (a, b) { return a.sortAt - b.sortAt || (a.id < b.id ? -1 : 1); })
    .forEach(function (t) { if (map[t.staffId]) map[t.staffId].push(t); });
  return map;
});

function turnsFor(staffId) { return turnsByStaff.value[staffId] || []; }

function missedTurns(staffId) {
  var d = state.dayStaff[staffId];
  return d ? d.missedTurns : 0;
}

function presentSince(staffId) {
  var d = state.dayStaff[staffId];
  return d ? d.presentSince : Date.now();
}

// Out of the rotation for either reason: the desk said so, or Mangomint's day
// does — not in yet, or a time block running.
function isSkipped(staffId) {
  var d = state.dayStaff[staffId];
  return !!(d && d.skipped) || !!mmOutOf(staffId);
}

function skipsFor(staffId) {
  var d = state.dayStaff[staffId];
  return (d && d.skips) || [];
}

// How a column of turns becomes squares, what a square is worth, and where the
// red ones go, all live in js/rules/turns.js — see the note at the top of that
// file. What is left here is the reactive half: which turns belong to whom, and
// keeping the answers cached as the day changes underneath them.
var slotsByStaff = computed(function () {
  var map = {};
  state.staff.forEach(function (s) { map[s.id] = buildSlots(turnsFor(s.id)); });
  return map;
});

function slotsFor(staffId) { return slotsByStaff.value[staffId] || []; }

var rowsByStaff = computed(function () {
  var map = {};
  state.staff.forEach(function (s) {
    map[s.id] = buildRows(slotsFor(s.id), skipsFor(s.id));
  });
  return map;
});

function rowsFor(staffId) { return rowsByStaff.value[staffId] || []; }

function creditBreakdown(staffId) {
  return creditFrom(slotsFor(staffId), missedTurns(staffId), skipsFor(staffId).length);
}

function turnCredit(staffId) { return creditBreakdown(staffId).credit; }

// A trailing, unpaired half turn. Its owner is next for any service.
function openHalfTurn(staffId) { return creditBreakdown(staffId).openHalf; }

function hasOpenHalf(staffId) { return creditBreakdown(staffId).openHalf != null; }

function rowEntry(staffId, row) {
  var offset = row - missedTurns(staffId);
  if (offset < 0) return null;
  return rowsFor(staffId)[offset] || null;
}

function slotAt(staffId, row) {
  var e = rowEntry(staffId, row);
  return e && !e.skip ? e : null;
}

function isSkippedCell(staffId, row) {
  var e = rowEntry(staffId, row);
  return !!(e && e.skip);
}

// The square a turn sits in, counting missed and skipped squares above it.
function turnRowNumber(turnId) {
  var t = turnOf(turnId);
  if (!t) return 0;
  var rows = rowsFor(t.staffId);
  for (var i = 0; i < rows.length; i++) {
    if (!rows[i].skip && rows[i].turns.indexOf(t) !== -1) {
      return missedTurns(t.staffId) + i + 1;
    }
  }
  return 0;
}

// The other turn sharing a square with this one, if any.
function slotMatesOf(turnId) {
  var t = turnOf(turnId);
  if (!t) return [];
  var slots = slotsFor(t.staffId);
  for (var i = 0; i < slots.length; i++) {
    if (slots[i].turns.indexOf(t) !== -1) return slots[i].turns;
  }
  return [t];
}

function activeTurn(staffId) {
  var list = turnsFor(staffId);
  for (var i = 0; i < list.length; i++) if (list[i].endedAt == null) return list[i];
  return null;
}

function isBusy(staffId) { return activeTurn(staffId) != null; }

// When a square is expected to be over. The booking's own length if there is
// one behind it, otherwise the turn type's usual — snapshotted onto the turn
// when it started. Null means nothing on the board says when it ends, which is
// a different answer from "not yet" and is treated as one everywhere it is read.
function turnEndsAt(turn) {
  if (!turn) return null;
  // Held for a check-in: the work has not begun, so it ends a booked length
  // from now, whenever now is.
  if (turn.waiting && turn.endedAt == null) {
    var mins = turn.scheduledMinutes ||
      ((serviceById.value[turn.serviceId] || {}).defaultMinutes) || null;
    return mins ? Date.now() + mins * 60000 : null;
  }
  if (turn.scheduledEndAt) return turn.scheduledEndAt;
  if (turn.scheduledMinutes) return turn.startedAt + turn.scheduledMinutes * 60000;
  return null;
}

function availableSince(staffId) {
  var list = turnsFor(staffId);
  if (!list.length) return presentSince(staffId);
  var last = 0;
  list.forEach(function (t) { if (t.endedAt && t.endedAt > last) last = t.endedAt; });
  return last || presentSince(staffId);
}

// When the floor started working. Everyone marked in before that was waiting
// for the salon to open, not sitting idle.
var dayStartedAt = computed(function () {
  var first = null;
  state.turns.forEach(function (t) {
    if (first == null || t.startedAt < first) first = t.startedAt;
  });
  return first;
});

/* Idle time, as the header clock counts it.
 *
 * The rotation's own `availableSince` runs from the moment somebody was marked
 * in, which is right for the queue — of two technicians on the same turns, the
 * one waiting longer goes first. It is wrong as a number on screen: opening at
 * nine and marking six people in gives everybody an hour of "idle" before the
 * first client is through the door, and the column headers read as though the
 * floor had been standing around.
 *
 * So the clock starts with the day. Nobody is idle before the first service of
 * the day begins, and until it does there is no number at all — a dash says
 * "the day has not started" where a zero would say "just finished something".
 */
function idleSince(staffId) {
  var started = dayStartedAt.value;
  if (started == null) return null;
  return Math.max(availableSince(staffId), started);
}

/* ---- where the idle clock stops ----
 *
 * Two things stop it. The book being shut is the whole board's: the floor has
 * gone home, and a board left on the wall overnight should not report fourteen
 * hours of idle against everybody who worked the day. The other is per person
 * — out of the rotation, for any of the three reasons, or marked off the board
 * altogether. See `skipSpansFor`.
 *
 * Off the close time, not off `now`, once the day is shut — and off the same
 * close time on every screen in the building, now that the close is a row on
 * tt_days rather than a marker on whichever device was used to shut up shop.
 * The back-room board used to keep counting idle against a floor that had gone
 * home, and the report you got depended on which screen you read it from.
 *
 * The minute clock rather than the second one: everything downstream is
 * whole minutes, so a second hand could only ever produce the same number
 * again, and a quiet board should not re-render for it.
 */
var idleCountsTo = computed(function () {
  return dayClosed.value && dayClosedAt.value ? dayClosedAt.value : minuteNow.value;
});

// The same moment for one technician: earlier than the board's if they are out
// of the rotation right now, so their header clock reads what they had built
// up when they stepped out rather than carrying on without them.
function idleUntil(staffId) {
  var stop = idleCountsTo.value;
  var spans = skipSpansFor(staffId);
  var open = spans.length && spans[spans.length - 1].to == null
    ? spans[spans.length - 1].from : null;
  return open != null ? Math.min(stop, open) : stop;
}

// Overlapping stretches flattened into non-overlapping ones, so a length can
// be taken off them. Everything below leans on this: a technician's last
// service runs on while "done for today" is ticked, and subtracting the
// service and the bypass separately would take that overlap off twice.
function mergeSpans(spans) {
  var out = [];
  spans.slice().sort(function (a, b) { return a[0] - b[0]; })
    .forEach(function (s) {
      var last = out[out.length - 1];
      if (last && s[0] <= last[1]) {
        if (s[1] > last[1]) last[1] = s[1];
      } else {
        out.push([s[0], s[1]]);
      }
    });
  return out;
}

/* ---- the day's idle total, per technician ----
 *
 * Time on the floor with nobody in the chair. The window runs from the day's
 * first service — the same start the header clock uses, and for the same
 * reason: nobody is idle before the salon has a client in it — or from when
 * they were marked in, whichever is later. It ends where `idleCountsTo` says.
 *
 * Out of that comes everything they were not idle for: every service they ran,
 * and every stretch they were out of the rotation, merged first so a service
 * running underneath a bypass is only taken off once.
 *
 * Null, not zero, before the day has started or for somebody who was never
 * marked in: those are "no answer", and a nought would read as "stood around
 * for none of it".
 */
function idleMsFor(staffId) {
  var started = dayStartedAt.value;
  if (started == null) return null;
  var d = state.dayStaff[staffId];
  if (!d) return null;
  var end = idleCountsTo.value;
  var from = Math.max(started, d.presentSince || started);
  if (end <= from) return 0;

  var busy = [];
  turnsFor(staffId).forEach(function (t) {
    busy.push([t.startedAt, t.endedAt == null ? end : t.endedAt]);
  });
  skipSpansFor(staffId).forEach(function (s) {
    busy.push([s.from, s.to == null ? end : s.to]);
  });
  // ...and the time Mangomint had them out: before their start, and every
  // break the desk did not bring them back from.
  mmOutSpansFor(staffId).forEach(function (s) { busy.push([s.from, s.to]); });

  var covered = 0;
  mergeSpans(busy.map(function (s) {
    return [Math.max(s[0], from), Math.min(s[1], end)];
  }).filter(function (s) { return s[1] > s[0]; }))
    .forEach(function (s) { covered += s[1] - s[0]; });

  return Math.max(0, (end - from) - covered);
}

// The square a new turn lands in, counting the slashed missed squares and any
// red skipped ones above it. Any turn fills the empty side of an open half
// square, which is why this looks past a trailing run of skips.
function nextOpenRow(staffId) {
  var rows = rowsFor(staffId);
  var base = missedTurns(staffId);
  for (var i = rows.length - 1; i >= 0; i--) {
    if (rows[i].skip) continue;
    return rows[i].open ? base + i : base + rows.length;
  }
  return base + rows.length;
}

function isMissedCell(staffId, row) { return row < missedTurns(staffId); }

// When the floor started each turn: the earliest service in that row across
// every column, indexed by row. A half turn shares its square, so the earlier
// of the pair is the one that opened it. Slashed squares above a late arrival
// are not services and contribute nothing.
var rowStartTimes = computed(function () {
  var out = [];
  presentStaff.value.forEach(function (s) {
    var base = missedTurns(s.id);
    rowsFor(s.id).forEach(function (entry, i) {
      if (entry.skip) return;
      var row = base + i;
      entry.turns.forEach(function (t) {
        if (out[row] == null || t.startedAt < out[row]) out[row] = t.startedAt;
      });
    });
  });
  return out;
});

// The hide switch is not offered on a board that has barely started. With one
// or two turns down there is nothing worth folding away, and a control that
// could empty most of the grid is not what belongs over a quiet morning. From
// the third turn on the board is long enough for the question to be real.
var HIDE_DONE_AFTER_TURNS = 3;

var canHideDoneRows = computed(function () {
  return rowStartTimes.value[HIDE_DONE_AFTER_TURNS - 1] != null;
});

// Off out of the box, and off again whenever the switch is not on screen: a
// preference remembered from yesterday must never fold rows away under a
// control nobody can see to turn back off.
var hidingDoneRows = computed(function () {
  return ui.hideDoneRows && canHideDoneRows.value;
});

// How many rows the board has room for, measured from the window by the board
// component. It is a floor, not a cap: once the day needs more rows than fit,
// the grid grows and scrolls.
var viewportRows = ref(MIN_ROWS);

function setViewportRows(n) {
  var v = Math.max(MIN_VISIBLE_ROWS, Math.floor(n) || 0);
  if (v !== viewportRows.value) viewportRows.value = v;
}

var rowCount = computed(function () {
  var max = 0;
  presentStaff.value.forEach(function (s) {
    var n = missedTurns(s.id) + rowsFor(s.id).length;
    if (n > max) max = n;
  });
  return Math.max(viewportRows.value, max + 1);
});

/* ---------- the board, read once instead of square by square ----------
 *
 * The grid is drawn from this. Every square used to work itself out from
 * scratch: the template asked slotAt() nineteen times for a single cell, and
 * again for half, zero, open, the colour strip, the running turn and the
 * mismatch flag — each one walking rowsFor() and missedTurns() from the top.
 * On a ten-by-twelve board that is several thousand lookups, and the board
 * redraws every second because running squares count in seconds.
 *
 * All of it is a function of the turns and of who is in today, so it is built
 * in one pass here and read as data.
 *
 * Deliberately NOT in here: anything that depends on the armed service or on
 * a drag in flight. Those change on a pointer move, and folding them in would
 * throw the whole map away several times a second — they stay as the small
 * guarded functions in turn-board.js, which cost nothing when nothing is
 * armed and nothing is being dragged.
 */
function stripFor(turns, colors) {
  if (!turns.length) return null;
  function colorOf(t) {
    var svc = colors[t.serviceId];
    return svc ? svc.color : DEFAULT_SERVICE_COLOR;
  }
  var a = colorOf(turns[0]);
  if (turns.length < 2) return a;
  return 'linear-gradient(to right, ' + a + ' 50%, ' + colorOf(turns[1]) + ' 50%)';
}

/* The colours of every service on one turn, or null for the ordinary square
 * that is only the one.
 *
 * What the square's stacked chips are drawn from. Colour rather than a count or
 * a second letter because a merged square has to say it at every size the board
 * is ever drawn at, including half a square on a narrow column where there is
 * no room for another character — and because the desk already reads this board
 * by colour. It says how many and roughly which; the menu a tap opens says the
 * rest, which is the right division of labour for a mark this small.
 */
function mixFor(turn, colors) {
  if (!turn || !turn.extras || !turn.extras.length) return null;
  return [turn].concat(turn.extras).map(function (s) {
    var svc = colors[s.serviceId];
    return svc ? svc.color : DEFAULT_SERVICE_COLOR;
  });
}

var EMPTY_TURNS = [];
var EMPTY_MIXES = [];

/* One column's squares, from the rows it was built into.
 *
 * Its own function because the board is not the only thing that draws a
 * column any more: a square held over another technician redraws that
 * technician's column as it *would* read, and a preview built by different
 * code from the board it is previewing is a preview that can lie. Same
 * function, different list of turns — see `turnDropPreview`.
 *
 * `ghostId` is the square being dragged, when this is that preview. It carries
 * no state of its own; the class it earns is the board's business.
 */
function cellsFrom(entries, missed, total, mismatches, colors, ghostId) {
  var cells = [];
  var skipsSeen = 0;
  for (var row = 0; row < total; row++) {
    var entry = row < missed ? null : entries[row - missed];
    var cell = {
      row: row,
      state: 'empty',
      turns: EMPTY_TURNS,
      // Aligned with `turns`: the colour stack for each, or null where that
      // turn is the one service it looks like.
      mixes: EMPTY_MIXES,
      first: null,
      half: false,
      zero: false,
      open: false,
      strip: null,
      runningTurnId: null,
      mismatch: false,
      labelled: false,
      ghost: false,
      // The n-th red square down the column — which stored skip it is, for
      // taking one off (js/rules/skip-marks.js).
      skipOrdinal: null
    };

    if (row < missed) {
      cell.state = 'missed';
    } else if (entry && entry.skip) {
      cell.state = 'skipped';
      cell.skipOrdinal = skipsSeen++;
    } else if (entry) {
      var turns = entry.turns;
      var live = null;
      var clash = false;
      var ghost = false;
      for (var i = 0; i < turns.length; i++) {
        if (live == null && turns[i].endedAt == null) live = turns[i];
        if (mismatches[turns[i].id]) clash = true;
        if (ghostId && turns[i].id === ghostId) ghost = true;
      }
      cell.state = live ? 'running' : 'done';
      cell.turns = turns;
      cell.mixes = turns.map(function (t) { return mixFor(t, colors); });
      cell.first = turns[0] || null;
      cell.half = !!entry.half;
      cell.zero = !!entry.zero;
      cell.open = !!entry.open;
      cell.strip = stripFor(turns, colors);
      cell.runningTurnId = live ? live.id : null;
      cell.mismatch = clash;
      cell.labelled = !entry.half && !!(cell.first && cell.first.clientName);
      cell.ghost = ghost;
    }

    cells.push(cell);
  }
  return cells;
}

var boardColumns = computed(function () {
  var mismatches = mismatchByTurn.value;
  var colors = serviceById.value;
  var total = rowCount.value;
  // A map that only changes when the answer does — see `spentStaff`. Read once
  // here rather than per column, so the board depends on the map and not on
  // the minute clock behind it.
  var spent = spentStaff.value;

  return presentStaff.value.map(function (s) {
    var cells = cellsFrom(
      rowsFor(s.id), missedTurns(s.id), total, mismatches, colors, null);

    return {
      staff: s,
      busy: isBusy(s.id),
      skipped: isSkipped(s.id),
      // Their day is over: the squares below whatever they are on now are
      // blocked off, and the queue has stopped offering them anything. Kept
      // apart from `skipped` on purpose — one is a decision somebody made
      // about the rotation, the other is the clock running out on a time
      // that was set this morning.
      offShift: !!spent[s.id],
      // Only taking clients who ask for them. Out of the queue like the two
      // above, but nobody is out: the column is working, just not for walk-ins.
      ownClients: isOwnClients(s.id),
      credit: turnCredit(s.id),
      openHalf: hasOpenHalf(s.id),
      targetRow: nextOpenRow(s.id),
      cells: cells
    };
  });
});

/* ---------- folding away finished rows ----------
 *
 * A row is finished when there is nothing left anybody could do in it: every
 * square across the floor is a stopped turn, a slashed missed square or a red
 * skip, at least one of them is real work, and no half square is still open —
 * an open half is a square a turn can still land in, so the row is not done
 * with. A row with a blank square in it is never finished either: that blank
 * is where the next turn goes.
 */
/* ---------- folding away whoever is done for today ----------
 *
 * Done for today only — marked at the desk, or by the board once their out
 * time passed. Somebody on a Break, or held out by a time block that ends
 * inside their day, is coming back, so their column stays.
 */
var doneStaffIds = computed(function () {
  var out = {};
  presentStaff.value.forEach(function (s) {
    if (skipReasonIs(s.id, 'done')) out[s.id] = true;
  });
  return out;
});

var doneStaffCount = computed(function () {
  return Object.keys(doneStaffIds.value).length;
});

// The columns the grid draws before any rows are folded. With the switch off —
// out of the box — it is boardColumns itself.
var staffColumns = computed(function () {
  var cols = boardColumns.value;
  if (!ui.hideDoneStaff) return cols;
  var done = doneStaffIds.value;
  return cols.filter(function (col) { return !done[col.staff.id]; });
});

// Judged across the columns that are drawn: a hidden column's blank squares
// are not where the next turn goes, so they do not hold a row open.
var completedRows = computed(function () {
  var cols = staffColumns.value;
  var out = [];
  if (!cols.length) return out;
  for (var row = 0; row < rowCount.value; row++) {
    var done = true;
    var worked = false;
    for (var c = 0; c < cols.length; c++) {
      var cell = cols[c].cells[row];
      if (!cell || cell.state === 'empty' || cell.state === 'running' || cell.open) {
        done = false;
        break;
      }
      if (cell.turns.length) worked = true;
    }
    out[row] = done && worked;
  }
  return out;
});

var doneRowCount = computed(function () {
  return completedRows.value.filter(Boolean).length;
});

// The rows the grid actually draws, by their real turn number. The gutter and
// every column read this same list, so the two can never fall out of step.
var visibleRows = computed(function () {
  var hidden = hidingDoneRows.value ? completedRows.value : null;
  var out = [];
  for (var i = 0; i < rowCount.value; i++) {
    if (!hidden || !hidden[i]) out.push(i);
  }
  return out;
});

// Switched off — the normal case — this hands back boardColumns untouched, so
// the feature costs the board nothing when nobody is using it.
var visibleColumns = computed(function () {
  var cols = staffColumns.value;
  if (!hidingDoneRows.value) return cols;
  var hidden = completedRows.value;
  return cols.map(function (col) {
    var cells = col.cells.filter(function (cell) { return !hidden[cell.row]; });
    if (cells.length === col.cells.length) return col;
    return Object.assign({}, col, { cells: cells });
  });
});

/* The board as it would read if the square in the air landed where it is being
 * held. Nothing being dragged — the board's normal state — and this hands back
 * `visibleColumns` itself, so the whole feature costs one null check per paint.
 *
 * Only the destination column is redrawn, and only that one: it is where other
 * work has to move out of the way, and rebuilding ten columns every time the
 * pointer crosses a row would throw away the map the grid is drawn from several
 * times a second. The square's own column keeps its square where it is, dimmed
 * — a drag that emptied its origin the moment it lifted would leave the desk
 * dragging out of a hole. When the two are the same column there is only one to
 * draw and the square travels through it, which is what a reorder looks like.
 */
var previewColumns = computed(function () {
  var pv = turnDropPreview.value;
  var cols = visibleColumns.value;
  if (!pv) return cols;
  return cols.map(function (col) {
    return col.staff.id === pv.staffId ? pv.column : col;
  });
});

/* Somebody who has just used *Skip this turn* has said no to the turn that was
 * coming to them, and the board must stop offering them until it has moved on.
 *
 * The skip already costs them a turn's credit, which is usually enough to drop
 * them — but not always, and the two ways it fails are the two the desk sees:
 * they hold an open half turn, which pins them at the head of the queue
 * whatever their credit; or the skip ties them with everybody else and the
 * idle tie-break, which does not know they just passed, puts them back on top.
 * Either way Next availability went on naming the very person who had said no.
 *
 * A trailing red square is what "passed on this one" looks like, and it clears
 * itself: the next turn they take lands under it and the last row is a turn
 * again. Someone marked Break or Done is excluded from the queue outright and
 * has a badge saying so, so their charged skips are not read as a pass.
 *
 * But a pass is about *the turn they passed on*, and a trailing red square on
 * its own outlives it: somebody who says no at eleven and is not asked again
 * has that square all day, so the board went on holding them behind a floor
 * that had since been round twice — the one person a turn was owed to was the
 * one person it would never be offered to. So the pass is also spent once the
 * rotation has moved past it: if everybody else on the floor is now further
 * ahead, the skip has already been paid for in credit and there is nothing
 * left for it to say. While they are still level with the floor it stands,
 * which is the case it was built for — that is exactly when the credit cost
 * ties them with everyone and the idle tie-break hands the turn straight back.
 */
function justPassed(staffId) {
  if (isSkipped(staffId)) return false;
  var rows = rowsFor(staffId);
  if (!rows.length || !rows[rows.length - 1].skip) return false;
  var floor = floorCreditExcept(staffId);
  return floor == null || turnCredit(staffId) >= floor;
}

// The lowest credit on the floor other than this tech's — where the rotation
// has got to, as far as they are concerned. Skipped-out techs, and anyone whose
// day has run out, are left out for the same reason `floorTurnsCompleted`
// leaves them out: they are not working the rotation, so their frozen count
// would hold the floor's number down and nobody would ever read as being behind
// it. Null when they are on their own.
function floorCreditExcept(staffId) {
  var min = null;
  rotationStaff.value.forEach(function (s) {
    if (s.id === staffId || isSkipped(s.id) || isShiftSpent(s.id)) return;
    var c = turnCredit(s.id);
    if (min == null || c < min) min = c;
  });
  return min;
}

// Someone holding an open half turn outranks everyone, including a tech on
// zero turns — they are owed the other half.
function buildQueue(includeSkipped, ignoreHalf, includeBusy) {
  return rotationStaff.value
    .map(function (s, i) {
      var b = creditBreakdown(s.id);
      return {
        staff: s, col: i, credit: b.credit,
        half: b.openHalf ? 0 : 1,
        // Only on the real queue. `fullQueue` measures who a booking went
        // past, and a pass is exactly the thing it is there to charge.
        passed: !includeSkipped && justPassed(s.id) ? 1 : 0,
        since: availableSince(s.id)
      };
    })
    /* Somebody whose day has run out drops out of the real queue exactly as
     * somebody bypassed does — there is nothing left they could be handed, so
     * standing them at the head of it on a low turn count would be the board
     * pointing at an empty chair.
     *
     * Not out of `fullQueue`, though. That one measures whose turn a booking
     * went past so the pass can be charged, and a technician who has gone home
     * is not passing on anything: leaving them in keeps the ordering it reads
     * honest, and `chargeSkips` only ever charges somebody marked skipped. */
    .filter(function (r) {
      return (includeBusy || !isBusy(r.staff.id)) &&
        (includeSkipped || (!isSkipped(r.staff.id) && !isShiftSpent(r.staff.id)));
    })
    // Behind everyone, rather than out of the queue: they are still free, and
    // dropping them altogether would make a service only they perform
    // unbookable for having passed on one turn of it.
    .sort(function (a, b) {
      return a.passed - b.passed ||
        (ignoreHalf ? 0 : a.half - b.half) ||
        a.credit - b.credit || a.since - b.since || a.col - b.col;
    })
    .map(function (r) { return r.staff; });
}

var availableQueue = computed(function () { return buildQueue(false, false); });

// The rotation as it would stand if nobody were bypassed, used only to work out
// whose turn a booking went past.
//
// Ordered on credit alone, deliberately: the open-half boost pins its holder at
// the head of the real queue until the half is filled, and a skipped tech can
// never fill it — so honouring it here would charge them a skip on every single
// booking for the rest of the day. On credit, each skip costs a turn and drops
// them down the order, which is what keeps the charging self-limiting.
var fullQueue = computed(function () { return buildQueue(true, true); });

// The rotation with nobody left out — the same sort `availableQueue` runs,
// over everyone in today rather than only those free this second. Read as a
// rank per technician, so "whose turn is it" can be asked about two people who
// are both mid-service and neither is on the real queue to be compared on.
var rotationRank = computed(function () {
  var out = {};
  buildQueue(true, false, true).forEach(function (s, i) { out[s.id] = i; });
  return out;
});

/* ---------- the same board, read down instead of across ----------
 *
 * The phone draws one row per technician from these — see turn-list.js and
 * rules/rotation.js. Built off `visibleColumns` rather than off the roster so
 * a row and a column are the same object: the phone cannot show a square the
 * grid does not have, and nothing has to be kept in step.
 *
 * The rotation sort is worked out whichever way the list is set, because the
 * UP NEXT badge is a claim about the rotation and not about whatever order the
 * rows happen to be in. Read board-order and the badge still lands on the
 * person a walk-in would go to; it is simply no longer at the top.
 */
var queueColumns = computed(function () {
  return queueOrder(visibleColumns.value, rotationRank.value);
});

var listColumns = computed(function () {
  return ui.listOrder === 'board' ? visibleColumns.value : queueColumns.value;
});

var upNextStaffId = computed(function () { return headOfQueue(queueColumns.value); });

var armedService = computed(function () {
  return ui.armedServiceId ? serviceById.value[ui.armedServiceId] || null : null;
});

/* A request goes to a named tech, so there is nothing to suggest.
 *
 * The rotation alone used to answer this, and the rotation knows nothing about
 * the book: whoever was next up got named, with a client of their own due or
 * not. It is whoever is next up *and* has room for the work now — see
 * `availableWithRoom`. With nobody free for long enough it names nobody rather
 * than the wrong person, and the hint falls back to the plain "tap a column"
 * line, which is all there is left to say. */
var suggestedStaffId = computed(function () {
  if (!ui.armedServiceId || ui.requestMode) return null;
  var match = availableWithRoom(ui.armedServiceId)[0];
  return match ? match.id : null;
});

/* Booked-service id -> the turn it was assigned to.
 *
 * The services merged into a square are in here beside the one its own columns
 * carry, and this is the index the whole console asks "is that booking on the
 * board yet". Everything that has to stay true of a seated booking is true of a
 * merged one through this one map: it leaves Due now, it stops being offered by
 * the assign sheet, and it cannot be dragged up a second time. Leaving them out
 * would not have looked broken — it would have quietly offered the same service
 * again, which is how a client ends up on the board twice. */
var turnByApptService = computed(function () {
  var map = {};
  state.turns.forEach(function (t) {
    if (t.appointmentServiceId) map[t.appointmentServiceId] = t;
    (t.extras || []).forEach(function (m) {
      if (m.appointmentServiceId) map[m.appointmentServiceId] = t;
    });
  });
  return map;
});

function turnForApptService(apptServiceId) {
  return turnByApptService.value[apptServiceId] || null;
}

var cellMenuTurn = computed(function () {
  if (!ui.cellMenuTurnId) return null;
  return turnOf(ui.cellMenuTurnId);
});

var shortcutMap = computed(function () {
  var map = {};
  activeServices.value.forEach(function (s) {
    if (s.shortcut && !map[s.shortcut]) map[s.shortcut] = s;
  });
  return map;
});

/* Two services on one key: the board answers the first and the second is dead,
 * which is only ever noticeable on a screen that counts them.
 *
 * Keyless services are skipped rather than counted. They all share the empty
 * string, and a salon that has taken the keys off three services has not made a
 * collision — it has made three services nobody presses. Warning about it would
 * put a red box under every one of them.
 */
var duplicateShortcuts = computed(function () {
  var seen = {}, dupes = {};
  activeServices.value.forEach(function (s) {
    if (!s.shortcut) return;
    if (seen[s.shortcut]) dupes[s.shortcut] = true;
    seen[s.shortcut] = true;
  });
  return dupes;
});

/* ---------- actions: booking ---------- */

// Bottom of a tech's column. Strictly increasing so two turns created in the
// same millisecond still read in the order they happened.
function nextSortAt(staffId) {
  var list = turnsFor(staffId);
  var last = list.length ? list[list.length - 1].sortAt : 0;
  return Math.max(Date.now(), last + 1);
}

// Techs in today who perform a service, and the subset free to take it now.
// The rotation's techs: somebody on their own clients is nobody's answer to
// "who could do this walk-in", now or later.
function providersFor(serviceId) {
  return rotationStaff.value.filter(function (s) {
    return s.serviceIds.indexOf(serviceId) !== -1;
  });
}

function availableFor(serviceId) {
  return availableQueue.value.filter(function (s) {
    return s.serviceIds.indexOf(serviceId) !== -1;
  });
}

/* ...and the ones the book leaves room for, which is a different question and
 * the one Next availability is actually asking.
 *
 * Free this second is not free for the work. Somebody standing up at ten past
 * with a client of their own due at quarter past has ten minutes, and a
 * walk-in handed to them at ten past is a client sitting in a chair when the
 * booked one walks through the door. The rotation cannot see that — it counts
 * turns, not hours — so every answer built on it alone over-promised.
 *
 * `earliestStartFor` is the whole measurement, and it returns the moment asked
 * about when there is nothing in the way. */
function availableWithRoom(serviceId, at) {
  var t = at == null ? minuteNow.value : at;
  return availableFor(serviceId).filter(function (s) {
    return hasRoomNow(s.id, serviceId, t);
  });
}

function hasRoomNow(staffId, serviceId, at) {
  return earliestStartFor(staffId, serviceId, at) === at;
}

function nextUpFor(serviceId) { return availableWithRoom(serviceId)[0] || null; }

/* Who this service goes to next, and when — the Next availability *Next* line.
 *
 * Somebody free is the easy case and the only one the line used to answer.
 * Everything else read **None**, which put two very different facts under one
 * word: nobody in today performs this at all, and everybody who does happens
 * to be mid-service. The first is a dead end — the button cannot be used and
 * greys out to say so. The second is a wait, and a wait has a length.
 *
 * The length comes off the running squares themselves: each one carries what
 * it is expected to run for, from its booking or its turn type. That is an
 * estimate and says so — it assumes the day is running to time. A square
 * already past its expected end counts as finishing now rather than never.
 *
 * Coming free is only half of it, though, because the book is the other half:
 * a walk-in has to *fit*. `earliestStartFor` measures each technician's next
 * clear run against what this service usually takes and past anything booked
 * on them that cannot be handed to somebody else. So the answer is the soonest
 * anyone could actually start the work, not the soonest anyone stands up.
 *
 * That measurement is what "free" means here too, and it did not used to be.
 * Anybody idle this second was named flat out, so a technician with a client
 * of their own due in ten minutes read as available for a ninety-minute set —
 * the one answer the desk cannot recover from, because the client is already
 * in the chair when the booked one arrives. Somebody idle with no room falls
 * through to the wait below, which names the soonest anyone genuinely has
 * some.
 *
 * Somebody bypassed at the desk is left out of the estimate entirely: break
 * and done for today end when a person says they do, not at a minute the board
 * can name. If they are all that is left the line says *Busy* rather than
 * invent one. Somebody Mangomint has out — not in until ten, or on a time
 * block — does have a minute, and is measured from it (`startPlanForNeed`).
 *
 * Ties go to turn order — the same rotation the board hands work out on, so
 * two squares finishing at half past means the walk-in goes to whoever the
 * next turn belongs to.
 *
 * Short waits read as a countdown and long ones as a clock: half an hour is
 * about as far as "in forty minutes" is worth doing in your head, and past it
 * the useful thing to tell somebody at the desk is a time they can come back.
 */
/* How the wait opens, which depends on what the desk can see across the room.
 *
 * Somebody standing idle and a card saying "not for another hour" is the one
 * combination that reads as a broken board, and it is now a combination that
 * happens: free of a square is not free for the work, so a technician with
 * nothing in front of her can still be spoken for. Naming her is the whole
 * explanation — the desk looks up, sees who it means and remembers the client
 * due at half past. With nobody idle the old flat sentence is the true one.
 */
function idleNote(providers) {
  var names = providers
    .filter(function (s) { return !isBusy(s.id) && !isSkipped(s.id); })
    .map(function (s) { return s.name; });
  if (!names.length) return 'Nobody is free for this right now. ';
  var label = names.length > 2
    ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
    : names.join(' and ');
  return label + (names.length > 1 ? ' are' : ' is') +
    ' free, but not for long enough — the book has somebody due before this ' +
    'would be finished. ';
}

/* Everybody idle and ahead of `namedId` in the rotation who does this service
 * but has no room for it now, with what is in the way — the raw material for
 * rules/swap.js `passedOverNote`. Somebody mid-service is left out: the board
 * already shows why they were not named. */
function passedOverFor(serviceId, namedId, t) {
  var rank = rotationRank.value;
  var mine = rank[namedId];
  if (mine == null) return [];
  var need = walkInFitMs(serviceId);
  var out = [];
  providersFor(serviceId).forEach(function (s) {
    if (s.id === namedId || rank[s.id] == null || rank[s.id] >= mine) return;
    if (isBusy(s.id) || isSkipped(s.id) || isShiftSpent(s.id)) return;
    var start = startPlanFor(s.id, serviceId, t);
    if (start && start.at === t) return;           // has room after all
    if (!start) {
      var cut = shiftCutoff(s.id);
      if (cut != null) out.push({ rank: rank[s.id], name: s.name, kind: 'shift',
                                  at: formatClock(cut) });
      return;
    }
    var plan = bookingPlanByStaff.value[s.id];
    var swaps = plan ? plan.swaps : [];
    var blocks = (blockingBlocksByStaff.value[s.id] || []).concat(
      start.swapped
        ? swaps.reduce(function (a, x) { return a.concat(x.back); }, [])
        : swaps.map(function (x) { return x.out; }));
    var hit = blocks
      .filter(function (b) { return b.to > t && b.from < t + need; })
      .sort(function (a, b) { return a.from - b.from; })[0];
    if (!hit) {
      var brk = mmBreakInWay(s.id, t, t + need);
      if (brk) out.push({ rank: rank[s.id], name: s.name, kind: 'break',
                          at: formatClock(brk.from), what: brk.label });
      return;
    }
    out.push({
      rank: rank[s.id], name: s.name,
      kind: hit.requested ? 'request' : 'booking',
      at: formatClock(hit.from),
      what: hit.svc.name + (hit.appt.clientName ? ' (' + hit.appt.clientName + ')' : '')
    });
  });
  return out.sort(function (a, b) { return a.rank - b.rank; });
}

function nextUpDetail(serviceId) {
  var t = minuteNow.value;                 // ticks, so the wait counts down
  // Free this second *and* with room for the work — the two together are what
  // "now" means, and `nextUpFor` is where both are asked.
  var free = nextUpFor(serviceId);
  if (free) {
    // Somebody free means it would start now, so that is what the end of the
    // day is measured from.
    var late = closingOverrun(serviceId, null);
    /* Free is not always free of consequence. The book may have named this
     * technician for something inside the run, and the only reason they read
     * as having room is that the desk is expected to hand it to a colleague.
     * That is a real instruction, not a footnote, so it goes in the card's own
     * tooltip as well as on the glyph that marks it. */
    var freeMoves = walkInMoves(free.id, serviceId, t);
    var freeNote = walkInMovesNote(free.name, freeMoves);
    // Usually nothing to explain: somebody is free and the card says who. But
    // when somebody ahead of them in the rotation is standing idle too, the
    // card has to say why it skipped them, or it reads as the rotation broken.
    var passed = passedOverNote(passedOverFor(serviceId, free.id, t), free.name);
    return { kind: 'staff', staff: free, at: t, minutes: null,
             name: free.name, wait: null, closing: late,
             moves: freeMoves, movesNote: freeNote,
             why: passed,
             note: [passed, late ? closingNote(serviceId, late) : '', freeNote]
               .filter(Boolean).join('\n') };
  }
  var providers = providersFor(serviceId);
  if (!providers.length) {
    return { kind: 'none', staff: null, at: null, minutes: null,
             name: 'None', wait: null, closing: null,
             moves: [], movesNote: '',
             why: 'Nobody in today performs this service.',
             note: 'Nobody in today performs this service.' };
  }

  var rank = rotationRank.value;
  var best = null;
  providers.forEach(function (s) {
    var at = earliestStartFor(s.id, serviceId, t);
    if (at == null) return;
    var order = rank[s.id] == null ? Infinity : rank[s.id];
    if (!best || at < best.at || (at === best.at && order < best.order)) {
      best = { staff: s, at: at, order: order };
    }
  });

  if (!best) {
    var outNote = 'Everyone in today who does this is out, and nothing on the ' +
      'board says when they are back.';
    return { kind: 'wait', staff: null, at: null, minutes: null,
             name: 'Busy', wait: null, closing: null,
             moves: [], movesNote: '',
             why: outNote, note: outNote };
  }

  var minutes = Math.round((best.at - t) / 60000);
  // Measured from the soonest it could start rather than from now: a wait is
  // most of what pushes a service past closing in the first place.
  var overrun = closingOverrun(serviceId, best.at);
  // The same question of the estimate: an hour's wait that also assumes two
  // bookings change hands is a different answer from an hour's wait.
  var moves = walkInMoves(best.staff.id, serviceId, best.at);
  var movesNote = walkInMovesNote(best.staff.name, moves);
  // Where the wait comes from, on its own. `note` still carries all three
  // parts joined for anything reading the whole thing as one tooltip; the
  // hover card lays them out separately and needs this one by itself.
  var why = idleNote(providers) + best.staff.name +
    (minutes < 1
      ? ' is over the expected time and should be clear any minute'
      : ' should have room for it about ' + formatClock(best.at)) +
    ' — the soonest anyone who does this is free for long enough, if the ' +
    'day runs to time.';
  return {
    kind: 'eta', staff: best.staff, at: best.at, minutes: minutes,
    name: best.staff.name, closing: overrun,
    moves: moves, movesNote: movesNote,
    wait: minutes < 1 ? 'any min'
      : minutes > ETA_CLOCK_AFTER_MIN ? formatClock(best.at)
      : '~' + formatMins(best.at - t),
    why: why,
    note: [why, overrun ? closingNote(serviceId, overrun) : '', movesNote]
      .filter(Boolean).join('\n')
  };
}

// A service with nobody free to perform it cannot be booked at all, and nor
// can one that would run well past closing. Split the blocked providers by
// why, so the warning can say "on break" rather than insisting everyone is
// mid-service.
function serviceAvailability(serviceId) {
  var svc = serviceById.value[serviceId];
  var names = function (list) { return list.map(function (p) { return p.name; }); };
  var next = nextUpDetail(serviceId);
  var free = availableFor(serviceId);

  /* What the sheet refuses over is unchanged by any of the room-in-the-book
   * measuring above it: whether the service can be armed at all is still a
   * question about the floor and the clock. Somebody free this second would
   * start now whatever the book says about how far they would get, so closing
   * is measured from now for them — the *Next* line may well have moved on to
   * a later technician with more room, and that is a suggestion, not a rule.
   * A short walk-in still fits in a short gap, and refusing to arm it would
   * take a decision that belongs to the desk. */
  var overrun = free.length ? closingOverrun(serviceId, null) : next.closing;
  var late = overrun && overrun.blocked ? overrun : null;

  /* Somebody is free and the day is not long enough. That is its own answer
   * and a different one — nothing is wrong with the floor, so none of the
   * "everyone is busy" lines belong in it. */
  if (free.length) {
    if (!late) return { ok: true };
    return {
      ok: false,
      serviceId: serviceId,
      serviceName: svc ? svc.name : 'That service',
      reason: 'closing',
      late: late,
      lateNote: closingNote(serviceId, late),
      freeNames: names(free),
      minutes: svc ? svc.defaultMinutes : null,
      closeClock: formatClock(late.closeAt),
      endClock: formatClock(late.endsAt),
      overLabel: formatMins(late.endsAt - late.closeAt),
      graceMinutes: late.grace,
      names: [], busyNames: [], skippedNames: [], goneNames: [],
      etaName: '', etaAt: null, etaSoon: false
    };
  }

  var providers = providersFor(serviceId);
  var busy = providers.filter(function (p) { return isBusy(p.id); });
  return {
    ok: false,
    serviceId: serviceId,
    serviceName: svc ? svc.name : 'That service',
    reason: providers.length ? 'busy' : 'none',
    // Nobody free AND the wait runs past closing: the wait is still the
    // headline, but a time to come back at is no use if the doors are shut by
    // then, so the sheet says so underneath. The same three strings the
    // closing answer above carries, so one paragraph reads either.
    late: late,
    lateNote: late ? closingNote(serviceId, late) : '',
    closeClock: late ? formatClock(late.closeAt) : '',
    endClock: late ? formatClock(late.endsAt) : '',
    overLabel: late ? formatMins(late.endsAt - late.closeAt) : '',
    graceMinutes: CLOSING_GRACE_MIN,
    freeNames: [],
    names: names(providers),
    busyNames: names(busy),
    skippedNames: names(providers.filter(function (p) {
      return isSkipped(p.id) && !isBusy(p.id);
    })),
    /* ...and the ones whose day has simply run out. Its own list rather than
     * folded in with the skipped: "on a break" and "went home at five" are
     * different answers, and only one of them can be undone by bringing
     * somebody back into the rotation, which is what that paragraph tells the
     * desk to go and do. */
    goneNames: names(providers.filter(function (p) {
      return isShiftSpent(p.id) && !isSkipped(p.id) && !isBusy(p.id);
    })),
    // The question the desk asks straight after "not now" is "then when".
    // A clock time rather than a countdown: the sheet sits open while somebody
    // reads it, and a countdown frozen at the moment of the click would lie.
    etaName: next.kind === 'eta' ? next.staff.name : '',
    etaAt: next.kind === 'eta' ? next.at : null,
    etaSoon: next.kind === 'eta' && next.minutes < 1
  };
}

function armService(serviceId) {
  if (ui.armedServiceId === serviceId) { ui.armedServiceId = null; return false; }
  // After the toggle-off, never before it: putting a service down is the one
  // thing a closed day should still let somebody finish.
  if (refuseWhileClosed()) return false;
  var check = serviceAvailability(serviceId);
  if (!check.ok) { ui.serviceWarning = check; return false; }
  ui.armedServiceId = serviceId;
  return true;
}

function closeServiceWarning() { ui.serviceWarning = null; }

function disarm() { ui.armedServiceId = null; }

function toggleRequestMode() {
  if (!ui.requestsOn) return;
  ui.requestMode = !ui.requestMode;
}

// opts: { serviceId, requested, quiet, sortAt, at, clientName, link: {
//         appointmentId, appointmentServiceId, clientName, sourceServiceName } }
// `clientName` is a name with no booking behind it — a walk-in who said who
// they were, which until the lobby tablet existed nobody ever did. Separate
// from `link` deliberately: a link means "this square IS that booking" and
// carries five other ids with it, so borrowing it to pass one string would put
// a turn on the board claiming a relationship to a book it has nothing to do
// with.
// `quiet` suppresses every toast this makes — for the caller that books
// several turns at once and would rather say so in one line.
// `at` is { startedAt, endedAt } when the times come from somewhere other than
// the clock — a booking's own start, and its end too when the service is
// already over. An end is what makes it a square being written down rather
// than one starting now, and only that relaxes the rules below.
// `sortAt` places it in the column instead of at the bottom.
// `id` is the square's id when the caller has to agree on it with other
// stations — see `autoWriteUp`. Otherwise one is drawn at random.
function bookTurn(staffId, opts) {
  opts = opts || {};
  var say = opts.quiet ? function () {} : toast;
  var at = opts.at || null;
  var recorded = !!(at && at.endedAt != null);
  var serviceId = opts.serviceId || ui.armedServiceId;
  if (!serviceId) return false;
  var svc = serviceById.value[serviceId];
  var staff = staffOf(staffId);
  if (!svc || !staff) return false;

  // The books are shut. Every path that puts a square up comes through here —
  // the chips, the hotkeys, the assign sheet, a drag off the book and
  // auto-start — so this one guard closes all of them at once. The ways in are
  // shut a step earlier as well; see `refuseWhileClosed`.
  if (dayClosed.value) {
    say(CLOSED_NOTE, 'warn');
    return false;
  }

  var link = opts.link || null;
  if (link && link.appointmentServiceId && turnByApptService.value[link.appointmentServiceId]) {
    say('That service already has a turn.', 'warn');
    return false;
  }

  // A square being recorded after the fact does not need them free now.
  if (!recorded && isBusy(staffId)) {
    say(staff.name + ' is still on a service.', 'warn');
    return false;
  }
  if (!state.dayStaff[staffId] || !state.dayStaff[staffId].present) {
    say(staff.name + ' is not marked in today.', 'warn');
    return false;
  }

  /* Would this walk-in still be running when their next client arrives?
   *
   * Only walk-ins: a square being seated off the book is the booking, and one
   * written up after the fact cannot be in anybody's way. `force` is the way
   * back through once the desk has read the warning and said yes anyway.
   *
   * This returns false either way. The sheet is the answer now — a request
   * stops it dead, and anything else waits on a decision — so the caller gets
   * "not booked", which is true in both cases. */
  if (!recorded && !link && !opts.force && !opts.quiet) {
    var clash = walkInCollision(staffId, serviceId, null);
    if (clash) {
      // What the desk had already ticked, carried through the sheet, so that
      // saying yes to the warning puts up the square that was asked for
      // rather than a plain one.
      clash.starWanted = opts.requested != null ? opts.requested : ui.requestMode;
      ui.bookClash = clash;
      return false;
    }
  }

  /* A service added at the chair, going to the technician whose square on the
   * visit it was added to, is part of that square — js/rules/add-on.js. Every
   * way of seating a booking by hand comes through here (a chip, the assign
   * sheet, a drag, the write-up button), so this is where it is caught. To
   * somebody else it is their own turn as always. */
  if (link && link.appointmentId && link.appointmentServiceId) {
    var visit = apptById.value[link.appointmentId];
    var line = visit && visit.services.find(function (s) { return s.id === link.appointmentServiceId; });
    var host = line && addOnHostTurn(visit, line);
    if (host && host.staffId === staffId) {
      foldInto(host, visit, line);
      say(visit.clientName + '’s ' + line.name + ' went onto ' + host.staffName + '’s ' +
        host.serviceName + ' square — added at the chair, so no new turn.', 'ok');
      return host;
    }
  }

  /* A later service of a visit going up before an earlier one booked with the
   * same technician — see js/rules/visit-order.js. Asked, not decided: the
   * sheet offers the earlier one or this one anyway (`orderOk`). Not for a
   * square nobody is putting up by hand, nor for one being written up after the
   * fact. Returns false for the same reason the clash above does. */
  if (link && link.appointmentId && link.appointmentServiceId && !recorded &&
      !opts.quiet && !opts.orderOk) {
    var earlier = earlierUnseatedFor(staffId, link);
    if (earlier) {
      ui.orderAsk = {
        staffId: staffId, opts: Object.assign({}, opts),
        appt: earlier.appt, earlier: earlier.svc, later: earlier.later
      };
      return false;
    }
  }

  /* With requests switched off no new turn is ever starred, whatever a caller
   * passes: the sheets that offer the box are hidden, and one left ticked in a
   * component's own state must not sneak a star onto the board.
   *
   * On, a booking the desk already marked as a request carries the star up
   * with it — the mark was made hours ago, on the booking, and nobody should
   * have to remember it again at the moment the client sits down. */
  var found = link && bookedServiceById(link.appointmentServiceId);
  var marked = !!(found && isApptRequested(found.svc, staffId));
  var requested = ui.requestsOn &&
    (marked || (opts.requested != null ? opts.requested : ui.requestMode));
  /* A client still in Mangomint's waiting room has not sat down. The square
   * goes up — the technician is spoken for — but held: its clock does not run
   * until Mangomint has them checked in, and then it starts from that moment.
   * See "Held until checked in" below. Not for a service being written up
   * after the fact, which is over whatever the waiting room says. */
  var hold = !recorded && !!(link && link.appointmentId) && isWaitingInBook(link.appointmentId);
  var startedAt = !hold && at && at.startedAt ? at.startedAt : Date.now();
  var sched = scheduleFor(link, serviceId, startedAt);
  var turn = {
    id: opts.id || turnIdFor(link),
    waiting: hold,
    scheduledMinutes: sched.minutes,
    scheduledEndAt: sched.endsAt,
    appointmentId: link ? link.appointmentId : null,
    appointmentServiceId: link ? link.appointmentServiceId : null,
    sourceServiceId: link && link.sourceServiceId != null ? link.sourceServiceId : null,
    clientId: link && link.clientId != null ? link.clientId : null,
    clientName: (link ? link.clientName : opts.clientName) || '',
    sourceServiceName: link ? link.sourceServiceName : '',
    // Snapshotted off the booking, the same way the schedule above is. A
    // walk-in has none, and gets an empty list rather than a null to compare.
    options: found ? bookedOptionsOf(found.svc) : [],
    // Nothing folded in yet. Every square starts as one service and only the
    // merge sheet ever adds to this; see `mergeIntoTurn`.
    extras: [],
    businessDate: state.businessDate,
    staffId: staffId,
    staffName: staff.name,
    serviceId: svc.id,
    serviceName: svc.name,
    serviceLetter: svc.abbrev,
    // Being asked for by name says nothing about how much work the service
    // is: a half turn stays a half turn. The star is a note on the square,
    // not an adjustment to it.
    turnWeight: svc.turnWeight,
    requested: requested,
    startedAt: startedAt,
    endedAt: recorded ? at.endedAt : null,
    /* Where the clock was told to start, on the rare square somebody was asked
     * about — see `assignLateStart`. Null on everything else, which is almost
     * everything: no question was put, so there is no answer to record and
     * nothing for the book to overwrite later.
     *
     * Both are set together or not at all. The choice alone would not survive
     * a re-timed appointment (there would be nothing to compare the book
     * against) and the remembered start alone would not say what was chosen. */
    startChoice: opts.startChoice || null,
    startBookedAt: opts.startChoice ? opts.startBookedAt : null,
    sortAt: opts.sortAt != null ? opts.sortAt : nextSortAt(staffId)
  };
  // Before the turn lands, so the rotation still reads as it did when the
  // booking was made. Nothing to charge when the turn being written down
  // happened hours ago: the rotation moved past it at the time.
  if (!recorded) chargeSkips(staffId, serviceId);
  state.turns.push(turn);
  pushTurn(turn);
  // A booking with a price is weighed by it, not by its type — and with the
  // rest of its sitting. See `applyPriceWeights`.
  applyPriceWeights();

  var offMenu = staff.serviceIds.indexOf(serviceId) === -1;
  var note = '';
  if (isSkipped(staffId)) note = ' — marked as skipped';
  else if (offMenu) note = ' — not on their service list';
  var label = link && link.sourceServiceName ? link.sourceServiceName : svc.name;
  say(
    label + (requested ? ' (request)' : '') + ' → ' + staff.name +
      (recorded ? ' — recorded as finished' : '') +
      (hold ? ' — waiting for check-in in ' + scheduling.name.value + ', timer not started' : '') + note,
    note ? 'warn' : 'ok'
  );
  ui.armedServiceId = null;
  ui.requestMode = false;
  /* The turn rather than `true`.
   *
   * Every caller before the check-in screen wanted only "did that work", and
   * still gets an answer that is truthy exactly when it did. The check-in needs
   * the square itself: it writes down which turn a waiting client was seated
   * into, and asking for "the newest turn in that column" a moment later is the
   * same answer only until two stations do it at once. */
  return turn;
}

/* Straight to whoever is next up.
 *
 * Two ways in and one act: the desk presses the service's key, or the phone
 * taps its row in the ＋ sheet. Both are somebody saying "this service, to
 * whoever the rotation says" — the choice of technician is the board's, which
 * is the whole promise the Next availability list is named after — so both come
 * through here rather than the phone growing a second, subtly different rule
 * about who gets the work.
 *
 * `armOnly` is the shift-held press: arm and stop, so the square can still be
 * placed by hand. The phone has no shift, and its own way of asking for that is
 * the row's ＞, which arms and lets a row be tapped.
 */
function bookNextUp(serviceId, armOnly) {
  var svc = serviceById.value[serviceId];
  if (!svc) return false;
  // The menu this is a shortcut to is not on screen at all once the books are
  // shut, so neither the key nor the sheet row should be either.
  if (refuseWhileClosed()) return false;

  var check = serviceAvailability(svc.id);
  if (!check.ok) { ui.serviceWarning = check; return false; }

  if (armOnly || ui.requestMode) {
    ui.armedServiceId = svc.id;
    if (ui.requestMode) toast(svc.name + ' request — pick the tech.', 'info');
    return true;
  }
  ui.armedServiceId = svc.id;
  // Whoever is next up with room for it, and failing that whoever is merely
  // next up. This is a deliberate act and the check above has already said the
  // service can go up, so it books somebody rather than stalling on an
  // afternoon where nobody has a clear run.
  //
  // Nobody at all should be unreachable — `serviceAvailability` said yes — but
  // the two disagreeing must not be a thrown error on a phone in somebody's
  // hand. Armed and left for a row to be tapped is the honest fallback.
  var who = availableWithRoom(svc.id)[0] || availableFor(svc.id)[0];
  if (!who) return false;
  return bookTurn(who.id);
}

// Hotkey path: the key names the service, and the act is the one above.
function bookByShortcut(key, armOnly) {
  var svc = shortcutMap.value[String(key).toUpperCase()];
  if (!svc) return false;
  return bookNextUp(svc.id, armOnly);
}

function stopTurn(turnId) {
  var t = turnOf(turnId);
  if (!t || t.endedAt != null) return;
  t.endedAt = Date.now();
  // Stopped before the client was ever checked in: nothing ran, and the square
  // keeps the moment it went up rather than waiting for a check-in to rewrite it.
  t.waiting = false;
  pushTurn(t);
  ui.cellMenuTurnId = null;
  ui.turnMenu = null;
}

function reopenTurn(turnId) {
  var t = turnOf(turnId);
  if (!t || t.endedAt == null) return;
  if (activeTurn(t.staffId)) {
    toast('That tech already has a service running.', 'warn');
    return;
  }
  t.endedAt = null;
  pushTurn(t);
  ui.cellMenuTurnId = null;
  ui.turnMenu = null;
}

/* ---------- correcting a square's clock ----------
 * Squares get put up late, stopped late, or started on the wrong tech and
 * moved. The times are what History and every length in it are built from, so
 * they have to be fixable. Both are wall-clock on the turn's own business
 * date; the square keeps its position in the column, because that is the
 * order the rotation handed the turns out, not the order they ran. */

function pad2(n) { return (n < 10 ? '0' : '') + n; }

function clockValue(ms) {
  if (ms == null) return '';
  var d = new Date(ms);
  return pad2(d.getHours()) + ':' + pad2(d.getMinutes());
}

function clockToMs(dateKey, hhmm) {
  if (!hhmm) return null;
  var p = String(hhmm).split(':');
  var h = Number(p[0]), m = Number(p[1]);
  if (isNaN(h) || isNaN(m)) return null;
  var d = String(dateKey).split('-');
  return new Date(Number(d[0]), Number(d[1]) - 1, Number(d[2]), h, m, 0, 0).getTime();
}

// An empty finish means the service is still going, which is how a square
// stopped by mistake is put back in service.
function setTurnTimes(turnId, startClock, endClock) {
  var t = turnOf(turnId);
  if (!t) return false;
  var start = clockToMs(t.businessDate, startClock);
  if (start == null) { toast('That start time could not be read.', 'warn'); return false; }
  var end = endClock ? clockToMs(t.businessDate, endClock) : null;
  if (endClock && end == null) { toast('That finish time could not be read.', 'warn'); return false; }

  var now = Date.now();
  if (start > now) { toast('A service cannot have started in the future.', 'warn'); return false; }
  if (end != null && end > now) { toast('A service cannot finish in the future.', 'warn'); return false; }
  if (end != null && end < start) { toast('A service cannot finish before it starts.', 'warn'); return false; }
  if (end == null && t.endedAt != null) {
    var busy = activeTurn(t.staffId);
    if (busy && busy.id !== t.id) {
      toast(t.staffName + ' already has a service running.', 'warn');
      return false;
    }
  }

  t.startedAt = start;
  t.endedAt = end;
  pushTurn(t);
  toast(end == null
    ? t.staffName + '’s ' + t.serviceName + ' is back in progress.'
    : 'Times updated — ' + formatDuration(end - start) + '.', 'ok');
  return true;
}

function setTurnService(turnId, serviceId) {
  var t = turnOf(turnId);
  var svc = serviceById.value[serviceId];
  if (!t || !svc) return;
  t.serviceId = svc.id;
  t.serviceName = svc.name;
  t.serviceLetter = svc.abbrev;
  // Re-typing the first service on a merged square does not re-answer what the
  // whole sitting counted for; the menu offers that separately. See
  // `applyServiceWeight`.
  applyServiceWeight(t, svc.turnWeight);
  // A priced booking still decides: the type is what the work is called, the
  // price is what it counts for. See js/rules/price-weight.js.
  applyPriceWeights();
  // The countdown belongs to whatever the square is now. A booking behind it
  // still outranks the new type's default — the client is booked for what
  // they are booked for, whatever the board is calling it.
  var sched = scheduleFor(
    { appointmentServiceId: t.appointmentServiceId }, svc.id, t.startedAt);
  t.scheduledMinutes = sched.minutes;
  t.scheduledEndAt = sched.endsAt;
  pushTurn(t);
}

// Only ever the star. What the turn is worth is the service's business.
function toggleTurnRequested(turnId) {
  var t = turnOf(turnId);
  if (!t || !ui.requestsOn) return;
  t.requested = !t.requested;
  pushTurn(t);
}

/* Why a column will not take the square being dragged over it, or null when it
 * will. A drop that quietly does nothing is indistinguishable from a broken
 * one, so the board says this rather than swallowing it.
 *
 * Two rules, and the difference between them is the difference between a
 * service and a record of one. A service still RUNNING needs its new
 * technician's hands: two of them on one person is the one thing the grid
 * cannot draw, and it is not a thing that can happen in the room either. A
 * square that has FINISHED asks nothing of anybody — it is the day's account of
 * work already done — so it can be filed against whoever actually did it,
 * including somebody who is in service this second. That is the ordinary case:
 * the desk notices at four that the two o'clock went up against the wrong name,
 * and by then everybody is busy with something else.
 *
 * Being marked in is required either way. A column that is not on the board is
 * not a place a square can be.
 */
function moveTurnBlockedReason(turn, staffId) {
  if (!turn || !staffId) return null;
  var s = staffOf(staffId);
  if (!s) return null;
  var d = state.dayStaff[staffId];
  if (!d || !d.present) return s.name + ' is not marked in today.';
  if (turn.endedAt != null) return null;
  var busy = activeTurn(staffId);
  if (busy && busy.id !== turn.id) {
    // The square they are on is not in the way, it is the other half of a
    // gesture the board has: dropped on it, the two trade places. Said here
    // because this refusal is where somebody aiming at a busy column lands.
    return s.name + ' is still on a service — drop it on that square to swap ' +
      'the two, or stop it first.';
  }
  return null;
}

/* Which square a drop at (column, row) means, as the turn it lands in front of.
 *
 * `anchorId` null with no reason is the end of the column — everything past the
 * last square is "after everything", which is where a square dropped on empty
 * space belongs.
 *
 * The two kinds of square that are not positions are refused by name. A slashed
 * square above a late arrival is a turn that had already gone past before they
 * walked in, and a red one is a turn the rotation was told to skip: neither is
 * a place in the order, and rounding a drop onto one to its nearest neighbour
 * would put the square somewhere nobody aimed at.
 */
function turnDropTarget(staffId, row) {
  var missed = missedTurns(staffId);
  if (row < missed) {
    return { blocked: 'Those squares are turns that had already gone past.' };
  }
  var rows = rowsFor(staffId);
  var offset = row - missed;
  if (offset >= rows.length) return { anchorId: null };
  if (rows[offset].skip) {
    return { blocked: 'That square is a turn that was skipped, not a place in the column.' };
  }
  var n = 0;
  for (var i = 0; i < offset; i++) if (!rows[i].skip) n += 1;
  var slot = slotsFor(staffId)[n];
  return { anchorId: slot && slot.turns[0] ? slot.turns[0].id : null };
}

/* A sort value that puts a square immediately in front of another one, on the
 * same arithmetic `sortAtBefore` uses for a booking dragged off the book —
 * order is `sortAt` and nothing else, so slotting in is finding a number
 * between two others, and everything below moves down by itself.
 *
 * The moving square is left out of the reckoning. It is about to leave the
 * position it is in, and measuring against its own neighbours would pin it
 * where it already is — the reorder-within-one-column case, which is most of
 * what this is used for.
 */
function sortAtBeforeTurn(staffId, anchorId, movingId) {
  var list = turnsFor(staffId).filter(function (t) { return t.id !== movingId; });
  var i = list.findIndex(function (t) { return t.id === anchorId; });
  if (i === -1) return nextSortAt(staffId);
  var anchor = list[i];
  var lo = i > 0 ? list[i - 1].sortAt : anchor.sortAt - 2000;
  var mid = Math.floor((lo + anchor.sortAt) / 2);
  if (mid > lo && mid < anchor.sortAt) return mid;
  return respaceColumn(staffId, anchorId);
}

/* ---------- what a drop would do ----------
 *
 * One answer, read by three things that must never disagree: the preview under
 * the pointer, the sheet that asks, and the move itself. Each of them re-reads
 * it rather than being handed a copy — a sheet can sit open across a webhook,
 * and a board that has moved on underneath one is the case where a stale copy
 * writes the square somewhere nobody chose.
 */
function turnMovePlan(turnId, staffId, row, anchorId) {
  var turn = turnOf(turnId);
  var to = staffOf(staffId);
  if (!turn || !to) return null;

  var target = anchorId === undefined ? turnDropTarget(staffId, row) : { anchorId: anchorId };
  var blocked = target.blocked || moveTurnBlockedReason(turn, staffId);
  var same = turn.staffId === staffId;
  var before = turnsFor(staffId);
  var after = moveRules.reorderTurns(before, turn, target.anchorId);

  return {
    turnId: turn.id,
    turn: turn,
    staffId: staffId,
    anchorId: target.anchorId == null ? null : target.anchorId,
    same: same,
    before: before,
    after: after,
    // Nothing to confirm and nothing to do: the square is already where it is
    // being dropped. Dragging one onto the square below it reads as a move and
    // is not one, and a sheet for that would teach the desk to dismiss sheets.
    noop: same && moveRules.sameOrder(after, before),
    blocked: blocked || null
  };
}

// The plan for whatever is in the air this second, or null when the board is at
// rest. Everything that follows the pointer hangs off this one computed.
var turnDropPlan = computed(function () {
  var d = ui.turnDrag;
  if (!d || !d.staffId) return null;
  return turnMovePlan(d.turnId, d.staffId, d.row);
});

// The destination column, redrawn as it would read — built by the same
// `cellsFrom` the board itself is built from, so the preview cannot draw a
// square the drop would not make. Nothing while the drop is refused or would
// change nothing: there is no point showing work moving out of the way for a
// square that is not going anywhere.
var turnDropPreview = computed(function () {
  var plan = turnDropPlan.value;
  if (!plan || plan.blocked || plan.noop) return null;
  var col = boardColumns.value.find(function (c) {
    return c.staff.id === plan.staffId;
  });
  if (!col) return null;

  var rows = buildRows(buildSlots(plan.after), skipsFor(plan.staffId));
  var cells = cellsFrom(rows, missedTurns(plan.staffId), rowCount.value,
    mismatchByTurn.value, serviceById.value, plan.turnId);
  var hidden = hidingDoneRows.value ? completedRows.value : null;

  return {
    staffId: plan.staffId,
    column: {
      staff: col.staff,
      busy: col.busy,
      skipped: col.skipped,
      offShift: col.offShift,
      credit: col.credit,
      openHalf: col.openHalf,
      targetRow: col.targetRow,
      cells: hidden
        ? cells.filter(function (cell) { return !hidden[cell.row]; })
        : cells
    }
  };
});

/* ---------- the move itself ----------
 *
 * `resetTimer` is only ever a question about a service still running: a client
 * who has changed chairs either began again or did not. A square that has
 * finished has both its times on it already, and they are the record — so the
 * sheet does not ask, and nothing here touches them.
 */
function moveTurnTo(turnId, toStaffId, anchorId, resetTimer) {
  var plan = turnMovePlan(turnId, toStaffId, null, anchorId == null ? null : anchorId);
  if (!plan) return false;
  if (plan.blocked) { toast(plan.blocked, 'warn'); return false; }
  if (plan.noop) return true;

  var t = plan.turn;
  var to = staffOf(toStaffId);
  var from = staffOf(t.staffId);
  var sortAt = plan.anchorId
    ? sortAtBeforeTurn(toStaffId, plan.anchorId, t.id)
    : nextSortAt(toStaffId);
  if (sortAt == null) return false;

  t.sortAt = sortAt;
  t.staffId = toStaffId;
  t.staffName = to.name;
  if (resetTimer && t.endedAt == null) t.startedAt = Date.now();
  pushTurn(t);

  // A reorder inside one column is not a handover, and reporting it as
  // "Ana → Ana" would read as a bug. The square number is what changed, so
  // that is what the line says.
  if (plan.same) {
    toast(t.serviceName + ' moved to turn ' + turnRowNumber(t.id) + ' — ' +
      to.name + '’s later squares shift down.', 'ok');
  } else {
    toast(
      t.serviceName + ': ' + (from ? from.name : '?') + ' → ' + to.name +
        (t.endedAt != null
          ? ' (turn ' + turnRowNumber(t.id) + ')'
          : (resetTimer ? ' (timer reset)' : ' (timer kept)')),
      'ok'
    );
  }
  return true;
}

function removeTurn(turnId) {
  var i = state.turns.findIndex(function (x) { return x.id === turnId; });
  if (i === -1) return;
  // A booked square taken off on purpose stays off: the automatic write-up
  // must not put it straight back at the next checkout refresh.
  noteWrittenUp(state.turns[i]);
  state.turns.splice(i, 1);
  sync.enqueue({ table: 'tt_turns', type: 'delete', match: { id: turnId } });
  ui.cellMenuTurnId = null;
  ui.turnMenu = null;
}

function openCellMenu(turnId) { ui.cellMenuTurnId = turnId; }
function closeCellMenu() { ui.cellMenuTurnId = null; }

// Assigning a booked service to a tech. Pre-picks the turn type from the
// service mapping, and the tech from `dueTakerFor` — the same answer the Due
// now chip draws, because the chip is what was clicked to get here and a sheet
// that opens on somebody else has just contradicted the row that opened it.
//
// Only when that answer can start it now, though. The chip may perfectly well
// name a technician who was asked for and is still mid-service, and the list
// below is everyone free — pre-picking somebody who is not on it would leave
// the sheet with a name in the button and no row lit up. That case falls to the
// fallback, which is who the sheet has always opened on.
function openAssign(appt, apptService) {
  if (refuseWhileClosed()) return;
  var mapped = trackerServiceForBooked(apptService);
  var allowed = allowedStaffForBooked(apptService);
  var permitted = function (s) { return staffAllowedForBooked(apptService, s.id); };
  var taker = dueTakerFor(appt, apptService);
  var booked = taker && taker.ready ? staffOf(taker.staffId) : null;
  var fallback = availableQueue.value.find(function (s) {
    return permitted(s) && (!mapped || s.serviceIds.indexOf(mapped.id) !== -1);
  }) || availableQueue.value.find(permitted) || null;
  var pick = booked ? booked.id : (fallback ? fallback.id : null);

  ui.assign = {
    appointmentId: appt.id,
    appointmentServiceId: apptService.id,
    sourceServiceId: apptService.serviceId,
    clientId: appt.clientId != null ? appt.clientId : null,
    staffIds: allowed,
    options: activeOptionsFor(apptService).map(function (o) { return o.name; }),
    clientName: appt.clientName,
    sourceServiceName: apptService.name,
    bookedTechName: (apptService.techs && apptService.techs[0]) || '',
    serviceId: mapped ? mapped.id : null,
    staffId: pick,
    // A booking the desk marked as a request hours ago opens with the box
    // already ticked. bookTurn carries that mark up whatever this sheet says,
    // so a box left clear was not a choice offered — it was the sheet failing
    // to mention that the square is going up starred.
    requested: isApptRequested(apptService, pick),
    // Where the timer starts, and only ever consulted when there is a question
    // — see `assignLateStart`. 'now' rather than 'booked' because the question
    // is only asked when the booked time is one this technician was provably
    // still working through, and a default has to be the answer that is right
    // when nobody reads it.
    startFrom: 'now'
  };
}

// Picking the technician in that sheet. Not a plain write to `staffId`, because
// a mark is for a PERSON: moving the sheet off the one who was asked for moves
// it off the request, and back on to them puts the star back.
function setAssignStaff(staffId) {
  if (!ui.assign) return;
  ui.assign.staffId = staffId;
  var found = bookedServiceById(ui.assign.appointmentServiceId);
  ui.assign.requested = !!(found && isApptRequested(found.svc, staffId));
  // And so is the start. Whether the booked time is a time this technician
  // could have begun at is a question about a PERSON — the one who ran over is
  // not the one the sheet has just moved to — so moving the sheet re-asks it
  // from the default rather than carrying the last person's answer across.
  ui.assign.startFrom = 'now';
}

function closeAssign() { ui.assign = null; }

// The times a booking would land with, wherever it is being assigned from.
// The sheet and the drag have to agree: the same appointment put up the same
// way is the same square, and a service that finished at noon is a finished
// square whether it was dragged onto the board or picked from a list.
function assignOutcome(appointmentId, appointmentServiceId) {
  var found = bookedServiceById(appointmentServiceId);
  var out = bookedOutcome({
    link: { appointmentId: appointmentId, appointmentServiceId: appointmentServiceId },
    startsAt: found ? found.svc.startsAt : null,
    durationMinutes: found ? found.svc.durationMinutes : 0
  });
  if (!out.startedAt) return null;
  return { startedAt: out.startedAt, endedAt: out.finished ? out.endedAt : null };
}

/* Whether this sheet has a start to ask about, and what it would say.
 *
 * Everything the question needs is already on the board; see js/rules/overrun.js
 * for which four times have to line up. What is added here is the two things a
 * sentence needs — who the technician was with, and what that service was —
 * because "the previous service ran over" is a claim the desk should be able to
 * check against their own memory of the last hour before answering it.
 *
 * Reactive on purpose, and on `minuteNow` rather than the second hand. The
 * question belongs to the technician the sheet is on, so picking somebody else
 * has to make it appear or disappear; and a sheet can sit open long enough for
 * a booking to come due, at which point the two answers converge and the
 * question takes itself away.
 */
var assignLateStart = computed(function () {
  var a = ui.assign;
  if (!a || !a.staffId || !a.appointmentServiceId) return null;
  var out = assignOutcome(a.appointmentId, a.appointmentServiceId);
  // A booking whose time is wholly past goes up as work that was done rather
  // than work that is starting. There is no timer to start, so nothing to ask.
  if (!out || out.endedAt != null) return null;

  var prev = overrun.lastFinished(turnsFor(a.staffId));
  var late = overrun.lateStart(prev, turnEndsAt(prev), out.startedAt, minuteNow.value);
  if (!late) return null;
  return {
    bookedAt: late.bookedAt,
    freedAt: late.freedAt,
    over: late.over,
    late: late.late,
    prevClient: prev.clientName || '',
    prevService: prev.serviceName || ''
  };
});

function confirmAssign() {
  var a = ui.assign;
  if (!a || !a.serviceId || !a.staffId) return false;

  /* The booking's own times, unless the desk has just said they are not this
   * square's. Only `startedAt` moves: the sheet's question is about when the
   * work began, and `out.endedAt` is null here anyway — a booking already over
   * is a record, and `assignLateStart` does not ask about those.
   *
   * `Date.now()` rather than the answer the sheet was drawn from: the sheet may
   * have been open for a while, and the square is starting when the button was
   * pressed. */
  var out = assignOutcome(a.appointmentId, a.appointmentServiceId);
  var late = assignLateStart.value;
  if (late && a.startFrom === 'now') out = { startedAt: Date.now(), endedAt: null };

  // Measured before the square lands; see "bringing the book into line" below.
  var found = bookedServiceById(a.appointmentServiceId);
  var bookMove = found ? prepareBookMove(a.staffId, found.appt, found.svc, out) : null;

  var ok = bookTurn(a.staffId, {
    serviceId: a.serviceId,
    requested: a.requested,
    at: out,
    // Only when a question was actually put. `late.bookedAt` is the book's own
    // start, whichever answer was given — it is what a later poll compares
    // against to find out the appointment has been re-timed, not a copy of
    // where the clock ended up.
    startChoice: late ? a.startFrom : null,
    startBookedAt: late ? late.bookedAt : null,
    link: {
      appointmentId: a.appointmentId,
      appointmentServiceId: a.appointmentServiceId,
      sourceServiceId: a.sourceServiceId,
      clientId: a.clientId,
      clientName: a.clientName,
      sourceServiceName: a.sourceServiceName
    }
  });
  if (ok) {
    ui.assign = null;
    bringBookInLine(bookMove);
  }
  return ok;
}

/* ---------- bringing the book into line under turn order ----------
 *
 * The desk's tap on the assign sheet, and only that, approves a change in
 * Mangomint. The order is the promise: measure, put the square up, and only then
 * touch the network. The plan is worked out before the square lands because the
 * square would otherwise be found in its own technician's way, and it is carried
 * out after because the client is in the building and nothing about the board waits
 * on a save. Nothing here can stop a square or take one down.
 *
 * The rules and the wording are in js/rules/pending-moves.js, the search in
 * js/rules/turn-order.js. What is here is the measuring only the board can do.
 */
var pendingStore = reactive({ rows: [], writeKeySet: null, busy: {} });
// Written here and not yet listed by the server, so a read cannot lose them.
var pendingUnsent = {};
// Closed here with the write still on its way, so a read cannot bring them back.
var pendingClosedHere = {};
// A station can hear about an item before it hears about the square, which went
// up first. Past this, a missing square really is missing.
var PENDING_SQUARE_GRACE_MS = 60000;
// How long a clean plan's row stays out of sight: as long as the call is given.
var PENDING_FIRST_TRY_MS = 60000;

var pendingMoves = computed(function () {
  if (!scheduling.enabled.value) return [];
  return pendingStore.rows.filter(function (r) {
    return r.date === state.businessDate && !r.closed_at;
  });
});

function pendingNames(item) {
  var staff = {};
  state.staff.forEach(function (s) {
    staff[s.id] = s.name;
    if (s.externalStaffId) staff[s.externalStaffId] = s.name;
  });
  return {
    staff: staff,
    bookings: (item.plan && item.plan.bookings) || {},
    writeKeySet: pendingStore.writeKeySet,
    // No grace: Swap goes the moment the square does, here and at the tap.
    square: squaresAreRead() ? squareOf(item, state.turns).where : 'unknown'
  };
}

var pendingMoveLines = computed(function () {
  return pendingMoves.value.filter(function (item) {
    return !onFirstTry(item, minuteNow.value, PENDING_FIRST_TRY_MS);
  }).map(function (item) {
    return { item: item, line: pendingLine(item, pendingNames(item)), busy: !!pendingStore.busy[item.id] };
  });
});

function mangomintId(value) {
  var n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function firstBookId(svc) {
  return mangomintId((svc.techIds || [])[0]);
}

function blockOf(appt, svc) {
  return { id: svc.id, from: svc.startsAt, to: svc.startsAt + bookedHoldMs(svc), svc: svc, appt: appt };
}

// What moves with this service: the visit's half turns the same technician has,
// counted the way `bookingPlanByStaff` counts them. See js/rules/same-time.js.
function unitOfBooking(appt, svc) {
  var mine = svc.techs || [];
  var lines = appt.services.filter(function (x) {
    if (x.id === svc.id) return true;
    return !turnByApptService.value[x.id] && !bookedLineOver(appt, x) &&
      (x.techs || []).some(function (t) { return mine.indexOf(t) !== -1; });
  });
  var units = sameTimeRules.halfPairs(lines.map(function (x) {
    var m = trackerServiceForBooked(x);
    return { id: x.id, apptId: appt.id, from: x.startsAt, to: x.startsAt + bookedHoldMs(x),
             weight: m ? m.turnWeight : null, svc: x };
  }));
  var unit = units.find(function (u) { return u.some(function (b) { return b.id === svc.id; }); });
  return unit.map(function (b) { return b.svc; });
}

/* A technician's book as planRoom wants it. Half of another client's pair can't go
 * alone, and a line with two technicians or an id the proxy couldn't take can't go
 * at all, so neither is free to move. Whether Mangomint will edit a booking isn't
 * known here for add-ons or repeating appointments, and the function refuses those. */
function bookOfForPlan(staffId) {
  var mine = pinnedBlocksByStaff.value[staffId] || [];
  var units = sameTimeRules.halfPairs(mine.map(function (b) {
    var m = trackerServiceForBooked(b.svc);
    return { id: b.svc.id, apptId: b.appt.id, from: b.from, to: b.to, weight: m ? m.turnWeight : null };
  }));
  var paired = {};
  units.forEach(function (u) {
    if (u.length > 1) u.forEach(function (b) { paired[b.id] = true; });
  });
  return mine.map(function (b) {
    return {
      id: b.svc.id, from: b.from, to: b.to, svc: b.svc, appt: b.appt,
      requested: !!requestInBook(b.svc),
      movable: !paired[b.svc.id] && (b.svc.techIds || []).length === 1 && firstBookId(b.svc) != null &&
        mangomintId(b.appt.id) != null && mangomintId(b.svc.serviceId) != null
    };
  });
}

function holderOfBooking(svcId) {
  var pinned = pinnedBlocksByStaff.value;
  return Object.keys(pinned).find(function (id) {
    return pinned[id].some(function (b) { return b.svc.id === svcId; });
  }) || null;
}

function describeBooking(svcId) {
  var found = bookedServiceById(svcId);
  if (!found) return { client: '', at: '' };
  return { client: shortClient(found.appt.clientName), at: clockOf(localStart(found.svc.startsAt)) };
}

function wireMove(appt, svc, fromId, toId) {
  return {
    appointment_id: mangomintId(appt.id),
    starts_at: localStart(svc.startsAt),
    service_id: mangomintId(svc.serviceId),
    from_staff_id: fromId,
    to_staff_id: toId
  };
}

function wireIsSound(wire) {
  return wire.every(function (m) {
    return m.appointment_id && m.service_id && m.from_staff_id && m.to_staff_id;
  });
}

function stuckPlan(reason) {
  return { kind: 'stuck', moves: [], why: '', reason: reason, blocking: [] };
}

// What the tap would mean, measured now. Null when it means nothing new, which is
// always the answer under follow the book, before anything else is looked at.
function prepareBookMove(staffId, appt, svc, at) {
  if (followsBook()) return null;
  var bring = null;
  try {
    var chosen = staffOf(staffId);
    var ids = svc.techIds || [];
    var techs = svc.techs || [];
    bring = shouldBringBookInLine({
      followBook: false,
      fromSheet: true,
      recorded: !!(at && at.endedAt != null),
      requested: !!requestInBook(svc),
      bookNamed: techs.length > 0 || ids.length > 0,
      chosenInBook: !!chosen && namedOnLine(chosen, svc, matchesStaff)
    });
    return bring ? planBookMove(chosen, appt, svc) : null;
  } catch (e) {
    // Whatever went wrong here, the client still gets their technician, and the desk
    // an item saying the book has to be looked at.
    console.error('Could not work out the move for the book.', e);
    return bookMoveAfterFailure({ boardStaffId: staffId, appt: appt, svc: svc, bring: bring });
  }
}

function planBookMove(chosen, appt, svc) {
  var lines = unitOfBooking(appt, svc);
  var toId = mangomintId(chosen.externalStaffId);
  var plan;
  var wire = [];
  var bookings = {};
  var blockers = [];

  if (lines.some(function (x) { return x.id !== svc.id && requestInBook(x); })) {
    plan = stuckPlan('pair_requested');
  } else if (unitAtSameTime(lines.map(function (x) { return blockOf(appt, x); }))) {
    plan = stuckPlan('same_time_pair');
  } else if (toId == null || lines.some(function (x) { return firstBookId(x) == null; })) {
    plan = stuckPlan('no_mangomint_id');
  } else {
    var giver = staffByExternalId((svc.techIds || [])[0]);
    plan = planRoom({
      unit: lines.map(function (x) { return blockOf(appt, x); }),
      toStaffId: chosen.id,
      fromStaffId: giver ? giver.id : null,
      bookOf: bookOfForPlan,
      clear: function (id, b) { return availabilityAgainst([], id, b.from, b.to).ok; },
      may: function (id, b) {
        var who = staffOf(id);
        return !!who && mayTakeBooked(who, b.svc);
      },
      staffIds: rotationStaff.value
        .filter(function (s) { return mangomintId(s.externalStaffId) != null; })
        .map(function (s, i) { return { id: s.id, credit: turnCredit(s.id), col: i }; })
        .sort(function (x, y) { return x.credit - y.credit || x.col - y.col; })
        .map(function (s) { return s.id; })
    });
    var unitIds = lines.map(function (x) { return x.id; });
    plan.moves.forEach(function (m) {
      var to = staffOf(m.toStaffId);
      var toMm = to ? mangomintId(to.externalStaffId) : null;
      if (unitIds.indexOf(m.id) !== -1) {
        var own = lines.find(function (x) { return x.id === m.id; });
        wire.push(wireMove(appt, own, firstBookId(own), toMm));
        return;
      }
      var found = bookedServiceById(m.id);
      var holder = holderOfBooking(m.id);
      var from = holder ? staffOf(holder) : null;
      bookings[m.id] = Object.assign(describeBooking(m.id), { fromStaffId: holder });
      wire.push(found
        ? wireMove(found.appt, found.svc, from ? mangomintId(from.externalStaffId) : null, toMm)
        : wireMove(appt, svc, null, null));
    });
    if (!wireIsSound(wire)) {
      plan = stuckPlan('no_mangomint_id');
      wire = [];
      bookings = {};
    }
    blockers = (plan.blocking || []).map(describeBooking);
  }

  return {
    appointmentId: appt.id,
    clientName: appt.clientName,
    clientId: appt.clientId,
    tappedServiceId: svc.serviceId,
    boardStaffId: chosen.id,
    bookStaffId: firstBookId(svc),
    bookName: (svc.techs || [])[0] || '',
    unit: lines.map(function (x) {
      return { lineId: x.id, serviceId: x.serviceId, startsAt: x.startsAt, label: x.name };
    }),
    plan: plan, wire: wire, bookings: bookings, blockers: blockers
  };
}

/* After the square. Fire and forget: every failure in here ends as an item on the
 * row, or at worst in the console, and never as anything the desk has to answer.
 *
 * The row goes on the write queue before the call, so a tab closed while the call is
 * out still leaves the disagreement on record, and the function has a row to write
 * each line to. The call does not wait for the queue: the square is up and the client
 * is here. If the call gets there first the function's writes find no row, and the
 * answer written below is the record. A clean plan that goes through closes its row,
 * and `onFirstTry` keeps it out of sight until then. */
async function bringBookInLine(p) {
  if (!p) return;
  var id = null;
  try {
    var against = assignAgainstOpen(pendingMoves.value, p);
    if (against.skip) return;
    against.replace.forEach(closePendingMove);
    var args = Object.assign({
      id: sync.uuid(),
      date: state.businessDate,
      notes: clientNotesFor(p.clientId),
      staffNames: rosterStaff.value.map(function (s) { return s.name; })
    }, p);
    addPendingRow(pendingRow(args));
    if (p.plan.kind !== 'clean') return;
    id = args.id;
    pendingStore.busy[id] = true;
    var answer = await optimizerApi.applyMoves(p.wire, id);
    var out = pendingFromResults(answer, new Date().toISOString());
    // Found again: a read may have replaced the row while this was out.
    var row = pendingStore.rows.find(function (r) { return r.id === id; }) || { id: id };
    // The book may have caught up first, and then the item is already closed.
    if (row.closed_at) return;
    if (!out.pending) { closePendingMove(row); return; }
    var kept = mergeLastResult(row.last_result, out.lastResult);
    row.last_result = kept;
    sync.enqueue({
      table: 'tt_pending_moves', type: 'update',
      patch: { last_result: kept }, match: { id: id }
    });
  } catch (e) {
    console.error('Could not bring the book into line.', e);
  } finally {
    if (id) delete pendingStore.busy[id];
  }
}

function addPendingRow(row) {
  pendingUnsent[row.id] = true;
  pendingStore.rows.push(Object.assign({ created_at: new Date().toISOString() }, row));
  sync.enqueue({ table: 'tt_pending_moves', type: 'upsert', row: row });
}

function closePendingMove(item) {
  item.closed_at = new Date().toISOString();
  pendingClosedHere[item.id] = true;
  delete pendingUnsent[item.id];
  sync.enqueue({
    table: 'tt_pending_moves', type: 'update',
    patch: { closed_at: item.closed_at }, match: { id: item.id }, isNull: ['closed_at']
  });
}

// The square went to somebody else. The item goes with it, and has no Swap from here.
function retargetPendingMove(item, staffId) {
  item.board_staff_id = staffId;
  item.plan = Object.assign({}, item.plan, { retargeted: true, retargetedAt: new Date().toISOString() });
  sync.enqueue({
    table: 'tt_pending_moves', type: 'update',
    patch: { board_staff_id: staffId, plan: item.plan }, match: { id: item.id }
  });
}

/* Swap, or Try again on a clean plan. The desk's own tap, and the server re-checks
 * every line, so a plan older than the book is refused rather than obeyed. */
async function runPendingMove(id) {
  var item = pendingMoves.value.find(function (r) { return r.id === id; });
  if (!item || pendingStore.busy[id]) return false;
  var wire = (item.plan && item.plan.wire) || [];
  if (!wire.length || !pendingLine(item, pendingNames(item)).canSwap) return false;
  pendingStore.busy[id] = true;
  // From before the call: the function writes each line to the row as it goes, and a
  // read can bring those in over what earlier tries saved.
  var before = item.last_result;
  try {
    var answer = await optimizerApi.applyMoves(wire, id);
    var out = pendingFromResults(answer, new Date().toISOString());
    // Found again: a read may have replaced the row while this was out.
    var now = pendingStore.rows.find(function (r) { return r.id === id; }) || item;
    var kept = mergeLastResult(before, out.lastResult);
    now.last_result = kept;
    sync.enqueue({
      table: 'tt_pending_moves', type: 'update',
      patch: { last_result: kept }, match: { id: id }
    });
  } catch (e) {
    console.error('Could not run the pending move.', e);
  } finally {
    delete pendingStore.busy[id];
  }
  return true;
}

// What the latest book and board say about an item, for `stillOpen`.
function bookRowFor(item, squareGone) {
  var row = bookRowFromVisit(item, apptById.value[item.appointment_id], {
    staff: state.staff,
    matchesLabel: matchesStaff,
    checkedOut: !!appts.ticketClosedAt[item.appointment_id]
  });
  if (!row || !row.lines) return row;
  var lines = row.lines;
  var ids = lines.map(function (x) { return x.id; });
  var overlaps = (pinnedBlocksByStaff.value[item.board_staff_id] || []).some(function (b) {
    return ids.indexOf(b.svc.id) === -1 && lines.some(function (x) {
      return b.from < x.startsAt + bookedHoldMs(x) && b.to > x.startsAt;
    });
  });

  return Object.assign(row, { squareGone: squareGone, overlaps: overlaps });
}

// On every fresh read of the book. Any station may do it, and two doing it at once
// come to the same answer.
function reconcilePendingMoves() {
  try {
    if (!pendingMoves.value.length || !appts.rows.length) return;
    if (appts.bookDate !== state.businessDate) return;
    pendingMoves.value.slice().forEach(function (item) {
      var square = squareOf(item, state.turns);
      var action = squaresAreRead()
        ? squareAction(item, square, Date.now(), PENDING_SQUARE_GRACE_MS) : 'none';
      if (action === 'retarget') retargetPendingMove(item, square.staffId);
      if (!stillOpen(item, bookRowFor(item, action === 'gone'))) closePendingMove(item);
    });
  } catch (e) {
    console.error('Could not check the pending moves against the book.', e);
  }
}

/* Separate from the day's load on purpose. A role RLS hides these from, or a schema
 * part not applied yet, comes to an empty row and nothing else. */
async function loadPendingMoves() {
  if (!sync.auth.user || !scheduling.enabled.value) return;
  var forDate = state.businessDate;
  try {
    var rows = await optimizerApi.fetchPendingMoves(forDate);
    var key = followsBook() ? pendingStore.writeKeySet : await optimizerApi.fetchWriteKeySet();
    if (forDate !== state.businessDate) return;
    rows.forEach(function (r) { delete pendingUnsent[r.id]; });
    var unsent = pendingStore.rows.filter(function (r) { return pendingUnsent[r.id] && r.date === forDate; });
    pendingStore.rows = rows.filter(function (r) { return !pendingClosedHere[r.id]; }).concat(unsent);
    pendingStore.writeKeySet = key;
    reconcilePendingMoves();
  } catch (e) { /* see above */ }
}

var pendingRefetchTimer = null;
function schedulePendingRefetch() {
  if (pendingRefetchTimer) clearTimeout(pendingRefetchTimer);
  pendingRefetchTimer = setTimeout(function () {
    pendingRefetchTimer = null;
    loadPendingMoves();
  }, 400);
}

/* A client note naming any technician, for the sheet. Any of them and not only the
 * one picked: under turn order this tap changes the book, and "only Ann" is as much
 * a reason to stop on Tam as on Ann. Follow the book has its own warning already. */
var assignNoteWarning = computed(function () {
  var a = ui.assign;
  if (!a || followsBook()) return null;
  var notes = clientNotesFor(a.clientId);
  var named = noteNamingStaff(notes, rosterStaff.value.map(function (s) { return s.name; }));
  return named.length ? { notes: notes, named: named } : null;
});

// Link a booked service to a turn that is already running, rather
// than starting a new one — the tech began the service before it was matched
// up with the appointment.
function attachToTurn(turnId, link) {
  var t = turnOf(turnId);
  if (!t || !link) return false;
  if (t.appointmentServiceId) {
    toast('That turn is already linked to an appointment.', 'warn');
    return false;
  }
  if (turnByApptService.value[link.appointmentServiceId]) {
    toast('That service already has a turn.', 'warn');
    return false;
  }
  t.appointmentId = link.appointmentId;
  t.appointmentServiceId = link.appointmentServiceId;
  if (link.sourceServiceId != null) t.sourceServiceId = link.sourceServiceId;
  if (link.clientId != null) t.clientId = link.clientId;
  t.clientName = link.clientName;
  t.sourceServiceName = link.sourceServiceName;
  // The booking takes over the countdown the type's default was standing in
  // for — and if the booking carries no length, the default keeps it.
  var attached = scheduleFor(link, t.serviceId, t.startedAt);
  t.scheduledMinutes = attached.minutes;
  t.scheduledEndAt = attached.endsAt;
  pushTurn(t);
  toast(link.clientName + ' → ' + t.staffName + "'s " + t.serviceName, 'ok');
  ui.assign = null;
  return true;
}

/* The other direction: booked services a given turn could be attached to.
 * Upcoming or in progress only — a service that is over is not what somebody
 * is sitting in the chair for — and never one already on the board.
 *
 * Asked of each SERVICE, not of the visit around it. A visit is not one thing
 * happening: a manicure at ten and a pedicure at half past are consecutive,
 * and they may well be two different technicians and so two different squares.
 * Reading the visit as a unit got both ends of that wrong — the pedicure
 * counted as under way because the manicure was, and the manicure stayed on
 * offer long after it was over because the pedicure had not finished. Each
 * line now stands on its own clock, which is the only one that describes it.
 */
function attachableAppointments(serviceId) {
  if (!scheduling.enabled.value) return [];
  var now = Date.now();
  var out = [];
  appts.rows.forEach(function (a) {
    if (a.cancelled) return;                  // the visit itself is off
    a.services.forEach(function (svc) {
      if (turnByApptService.value[svc.id]) return;
      if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
      if (bookedServiceEnd(svc) < now) return;
      var mapped = trackerServiceForBooked(svc);
      if (serviceId && (!mapped || mapped.id !== serviceId)) return;
      out.push({ appt: a, svc: svc, upcoming: svc.startsAt > now });
    });
  });
  return out.sort(function (x, y) { return x.svc.startsAt - y.svc.startsAt; });
}

/* ---------- dropping a booked service on the board ----------
 * The rules a square has to satisfy to take a dragged booking, asked once so
 * that what lights up and what accepts the drop cannot drift apart. */

// A booking whose time is already over goes up as work that was done rather
// than work that is starting: the square lands finished, timed from the
// booking itself. That is what makes the end of the day fixable — a client
// nobody logged at eleven can be put in at four, in the right square, with the
// right length, without a turn appearing to run now.
//
// The ticket's close time wins over the booked length when the visit had only
// this service, because then it is when the service ended. On a visit with
// several, the close is when the client paid for all of them — after the
// last technician finished and however long they stayed — so the booked
// length is the better guess. Same rule as writing a day up; see
// rules/checkout.js.
// A booking that is under way keeps the booking's own start: a client booked
// at ten who is seated at ten past is twenty minutes into a thirty minute
// service, not starting one. That is what makes the square's countdown agree
// with the appointment it came from. Never a start in the future, though — a
// booking dragged over before its time begins when it was dragged.
/* A running booked square's start: the booked time, or for the visit's first
 * service the moment the booking system started it — js/rules/seated-start.js.
 * Mangomint's check-in is that moment; a visit still Waiting has not started. */
var BOOK_STARTED = { CheckedIn: true, Finished: true };
function seatedStartAt(appt, bookedAt, now) {
  var st = appt && bookStatus.byAppt[appt.id];
  var startedAt = st && BOOK_STARTED[st.status] ? st.checkedInAt : null;
  return seatRules.seatedStart(bookedAt,
    seatRules.firstBookedStart(appt ? appt.services : null), startedAt || null, now);
}

function bookedOutcome(drag) {
  var now = Date.now();
  var starts = drag && drag.startsAt;
  if (!starts) return { finished: false, startedAt: null };
  var appt = drag.link && appts.rows.find(function (a) {
    return a.id === drag.link.appointmentId;
  });
  var closed = drag.link && appts.ticketClosedAt[drag.link.appointmentId];
  // A visit the book no longer shows is counted as several services, so its
  // close is not taken as this service's end on a guess.
  var ends = bookedServiceEndFromClose(closed, starts, drag.durationMinutes,
    appt ? appt.services.length : 0) || starts;
  var running = { finished: false, startedAt: seatedStartAt(appt, starts, now) };
  if (ends <= starts) return running;     // no length to record
  if (ends >= now) return running;        // still under way
  return { finished: true, startedAt: starts, endedAt: ends };
}

/* What a booking has to satisfy to be written onto a square that already
 * exists — as against starting one of its own, which is `canStartBooked` below.
 *
 * Four questions, and they are the four that are about the BOOKING rather than
 * about the floor: the books are open, this service is not already on the board
 * somewhere, the booking does not name a different technician, and there is a
 * roster row for today to hang it on at all. Nothing here asks whether anybody
 * is free, or on shift, or still in today — a service performed at eleven is a
 * fact about eleven, and the state of the floor at five has no bearing on
 * writing it down.
 *
 * Split out from canStartBooked because that one had grown a second job. It is
 * the test for "may this become a new turn", and it says no to two things a
 * merge has every right to do: record work against somebody whose day the board
 * has since moved past, and record a booking that maps to no tracker service.
 * The second is the one that mattered — an unmapped booking is exactly the kind
 * of thing that gets written up after the fact, and gating the question on a
 * service type it has not got meant the desk was never asked.
 */
function canMergeBooked(staffId, drag) {
  if (!drag || !staffId) return false;
  if (dayClosed.value) return false;
  if (drag.link && turnByApptService.value[drag.link.appointmentServiceId]) return false;
  if (drag.staffIds && drag.staffIds.length && drag.staffIds.indexOf(staffId) === -1) return false;
  return !!state.dayStaff[staffId];
}

// An unmapped booking has no turn type to start, so it can only be attached
// to a turn somebody has already begun, or merged into one — see above.
function canStartBooked(staffId, drag) {
  if (!drag || !drag.serviceId || !staffId) return false;
  // Silent, unlike the other guards: this is asked of every square under the
  // pointer as a card moves, and it is what decides whether one lights up. A
  // closed day simply lights nothing, and the card can still be dropped on a
  // turn already up to attach the client to it — that is a correction to the
  // day, not another square on it.
  if (dayClosed.value) return false;
  if (drag.link && turnByApptService.value[drag.link.appointmentServiceId]) return false;
  // An option can limit the work to certain technicians. Their columns are the
  // only ones that light up, so the restriction is visible before the drop.
  if (drag.staffIds && drag.staffIds.length && drag.staffIds.indexOf(staffId) === -1) return false;
  var d = state.dayStaff[staffId];
  if (!d || !d.present) return false;
  // Whether they are mid-service now says nothing about a service that ran
  // hours ago, so a finished booking can go on a busy technician.
  if (bookedOutcome(drag).finished) return true;
  // ...and for the same reason a square being recorded after the fact goes
  // through above, a square starting NOW does not: their day is spent, and the
  // column under the card is drawn blocked off. A drop that lit up anyway
  // would be the board contradicting itself under the pointer.
  // ...unless it is booked on them. Kevin, 2026-10-22: a service the book has
  // on somebody that runs past their out time is still theirs to start.
  if (isShiftSpent(staffId) &&
      !bookedOnStaff(staffId, drag.link && drag.link.appointmentServiceId)) return false;
  return !isBusy(staffId);
}

/* Which turn in a square the pointer is actually on.
 *
 * A whole square is its one turn. A half square is two, split by the slash, and
 * which of them a card lands on is the difference between merging into the
 * manicure and merging into the pedicure — so it is measured, not assumed.
 * `atLowerHalf` is the far side of that diagonal, worked out from the geometry
 * by appt-drag; on a whole square it means nothing and the answer is the same
 * either way.
 */
function turnUnderDrop(slot, atLowerHalf) {
  if (!slot || !slot.turns.length) return null;
  if (!slot.half) return slot.turns[0];
  return atLowerHalf ? (slot.turns[1] || slot.turns[0]) : slot.turns[0];
}

/* A booking that is already over, dropped on a square that already holds a
 * turn. There are two honest readings of that and the board cannot pick between
 * them, so it asks — see `openDropChoice` and the merge sheet.
 *
 *   merged     the same sitting. A manicure and a pedicure done one after the
 *              other for one client are two lines in the book, two services the
 *              technician performed, and one turn. The square keeps its place
 *              and grows a second service.
 *   separate   its own turn, taking that square's position, with what was there
 *              moving down — the same act the top strip performs, aimed at a
 *              square instead of at the gap above it.
 *
 * Only for a booking that is OVER. One starting now cannot have happened
 * alongside a square already on the board without the clock saying so, and the
 * silent attach that a running booking gets is still the right answer for it.
 */
function isFinishedDrag(drag) { return !!drag && bookedOutcome(drag).finished; }

// What the square under the pointer would do with the card, or null for one
// that would do nothing. `atEdge` is the top strip of a square: a place to put
// a turn rather than a turn to join, which is the only way to land above
// something that is already on the board. `atLowerHalf` is the far side of a
// half square's slash — the empty half of one still open, or the second turn of
// one already paired.
function bookedDropAt(staffId, row, drag, atEdge, atLowerHalf) {
  if (!drag || !staffId) return null;
  if (isMissedCell(staffId, row) || isSkippedCell(staffId, row)) return null;
  var slot = slotAt(staffId, row);
  if (slot) {
    if (atEdge && canStartBooked(staffId, drag)) {
      return { kind: 'insert', staffId: staffId, row: row };
    }
    /* The empty half of an open half square. A click with a service armed has
     * always filled one — that is what the dashed box is for — and a card
     * dragged into the same box has to mean the same thing, or the board is
     * offering a hole it will not let anything into. It is a start, not a join:
     * the square already has somebody's half turn in it and this is the other
     * half, a service of its own that happens to share the square.
     *
     * Only when the pointer is actually on that side. The other side is a turn
     * that has already happened, and dropping a booking on a turn means what it
     * means everywhere else on the board — attach the client to it.
     *
     * `isBusy` on top of canStartBooked, which lets a finished booking past a
     * busy technician everywhere else: a landing here is only a pairing because
     * the new turn goes on the end of the column and an open half is the last
     * thing in it. The one case where it is not — a zero-turn service running
     * underneath it — is a column with somebody in service, and refusing it
     * keeps the drop saying what the dashed box promises. It is the same guard
     * the armed-service click has had. */
    if (slot.open && atLowerHalf && !isBusy(staffId) && canStartBooked(staffId, drag)) {
      return { kind: 'pair', staffId: staffId, row: row };
    }
    /* Work that is over, landing on a square that is taken. Both readings are
     * on offer and the sheet is where they are chosen between, so the drop
     * itself only has to say which square and which of its turns.
     *
     * Guarded on the WEAKER of the two tests, deliberately. The sheet offers
     * merge-or-separate and decides for itself whether the separate side is
     * available — `separate.can` asks canStartBooked — so gating the question
     * on the stronger test would refuse to ask it in exactly the cases where
     * only one answer is possible. An unmapped booking is the plain example:
     * it cannot become a square of its own, it can perfectly well be recorded
     * on one, and before this it got a silent attach and no question at all. */
    if (isFinishedDrag(drag) && canMergeBooked(staffId, drag)) {
      var hit = turnUnderDrop(slot, atLowerHalf);
      if (hit) {
        return { kind: 'choose', staffId: staffId, row: row, turnId: hit.id };
      }
    }
    // A finished square is still the client's service — the tech simply
    // started it before anyone matched it to the booking.
    var free = slot.turns.filter(function (t) { return !t.appointmentServiceId; });
    var pick = free.find(function (t) { return t.endedAt == null; }) || free[free.length - 1];
    return pick ? { kind: 'attach', turnId: pick.id } : null;
  }
  if (!canStartBooked(staffId, drag)) return null;
  return { kind: 'start', staffId: staffId };
}

// Dropped on a column: the same booking the assign sheet would have made,
// without the sheet. Request mode still applies, exactly as it does when a
// service is armed and a column is tapped.
function startBookedOn(staffId, drag, sortAt) {
  if (!canStartBooked(staffId, drag)) return false;
  var out = bookedOutcome(drag);
  return bookTurn(staffId, {
    serviceId: drag.serviceId,
    link: drag.link,
    sortAt: sortAt,
    // Both cases carry a start from the booking; only a finished one carries
    // an end, and that is what marks it as a square being written down rather
    // than one starting now.
    at: out.startedAt ? { startedAt: out.startedAt, endedAt: out.finished ? out.endedAt : null } : null
  });
}

/* ---------- putting a turn between two others ----------
 * Order is decided by `sortAt` and nothing else, so slotting a square in is a
 * matter of finding a value that sits between its neighbours. Everything below
 * moves down a row on its own, because the rows are only ever a reading of the
 * order. */

// Which slot a row is, ignoring the missed and skipped squares woven in.
function slotIndexAt(staffId, row) {
  var offset = row - missedTurns(staffId);
  var rows = rowsFor(staffId);
  if (offset < 0 || offset >= rows.length || rows[offset].skip) return null;
  var n = 0;
  for (var i = 0; i < offset; i++) if (!rows[i].skip) n += 1;
  return n;
}

// Spread a column's turns out again and leave a hole where the new one goes.
// Only needed when two neighbours were created inside a millisecond of each
// other and there is genuinely nothing between them.
function respaceColumn(staffId, beforeTurnId) {
  var list = turnsFor(staffId);
  var base = list.length ? list[0].sortAt : Date.now();
  var step = 1000;
  var hole = null;
  var n = 0;
  list.forEach(function (t) {
    if (t.id === beforeTurnId) { hole = base + n * step; n += 1; }
    var v = base + n * step;
    if (t.sortAt !== v) { t.sortAt = v; pushTurn(t); }
    n += 1;
  });
  return hole == null ? base + n * step : hole;
}

/* Where a booking dropped on a square goes in the column.
 *
 * Asked of the square's FIRST turn, which is the whole of what "above this
 * square" means. `sortAtBeforeTurn` is the same arithmetic a square being
 * dragged uses, with nothing moving out of the way — a booking coming off the
 * book is not already in the column, so there is no square to leave out.
 */
function sortAtBefore(staffId, slotIndex) {
  var slots = slotsFor(staffId);
  var target = slots[slotIndex];
  var first = target && target.turns[0];
  return first ? sortAtBeforeTurn(staffId, first.id, null) : null;
}

function insertBookedAt(staffId, row, drag) {
  var index = slotIndexAt(staffId, row);
  if (index == null) return false;
  var sortAt = sortAtBefore(staffId, index);
  if (sortAt == null) return false;
  return startBookedOn(staffId, drag, sortAt);
}

/* ---------- the two readings of a finished booking on a taken square ----------
 * Which of these runs is the desk's answer on the merge sheet. Both are reached
 * only through `confirmDropChoice`, so the sheet is the one place the question
 * is put and neither can happen without it. */

/* Its own turn, taking the position of the turn it was dropped on.
 *
 * Everything from that turn down moves one square, which for the lower half of
 * a half square means the service that was sharing it drops into the next one —
 * the new turn pairs with the half above and the old neighbour starts a square
 * of its own. Nothing about the turn that moved changes but where it is drawn;
 * a running one keeps running, on the same clock, in the square below.
 */
function separateBookedAt(staffId, turnId, drag) {
  var t = turnOf(turnId);
  if (!t || t.staffId !== staffId) return false;
  /* In front of that TURN, not of its square — which is the difference the
   * whole gesture turns on. Landing in front of the lower half of a shared
   * square leaves the upper half where it is and pushes only the lower one
   * down; the new turn pairs with the half above it, and the service that was
   * sharing that square starts a square of its own. */
  var sortAt = sortAtBeforeTurn(staffId, t.id, null);
  if (sortAt == null) return false;
  return startBookedOn(staffId, drag, sortAt);
}

/* The same sitting: the booking becomes a second service on a turn already up.
 *
 * The square keeps its own place, its own clock and its own first service, and
 * grows a line saying what else was done. `weight` is what the desk said the
 * whole sitting counts for — the one weight on the board that is not any single
 * type's, and the reason `applyServiceWeight` stops following the book once a
 * square is merged.
 *
 * The merged service's own times and weight are snapshotted beside it, not read
 * back through the book. A booking edited this evening does not change what the
 * technician did this morning, which is the rule finished squares already live
 * by everywhere else.
 */
function mergeIntoTurn(turnId, drag, weight) {
  var t = turnOf(turnId);
  var link = drag && drag.link;
  if (!t || !link) return false;
  // Asked again here, of the board as it stands rather than as it stood when
  // the card landed. The sheet can sit open across a webhook, an auto-stop or
  // another station seating the same booking — the same reason the move sheet
  // re-reads its plan on every paint.
  if (!canMergeBooked(t.staffId, drag)) {
    toast(dayClosed.value
      ? CLOSED_NOTE
      : 'That service cannot go on this square any more.', 'warn');
    return false;
  }
  var svc = drag.serviceId ? serviceById.value[drag.serviceId] : null;
  var found = bookedServiceById(link.appointmentServiceId);
  var out = bookedOutcome(drag);
  t.extras = (t.extras || []).concat([{
    appointmentId: link.appointmentId || null,
    appointmentServiceId: link.appointmentServiceId || null,
    sourceServiceId: link.sourceServiceId == null ? null : link.sourceServiceId,
    clientId: link.clientId == null ? null : link.clientId,
    clientName: link.clientName || '',
    sourceServiceName: link.sourceServiceName || '',
    // The tracker type it maps to, when it maps to one. An unmapped booking can
    // still be merged — it is a record of work done, and the letter it has no
    // type for is the one thing it gives up.
    serviceId: svc ? svc.id : null,
    serviceName: svc ? svc.name : (link.sourceServiceName || ''),
    serviceLetter: svc ? svc.abbrev : '?',
    turnWeight: svc ? svc.turnWeight : 0,
    startedAt: out.startedAt || null,
    endedAt: out.finished ? out.endedAt : null,
    options: found ? bookedOptionsOf(found.svc) : []
  }]);
  if (weight != null) t.turnWeight = weight;
  pushTurn(t);
  toast((link.clientName || 'That booking') + '’s ' +
    (link.sourceServiceName || 'service') + ' → ' + t.staffName + '’s square — ' +
    pluralTurns(t.turnWeight) + ' altogether', 'ok');
  return true;
}

/* Changing what a merged square counts for, after the fact.
 *
 * The same answer the merge sheet asks for, asked again from the square's own
 * menu — because the sheet is a decision made in a second at a busy counter and
 * the square it made is read for the rest of the day.
 */
function setMergedWeight(turnId, weight) {
  var t = turnOf(turnId);
  if (!t || !isMergedTurn(t) || weight == null) return false;
  if (t.turnWeight === weight) return true;
  t.turnWeight = weight;
  pushTurn(t);
  return true;
}

/* Taking a merged service back off a square.
 *
 * A merge is one press at a busy counter and the sheet it is decided on opens
 * under a card that is already moving, so it has to be undoable — and undoable
 * from the square, which is where somebody notices. The booking goes back to
 * being unseated: out of the square, back into Due now and back on offer to the
 * assign sheet, all of which follow from `turnByApptService` losing it.
 *
 * The square's weight is left where the desk set it. Coming back to one turn
 * does not mean coming back to that type's weight — the square may have been
 * hand-set for reasons that have nothing to do with the service being removed,
 * and quietly re-costing somebody's column on the way out of a correction is
 * exactly the kind of thing that has to be asked for. The menu offers it.
 */
function unmergeFromTurn(turnId, apptServiceId) {
  var t = turnOf(turnId);
  if (!t || !t.extras || !t.extras.length) return false;
  var kept = t.extras.filter(function (m) {
    return m.appointmentServiceId !== apptServiceId;
  });
  if (kept.length === t.extras.length) return false;
  t.extras = kept;
  pushTurn(t);
  toast('Taken off ' + t.staffName + '’s square — it is back in Due now.', 'ok');
  return true;
}

/* What a combined square may be set to count for.
 *
 * The two services' own weights and their sum, which are the three answers with
 * an argument behind them, plus half a turn and a whole one, which are what the
 * board is mostly made of. Sorted, deduplicated, and never zero: a square that
 * counts for nothing is what a zero-turn service is for, and it leaves the grid
 * when it finishes — not something two real services should be able to become.
 */
function mergeWeightChoices(a, b) {
  var seen = {};
  var out = [];
  [a, b, (a || 0) + (b || 0), 0.5, 1].forEach(function (w) {
    if (!w || w <= 0 || seen[w]) return;
    seen[w] = true;
    out.push(w);
  });
  return out.sort(function (x, y) { return x - y; });
}

/* ---------- the merge sheet ----------
 * Opened by a finished booking landing on a taken square, and the only way
 * either of the two acts above runs. */

function openDropChoice(staffId, turnId, drag) {
  var t = turnOf(turnId);
  if (!t || !drag) return;
  var svc = drag.serviceId ? serviceById.value[drag.serviceId] : null;
  ui.dropChoice = {
    staffId: staffId,
    turnId: turnId,
    // A copy, because the card is out of the air the moment this opens and
    // `ui.apptDrag` is cleared behind it.
    drag: {
      link: drag.link, serviceId: drag.serviceId, staffIds: drag.staffIds,
      startsAt: drag.startsAt, durationMinutes: drag.durationMinutes
    },
    // The heavier of the two to begin with: a half turn with a full service
    // done alongside it was a full turn's work, and a full turn with a half
    // added is still a full turn. Never the sum, which would make one sitting
    // count as more than the squares it took.
    weight: Math.max(t.turnWeight || 0, svc ? svc.turnWeight : 0) || 0.5
  };
}

function closeDropChoice() { ui.dropChoice = null; }

function setDropChoiceWeight(w) {
  if (ui.dropChoice) ui.dropChoice.weight = w;
}

/* Nothing here works anything out — the same rule the move sheet follows. A
 * sheet can sit open across a webhook, an auto-stop or another station's drag,
 * and one describing a board that has moved on is one that gets said yes to. */
var dropChoiceDetail = computed(function () {
  var c = ui.dropChoice;
  if (!c) return null;
  var t = turnOf(c.turnId);
  var staff = staffOf(c.staffId);
  if (!t || !staff) return null;

  var link = c.drag.link || {};
  var svc = c.drag.serviceId ? serviceById.value[c.drag.serviceId] : null;
  var out = bookedOutcome(c.drag);

  /* Where the separate square would land, and what moves for it. Measured by
   * building the column both ways rather than by counting rows, because half
   * turns re-pair around anything put between them: the square that moves down
   * is sometimes the one that was sharing the target's square, and no amount of
   * row arithmetic says that as plainly as the two columns side by side. */
  var before = turnsFor(c.staffId);
  var at = before.indexOf(t);
  var stand = { id: '__new__', turnWeight: svc ? svc.turnWeight : 0, endedAt: out.endedAt || 1 };
  var after = before.slice(0, at < 0 ? before.length : at)
    .concat([stand], before.slice(at < 0 ? before.length : at));
  var missed = missedTurns(c.staffId);
  var wasRows = buildRows(buildSlots(before), skipsFor(c.staffId));
  var rows = buildRows(buildSlots(after), skipsFor(c.staffId));
  var square = moveRules.squareNumberOf(rows, '__new__', missed);

  /* The service that gets pushed out of the square it was sharing.
   *
   * Only when the card landed on the LOWER half of a paired square, and then it
   * is the turn under the pointer itself: the newcomer takes that half, pairs
   * with the half above, and this one starts a square of its own below. That is
   * the one case worth a sentence of its own, because "1 square below moves
   * down" describes a column and this is about the service somebody is in the
   * middle of.
   *
   * Landing on the upper half moves the whole square down instead — both halves
   * together, nothing pushed out of anything — and the count says that better
   * than a name would. */
  var wasSlot = buildSlots(before).find(function (s) {
    return s.turns.indexOf(t) !== -1;
  });
  var displaced = wasSlot && wasSlot.turns.length > 1 && wasSlot.turns[1] === t
    ? t
    : null;

  /* The board moving on underneath the sheet. Both readings need the same four
   * things of the booking, so `canMergeBooked` is what decides whether there
   * are any buttons at all; the reasons are spelled out separately because "it
   * cannot" and "here is why" are different sentences to a desk mid-correction.
   */
  var blocked = null;
  if (dayClosed.value) blocked = 'The day is closed — nothing can be added to it.';
  else if (turnByApptService.value[link.appointmentServiceId]) {
    blocked = 'That service is already on the board.';
  } else if (!canMergeBooked(c.staffId, c.drag)) {
    blocked = staff.name + ' is no longer on today’s board.';
  }

  // Merging records work against this technician, so the booking naming
  // somebody else is worth saying — the same claim the board's warning triangle
  // makes, made before the square is changed rather than after.
  var notes = [];
  var labels = bookedTechLabels(t);
  if (svc && staff.serviceIds.indexOf(svc.id) === -1) {
    notes.push(staff.name + ' is not set up for ' + svc.name + '.');
  }
  if (!svc) {
    notes.push('Nothing in the tracker maps to ' + (link.sourceServiceName || 'that service') +
      ', so it can be recorded on the square but cannot become one of its own.');
  }
  if (labels.length && !bookNames(labels, staff)) {
    notes.push(scheduling.name.value + ' has ' + labels.join(', ') +
      ' against the square this would join, not ' + staff.name + '.');
  }

  return {
    turnId: t.id,
    staffName: staff.name,
    color: svc ? svc.color : DEFAULT_SERVICE_COLOR,
    letter: svc ? svc.abbrev : '?',
    clientName: link.clientName || '',
    clientId: link.clientId == null ? null : link.clientId,
    // The two services side by side, each with its own clock, which is what the
    // desk is actually comparing: did these two happen in one sitting.
    landing: {
      name: link.sourceServiceName || (svc ? svc.name : 'That service'),
      from: formatClock(out.startedAt),
      to: out.endedAt ? formatClock(out.endedAt) : '—',
      weight: svc ? svc.turnWeight : 0
    },
    /* The whole SQUARE, not only the turn under the pointer — the heading over
     * this list names the square, and on a half square the other half is a
     * second client whose sitting this may or may not have been part of. Seeing
     * only one of them is how a gel polish gets merged into the pedicure when
     * it belonged to the manicure.
     *
     * The one being joined is marked, because the list is wider than the choice:
     * the merge lands on exactly one of these, settled by which side of the
     * slash the card came down on. */
    existing: (wasSlot ? wasSlot.turns : [t]).reduce(function (acc, turn) {
      servicesOfTurn(turn).forEach(function (s) {
        acc.push({
          key: turn.id + ':' + (s.appointmentServiceId || s.name),
          name: s.bookedName || s.name,
          client: s.clientName || turn.clientName || '',
          from: formatClock(s.startedAt),
          to: s.endedAt ? formatClock(s.endedAt) : null,
          running: s.endedAt == null,
          color: s.color,
          letter: s.letter,
          joining: turn === t
        });
      });
      return acc;
    }, []),
    // Both halves of the choice, said as what the board would look like.
    merged: {
      was: t.turnWeight,
      weight: c.weight,
      choices: mergeWeightChoices(t.turnWeight, svc ? svc.turnWeight : 0),
      // The column re-costed with the square set to the chosen weight. Merging
      // adds no square, so nothing re-pairs and the change is the one number —
      // but it is still read off the column rather than added to the total, so
      // the two sides of this sheet are worked out the same way.
      credit: {
        was: turnCredit(c.staffId),
        now: creditAfter(c.staffId, before.map(function (x) {
          return x === t ? { turnWeight: c.weight, endedAt: x.endedAt } : x;
        }))
      }
    },
    separate: {
      // A booking with no tracker type behind it has no turn to start, so this
      // side of the sheet is off — see canStartBooked.
      can: canStartBooked(c.staffId, c.drag),
      square: square,
      moved: moveRules.shiftedSquares(wasRows, rows, missed, '__new__'),
      displaced: displaced
        ? (displaced.clientName ? displaced.clientName + '’s ' : '') +
          (displaced.sourceServiceName || displaced.serviceName)
        : null,
      displacedRunning: !!(displaced && displaced.endedAt == null),
      // Off the rebuilt column, because a square put between two others can
      // change what the squares below it are worth without anybody touching
      // them — and what a square is worth is a place in the rotation.
      credit: { was: turnCredit(c.staffId), now: creditAfter(c.staffId, after) }
    },
    notes: notes,
    blocked: blocked
  };
});

function confirmDropChoice(how) {
  var c = ui.dropChoice;
  if (!c) return false;
  var ok = how === 'separate'
    ? separateBookedAt(c.staffId, c.turnId, c.drag)
    : mergeIntoTurn(c.turnId, c.drag, c.weight);
  if (ok) ui.dropChoice = null;
  return ok;
}

function openAttachPick(turnId) { ui.attachPick = { turnId: turnId }; }
function closeAttachPick() { ui.attachPick = null; }

/* Right-click on a booking, wherever it is drawn — the line in today's book or
 * the chip in Due now. Anchored at the pointer, and carrying the ids rather
 * than the objects so it follows the book: an appointment refreshed underneath
 * an open menu must not leave it acting on a stale copy. */
function openApptMenu(appt, svc, x, y) {
  if (!deskMarksRequests.value || !svc) return;
  ui.apptMenu = { apptId: appt ? appt.id : null, svcId: svc.id, x: x, y: y };
}

function closeApptMenu() { ui.apptMenu = null; }

/* Right-click on a square. The same question the booking answers — was this
 * one asked for — put to the square, because by the time somebody is in the
 * chair the grid is where the floor is looking. */
function openTurnMenu(turnId, x, y) {
  if (!turnOf(turnId)) return;
  ui.turnMenu = { turnId: turnId, x: x, y: y };
}

function closeTurnMenu() { ui.turnMenu = null; }

var turnMenuTurn = computed(function () {
  return ui.turnMenu ? turnOf(ui.turnMenu.turnId) : null;
});

/* Star a square, and the booking behind it when there is one.
 *
 * A linked square and its booking are the same service, so they must not be
 * able to disagree: marking here goes through the booking, which stars the
 * square on its way past, and unmarking it in the book takes the star off
 * again. Only a walk-in — a square with no booking behind it — carries a star
 * of its very own.
 *
 * The exception is a square the book has on somebody else. That mismatch is
 * already flagged on the grid, and recording a client request against the
 * wrong name would only compound it, so that one is starred as a square and
 * the booking is left alone. */
function toggleTurnRequest(turnId) {
  var t = turnOf(turnId);
  if (!t || !ui.requestsOn) return;
  var found = bookedServiceById(t.appointmentServiceId);
  var booked = found && bookedStaffFor(found.svc);
  if (found && booked && booked.id === t.staffId) {
    toggleApptRequest(found.appt, found.svc);
    return;
  }
  toggleTurnRequested(turnId);
}

// Whether a square reads as a request, wherever the star came from.
function isTurnRequested(turnId) {
  var t = turnOf(turnId);
  return !!(t && ui.requestsOn && t.requested);
}

// What an open menu is about, read back out of the book each time so it is
// never a stale copy. Null once the booking is gone, which closes the menu.
var apptMenuTarget = computed(function () {
  var m = ui.apptMenu;
  if (!m) return null;
  var found = bookedServiceById(m.svcId);
  if (!found) return null;
  return { appt: found.appt, svc: found.svc };
});

// Providers hand back tech labels as "First L."; the roster holds first names.
function matchesStaff(staff, techLabel) {
  if (!staff || !techLabel) return false;
  return String(techLabel).split(' ')[0].toLowerCase() === staff.name.toLowerCase();
}

// What this tech is booked for shortly: due within `minutes`, or already due
// and still unassigned. Excludes anything finished, cancelled or on the board.
// Per booked service, for the reason `attachableAppointments` sets out: one
// line of a visit being over says nothing about the next one.
function upcomingForStaff(staffId, minutes) {
  if (!scheduling.enabled.value) return [];
  var staff = staffOf(staffId);
  if (!staff) return [];
  var now = Date.now();
  var horizon = now + (minutes || 30) * 60000;
  var out = [];
  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      if (turnByApptService.value[svc.id]) return;
      if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
      if (svc.startsAt > horizon) return;
      if (bookedServiceEnd(svc) < now) return;
      var mine = (svc.techs || []).some(function (t) { return matchesStaff(staff, t); });
      if (!mine) return;
      out.push({
        appt: a, svc: svc,
        mapped: trackerServiceForBooked(svc),
        late: svc.startsAt < now
      });
    });
  });
  return out.sort(function (x, y) { return x.svc.startsAt - y.svc.startsAt; });
}

/* A booked line that is over whatever its time says: the client has checked
 * out — the ticket settled, or Mangomint has the visit Finished or No-show.
 * Vic McWherter, 2026-10-19: a manicure booked for 11:55 on a visit checked out
 * at 11:41 sat in Due now as late, and counted against Zenny's afternoon.
 *
 * Deliberately NOT "the board dealt with it once" (`written_up_at`). Deleting a
 * square sets that mark too, so the automatic write-up does not put it back —
 * and a booking whose square the desk deleted by mistake has to stay on offer
 * to seat again. Edna, the same afternoon: her Hard Gel Manicure square was
 * deleted, the manicure dropped out of Due now, and the next chip to hand was
 * her Classic Pedicure. Asked everywhere the book is read for what is still to
 * come. */
var VISIT_OVER = { Finished: true, NoShow: true };
function bookedLineOver(a, svc) {
  if (!a || !svc) return false;
  if (appts.ticketClosedAt[a.id] != null) return true;
  var st = bookStatus.byAppt[a.id];
  return !!(st && VISIT_OVER[st.status]);
}

// Everything about to start, whoever it is booked with — the floor-wide version
// of upcomingForStaff, for the Due now row. A booking whose start time has
// passed but that nobody has seated stays on the list until it would have
// finished: a client standing at the desk ten minutes late is the most urgent
// thing on it, not the least.
var dueSoon = computed(function () {
  if (!scheduling.enabled.value) return [];
  var t = minuteNow.value;                 // ticks so the list keeps itself current
  var horizon = t + dueSoonMinutes.value * 60000;
  var out = [];
  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      if (turnByApptService.value[svc.id]) return;          // already on the board
      if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
      if (svc.startsAt > horizon) return;
      if (svc.startsAt + (svc.durationMinutes || 0) * 60000 < t) return;   // done
      out.push({
        appt: a, svc: svc,
        mapped: trackerServiceForBooked(svc),
        tech: (svc.techs && svc.techs[0]) || '',
        late: svc.startsAt < t
      });
    });
  });
  return out.sort(function (x, y) { return x.svc.startsAt - y.svc.startsAt; });
});

/* Who a Due now chip is going to: the measurement behind js/rules/taker.js.
 *
 * The tiers and their order are in that file. This is the half that needs the
 * board — who is on the floor, what the options allow, whether the window is
 * clear — flattened into the three lookups the rule reads.
 *
 * Two things are worth being deliberate about.
 *
 * `roomAt` with `svc.id` passed through, rather than `hasRoomNow`: this is not a
 * walk-in, it is *this booking*, so the technician the book names must not be
 * found blocked by the very appointment being seated. Everyone else is
 * unaffected — a pinned block only exists on the staff the booking names.
 *
 * It asked `availabilityAt` first, and that is the bug that brought this back.
 * The window is measured from now, so a booking running two hours late drags it
 * down the afternoon until it lands on whatever the book has next for that
 * technician — and `availabilityAt` counts every line of the book, movable or
 * not. A four-fifty pedicure four idle colleagues could take had the chip
 * reporting the booked technician not free while the board's own badge beside it
 * had her available. Bookings that can move are not in anybody's way, and
 * `roomAt` counts only the ones that cannot, which is what the rest of the floor
 * means by free.
 *
 * But `isBusy` on top of it, and that is not belt and braces. `roomAt` is a
 * FORECAST — it answers "could this person be given work running from→to", and a
 * square already past its expected end counts there as finishing now, which is
 * the right reading for a window later in the afternoon and the wrong one for
 * the word `ready` means here. Without this the chip called somebody free while
 * their square was still running five minutes over: it read as an available
 * name, the sheet pre-picked them onto a list they were not on, and `bookTurn`
 * then refused the assign outright. Free NOW is a question about a square
 * existing at all, and that is the one `isBusy` answers.
 *
 * The rotation tier goes through `availableWithRoom`, which is the same call
 * the UP NEXT badge and the armed-service hint are built on. A chip naming
 * somebody the board's own badge disagrees with would be two answers to one
 * question, and the desk would be right not to believe either.
 *
 * With the booked-clients setting on turn order the book's name stops outranking
 * the rotation (`followBook`, see js/rules/taker.js); a request still does.
 */
function dueTakerFor(appt, svc) {
  if (!svc) return null;
  var mapped = trackerServiceForBooked(svc);
  var t = minuteNow.value;                 // ticks, so a chip keeps up with the floor
  var to = t + bookedHoldMs(svc, mapped);
  var eligible = {}, ready = {};
  presentStaff.value.forEach(function (s) {
    if (!staffAllowedForBooked(svc, s.id)) return;
    eligible[s.id] = true;
    if (!isBusy(s.id) && roomAt(s.id, t, to, svc.id).ok) ready[s.id] = true;
  });
  var bookIsKept = followsBook();
  // Under the book the request is what the display switch shows (unchanged).
  // Under turn order it is read from the booking whatever the switch says.
  var mark = bookIsKept ? apptRequestFor(svc) : requestInBook(svc);
  var bookedWith = bookedStaffFor(svc);
  var nextUp = (mapped ? availableWithRoom(mapped.id) : [])
    .filter(function (s) { return staffAllowedForBooked(svc, s.id); });
  // Under turn order the rotation is measured the way `ready` is, leaving this
  // booking out of the booked tech's own afternoon. Measured with it in, an idle
  // Ann would be shut out of next up by her own booking and the chip would say
  // she is not free while she stands there. Unmapped work has no rotation.
  if (!bookIsKept) {
    nextUp = (mapped ? availableFor(mapped.id) : [])
      .filter(function (s) { return ready[s.id]; });
  }
  var pick = takerFor({
    requestedStaffId: mark ? mark.staffId : null,
    bookedStaffId: bookedWith ? bookedWith.id : null,
    followBook: followBookFor(bookIsKept, mark),
    eligible: eligible,
    ready: ready,
    nextUp: nextUp.map(function (s) { return s.id; })
  });
  if (!pick) return null;
  var staff = staffOf(pick.staffId);
  if (!staff) return null;
  return { staffId: staff.id, name: staff.name, kind: pick.kind, ready: pick.ready };
}

/* ---------- the next three, for the hover cards ----------
 *
 * Who could take the work after whoever the row names, soonest first — see
 * js/rules/next-up.js for the order. Each time comes off the same measurement
 * the Next line is built on (`startPlanFor`), so it counts the bookings that
 * can move, and the two-way swaps, exactly as the board does everywhere else:
 * a colleague free once her four o'clock goes to somebody else is free here at
 * the same minute the Next line would call her free.
 *
 * Asked only for the card that is open, not for the whole menu every minute —
 * three measurements per technician per service would be most of a tick.
 */
var NEXT_LIST_LEN = 3;

// The same three ways a wait reads on the Next line. "now" only for somebody
// actually standing free: a square already over its time also measures as
// starting this minute, and that is "any min", not "now".
function nextWaitLabel(staffId, at, t) {
  if (at == null) return '';
  if (at <= t && !isBusy(staffId)) return 'now';
  var minutes = Math.round((at - t) / 60000);
  if (minutes < 1) return 'any min';
  return minutes > ETA_CLOCK_AFTER_MIN ? formatClock(at) : '~' + formatMins(at - t);
}

function nextListOf(candidates, leadId, t) {
  return nextRules.nextByTime(candidates, leadId, NEXT_LIST_LEN).map(function (c) {
    return {
      staff: c.staff, at: c.at, lead: c.id === leadId,
      wait: nextWaitLabel(c.id, c.at, t),
      moves: !!c.moves
    };
  });
}

// A Next availability row: everybody in today who performs it, the row's own
// Next first.
function nextUpList(serviceId) {
  var t = minuteNow.value;
  var lead = nextUpDetail(serviceId).staff;
  var rank = rotationRank.value;
  var cands = providersFor(serviceId).map(function (s) {
    var plan = startPlanFor(s.id, serviceId, t);
    return {
      id: s.id, staff: s, at: plan ? plan.at : null, rank: rank[s.id],
      // Only free because the book changes hands — the same mark the row has.
      moves: plan ? walkInMoves(s.id, serviceId, plan.at).length > 0 : false
    };
  });
  return nextListOf(cands, lead ? lead.id : null, t);
}

/* A Next availability row for a party: the same service for `ui.partySize`
 * people, all starting within a few minutes of each other — see
 * rules/party-avail.js for how the moment is found.
 *
 * Each technician is measured exactly as the Next line measures them,
 * `earliestStartFor`, so the party's answer and the row's never disagree about
 * one person. What each chair would cost the book is `walkInMoves` for that
 * technician at that start, and a handover that leans on somebody who is in
 * the next chair is called out: the board's plan was drawn up for one walk-in,
 * and cannot see that it is spending the same technician twice.
 *
 * The same shape as `nextUpDetail`, so the row reads it unchanged — `name`,
 * `wait`, `closing`, `moves` — plus `seats` and `clashes` for the card.
 */
function partyDetail(serviceId, size) {
  var n = partyRules.cleanPartySize(size == null ? ui.partySize : size);
  var t = minuteNow.value;
  var providers = providersFor(serviceId);
  var base = { party: n, staff: null, at: null, minutes: null, wait: null,
               closing: null, moves: [], movesNote: '', seats: [], clashes: [] };
  if (providers.length < n) {
    var few = !providers.length ? 'Nobody in today performs this service.'
      : 'Only ' + providers.length + ' in today ' +
        (providers.length === 1 ? 'does' : 'do') + ' this — not enough for ' + n + '.';
    return Object.assign(base, { kind: 'none', name: 'None', why: few, note: few });
  }
  var rank = rotationRank.value;
  var found = partyRules.soonestTogether(
    providers.map(function (s) { return { id: s.id, rank: rank[s.id] }; }),
    n, t, partyRules.PARTY_WINDOW_MIN * 60000,
    function (id, from) { return earliestStartFor(id, serviceId, from); });
  if (!found) {
    var none = 'No moment today has ' + n + ' of them free for this within ' +
      partyRules.PARTY_WINDOW_MIN + ' minutes of each other.';
    return Object.assign(base, { kind: 'wait', name: 'Not today', why: none, note: none });
  }

  var seats = found.seats.map(function (c) {
    var s = staffOf(c.id);
    return { id: c.id, staff: s, at: c.at, wait: nextWaitLabel(c.id, c.at, t),
             moves: walkInMoves(c.id, serviceId, c.at) };
  });
  var clashes = partyRules.partyClashes(seats, walkInFitMs(serviceId));
  var names = seats.map(function (x) { return x.staff ? x.staff.name : '?'; });
  var moves = seats.reduce(function (all, x) {
    return all.concat(x.moves.map(function (m) { return Object.assign({ off: x.staff }, m); }));
  }, []);
  var minutes = Math.round((found.by - t) / 60000);
  var now = seats.every(function (x) { return x.wait === 'now'; });
  var overrun = closingOverrun(serviceId, found.by);
  var why = (now
      ? 'All ' + n + ' can start now'
      : 'All ' + n + ' seated by ' + formatClock(found.by) +
        (found.from < found.by ? ', the first from ' + formatClock(found.from) : '')) +
    ' — the soonest ' + n + ' who do this are free for long enough within ' +
    partyRules.PARTY_WINDOW_MIN + ' minutes of each other, if the day runs to time.';
  return Object.assign(base, {
    kind: now ? 'staff' : 'eta',
    staff: seats[0].staff, at: found.by, from: found.from, minutes: minutes,
    name: n === 2 ? names.join(' + ') : names[0] + ' +' + (n - 1),
    wait: now ? null
      : minutes < 1 ? 'any min'
      : minutes > ETA_CLOCK_AFTER_MIN ? formatClock(found.by)
      : '~' + formatMins(found.by - t),
    closing: overrun, moves: moves, seats: seats, clashes: clashes,
    why: why,
    note: [why, overrun ? closingNote(serviceId, overrun) : ''].filter(Boolean).join('\n')
  });
}

// A Due now chip: the booking, for as long as it is booked, with whoever the
// chip names first — the client's request, or the book's own technician, comes
// first there for reasons a sort by time knows nothing about.
function dueNextList(appt, svc) {
  if (!svc) return [];
  var mapped = trackerServiceForBooked(svc);
  var t = minuteNow.value;
  var need = bookedHoldMs(svc, mapped);
  var taker = dueTakerFor(appt, svc);
  var leadId = taker ? taker.staffId : null;
  var rank = rotationRank.value;
  var cands = presentStaff.value
    .filter(function (s) {
      if (!staffAllowedForBooked(svc, s.id)) return false;
      if (s.id === leadId) return true;
      // Only on the card when it names them: a client asked for them, or the
      // book has it with them. Never as the "and if not her?".
      if (isOwnClients(s.id)) return false;
      if (!mapped) return true;
      return s.serviceIds.indexOf(mapped.id) !== -1;
    })
    .map(function (s) {
      var plan = startPlanForNeed(s.id, need, t, svc.id);
      return { id: s.id, staff: s, at: plan ? plan.at : null, rank: rank[s.id] };
    });
  return nextListOf(cands, leadId, t);
}

/* ---------- what the book says about who is working ---------- */

// The roster member behind a provider's tech label ("Ann D." -> Ann), or null
// if the book names somebody who is not on the roster at all.
function staffForTechLabel(label) {
  if (!label) return null;
  return rosterStaff.value.find(function (s) { return matchesStaff(s, label); }) || null;
}

// Everyone the book has on a service today, finished or not. This is the set
// the day opens with.
var bookedStaffToday = computed(function () {
  if (!scheduling.enabled.value) return [];
  var seen = {};
  var out = [];
  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      (svc.techs || []).forEach(function (label) {
        var s = staffForTechLabel(label);
        if (!s || seen[s.id]) return;
        seen[s.id] = true;
        out.push(s);
      });
    });
  });
  return out;
});

// Booked but not on the board — the ones worth prompting about. Only counts
// work that has not already finished: a tech whose only appointment ended
// hours ago is not somebody the front desk needs to chase now. Ordered by the
// soonest booking, so the notice names whoever is needed first.
//
// Dismissal is on the day-staff row (`missingDismissed`), not in this station's
// memory. Somebody decided the notice was answered; a reload and the screen in
// the back room should not go on asking a question the desk has already dealt
// with.
var missingBookedStaff = computed(function () {
  if (!scheduling.enabled.value) return [];
  var t = minuteNow.value;
  var byId = {};
  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      if (svc.startsAt + (svc.durationMinutes || 0) * 60000 < t) return;
      (svc.techs || []).forEach(function (label) {
        var s = staffForTechLabel(label);
        if (!s) return;
        var d = state.dayStaff[s.id];
        if (d && (d.present || d.missingDismissed)) return;
        if (!byId[s.id] || svc.startsAt < byId[s.id].startsAt) {
          byId[s.id] = {
            staff: s, startsAt: svc.startsAt,
            clientName: a.clientName, serviceName: svc.name
          };
        }
      });
    });
  });
  return Object.keys(byId).map(function (k) { return byId[k]; })
    .sort(function (x, y) { return x.startsAt - y.startsAt; });
});

/* Wave the notice away for everybody it currently names.
 *
 * Somebody who was never marked in has no day-staff row to carry the answer, and
 * that is exactly who this notice is about — so one is made for them, marked not
 * present, which is what "no row" already meant everywhere that reads it. The
 * row is now carrying a fact about their day: the desk knows they are booked and
 * is not going to be told again.
 */
function dismissMissingBooked() {
  missingBookedStaff.value.forEach(function (m) {
    var id = m.staff.id;
    if (!state.dayStaff[id]) {
      state.dayStaff[id] = {
        present: false, presentSince: Date.now(), missedTurns: 0,
        skipped: false, skipReason: '', skips: [], skipWhy: [], skipSpans: [],
        shiftStartAt: null, shiftEndAt: null, doneSweptAt: null,
        missingDismissed: false, workHours: workHoursRow(null),
        ownClientsOnly: null
      };
    }
    state.dayStaff[id].missingDismissed = true;
    pushDayStaff(id);
  });
}

function addMissingBooked() {
  openAddStaff(missingBookedStaff.value.map(function (m) { return m.staff.id; }));
}

// Every booked service on today's book, keyed by its own id.
//
// Three separate functions used to walk `appts.rows × services` to answer
// this same question — by id, by turn, and for the appointment id behind a
// turn — and two of them ran once per turn per render. One pass, rebuilt
// only when the book itself changes.
var bookedServiceIndex = computed(function () {
  var map = {};
  appts.rows.forEach(function (a) {
    a.services.forEach(function (svc) { map[svc.id] = { appt: a, svc: svc }; });
  });
  return map;
});

// The booked service behind an id, if the book still carries it.
function bookedServiceById(apptServiceId) {
  if (!apptServiceId) return null;
  return bookedServiceIndex.value[apptServiceId] || null;
}

// How long the book says a linked service runs, and when it had it finishing.
// Snapshotted onto the turn so the countdown survives the booking changing or
// the plugin being switched off.
function scheduleFromLink(link) {
  var found = link && bookedServiceById(link.appointmentServiceId);
  if (!found || !found.svc.durationMinutes) return { minutes: null, endsAt: null };
  return {
    minutes: found.svc.durationMinutes,
    endsAt: found.svc.startsAt + found.svc.durationMinutes * 60000
  };
}

// The booked service behind a turn, if the book still carries it.
function bookedServiceFor(turn) {
  return turn ? bookedServiceById(turn.appointmentServiceId) : null;
}

/* How far this square started from its booking — see js/rules/start-offset.js.
 *
 * Read off the book as it is now, not as it was when the square went up, so
 * moving the booking in the booking system to when the client really sat down
 * takes the mark away. `diff` is the signed difference whatever the margin,
 * for the turn menu, which says it even when it is too small to mark; `flag`
 * is the mark itself, or null. Null overall for a square with no booking the
 * book still carries. */
var startOffsetMinutes = computed(function () {
  return offsetRules.cleanOffsetMinutes(prefs.get('startOffsetMinutes'));
});

function startOffsetOf(turn) {
  var found = bookedServiceFor(turn);
  if (!found || found.svc.startsAt == null) return null;
  var bookedAt = found.svc.startsAt;
  return {
    bookedAt: bookedAt,
    diff: offsetRules.startOffsetMs(turn.startedAt, bookedAt),
    flag: offsetRules.startOffset(turn.startedAt, bookedAt, startOffsetMinutes.value)
  };
}

/* Everything a square is, as a list: the service its own columns carry first,
 * then whatever was merged into it, in the order it was merged.
 *
 * One reader for every place that has to say what was done — the square's
 * colour chips and its tooltip, the menu a tap opens, History, and the day's
 * service mix. They were all reading `t.serviceName` and would each have needed
 * their own idea of what a second service means; this is the one idea.
 *
 * Always at least one entry, so a caller can take `[0]` without checking. The
 * colour is looked up live rather than snapshotted — a service recoloured this
 * afternoon should recolour the squares it is on, the same as everywhere else —
 * and falls back for one that has since been deleted.
 */
function servicesOfTurn(t) {
  if (!t) return [];
  var out = [{
    serviceId: t.serviceId,
    name: t.serviceName || 'Removed service',
    letter: t.serviceLetter || '?',
    weight: t.turnWeight,
    startedAt: t.startedAt,
    endedAt: t.endedAt,
    bookedName: t.sourceServiceName || '',
    appointmentServiceId: t.appointmentServiceId || null,
    clientName: t.clientName || '',
    options: optionsOfTurn(t),
    merged: false
  }];
  (t.extras || []).forEach(function (m) {
    out.push({
      serviceId: m.serviceId,
      name: m.serviceName || 'Removed service',
      letter: m.serviceLetter || '?',
      // What it was worth on its own. The square's combined weight is on the
      // turn and is the desk's answer, not the sum of these — see mergeIntoTurn.
      weight: m.turnWeight,
      startedAt: m.startedAt,
      endedAt: m.endedAt,
      bookedName: m.sourceServiceName || '',
      appointmentServiceId: m.appointmentServiceId || null,
      clientName: m.clientName || '',
      options: m.options || [],
      merged: true
    });
  });
  out.forEach(function (s) {
    var svc = s.serviceId && serviceById.value[s.serviceId];
    s.color = svc ? svc.color : DEFAULT_SERVICE_COLOR;
  });
  return out;
}

// The square carries more than one service. Asked once per square per paint, so
// it reads the list rather than building one.
function isMergedTurn(t) { return !!(t && t.extras && t.extras.length); }

/* How long a square is expected to run, and when it is due to finish.
 *
 * Two sources, in this order and never the other way round:
 *
 *   the booking   what the client is actually booked for, in minutes, off the
 *                 book. A 90-minute pedicure is 90 minutes whatever the type
 *                 usually takes.
 *   the type      the default length set against the turn type in Settings,
 *                 which is all a walk-in has to go on.
 *
 * Neither, and there is no countdown — the square counts up, which is what
 * every square did before types had a default.
 *
 * `startedAt` is the square's own start, not the booking's: a client seated
 * twenty minutes late has twenty minutes more, not twenty minutes less.
 */
function scheduleFor(link, serviceId, startedAt) {
  var booked = scheduleFromLink(link);
  if (booked.minutes != null) return booked;
  var svc = serviceById.value[serviceId];
  var mins = svc && svc.defaultMinutes;
  if (!mins) return { minutes: null, endsAt: null };
  return { minutes: mins, endsAt: (startedAt || Date.now()) + mins * 60000 };
}

// How many options each turn carries, for the + the square draws. Worked out
// for every turn at once for the same reason the mismatch flag below is: the
// board redraws every second and reads this once per square each time.
var optionCountByTurn = computed(function () {
  var out = {};
  state.turns.forEach(function (t) {
    var n = optionsOfTurn(t).length;
    if (n) out[t.id] = n;
  });
  return out;
});

// The board says one technician did the service and the book says another.
// The board is the record of what actually happened, so this is not something
// to fix here — it is pointed at because the ticket, and the commission on it,
// is about to go to the wrong person in the outside system.
//
// Worked out for every turn at once. The board asks this three times per
// filled square — once for the square's own flag and twice per turn for the
// tooltip — and it used to rescan the entire book on each of them.
var mismatchByTurn = computed(function () {
  var out = {};
  if (!scheduling.enabled.value) return out;
  var index = bookedServiceIndex.value;
  var byId = staffById.value;
  var sold = saleLineByService.value;
  state.turns.forEach(function (t) {
    if (!t.appointmentServiceId) return;
    var link = index[t.appointmentServiceId];
    if (!link) return;
    var staff = byId[t.staffId];
    if (!staff) return;

    /* Once the client has paid, the sale is the record to agree with, not the
     * booking: it is what the commission goes on, and it is what the desk
     * corrects at checkout. A square written up from the sale on the person
     * who actually did the work is right, whatever the booking still says. */
    var line = sold[t.appointmentServiceId];
    if (line && line.staffId) {
      var paid = staffForSaleLine(line);
      if (paid && paid.id === staff.id) return;
      if (!paid && matchesStaff(staff, line.label)) return;
      out[t.id] = {
        onBoard: t.staffName || staff.name,
        inBook: paid ? paid.name : (line.label || 'somebody else'),
        onSale: true,
        clientName: link.appt.clientName,
        serviceName: link.svc.name
      };
      return;
    }

    var labels = (link.svc.techs || []).filter(Boolean);
    if (!labels.length) return;
    if (labels.some(function (l) { return matchesStaff(staff, l); })) return;
    out[t.id] = {
      onBoard: t.staffName || staff.name,
      inBook: labels.join(', '),
      onSale: false,
      clientName: link.appt.clientName,
      serviceName: link.svc.name
    };
  });
  return out;
});

function turnStaffMismatch(turn) {
  return (turn && mismatchByTurn.value[turn.id]) || null;
}

/* ---------- two squares the wrong way round ----------
 *
 * A mismatch on its own has no fix on the board. The board is the record of
 * what happened, so the wrong name is the booking system's to correct, and
 * that is all the flag on the square has ever said.
 *
 * A *pair* of them is a different fact. When the book has each of two squares
 * against the other's technician, by far the likeliest reading is that the two
 * were put up crossed over — and then one swap puts both records straight
 * without anybody touching the booking system at all.
 *
 * The book is the authority throughout, and it is read live rather than off
 * the flag that raised the offer: who Mangomint has on the appointment decides
 * whether the swap resolves anything, both when the offer is made and again at
 * the moment it is taken. If the book has moved on in between, the swap does
 * not happen and says why.
 *
 * Offered only where there is exactly one way to read it. Two squares that
 * could each pair with the same third are three squares in a muddle, not a
 * swap, and a one-press fix there would be a guess wearing a button.
 */

// Who the book has on the booking behind a square, as it stands this second.
function bookedTechLabels(turn) {
  var link = turn && bookedServiceIndex.value[turn.appointmentServiceId];
  return link ? (link.svc.techs || []).filter(Boolean) : [];
}

function bookNames(labels, staff) {
  return !!staff && labels.some(function (l) { return matchesStaff(staff, l); });
}

/* Why these two cannot trade places, whatever raised the offer.
 *
 * Structural only: this is what the room and the grid will allow. Whether the
 * BOOK endorses the swap is a separate question, asked separately, because the
 * two swaps on this board are asked for two different reasons — see
 * `swapBookReason` and `swapReasonFor` below.
 */
function swapBlockedReason(a, b) {
  if (!a || !b || a.id === b.id) return 'That is one square, not two.';
  var sa = staffOf(a.staffId), sb = staffOf(b.staffId);
  if (!sa || !sb) return 'One of those squares has nobody on it.';
  if (sa.id === sb.id) return 'Both of those are on the same technician.';
  var da = state.dayStaff[sa.id], db = state.dayStaff[sb.id];
  if (!da || !da.present) return sa.name + ' is not marked in today.';
  if (!db || !db.present) return sb.name + ' is not marked in today.';

  // Two services running on one technician is the one thing the grid cannot
  // draw, so a swap that would leave one is not offered. Only a running square
  // can land on somebody; a finished one takes no chair with it. In the
  // ordinary swap each of these finds the other half of the pair and passes.
  if (a.endedAt == null) {
    var busyB = activeTurn(sb.id);
    if (busyB && busyB.id !== b.id) return sb.name + ' has another service running.';
  }
  if (b.endedAt == null) {
    var busyA = activeTurn(sa.id);
    if (busyA && busyA.id !== a.id) return sa.name + ' has another service running.';
  }
  return null;
}

/* Why the BOOK will not have these two traded.
 *
 * Asked only of the swap the book itself proposed — the crossed-over pair off
 * the conflicts pane. That offer exists because the book disagrees with the
 * board, so a book that stops disagreeing takes the offer away with it; there
 * is nothing left for the swap to put right.
 *
 * Deliberately NOT asked of a swap the desk performed by hand. Two clients
 * changing chairs is a thing that happens in the room, and the desk watching it
 * happen outranks the booking system — see `swapTargetFor`.
 */
function swapBookReason(a, b) {
  var sa = staffOf(a.staffId), sb = staffOf(b.staffId);
  if (!sa || !sb) return 'One of those squares has nobody on it.';

  // The whole point of this swap is that it leaves nothing behind, so both
  // halves are checked against the book rather than one.
  var la = bookedTechLabels(a), lb = bookedTechLabels(b);
  if (!la.length || !lb.length) {
    return 'The book no longer says who one of those is for.';
  }
  if (!bookNames(la, sb)) {
    return 'The book does not have ' + (a.clientName || 'that client') +
      ' with ' + sb.name + '.';
  }
  if (!bookNames(lb, sa)) {
    return 'The book does not have ' + (b.clientName || 'that client') +
      ' with ' + sa.name + '.';
  }
  // Nothing to resolve: the book already agrees with the board on one of them,
  // so swapping would take a right answer off and put a wrong one on.
  if (bookNames(la, sa) || bookNames(lb, sb)) {
    return 'The book already agrees with the board on one of those.';
  }
  return null;
}

// The whole of the check for one swap, by what raised it. Every path in and out
// of a swap goes through this, so what the board offers, what the sheet says
// and what the write does can never disagree about it.
function swapReasonFor(a, b, kind) {
  return swapBlockedReason(a, b) || (kind === 'book' ? swapBookReason(a, b) : null);
}

// turnId -> the one square it would trade places with. Only ever a pair that
// reads one way: anything with a second candidate is left off entirely, on
// both sides, rather than offered as whichever was found first.
var swapPartnerByTurn = computed(function () {
  var out = {};
  if (!scheduling.enabled.value) return out;
  var mm = mismatchByTurn.value;
  var list = state.turns.filter(function (t) { return !!mm[t.id]; });
  if (list.length < 2) return out;

  var cand = {};
  for (var i = 0; i < list.length; i++) {
    for (var j = i + 1; j < list.length; j++) {
      if (swapReasonFor(list[i], list[j], 'book')) continue;
      (cand[list[i].id] = cand[list[i].id] || []).push(list[j].id);
      (cand[list[j].id] = cand[list[j].id] || []).push(list[i].id);
    }
  }
  Object.keys(cand).forEach(function (id) {
    if (cand[id].length !== 1) return;               // more than one reading
    var other = cand[id][0];
    if (!cand[other] || cand[other].length !== 1) return;
    out[id] = other;
  });
  return out;
});

// Whether a square shares its place with another. Half turns pair with
// whatever follows them, so moving one out from under its partner re-forms the
// square below as a side effect — which is fine for a move, where the sheet
// says so, and is not something a two-square swap has anywhere to say.
function sharesSquare(turn) {
  var slot = slotsFor(turn.staffId).find(function (s) {
    return s.turns.some(function (t) { return t.id === turn.id; });
  });
  return !!slot && slot.turns.length > 1;
}

/* What dragging a square onto a technician's column would trade it with: the
 * one they have running, and why the trade is being offered. Null everywhere
 * else, which is most of the board.
 *
 * Two swaps, and they are asked for different reasons:
 *
 *   book    the crossed-over pair. The book has each of these two against the
 *           other's technician, so the swap is a correction and the book is the
 *           authority on whether it still resolves anything.
 *   board   two clients in chairs. Nothing is wrong with the record — the room
 *           changed, the desk saw it, and the board is being told. This needs
 *           no book at all, which is the point: most salons run without one.
 *
 * The book reading wins where both are true, because it has more to say.
 */
function swapTargetFor(turn, staffId) {
  if (!turn || !staffId || turn.staffId === staffId) return null;
  var busy = activeTurn(staffId);
  if (!busy) return null;
  if (swapPartnerByTurn.value[turn.id] === busy.id) {
    return { mate: busy, kind: 'book' };
  }
  // A finished square is a record and takes no chair with it, so it has nothing
  // to trade — it moves, and the move sheet is where that conversation happens.
  if (turn.endedAt != null) return null;
  if (sharesSquare(turn) || sharesSquare(busy)) return null;
  if (swapBlockedReason(turn, busy)) return null;
  return { mate: busy, kind: 'board' };
}

/* ---------- opening the day from the book ---------- */

/* Run once per business date, and only on a day nobody has touched yet: with
 * no day records at all, the board has not been set up, so the book is the
 * best guess at who is in. Anyone it does not know about is left to the prompt.
 *
 * "Once per business date" used to mean once per tab — a module variable, set
 * when this ran and gone the moment the page did. So a station that had set the
 * morning up, and then signed out and back in at lunchtime, was asked who was
 * in all over again; and because the roster it came back to was whatever the
 * server had, answering that second prompt marked everybody in afresh and
 * charged them for the turns already on the board. The record of having asked
 * now sits in tt_days, where the answer to it already lived.
 */
function openDayFromBook() {
  if (dayOpened.value) return;
  if (ui.loading) return;
  // Not before three quarters of an hour to opening — and never at all on a
  // day the salon is shut. Deliberately not remembered: this is the one guard
  // here that stops being true and then starts being true again, so it is
  // asked every minute rather than settled once. See the business-hours
  // section.
  if (!dayStartDue.value) return;
  if (!scheduling.enabled.value) return;
  // Not before Mangomint's shifts have been asked for, where the provider has
  // them: setting the day up off the book alone and then finding out who else
  // is working would put the Who else is in sheet up for nothing.
  if (scheduling.can('workHours') && workHours.loadedFor !== state.businessDate) return;
  // The book not in yet — unless Mangomint's shifts are, which say who is in
  // on their own.
  if (!appts.rows.length && !Object.keys(mmDayByStaff.value).length) return;
  // A day already under way is a day somebody set up on a build that had
  // nowhere to record it, or one worked without the book. Either way the
  // question has been answered — so it is written down as answered rather than
  // asked again, which is also what retires the old marker without a migration.
  if (Object.keys(state.dayStaff).length) {
    setDay(sheetStartedAt.value || Date.now(), null);
    return;
  }
  setDay(Date.now(), null);

  // A fresh day starts with the columns movable, whatever yesterday's station
  // left the lock on: the order is about to be set up from scratch.
  if (ui.lockOrder) setLockOrder(false);

  // Everybody booked, and everybody Mangomint has working today (Kevin,
  // 2026-10-22) — a later start goes on too, held out until then.
  var booked = bookedStaffToday.value.slice();
  rosterStaff.value.forEach(function (s) {
    if (mmDayOf(s.id) && booked.indexOf(s) === -1) booked.push(s);
  });
  if (!booked.length) return;
  // One number for the whole crew, for the reason spelled out on
  // confirmAddStaff. It is 0 on the ordinary morning this runs on — an empty
  // board, nothing missed — but this is also the path that runs on a day whose
  // turns are already up and whose roster is not, and marking the book's crew
  // in one at a time there would charge all but the first of them for the
  // squares the first one had.
  var missed = floorTurnsCompleted();
  booked.forEach(function (s) { setPresent(s.id, true, true, missed); });
  // The columns in the order people are in, ties alphabetically (Kevin,
  // 2026-10-25) — on a fresh morning that order is the rotation.
  arrangeOpeningColumns(booked);
  // Marked in inside one loop, so their times are milliseconds apart in
  // whatever order the book listed them. Left alone, that arbitrary order
  // would decide the whole morning's rotation — everyone is on zero turns, so
  // "who has been waiting longest" is the only rule in play. The columns are
  // the running order, so the day starts with the two agreeing.
  reorderArrivals();
  toast(booked.length + (booked.length === 1 ? ' tech is' : ' techs are') +
    ' in from today’s book.', 'ok');
  // Everyone else is a prompt, not an assumption — unless Mangomint has said
  // who is working today. Then the shifts are the answer the sheet was asking
  // for (Kevin, 2026-10-22), and anybody else is the + past the last column.
  if (Object.keys(mmDayByStaff.value).length) return;
  if (addableStaff.value.length) Vue.nextTick(function () { openAddStaff(); });
}

/* The columns the day opens with: the crew by when they are in, ties
 * alphabetically (work-hours.js `openingOrder`). An in time is known when
 * Mangomint has a shift for them or the desk typed one — not the board's
 * default, which is only a guess. Anybody without one keeps their column
 * order, after everybody who has. Only the crew's own places in the roster
 * move; somebody not in today stays where they were.
 *
 * Only ever run as a day opens: an order the desk has set, or dragged, is
 * never redone under it. */
function arrangeOpeningColumns(crew) {
  var roster = rosterStaff.value;
  var inCrew = {};
  crew.forEach(function (s) { inCrew[s.id] = true; });
  var ordered = workRules.openingOrder(roster
    .filter(function (s) { return inCrew[s.id]; })
    .map(function (s) {
      var sh = shiftOf(s.id);
      return { id: s.id, name: s.name, startAt: sh && sh.customStart ? sh.startAt : null };
    }))
    .map(function (c) { return c.id; });
  var ids = workRules.placeInRoster(roster.map(function (s) { return s.id; }), ordered);
  ids.forEach(function (id, i) {
    var s = staffOf(id);
    var order = (i + 1) * 10;
    if (s && s.sortOrder !== order) { s.sortOrder = order; pushStaff(s); }
  });
}

// The one tech in today who can take a service, if there is only one. Skipped
// techs are not available, so bypassing someone can make a colleague sole
// provider — which is exactly when the front desk needs the warning.
function soleProviderFor(ttServiceId) {
  var able = rotationStaff.value.filter(function (s) {
    return !isSkipped(s.id) && s.serviceIds.indexOf(ttServiceId) !== -1;
  });
  return able.length === 1 ? able[0] : null;
}

/* ---------- client requests on a booking ----------
 *
 * Two places can say a client asked for a technician, and Mangomint wins.
 *
 * Mangomint records it per booked service (`SpecificStaff`), but its webhooks
 * never carry it: every booked service arrives with one assigned tech whether
 * the client asked for them or the desk simply put them there. The board used
 * to read a named tech AS a request, which made every booking a request and
 * the star worth nothing. So the server polls Mangomint's calendar once a
 * minute (`requests_sync`, schema part 12) and the scheduling plugin hands the
 * result over as `fetchBookedRequests` — see `bookedRequestFor` below. Kevin,
 * 2026-10-18: SpecificStaff is a request however it was booked, and Mangomint
 * is authoritative — what it reports as a request cannot be unmarked here.
 *
 * The desk's own mark is for everything else: a service Mangomint reports as
 * `Anyone`, or one it has not reported on yet, or a provider that cannot say.
 * Right-click a service in today's book — or a Due now chip — and mark it.
 *
 * Either way the request is for a PERSON, not just for a line. Move the
 * appointment to somebody else and it stops counting, because moving an
 * appointment did not ask the client anything.
 */
var apptRequests = reactive({ rows: {} });   // appointment_service_id -> { staffId }

// What the booking system says, keyed by appointment|service|start (line ids
// do not survive an edit). Empty for a provider without the `requests` ability.
var bookedRequests = reactive({ byKey: {} });

function bookedRequestKey(apptId, serviceId, startsAt) {
  return apptId + '|' + serviceId + '|' + startsAt;
}

function staffByExternalId(externalId) {
  if (externalId == null || externalId === '') return null;
  var id = String(externalId);
  return state.staff.find(function (s) { return s.externalStaffId === id; }) || null;
}

/* What the booking system says about this booked service:
 *   undefined   it has not said — the desk's mark decides;
 *   null        it says nobody was asked for — the desk's mark decides;
 *   { staffId, staffIds, source: 'booked' }   a request, for those people.
 * A request for somebody the board has no technician linked to still counts as
 * a request; it just cannot say who for (`staffId` null), the same as a mark
 * made on a booking that named nobody. */
function bookedRequestFor(svc) {
  if (!svc || svc.appointmentId == null) return undefined;
  var row = bookedRequests.byKey[bookedRequestKey(svc.appointmentId, svc.serviceId, svc.startsAt)];
  var req = bookedRequest(row, staffByExternalId);
  if (!req) return req;
  return { staffId: req.staffIds[0] || null, staffIds: req.staffIds, source: 'booked' };
}

function isBookedRequest(svc) {
  return !!(ui.requestsOn && bookedRequestFor(svc));
}

async function loadBookedRequests() {
  var p = scheduling.provider.value;
  if (!scheduling.enabled.value || !sync.auth.user || !scheduling.can('requests') ||
      !p || !p.fetchBookedRequests) {
    bookedRequests.byKey = {};
    return;
  }
  var forDate = state.businessDate;
  try {
    var rows = await p.fetchBookedRequests(forDate);
    if (forDate !== state.businessDate) return;
    var out = {};
    rows.forEach(function (r) {
      out[bookedRequestKey(r.appointmentId, r.serviceId, r.startsAt)] = r;
    });
    bookedRequests.byKey = out;
    starBookedRequests();
  } catch (e) { /* offline — keep what we have */ }
}

/* ---------- held until checked in ----------
 *
 * Mangomint has a waiting room: a client who has arrived is Waiting, and is
 * moved to Checked In ("In Service") when a technician takes them back. Its
 * webhooks say neither, so the server's minute poll keeps
 * `appointment_status` (schema part 15), with the exact check-in time read off
 * the appointment's history.
 *
 * Kevin, 2026-10-19: a square put up for a visit still in the waiting room
 * does not start its timer until Mangomint has the client checked in. It goes
 * up held (`waiting`) — on the board, its technician spoken for — reads
 * "Waiting" instead of a clock, and starts from the check-in moment when the
 * status moves on, whichever station sees it first. The desk can start one by
 * hand from the square's menu, for the client nobody remembers to check in.
 *
 * A held square is not the visit being in service, so it does not set
 * auto-start off; the check-in does, once the square starts.
 */
var bookStatus = reactive({ byAppt: {} });

function isWaitingInBook(apptId) {
  var s = bookStatus.byAppt[apptId];
  return !!(s && s.status === 'Waiting');
}

async function loadBookStatus() {
  var p = scheduling.provider.value;
  if (!scheduling.enabled.value || !sync.auth.user || !scheduling.can('waitingRoom') ||
      !p || !p.fetchBookStatus) {
    bookStatus.byAppt = {};
    return;
  }
  var forDate = state.businessDate;
  try {
    var rows = await p.fetchBookStatus(forDate);
    if (forDate !== state.businessDate) return;
    bookStatus.byAppt = rows;
    releaseWaitingTurns();
    foldAddOns();
  } catch (e) { /* offline — keep what we have */ }
}

var bookStatusRefetchTimer = null;
function scheduleBookStatusRefetch() {
  if (bookStatusRefetchTimer) clearTimeout(bookStatusRefetchTimer);
  bookStatusRefetchTimer = setTimeout(function () {
    bookStatusRefetchTimer = null;
    loadBookStatus();
  }, 500);
}

// Past the waiting room: checked in, or checked out straight from it.
var PAST_WAITING = { CheckedIn: true, Finished: true };

/* Start every held square whose client Mangomint has now checked in, from the
 * check-in time — never later than now, never after the square was stopped.
 * A square stopped while held only loses its hold; nothing ran. */
function releaseWaitingTurns() {
  var released = [];
  state.turns.forEach(function (t) {
    if (!t.waiting || !t.appointmentId) return;
    var s = bookStatus.byAppt[t.appointmentId];
    if (!s || !PAST_WAITING[s.status]) return;
    startHeldTurn(t, s.checkedInAt);
    released.push(t);
  });
  if (released.length === 1) {
    var one = released[0];
    toast((one.clientName || 'The client') + ' checked in — ' + one.staffName + '’s ' +
      one.serviceName + ' started at ' + formatClock(one.startedAt) + '.', 'ok');
  } else if (released.length) {
    toast(released.length + ' clients checked in — their squares started.', 'ok');
  }
}

function startHeldTurn(t, at) {
  var now = Date.now();
  var start = at && at < now ? at : now;
  if (t.endedAt != null && start > t.endedAt) start = t.startedAt;
  t.waiting = false;
  t.startedAt = start;
  if (t.endedAt == null) {
    var sched = scheduleFor({ appointmentServiceId: t.appointmentServiceId }, t.serviceId, start);
    t.scheduledMinutes = sched.minutes;
    t.scheduledEndAt = sched.endsAt;
  }
  pushTurn(t);
}

/* ---------- a service added at the chair is the same turn ----------
 * See js/rules/add-on.js. A booked service with no square of its own, added
 * to the visit after it started, and booked with a technician who already has
 * a square on it and does that work, goes onto that square as part of the same
 * sitting — no new square, no extra turn, the square's weight as it was. Unless
 * it counts as a full turn by itself, which is a new turn like any other. Done
 * once per line: a fold the desk takes back off stays off (`written_up_at`,
 * `seatedEver`), the same memory the automatic write-up keeps.
 */
function foldAddOns() {
  if (!scheduling.enabled.value || !scheduling.can('waitingRoom')) return;
  if (ui.loading || dayClosed.value || appts.bookDate !== state.businessDate) return;
  var seated = turnByApptService.value;
  var folded = [];
  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    var st = bookStatus.byAppt[a.id];
    if (!st || !st.servicesAdded || !st.servicesAdded.length) return;
    var visitTurns = state.turns.filter(function (t) { return t.appointmentId === a.id; });
    if (!visitTurns.length) return;
    a.services.forEach(function (svc) {
      if (seated[svc.id] || seatedEver[svc.id]) return;
      var seen = bookSeen[svc.id];
      if (seen && seen.writtenUpAt) return;
      var host = addOnHostTurn(a, svc, st, visitTurns);
      if (!host) return;
      foldInto(host, a, svc);
      folded.push({ host: host, appt: a, svc: svc });
    });
  });
  if (folded.length === 1) {
    var f = folded[0];
    toast(f.appt.clientName + ' added a ' + f.svc.name + ' with ' + f.host.staffName +
      ' — part of the same ' + f.host.serviceName + ' turn, not a new one.', 'info');
  } else if (folded.length) {
    toast(folded.length + ' services added at the chair went onto the squares already ' +
      'running for them — no new turns.', 'info');
  }
}

/* The square an unseated booked line is an add-on to, or null — the question
 * `foldAddOns` acts on, asked on its own so the appointments pane can leave a
 * line off under "Hide on board" before the fold has landed (the poll can be
 * a minute behind the book). */
function addOnHostTurn(a, svc, st, visitTurns) {
  if (!a || !svc || a.cancelled) return null;
  var status = st || bookStatus.byAppt[a.id];
  if (!status || !status.servicesAdded || !status.servicesAdded.length) return null;
  var turns = visitTurns || state.turns.filter(function (t) { return t.appointmentId === a.id; });
  if (!turns.length) return null;
  var onLine = rosterStaff.value.filter(function (s) {
    return (svc.techs || []).some(function (x) { return matchesStaff(s, x); }) &&
      mayTakeBooked(s, svc);
  }).map(function (s) { return s.id; });
  var mapped = trackerServiceForBooked(svc);
  var fullTurn = lineIsFullTurn(svc.price, mapped ? mapped.turnWeight : null, fullTurnPrice.value);
  var hostId = addOnHost({ serviceId: svc.serviceId, fullTurn: fullTurn },
    turns, status.servicesAdded, onLine);
  return (hostId && turnOf(hostId)) || null;
}

function foldInto(host, a, svc) {
  var mapped = trackerServiceForBooked(svc);
  var now = Date.now();
  var start = Math.max(host.startedAt, Math.min(svc.startsAt || now, now));
  var end = host.endedAt != null ? host.endedAt : null;
  if (end != null && start > end) start = end;
  host.extras = (host.extras || []).concat([{
    appointmentId: a.id, appointmentServiceId: svc.id,
    sourceServiceId: svc.serviceId == null ? null : svc.serviceId,
    clientId: a.clientId == null ? null : a.clientId,
    clientName: a.clientName || '', sourceServiceName: svc.name || '',
    serviceId: mapped ? mapped.id : null,
    serviceName: mapped ? mapped.name : (svc.name || ''),
    serviceLetter: mapped ? mapped.abbrev : '?',
    turnWeight: mapped ? mapped.turnWeight : 0,
    startedAt: start, endedAt: end,
    options: bookedOptionsOf(svc),
    addOn: true
  }]);
  // Still one sitting and one square, but what was added counts toward its
  // price, which can take it from a half turn to a full one.
  pushTurn(host);
  applyPriceWeights();
  noteWrittenUp(host);
}

// The desk's own answer, from the square's menu: start it now.
function startWaitingNow(turnId) {
  var t = turnOf(turnId);
  if (!t || !t.waiting) return;
  startHeldTurn(t, Date.now());
  ui.cellMenuTurnId = null;
  ui.turnMenu = null;
  toast(t.staffName + '’s ' + t.serviceName + ' started.', 'ok');
}

/* A square already up for a service Mangomint has as a request gets its star,
 * the same as marking it by hand does. Only ever added here, never taken off:
 * a square is its own record from the moment it goes up, and a request that
 * later reads as Anyone in Mangomint is more likely an edit to the booking
 * than the client changing their mind about who did the work. */
function starBookedRequests() {
  if (!ui.requestsOn) return;
  (appts.rows || []).forEach(function (a) {
    (a.services || []).forEach(function (svc) {
      var t = turnByApptService.value[svc.id];
      if (!t || t.requested) return;
      var req = bookedRequestFor(svc);
      if (!req || req.staffIds.indexOf(t.staffId) === -1) return;
      t.requested = true;
      pushTurn(t);
    });
  });
}

// Whether this booking is a request for the technician the book currently has
// on it — Mangomint's word first, then the desk's mark. `staffId` optional:
// pass it to ask about a particular person rather than about whoever is named.
function apptRequestFor(svc, staffId) {
  if (!ui.requestsOn) return null;
  return requestInBook(svc, staffId);
}

// The same answer without the display switch. "Track client requests" decides
// whether the board SHOWS requests; under turn order the booking's request also
// decides who takes the client, and a SpecificStaff booking is a request
// whether or not the salon looks at stars. The desk's own mark still counts
// only where `deskMarksRequests` lets it, which itself needs the switch.
function requestInBook(svc, staffId) {
  if (!svc) return null;
  var booked = bookedRequestFor(svc);
  if (booked) {
    // Still with somebody it was asked for? The poll catches up within a
    // minute; the book can move sooner. See js/rules/booked-request.js.
    var onBooking = booked.staffIds.filter(function (id) {
      var s = staffOf(id);
      return s && (svc.techs || []).some(function (x) { return matchesStaff(s, x); });
    });
    var still = requestStillHolds(booked, onBooking, staffId);
    if (!still) return null;
    return { staffId: still[0] || null, source: 'booked' };
  }
  // The desk's own mark counts only where the desk may make one — with a book
  // connected, the book is the only word on it (`deskMarksRequests`).
  var mark = deskMarksRequests.value ? apptRequests.rows[svc.id] : null;
  if (!mark) return null;
  // A mark with nobody on it — the booking named no one when it was made — is
  // still a request; the board just cannot say who for.
  if (mark.staffId) {
    // Does the book still have it with the person who was asked for? This is
    // checked before the caller's own question, not after: a booking moved to
    // somebody else is not a request for ANYBODY, least of all for the one it
    // was moved away from.
    var s = staffOf(mark.staffId);
    if (!(svc.techs || []).some(function (x) { return matchesStaff(s, x); })) return null;
    if (staffId && mark.staffId !== staffId) return null;
  }
  return mark;
}

function isApptRequested(svc, staffId) { return !!apptRequestFor(svc, staffId); }

/* Whether the desk may say a client asked for somebody (Kevin, 2026-10-25).
 * Not while a booking system is connected: then it is read from Mangomint, or
 * whatever book the salon has, and the board offers no way to mark it — not
 * on a square, a line in today's book, a Due now chip or the hover card — and
 * counts no mark the desk made before. A walk-in asking for somebody at the
 * desk is not a booking, and is still put up as a request from the sheet. */
var deskMarksRequests = computed(function () {
  return !!ui.requestsOn && !scheduling.enabled.value;
});

// The tech a booking would be a request for: the one the book names, if they
// are on the roster. Null when the book names nobody the board knows.
function bookedStaffFor(svc) {
  var labels = (svc && svc.techs) || [];
  if (!labels.length) return null;
  var found = null;
  rosterStaff.value.forEach(function (s) {
    if (!found && labels.some(function (x) { return matchesStaff(s, x); })) found = s;
  });
  return found;
}

/* ---------------- desk notes ---------------- */

/* Stickies on the front-desk screen; the rules for which to show are in
 * js/rules/notes.js and this is the state around them.
 *
 * Three things live OUTSIDE what a reload replaces, on purpose. `fetched` is the
 * server's answer and is swapped wholesale. `localPosted` is notes this screen
 * posted that a fetch may not have caught yet, and `dismissedIds` is notes
 * waved away on this screen. If those were part of `fetched`, a refetch that
 * was already in flight when someone posted or dismissed would bring the old
 * picture back for a moment, and a note would vanish and return.
 *
 * mergeNotes does not prune; the two loops in loadNotes do. */
var deskNoteState = reactive({ fetched: [] });
var localPosted = reactive(new Map());
var dismissedIds = reactive(new Set());
var notesLoadSeq = 0;

async function loadNotes() {
  if (!sync.auth.user) { deskNoteState.fetched = []; return; }
  var seq = ++notesLoadSeq;
  try {
    var rows = await sync.fetchNotes();
    // An older answer arriving after a newer one is the stale one.
    if (seq !== notesLoadSeq) return;
    var returned = {};
    rows.forEach(function (r) { returned[r.id] = true; });
    // The server has these now, and its copy is the one to trust.
    Array.from(localPosted.keys()).forEach(function (id) {
      if (returned[id]) localPosted.delete(id);
    });
    // A dismissal is only needed while the server still lists the note. A note
    // this screen posted and has not yet been told about is still being held
    // in localPosted, so its dismissal must outlive that.
    Array.from(dismissedIds).forEach(function (id) {
      if (!returned[id] && !localPosted.has(id)) dismissedIds.delete(id);
    });
    deskNoteState.fetched = rows;
  } catch (e) { /* offline — keep what we have */ }
}

// Catch up on notes missed while the screen was asleep or offline.
window.addEventListener('online', function () { loadNotes(); });
document.addEventListener('visibilitychange', function () {
  if (!document.hidden) loadNotes();
});

var deskNotes = computed(function () {
  return deskNoteRules.visibleNotes(
    deskNoteRules.mergeNotes(deskNoteState.fetched, localPosted),
    minuteNow.value, dismissedIds);
});

function localDateKey(d) {
  return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

/* When the salon closes on the calendar day `d` falls on, or null if it is shut
 * or has no hours. A closing time at or before opening is the small hours of the
 * next morning, the same reading todayHours gives it. */
function closeMsOn(d) {
  var h = businessHours.value;
  var day = h && h[d.getDay()];
  if (!day) return null;
  var key = localDateKey(d);
  var openAt = clockToMs(key, day.open);
  var closeAt = clockToMs(key, day.close);
  if (openAt == null || closeAt == null) return null;
  if (closeAt <= openAt) closeAt += 86400000;
  return closeAt;
}

var NOTE_MAX_AHEAD_MS = 366 * 86400000;

/* Leave a note. Returns false, writing nothing, when it cannot be one: no text,
 * too much, a colour the table would refuse, or an end time that is not in the
 * future (or is more than a year out). The form reads false as "ask again". */
function postNote(o) {
  var body = String((o && o.body) || '').trim();
  if (!body || body.length > deskNoteRules.NOTE_BODY_MAX) return false;
  if (deskNoteRules.NOTE_COLORS.indexOf(o.color) < 0) return false;
  var nowMs = Date.now();
  var today = new Date(nowMs);
  var tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  var expiresAt = deskNoteRules.noteExpiry(o.choice, {
    nowMs: nowMs,
    closeTodayMs: closeMsOn(today),
    closeTomorrowMs: closeMsOn(tomorrow),
    endOfTodayMs: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 0, 0).getTime(),
    endOfTomorrowMs: new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 23, 59, 0, 0).getTime(),
    pickedMs: o.pickedMs
  });
  if (expiresAt == null || expiresAt > nowMs + NOTE_MAX_AHEAD_MS) return false;

  var author = String(o.author || '').trim();
  var id = sync.uuid();
  var stamp = new Date(nowMs).toISOString();
  localPosted.set(id, {
    id: id, body: body, author: author || null, source: 'desk', color: o.color,
    urgent: !!o.urgent, expiresAt: expiresAt, createdAt: nowMs, closedAt: null
  });
  sync.enqueue({
    table: 'tt_notes', type: 'upsert', onConflict: 'id',
    row: {
      id: id, body: body, author: author || null, source: 'desk', external_id: null,
      color: o.color, urgent: !!o.urgent,
      expires_at: new Date(expiresAt).toISOString(),
      created_at: stamp, updated_at: stamp
    }
  });
  return true;
}

/* Dismiss on this screen at once, and close the note for every screen. The
 * write only lands on a note nobody has closed, so a system note that was
 * resolved meanwhile keeps its own reason. */
function dismissNote(id) {
  dismissedIds.add(id);
  // Posted here and never fetched: nothing for a fetch to hand back, and the
  // dismissal no longer has a row to hide.
  localPosted.delete(id);
  var stamp = new Date().toISOString();
  sync.enqueue({
    table: 'tt_notes', type: 'update', match: { id: id }, isNull: ['closed_at'],
    patch: { closed_at: stamp, closed_how: 'dismissed', updated_at: stamp }
  });
}

async function loadApptRequests() {
  if (!scheduling.enabled.value || !sync.auth.user) { apptRequests.rows = {}; return; }
  try {
    var rows = await sync.fetchApptRequests(state.businessDate);
    var out = {};
    rows.forEach(function (r) { out[r.appointmentServiceId] = { staffId: r.staffId }; });
    apptRequests.rows = out;
  } catch (e) { /* offline — keep what we have */ }
}

/* Mark or unmark. Optimistic like every other write, and keyed by the booking
 * line, so an appointment edited in Mangomint — which reissues its line ids —
 * drops the mark rather than carrying it onto a service nobody asked for. */
function toggleApptRequest(appt, svc) {
  if (!deskMarksRequests.value || !svc) return;
  if (bookedRequestFor(svc)) {
    // Mangomint is authoritative on this one, so the board has nothing to undo:
    // it would only come back on the next poll.
    toast('Booked as a request in Mangomint — change it there.', 'warn');
    return;
  }
  var existing = apptRequests.rows[svc.id];
  if (existing) {
    delete apptRequests.rows[svc.id];
    sync.enqueue({
      table: 'tt_appt_requests', type: 'delete',
      match: { appointment_service_id: svc.id }
    });
    // A turn already on the board is its own record from the moment it goes
    // up, so the star comes off that too — otherwise unmarking here would
    // leave a starred square nobody could explain.
    var live = turnByApptService.value[svc.id];
    if (live && live.requested) { live.requested = false; pushTurn(live); }
    toast('Not a request.', 'ok');
    return;
  }
  var staff = bookedStaffFor(svc);
  apptRequests.rows[svc.id] = { staffId: staff ? staff.id : null };
  sync.enqueue({
    table: 'tt_appt_requests', type: 'upsert', onConflict: 'tenant_id,appointment_service_id',
    row: {
      appointment_service_id: svc.id,
      appointment_id: appt ? appt.id : null,
      business_date: state.businessDate,
      staff_id: staff ? staff.id : null
    }
  });
  var seated = turnByApptService.value[svc.id];
  if (seated && !seated.requested) { seated.requested = true; pushTurn(seated); }
  toast('Marked as a request' + (staff ? ' for ' + staff.name : '') + '.', 'ok');
}

// The next booking that is *locked* to a tech, per column — not merely one the
// book has them pencilled against. Empty whenever the scheduling plugin is off:
// with no book there is no future to know about, only whose turn is next.
// Three ways a booking gets locked:
//
//   booked     the booking names them. The book has that client with that
//              technician at that time, which pins the column whatever else
//              is true. This is all a booking is until somebody says more.
//   requested  the desk has marked it as a client request for them. Only ever
//              reported while requests are switched on, and never inferred —
//              see the note above.
//   only       nobody else in today performs that service, whoever the book has
//              on it — the appointment has to wait for this tech.
//
// A booking whose start time has passed but that nobody has seated still counts:
// a running-late client is exactly what to know before handing out a walk-in.
// Anything already on the board is somebody's current turn, not their next.
var nextBookingByStaff = computed(function () {
  var t = minuteNow.value;                 // ticks so bookings fall off as they end
  var out = {};
  var roster = presentStaff.value;
  if (!roster.length || !appts.rows.length) return out;

  // `mapped` rides along: the column header draws the turn type's letter, and
  // working it out again per column per frame is the same answer at a cost.
  function claim(staff, why, a, svc, mapped) {
    var cur = out[staff.id];
    if (!cur || svc.startsAt < cur.svc.startsAt) {
      out[staff.id] = { appt: a, svc: svc, why: why, mapped: mapped };
    }
  }

  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      if (turnByApptService.value[svc.id]) return;
      if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
      if (svc.startsAt + (svc.durationMinutes || 0) * 60000 < t) return;

      var mapped = trackerServiceForBooked(svc);
      var named = null;
      roster.forEach(function (s) {
        if ((svc.techs || []).some(function (x) { return matchesStaff(s, x); })) {
          named = s;
          claim(s, isApptRequested(svc, s.id) ? 'requested' : 'booked', a, svc, mapped);
        }
      });

      var sole = mapped ? soleProviderFor(mapped.id) : null;
      if (sole && (!named || named.id !== sole.id)) claim(sole, 'only', a, svc, mapped);
    });
  });
  return out;
});

function nextBookingFor(staffId) { return nextBookingByStaff.value[staffId] || null; }

/* ---------- who else could take what is coming ----------
 *
 * The header clock says what is next and how long there is. The question the
 * front desk asks straight afterwards is "does it have to be her?", because
 * the answer is what decides whether she can be handed a walk-in meanwhile.
 *
 * A booking with a technician's name on it pins that technician for as long
 * as it runs: nobody the book has with a client at four o'clock is available
 * to be volunteered for something else at four o'clock, whatever the rotation
 * makes of it. A booking with nobody named on it pins nobody — work that can
 * move to whoever is free is exactly the work being asked about.
 *
 * The name on the booking is all this reads, which is why it works the same
 * whether requests are switched on or off. With them on the card calls it a
 * request; with them off it calls it what the book actually says. The
 * technician is unavailable either way, and that part was never a guess.
 *
 * Anything already seated is left out of all of this. From the moment a
 * square goes up the board is the record of that service, and a square still
 * running blocks its owner on its own account.
 */

// How long a booking holds somebody for. The book's own length first, then
// the turn type's usual — the same order the countdown uses, so a request
// blocks for exactly as long as its square would have run. Thirty minutes is
// the last resort: a request of no length at all would block nothing, which
// is the one answer that is certainly wrong.
function bookedHoldMs(svc, mapped) {
  var m = mapped === undefined ? trackerServiceForBooked(svc) : mapped;
  var mins = svc.durationMinutes || (m && m.defaultMinutes) || 30;
  return mins * 60000;
}

/* When the book has a booked service finishing — the one clock that says
 * whether that line is still to come, under way, or over.
 *
 * There used to be an appointment-level version of this beside it, the latest
 * finish across the whole visit, and everything that asked about a service
 * asked that instead. It is the wrong question in both directions on a visit
 * with more than one line on it, which is most of them: a two o'clock and a
 * three o'clock in the same booking are two pieces of work, usually with two
 * technicians and always with two squares, and neither one's state describes
 * the other. What is genuinely about the visit — whether it was cancelled —
 * is still read off the visit.
 */
function bookedServiceEnd(svc, mapped) {
  return svc.startsAt + bookedHoldMs(svc, mapped);
}

// Every request on today's book, per technician, in time order. Keyed off the
// same "the book names them" rule the next-booking clock uses, so a booking
// that shows as a request in one place is a request in the other.
var pinnedBlocksByStaff = computed(function () {
  var out = {};
  if (!scheduling.enabled.value) return out;
  var roster = presentStaff.value;
  if (!roster.length || !appts.rows.length) return out;
  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      if (turnByApptService.value[svc.id]) return;      // seated: the board has it
      if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
      var techs = svc.techs || [];
      if (!techs.length) return;
      roster.forEach(function (s) {
        if (!techs.some(function (x) { return matchesStaff(s, x); })) return;
        if (!out[s.id]) out[s.id] = [];
        out[s.id].push({
          from: svc.startsAt, to: svc.startsAt + bookedHoldMs(svc),
          appt: a, svc: svc, requested: isApptRequested(svc, s.id)
        });
      });
    });
  });
  Object.keys(out).forEach(function (k) {
    out[k].sort(function (x, y) { return x.from - y.from; });
  });
  return out;
});

/* Could this technician be given work that runs from `from` to `to`, and if
 * not, what is in the way? Deliberately not `availableQueue`, which answers a
 * different question — who is free *this second* — and would call somebody
 * available at half past three for a client they are promised to at half past
 * three.
 *
 * Reasons in the order they are worth knowing: a name in the book is a fact
 * about the time asked about; a running square is a fact about that time only
 * if it is still going then; being bypassed is a fact about now that may well
 * have lapsed by then, so it comes last and reads as a caveat.
 *
 * `exceptSvcId` leaves one booking out of the pinned list. Asking whether a
 * technician is free for a booking they are already named on would otherwise
 * always find that same booking in the way of itself.
 *
 * The book is every line of it here, movable or not, which is right for what
 * this is used for — the conflicts pane is *about* the book, and two clients in
 * one chair is worth saying whoever ends up sorting it out. `roomAt` below asks
 * the same question of the shorter list, for the callers that want the floor's
 * answer rather than the book's.
 */
function availabilityAt(staffId, from, to, exceptSvcId, bufferMs) {
  return availabilityAgainst(pinnedBlocksByStaff.value[staffId] || [],
    staffId, from, to, exceptSvcId, bufferMs);
}

// The measuring, shared by both of them. Which bookings count is the whole of
// the difference between the two, and a second copy of the rest would be two
// answers waiting to drift apart over a running square or a shift ending.
//
// `bufferMs` is the conflicts list's allowance for somebody running a few
// minutes behind (js/rules/conflict.js). Everything else passes nothing and
// gets the strict reading, which is the same overlap test as ever.
function availabilityAgainst(blocks, staffId, from, to, exceptSvcId, bufferMs) {
  var slack = bufferMs || 0;
  for (var i = 0; i < blocks.length; i++) {
    if (exceptSvcId && blocks[i].svc.id === exceptSvcId) continue;
    if (conflictRules.bookingsCollide(blocks[i], { from: from, to: to }, slack)) {
      return {
        ok: false, why: 'pinned', at: blocks[i].from, block: blocks[i],
        requested: !!blocks[i].requested
      };
    }
  }
  var cur = activeTurn(staffId);
  if (cur) {
    // A square with no expected end counts as in the way: nothing on the
    // board says when it finishes, so nothing says they will be out of it.
    var end = turnEndsAt(cur);
    if (conflictRules.readyTooLate(end, from, slack)) {
      return { ok: false, why: 'busy', until: end, turn: cur };
    }
  }
  // Held out by Mangomint's day until a time before this starts — not in yet,
  // or a lunch that will be over — is not in the way of it.
  var held = !(state.dayStaff[staffId] || {}).skipped && mmOutOf(staffId);
  if (isSkipped(staffId) && !(held && held.until <= from + slack)) return { ok: false, why: 'skipped' };
  // Still on the board and still in the rotation, but gone by the time this
  // runs. The book is the one place this comes up as a plan rather than as a
  // walk-in — a five o'clock finish and a six o'clock booking is a conflict
  // hours before anybody would otherwise notice it.
  var cut = shiftCutoff(staffId);
  if (cut != null && to > cut) {
    return { ok: false, why: 'shift', until: cut, endFrom: (shiftOf(staffId) || {}).endFrom };
  }
  // A time block coming up that this would run into. After the out time: a
  // finish is the bigger fact. The conflicts list passes this over for a
  // booking on them — Mangomint booked it knowing its own calendar.
  var brk = mmBreakInWay(staffId, from, to);
  if (brk) return { ok: false, why: 'break', at: brk.from, until: brk.to, label: brk.label };
  return { ok: true, why: 'free' };
}

// Everyone in today who could do a booking, and how each of them stands at
// the time it runs. Restricted to whoever performs what the booking really is
// — options included — and whoever its options allow; a tech who cannot do
// the work is not a candidate whose availability is worth reporting.
//
// Free first, then by turns, so the top of the list is the person the
// rotation would actually hand it to.
//
// `measure` is how each candidate's time is judged, and defaults to the whole
// of their book. The booking ledger below has to have it that way — it is the
// thing that decides which bookings can move, so it cannot ask a question that
// already assumes the answer. The conflicts list passes `roomAt` instead: see
// `conflictCover`.
//
// A service the visit lets run alongside another (js/rules/same-time.js) may be
// started early by whoever takes it: a 12:45 pedicure after a noon manicure can
// begin any time from noon. So a candidate not free at the booked time is tried
// at every earlier start in that window where a stretch of theirs could open,
// and `startAt` says when they would begin. The booked time is tried first —
// a stand-in who can keep to the book is the plan that changes least.
function coverForBooking(appt, svc, exceptStaffId, measure, comingIn) {
  var mapped = trackerServiceForBooked(svc);
  if (!mapped) return [];
  var from = svc.startsAt;
  var hold = bookedHoldMs(svc, mapped);
  var to = from + hold;
  var earliest = sameTimeEarliest(svc);
  var judge = measure || function (id, f, t) { return availabilityAt(id, f, t); };
  // The rotation's techs only: standing in for a colleague's booking, or
  // trading one in a swap, is taking a client who did not ask for them.
  // `comingIn` adds anybody due on the board later, judged the same way.
  return rotationStaff.value.concat(ownRules.inRotation(comingIn || [], isOwnClients))
    .filter(function (s) {
      return s.id !== exceptStaffId &&
        s.serviceIds.indexOf(mapped.id) !== -1 &&
        staffAllowedForBooked(svc, s.id);
    })
    .map(function (s, i) {
      var a = judge(s.id, from, to);
      var startAt = from;
      if (!a.ok && earliest < from) {
        var tries = sameTimeRules.startsToTry(earliest, from, freeingMomentsOf(s.id));
        for (var k = 0; k < tries.length; k++) {
          if (tries[k] === from) continue;
          var b = judge(s.id, tries[k], tries[k] + hold);
          if (b.ok) { a = b; startAt = tries[k]; break; }
        }
      }
      return { staff: s, ok: a.ok, why: a.why, until: a.until, at: a.at, col: i,
               requested: !!a.requested, credit: turnCredit(s.id), startAt: startAt };
    })
    .sort(function (x, y) {
      return (x.ok === y.ok ? 0 : x.ok ? -1 : 1) ||
        x.credit - y.credit || x.col - y.col;
    });
}

// Every moment something of this technician's comes to an end: the square
// they are on, and each booking of theirs. The only places a free stretch of
// theirs can begin, so the only early starts worth trying.
function freeingMomentsOf(staffId) {
  var out = (pinnedBlocksByStaff.value[staffId] || []).map(function (b) { return b.to; });
  var cur = activeTurn(staffId);
  var end = cur ? turnEndsAt(cur) : null;
  if (end != null) out.push(end);
  // Walking in counts: nothing of theirs can start before it.
  var inAt = readyFromFor(staffId);
  if (inAt != null) out.push(inAt);
  return out;
}

/* Who could take a booking the conflicts list is worried about, judged the way
 * the rest of the board judges room — `roomAt`, so a colleague whose own
 * booking would simply move to somebody else, or trade places in a swap, is
 * free for it here exactly as Next availability would call them free. Judging
 * the whole book instead was how the list came to say "nobody else is free"
 * beside a board offering three people for the same half hour.
 *
 * With the salon's buffer, so somebody finishing two minutes after it starts is
 * not reported as unable to take it.
 *
 * And with everybody's In time (Kevin, 2026-11-02): somebody not in yet is
 * free for a booking from the time they are scheduled in, and not before —
 * including the crew Mangomint has working whom the board has not put on yet. */
function conflictCover(appt, svc, exceptStaffId, bufferMs) {
  return coverForBooking(appt, svc, exceptStaffId, function (id, from, to) {
    var inAt = readyFromFor(id);
    if (inAt != null && conflictRules.readyTooLate(inAt, from, bufferMs)) {
      return { ok: false, why: 'notin', until: inAt };
    }
    return roomAt(id, from, to, svc.id, bufferMs);
  }, scheduledOffBoard.value);
}

/* Which of a technician's coming bookings genuinely stand in a walk-in's way,
 * per technician, in time order.
 *
 * Not all of them do, and this is the same judgement the clash warning makes
 * when a walk-in runs into one: the book naming somebody is a plan, and a plan
 * somebody else could carry out is a plan that can move. Two kinds cannot:
 *
 *   requested   a promise to a person. The board never offers to break one.
 *   uncovered   nobody else in today performs it, or nobody else who does will
 *               be free while it runs. It has to wait for this technician,
 *               whatever else is standing at the desk.
 *
 * A booking whose service is not mapped to a turn type counts as uncovered,
 * because it is: `coverForBooking` cannot name a replacement for work the
 * board has no way to classify, so there is nobody to move it to.
 *
 * Everything else is left out on purpose. Holding an estimate back for a
 * booking that the desk would simply hand to a colleague would report a wait
 * the salon does not actually have.
 *
 * But a colleague can only be in one chair at a time, and each booking used to
 * be asked its own question with no memory of the last one. A technician with
 * two bookings over the same stretch — a manicure booked across the end of a
 * pedicure, which the book allows — was handed the same list of stand-ins
 * twice and had both bookings written off against the same free person. The
 * board then called her clear from the moment she stood up, on the strength of
 * one colleague being in two places.
 *
 * So the stand-ins are spent rather than merely counted: each booking takes a
 * colleague who is free of their own book *and* not already promised to
 * something of this technician's that overlaps, and a booking with nobody left
 * to take it stays where it is.
 *
 * The ledger is per technician on purpose, because the question is per
 * technician: a walk-in is one client, and answering "when could Rosy take
 * one" only ever moves Rosy's own bookings. Two technicians may each read as
 * free at four on the strength of the same colleague, and both answers are
 * true — one of them is offered, the other keeps their own book. What is never
 * true is a single technician freed twice over by one person, and that is what
 * this stops.
 *
 * The stand-ins are kept as well as counted, because writing a booking off is
 * a plan somebody has to carry out. A technician reads as free at five on the
 * strength of a pedicure going to a colleague, and until that pedicure
 * actually moves the board is quietly promising a chair twice over. Next availability
 * says so on the card now — see `walkInMoves` — and it says it out of this
 * ledger rather than working the plan out a second time, so the name on the
 * card is the one the decision was made on.
 */
var bookingPlanByStaff = computed(function () {
  var out = {};
  var pinned = pinnedBlocksByStaff.value;
  Object.keys(pinned).forEach(function (staffId) {
    // Stand-in id -> the windows they have already been promised, for this
    // technician's bookings alone. A day's list per person, so a walk over it
    // beats keeping an index.
    var spent = {};
    function free(id, b) {
      var busy = spent[id] || [];
      for (var i = 0; i < busy.length; i++) {
        if (busy[i].from < b.to && busy[i].to > b.from) return false;
      }
      return true;
    }
    var keep = [], moves = [], swaps = [];
    // Bookings of colleagues this technician has agreed to take back in a
    // swap. They are hers if the swap is made, so a second swap cannot land
    // on top of one.
    var incoming = [];
    var mine = pinned[staffId];
    var me = staffOf(staffId);

    /* Could she take this booking of a colleague's in exchange? Performs it,
     * is allowed to by its options, and is free for it — against her whole
     * book except the booking she is swapping away, and against anything
     * already coming back to her. Her whole book on purpose: the rest of it
     * has not been decided yet, and counting it keeps the answer safe. */
    function takeBack(out, c) {
      if (!mayTakeBooked(me, c.svc)) return false;
      var others = mine.filter(function (x) { return x !== out; }).concat(incoming);
      return availabilityAgainst(others, staffId, c.from, c.to).ok;
    }

    // A visit's half turns are one technician's job, so they move together or
    // not at all — see js/rules/same-time.js. Everything else is its own unit.
    var units = sameTimeRules.halfPairs(mine.map(function (b) {
      var m = trackerServiceForBooked(b.svc);
      return { id: b.svc.id, apptId: b.appt.id, from: b.from, to: b.to,
               weight: m ? m.turnWeight : null, block: b };
    })).map(function (u) { return u.map(function (x) { return x.block; }); });

    // `units` keeps the order, and pinned blocks are already sorted by start.
    units.forEach(function (unit) {
      if (unit.length > 1) { planPair(unit); return; }
      var b = unit[0];
      if (b.requested) { keep.push(b); return; }
      // `coverForBooking` puts whoever is genuinely free at the front, so the
      // first one not already spent is the one the desk would reach for. What
      // they are spent for is the stretch they would actually work, which can
      // start before the book's time when the visit lets the service run
      // alongside another (`startAt`).
      var cover = coverForBooking(b.appt, b.svc, staffId);
      var stand = cover.find(function (c) {
        return c.ok && free(c.staff.id, workedBy(c, b));
      });
      if (stand) {
        (spent[stand.staff.id] = spent[stand.staff.id] || []).push(workedBy(stand, b));
        moves.push({ block: b, to: stand.staff, startsAt: stand.startAt });
        return;
      }
      // Nobody free of their own book. Somebody free apart from a booking
      // she could take back instead — see rules/swap.js.
      var swap = findSwap(b, cover.map(function (c) {
        return {
          id: c.staff.id, staff: c.staff,
          able: mayTakeBooked(c.staff, b.svc),
          clear: availabilityAgainst([], c.staff.id, b.from, b.to).ok,
          spent: !free(c.staff.id, b),
          book: pinned[c.staff.id] || []
        };
      }), function (c) { return takeBack(b, c); });
      if (!swap) { keep.push(b); return; }
      var to = staffOf(swap.to);
      (spent[swap.to] = spent[swap.to] || []).push(b);
      incoming = incoming.concat(swap.back);
      swaps.push({ out: b, to: to, back: swap.back });
    });

    /* A half-turn pair: both halves to one colleague who may do both and is
     * free for the whole stretch, one after the other, or both stay. No swaps
     * and no early starts — trading half of a pair away is splitting it. */
    function planPair(unit) {
      if (unit.some(function (b) { return b.requested; })) { keep.push.apply(keep, unit); return; }
      var span = { from: unit[0].from, to: Math.max.apply(null, unit.map(function (b) { return b.to; })) };
      var stand = coverForBooking(unit[0].appt, unit[0].svc, staffId).find(function (c) {
        return unit.every(function (b) { return mayTakeBooked(c.staff, b.svc); }) &&
          availabilityAt(c.staff.id, span.from, span.to).ok && free(c.staff.id, span);
      });
      if (!stand) { keep.push.apply(keep, unit); return; }
      (spent[stand.staff.id] = spent[stand.staff.id] || []).push(span);
      unit.forEach(function (b) { moves.push({ block: b, to: stand.staff, startsAt: b.from }); });
    }

    out[staffId] = { blocking: keep, moves: moves, swaps: swaps };
  });
  return out;
});

// The stretch a stand-in would actually work on a booking: from when they
// would start it (`startAt`, which a same-time visit can bring forward) for as
// long as the booking runs.
function workedBy(c, b) {
  var at = c.startAt == null ? b.from : c.startAt;
  return { from: at, to: at + (b.to - b.from) };
}

// Both halves of a swap ask this, so the booking going out and the one coming
// back are judged on the same terms: the turn type its options make it, and
// whoever those options limit it to (a design, say). See rules/swap.js.
function mayTakeBooked(staff, svc) {
  var mapped = trackerServiceForBooked(svc);
  return mayTake(staff, mapped ? mapped.id : null, allowedStaffForBooked(svc));
}

/* Who a swap would take this technician's work to, as move lines: the booking
 * going out, then what comes back to her and from whom. */
function swapMoves(staffId, swaps) {
  var me = staffOf(staffId);
  var out = [];
  swaps.forEach(function (s) {
    out.push({ appt: s.out.appt, svc: s.out.svc, to: s.to,
               startsAt: s.out.from, endsAt: s.out.to });
    s.back.forEach(function (c) {
      out.push({ appt: c.appt, svc: c.svc, to: me, from: s.to,
                 startsAt: c.from, endsAt: c.to });
    });
  });
  return out;
}

var blockingBlocksByStaff = computed(function () {
  var out = {};
  var plans = bookingPlanByStaff.value;
  Object.keys(plans).forEach(function (staffId) {
    if (plans[staffId].blocking.length) out[staffId] = plans[staffId].blocking;
  });
  return out;
});

/* `availabilityAt`'s question asked of the floor rather than of the book: could
 * this technician be given work over this stretch, counting only the bookings
 * of theirs that genuinely cannot move.
 *
 * Most bookings are not walls. An unrequested pedicure four other people are
 * free to do is a plan the desk moves without thinking about it, and the ledger
 * above has already worked out which ones those are — it is the whole reason
 * Next availability and the UP NEXT badge name anybody at all on a full
 * afternoon. Reading the raw pinned list instead is how the Due now chip came to
 * call somebody not free on the strength of a booking the badge beside it had
 * already written off: one question with two answers, and the desk right not to
 * believe either.
 *
 * Same shape and same reasons in the same order, so a caller can move between
 * the two by changing the call. `exceptSvcId` still leaves one booking out, and
 * still has to: a booking must not be found in the way of itself.
 */
function roomAt(staffId, from, to, exceptSvcId, bufferMs) {
  var plan = bookingPlanByStaff.value[staffId];
  var kept = blockingBlocksByStaff.value[staffId] || [];
  var swaps = plan ? plan.swaps : [];
  var outs = swaps.map(function (s) { return s.out; });
  var asIs = availabilityAgainst(kept.concat(outs), staffId, from, to, exceptSvcId, bufferMs);
  if (asIs.ok || !swaps.length) return asIs;
  // The same afternoon with the swaps made: what goes out is gone, what comes
  // back is in the way. A swap of the booking being asked about is no swap at
  // all — it is that booking, and it is already left out.
  var backs = [];
  swaps.forEach(function (s) {
    if (exceptSvcId && s.out.svc.id === exceptSvcId) return;
    backs = backs.concat(s.back);
  });
  var traded = availabilityAgainst(kept.concat(backs), staffId, from, to, exceptSvcId, bufferMs);
  return traded.ok ? traded : asIs;
}

/* How much room a walk-in of this type needs before it can be called a fit.
 *
 * A turn type with no usual length used to need none: nothing said how much
 * room it wanted, so the check was skipped and every answer about it was the
 * bare moment the technician came free. That reads as caution and is not —
 * most of the menu has no length set, so for most of Next availability the book was
 * not being consulted at all, and somebody with a client due in ten minutes
 * was offered for anything. An unmeasured walk-in still takes up the chair;
 * what is unknown is only how long for. So it is held for the same half hour
 * a booking with no length is held for — see `bookedHoldMs`, which has always
 * had to answer this exact question from the other side.
 *
 * The assumption works out who has room and does nothing else. It never
 * lengthens a square, and it is not what the clash warning or the closing
 * check measure: those two refuse things, and refusing on a guess is a
 * different kind of wrong. Setting the real length under Settings → Services
 * is what turns those on, and it sharpens this at the same time.
 */
var ASSUMED_WALKIN_MIN = 30;

function walkInFitMs(serviceId) {
  var svc = serviceById.value[serviceId];
  return ((svc && svc.defaultMinutes) || ASSUMED_WALKIN_MIN) * 60000;
}

/* The soonest this technician could *start* a walk-in of this type: after
 * whatever they are on now, and then past anything in the book that cannot be
 * moved off them, until there is a clear run long enough to do the work.
 *
 * Coming free is not the same as having room. Somebody finishing at two with a
 * request at ten past is free for ten minutes, which is no use at all to a
 * ninety-minute set — the walk-in has to fit, so it is measured against what
 * the work needs the same way the clash warning measures it.
 *
 * The moment asked about is the answer when nothing is in the way, which is
 * what makes this the whole of "is anybody free now" as well: see
 * `hasRoomNow`. Somebody idle is free from `from`; somebody on a square is
 * free when it is expected to end, or now if it is already over its time.
 *
 * Null when there is no answer: somebody bypassed, whose return is a decision
 * rather than a time, or a square with no expected end — nothing on the board
 * says when either of them is done.
 */
function earliestStartFor(staffId, serviceId, from) {
  var plan = startPlanFor(staffId, serviceId, from);
  return plan ? plan.at : null;
}

// The same measurement, saying as well whether it leans on the swaps in the
// booking plan — measured both ways in rules/swap.js `bestStart`, because a
// swap that hands a booking back can make a long walk-in start later rather
// than sooner. `walkInMoves` reads `swapped` to know which plan to report.
function startPlanFor(staffId, serviceId, from) {
  return startPlanForNeed(staffId, walkInFitMs(serviceId), from, null);
}

/* The same measurement for a run of a given length, optionally leaving one
 * booking out. A Due now chip is asking about a booking rather than a walk-in:
 * it runs as long as the book says, and it must not be found standing in its
 * own way on the technician it is booked with — the same reason `roomAt` takes
 * `exceptSvcId`. Leaving it out drops it from what cannot move and drops any
 * swap that would trade it away, since that swap is this booking. */
function startPlanForNeed(staffId, need, from, exceptSvcId) {
  var at = from;
  // Held out by Mangomint's day — not in yet, or a time block — is measured
  // from when the hold ends (work-hours.js `startAfterHold`). Bypassed at the
  // desk has no end the board can name, and is no answer at all.
  if (isSkipped(staffId)) {
    var desk = !!(state.dayStaff[staffId] || {}).skipped;
    at = workRules.startAfterHold(from, desk ? null : mmOutOf(staffId));
    if (at == null) return null;
  }
  var cur = activeTurn(staffId);
  if (cur) {
    // Nobody idle: a square with no expected end is one nothing can be
    // measured from, and one already over its time clears any minute now.
    var end = turnEndsAt(cur);
    if (end == null) return null;
    if (end > at) at = end;
  }

  var plan = bookingPlanByStaff.value[staffId];
  var kept = blockingBlocksByStaff.value[staffId] || [];
  var swaps = plan ? plan.swaps : [];
  if (exceptSvcId) {
    kept = kept.filter(function (b) { return b.svc.id !== exceptSvcId; });
    swaps = swaps.filter(function (s) { return s.out.svc.id !== exceptSvcId; });
  } else {
    // A walk-in must not run into a time block either (work-hours.js, BLOCK
    // AHEAD); they stand in its way as a booking would, so it can still go in
    // a gap after one. A booking is Mangomint's, made knowing its own blocks.
    kept = kept.concat(mmBreaksFor(staffId));
  }
  var best = bestStart(at, need, kept, swaps);

  /* ...and there has to be enough of their own day left for it. Last, because
   * it is the one thing no later gap can fix: a booking in the way moves the
   * answer down the afternoon, and going home at five moves it off the day
   * altogether. Null rather than a time tomorrow — the whole board is one
   * business date, and "not today" is the answer the desk needs. */
  if (!shiftHasRoom(staffId, need, best.at)) return null;
  return best;
}

/* What putting this walk-in on this technician at this time would cost the
 * book: the bookings of theirs it lands on top of, and who each one would have
 * to go to for the answer above to hold.
 *
 * None of this is new reasoning. `blockingBlocksByStaff` already decided the
 * technician has room by writing those bookings off against colleagues, and
 * that decision is the only reason Next availability names them at all — this reads the
 * plan back out so the desk can see what the card is quietly assuming. Working
 * it out afresh would risk naming a stand-in the decision did not use.
 *
 * Anything the client asked for by name is never in here. A request is not
 * written off, so it stays in the way and the walk-in was measured past it
 * rather than over it — which is also why a technician sitting on a request is
 * not offered as a stand-in for anybody else's booking.
 *
 * Only the bookings the walk-in actually overlaps. The ledger clears a
 * technician's whole day, and a pedicure at ten has nothing to do with the
 * client standing at the desk at five.
 */
function walkInMoves(staffId, serviceId, at) {
  var plan = bookingPlanByStaff.value[staffId];
  if (!plan || (!plan.moves.length && !plan.swaps.length)) return [];
  var from = at == null ? minuteNow.value : at;
  var to = from + walkInFitMs(serviceId);
  var lines = plan.moves
    .filter(function (m) { return m.block.from < to && m.block.to > from; })
    .map(function (m) {
      // When the colleague would start it, which a same-time visit can bring
      // forward of the book's time — the move line says so.
      var begin = m.startsAt == null ? m.block.from : m.startsAt;
      return { appt: m.block.appt, svc: m.block.svc, to: m.to,
               startsAt: begin, endsAt: begin + (m.block.to - m.block.from),
               early: begin < m.block.from, bookedAt: m.block.from };
    });
  /* A swap is only part of the plan when the measurement used it — see
   * `startPlanFor`. When it did, the whole of each swap the walk-in lands on
   * is reported, the booking coming back included, even though that one sits
   * after the walk-in: it is half of the same change to the book. */
  var start = startPlanFor(staffId, serviceId, minuteNow.value);
  if (start && start.swapped) {
    var hit = plan.swaps.filter(function (s) { return s.out.from < to && s.out.to > from; });
    lines = lines.concat(swapMoves(staffId, hit));
  }
  return lines.sort(function (x, y) { return x.startsAt - y.startsAt; });
}

/* The handover, in the order the book has it.
 *
 * One move reads as a sentence and several read as a list, because several is
 * a job: each line is a booking somebody has to go and change, and a paragraph
 * of them hides how many that is.
 *
 * The last line is the half worth reading. The board does not own the book, so
 * nothing here moves anything — taking the card is agreeing to go and do this
 * afterwards, and a plan nobody carries out is two clients in one chair. The
 * clash sheet says the same thing in the same words for the same reason.
 */
function walkInMovesNote(name, moves) {
  if (!moves.length) return '';
  var who = name || 'That technician';
  // A line coming back in a swap is somebody else's booking, and says whose.
  function what(m) {
    return formatClock(m.startsAt) + ' ' + m.svc.name +
      (m.appt.clientName ? ' (' + m.appt.clientName + ')' : '') +
      (m.from ? ', ' + m.from.name + '\'s' : '');
  }
  function warn(m) {
    return noteWarningFor(m.appt.clientId, m.to.id)
      ? ' ⚠ (their notes mention ' + m.to.name + ')' : '';
  }
  var tail = '\nNothing here moves ' + (moves.length === 1 ? 'it' : 'them') +
    ' — that is a change to make in the booking system.';
  if (moves.length === 1) {
    return 'Taking this means moving work off ' + who + ': the ' + what(moves[0]) +
      ' would go to ' + moves[0].to.name + '.' + warn(moves[0]) + tail;
  }
  var swap = moves.some(function (m) { return m.from; });
  return 'Taking this means ' + (swap
      ? 'swapping bookings so ' + who + ' is free:\n'
      : 'moving ' + moves.length + ' bookings off ' + who + ':\n') +
    moves.map(function (m) {
      return '  • ' + what(m) + ' → ' + m.to.name + warn(m);
    }).join('\n') + tail;
}

/* ---------- a walk-in that would run into a booking ----------
 *
 * Next availability asks one question — who takes this client now — and used to
 * answer it out of the rotation alone. The rotation knows nothing about the
 * book, so the tech whose turn it is could be the one with a client due in
 * twenty minutes, and the desk found out when that client was standing there.
 *
 * Now the walk-in is measured: how long the turn type usually takes says when
 * it would finish, and anything of theirs starting before then is in the way.
 * A booking the client ASKED for them is a promise, so it is not offered to be
 * broken at all — that technician cannot be chosen. Anything else is a warning
 * with the way out attached: whoever else could take the appointment instead.
 *
 * A turn type with no usual length cannot be checked. Nothing says how long it
 * would run, so nothing says what it would run into, and the board keeps
 * quiet rather than guessing. Set it under Settings → Services.
 */

// When a walk-in of this type put up now would be finished, or null if the
// type has no usual length.
function walkInEndsAt(serviceId, startedAt) {
  var svc = serviceById.value[serviceId];
  var mins = svc && svc.defaultMinutes;
  return mins ? (startedAt || Date.now()) + mins * 60000 : null;
}

/* ---------- a walk-in that would run past closing ----------
 *
 * The same measurement as the clash above, against the end of the day instead
 * of against a booking. Next availability knew who was free and what the work takes,
 * and would happily start a ninety-minute set at twenty to close — the desk
 * found out when the technician did.
 *
 * Two answers, because a few minutes over and an hour over are not the same
 * problem. Inside the grace it is a QUESTION, marked on the card so the desk
 * can ask whoever it lands on whether they mind staying; past the grace it is
 * not offered at all. Neither one is a rule about a person — the empty-square
 * sheet still starts anything for anybody, which is where a technician who has
 * said yes gets the work put up.
 *
 * Null when there is nothing to measure: no hours set, closed today, or a turn
 * type with no usual length. The board keeps quiet rather than guessing, the
 * same as the clash check.
 *
 * `minuteNow` rather than the wall clock by default, so a card marks itself as
 * the afternoon runs out instead of at the next click.
 */
function closingOverrun(serviceId, startAt) {
  var h = todayHours.value;
  if (!h) return null;
  var endsAt = walkInEndsAt(serviceId, startAt || minuteNow.value);
  if (endsAt == null || endsAt <= h.closeAt) return null;
  var over = endsAt - h.closeAt;
  return {
    endsAt: endsAt, closeAt: h.closeAt, close: h.close,
    minutes: Math.round(over / 60000),
    grace: CLOSING_GRACE_MIN,
    blocked: over > CLOSING_GRACE_MIN * 60000
  };
}

// The sentence under a marked card, and the one the warning sheet repeats.
function closingNote(serviceId, late) {
  var svc = serviceById.value[serviceId];
  return (svc ? svc.name : 'That service') + ' would finish about ' +
    formatClock(late.endsAt) + ', ' + formatMins(late.endsAt - late.closeAt) +
    ' past the ' + formatClock(late.closeAt) + ' close. ' +
    (late.blocked
      ? 'That is more than ' + late.grace + ' minutes over, so Next availability does ' +
        'not offer it. Start it from the empty square if a technician is ' +
        'staying for it.'
      : 'Check the technician is happy to stay late before putting it up.');
}

/* ---------- when the floor is expected to be clear ----------
 *
 * One time, in the top bar, for the last two hours of the day: when the last
 * thing the board knows about is expected to finish.
 *
 * It is the question the desk starts asking somewhere around five — whether
 * anybody can be let go early, whether the person on the phone can be squeezed
 * in, whether tonight is a late one. Until now it was answered by reading every
 * countdown on the board and every line left in the book and taking the largest,
 * which is arithmetic a person should not be doing at the end of a shift.
 *
 * TWO HOURS and not all day, because it is a closing-time question. Earlier it
 * is noise — a number that moves every time a walk-in goes up, sitting in a bar
 * beside three counts that are facts. It appears when it starts being worth
 * something and it goes when the floor is clear.
 *
 * WHAT COUNTS is everything still to happen today:
 *
 *   running squares   their expected end, the same `turnEndsAt` the countdowns
 *                     are drawn from. One already past its time counts as
 *                     finishing now rather than never, which is what the rest of
 *                     the board does with an overrun.
 *   the book          services still to start, with nobody seated for them yet.
 *                     Leaving these out would have the bar reading 5:45 at five
 *                     o'clock with a six-thirty booking sitting in the pane —
 *                     the one reading that would send somebody home early.
 *
 * WHAT CANNOT COUNT is a square with no expected end: a walk-in of a type with
 * no usual length says nothing about when it finishes. It is not silently
 * dropped, because a time that quietly excludes two of the five things running
 * is worse than no time at all. It comes back as `unknown` and the bar says so.
 *
 * Null when there is nothing to say: no hours set, closed today, more than two
 * hours to go, or a floor with nothing left on it.
 */
var LAST_FINISH_WINDOW_MIN = 120;

var lastFinish = computed(function () {
  var h = todayHours.value;
  if (!h) return null;
  var t = minuteNow.value;              // ticks, so it appears on the hour itself
  if (t < h.closeAt - LAST_FINISH_WINDOW_MIN * 60000) return null;

  var at = null, who = '', fromBook = false, unknown = 0;
  function take(when, name, book) {
    if (when == null || when <= at) return;
    at = when; who = name || ''; fromBook = !!book;
  }

  var byStaff = staffById.value;
  state.turns.forEach(function (turn) {
    if (turn.endedAt != null) return;
    var ends = turnEndsAt(turn);
    if (ends == null) { unknown++; return; }
    var s = byStaff[turn.staffId];
    // Past its time is "any minute now", not a finish in the past that would
    // drag the whole answer backwards.
    take(Math.max(ends, t), s ? s.name : '', false);
  });

  /* The book, on the same terms the rest of the console reads it on: only with
   * a plugin on, and only when the book actually belongs to the day the board
   * is showing. A book from yesterday against tonight's closing time would put
   * a finishing time on the bar that nobody in the building can act on. */
  if (scheduling.enabled.value && appts.bookDate === state.businessDate) {
    var seated = turnByApptService.value;
    appts.rows.forEach(function (a) {
      if (a.cancelled) return;
      a.services.forEach(function (svc) {
        if (seated[svc.id]) return;                 // it is a square, counted above
        if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
        if (!svc.durationMinutes) { unknown++; return; }
        var ends = svc.startsAt + svc.durationMinutes * 60000;
        if (ends <= t) return;                      // its window has been and gone
        take(ends, (svc.techs && svc.techs[0]) || '', true);
      });
    });
  }

  if (at == null) return null;
  return {
    at: at,
    clock: formatClock(at),
    who: who,
    fromBook: fromBook,
    unknown: unknown,
    closeAt: h.closeAt,
    // Past the doors shutting, and by how long. The bar marks it: "we are here
    // until 7:40" is the whole reason somebody looks at this number.
    over: at > h.closeAt ? at - h.closeAt : 0
  };
});

// The sentence behind it. Built here rather than in the bar because it is three
// facts about the board and the bar's job is to have room for it.
function lastFinishNote(f) {
  if (!f) return '';
  var lead = f.fromBook
    ? 'The last thing in the book today' + (f.who ? ' (' + f.who + ')' : '') +
      ' is due to finish about ' + f.clock + '.'
    : 'The last square still running' + (f.who ? ' (' + f.who + ')' : '') +
      ' is expected to finish about ' + f.clock + '.';
  var late = f.over
    ? ' That is ' + formatMins(f.over) + ' past the ' + formatClock(f.closeAt) +
      ' close.'
    : '';
  /* The honest half. A number with two squares missing from it is a number
   * somebody will plan the evening around, so what is missing is said in the
   * same breath rather than left for them to notice. */
  var gap = f.unknown
    ? ' ' + f.unknown + (f.unknown === 1
        ? ' service has no expected length, so it is not in this.'
        : ' services have no expected length, so they are not in this.')
    : '';
  return lead + late + gap;
}

/* ---------- one technician's own hours ----------
 *
 * The closing check above is about the salon. This is the same measurement
 * against one person's day, and it exists because the two are not the same
 * fact: the doors shut at seven and Maria goes at five, and until now the
 * board only knew the first of those. The desk knew the second, which is why
 * it was the desk that had to remember it every time a card came up.
 *
 * The arithmetic is in js/rules/shift.js — the two defaults, whether a finish
 * counts as early at all, and the two questions asked of it. What is here is
 * the reactive half: today's hours, this technician's stored times, and the
 * one cached answer the board and the rotation both read.
 *
 * Three things read it, and each asks a different question:
 *
 *   `earliestStartFor`   would this service be over before they go? This is
 *                        the half four / five o'clock case, per service: a
 *                        thirty-minute one still fits and a ninety does not.
 *   `buildQueue`         are they still in the rotation at all?
 *   `boardColumns`       is there anything left they could be handed? Once
 *                        there is not, the rest of their column is blocked
 *                        off — see `spentStaff`.
 *
 * Nothing here is written to the day. The blocked squares are drawn, not
 * recorded: they are a reading of the clock against a time somebody typed, and
 * a red square costs a turn's credit, which somebody who has gone home has not
 * earned and cannot spend. Change the out time and the column redraws itself.
 */

/* How far past closing a shift runs when nobody has said otherwise.
 *
 * Fixed rather than a setting for the same reason the opening lead and the
 * closing grace are: it is about how a working day ends rather than about this
 * salon. The number matters less than the sign — the default has to land PAST
 * the close, so that a salon which fills its hours in and never touches this
 * screen gets exactly the board it had before.
 */
var SHIFT_OUT_GRACE_MIN = 15;

// The earliest anybody was marked in today: when the sheet itself went up.
// Read only by the default start time, which uses it to tell the crew who were
// on the sheet from the beginning from somebody who walked in at one.
var sheetStartedAt = computed(function () {
  var first = null;
  Object.keys(state.dayStaff).forEach(function (id) {
    var d = state.dayStaff[id];
    if (!d || d.presentSince == null) return;
    if (first == null || d.presentSince < first) first = d.presentSince;
  });
  return first;
});

/* One technician's day, defaults filled in. Null for somebody with no row on
 * today at all — never been marked in, so there is no day to have hours in.
 *
 * `custom` is the difference between "this is what the board assumes" and
 * "somebody typed this", which is the whole of what the menu has to show: the
 * fields read the same either way, and only one of them is a decision.
 */
function shiftOf(staffId) {
  var d = state.dayStaff[staffId];
  if (!d) return null;
  var h = todayHours.value;
  // Typed at the desk, or Mangomint's — see `effectiveHours`. Either end that
  // neither has said falls back to what the board assumes.
  var eff = workRules.effectiveHours({
    startAt: d.shiftStartAt, endAt: d.shiftEndAt, typedAt: workHoursOf(d).typedAt
  }, mmDayOf(staffId));
  var startAt = eff.startAt != null ? eff.startAt
    : shiftRules.defaultShiftStart(d.presentSince, h, sheetStartedAt.value);
  var endAt = eff.endAt != null ? eff.endAt
    : shiftRules.defaultShiftEnd(h, SHIFT_OUT_GRACE_MIN * 60000);
  return {
    startAt: startAt,
    endAt: endAt,
    startClock: clockValue(startAt),
    endClock: clockValue(endAt),
    // A Mangomint time counts as one somebody typed (Kevin, 2026-10-22): it is
    // a decision about the day, and its out time sends them home.
    customStart: eff.startFrom != null,
    customEnd: eff.endFrom != null,
    startFrom: eff.startFrom,
    endFrom: eff.endFrom,
    closeAt: h ? h.closeAt : null,
    // The only field anything downstream reads: the finish, when it is early
    // enough to be a constraint, and null when it is not.
    early: shiftRules.earlyEndAt(endAt, h ? h.closeAt : null)
  };
}

/* When this technician will be in and ready, for the conflicts list: their In
 * time when somebody gave one (work-hours.js `readyFrom`). Somebody Mangomint
 * has working whom the board has not put on yet — everybody, before the day
 * opens — counts from their shift's start. Null when there is nothing to wait
 * for. */
function readyFromFor(staffId) {
  var d = state.dayStaff[staffId];
  if (!d) {
    var mm = mmDayOf(staffId);
    return mm ? mm.startAt : null;
  }
  var sh = shiftOf(staffId);
  if (!sh || !sh.customStart) return null;
  return workRules.readyFrom(sh.startAt, workHoursOf(d).released);
}

/* Who Mangomint has working today and the board has not put on yet: before the
 * day opens, the whole crew. Somebody the desk took off has a day row, and
 * stays off. The conflicts list counts these as coming in at their start
 * rather than as not in today. */
var scheduledOffBoard = computed(function () {
  return rosterStaff.value.filter(function (s) {
    return !state.dayStaff[s.id] && mmDayOf(s.id);
  });
});

function shiftCutoff(staffId) {
  var sh = shiftOf(staffId);
  return sh ? sh.early : null;
}

/* The shortest thing this technician could still be handed, in milliseconds.
 *
 * The shortest and not the average: the question it answers is whether there
 * is room for ANY more work, so it has to be the smallest piece of work there
 * is. A service with no usual length is held for the same half hour
 * `walkInFitMs` holds it for — the two are the same assumption about the same
 * unknown, and they must not disagree about whether something fits.
 *
 * Null when they perform nothing measurable, which is also null when they
 * perform nothing at all.
 */
function shortestServiceMs(staffId) {
  var s = staffOf(staffId);
  if (!s) return null;
  var best = null;
  s.serviceIds.forEach(function (id) {
    var svc = serviceById.value[id];
    if (!svc || svc.active === false) return;
    var m = svc.defaultMinutes || ASSUMED_WALKIN_MIN;
    if (best == null || m < best) best = m;
  });
  return best == null ? null : best * 60000;
}

// Whether work of this length started then would be over before they go.
// True for everybody whose day does not end early, which is everybody until
// somebody types an out time.
function shiftHasRoom(staffId, needMs, at) {
  return shiftRules.fitsBeforeEnd(shiftCutoff(staffId), at, needMs);
}

/* Who has run out of day, as a map of staff id -> true.
 *
 * Cached through a key rather than rebuilt, and that is the whole reason it is
 * two computeds instead of one. `boardColumns` is the map the grid is drawn
 * from and the file is careful about what invalidates it; a computed reading
 * the minute clock hands back a new object every minute, which would throw the
 * board away sixty times an hour to say the same thing. The key is a string,
 * so a minute where nothing changed compares equal and the map below — and the
 * board above it — stay exactly where they were.
 */
var spentKey = computed(function () {
  var out = [];
  presentStaff.value.forEach(function (s) {
    if (shiftRules.shiftSpent(shiftCutoff(s.id), minuteNow.value, shortestServiceMs(s.id))) {
      out.push(s.id);
    }
  });
  return out.join(',');
});

var spentStaff = computed(function () {
  var out = {};
  spentKey.value.split(',').forEach(function (id) { if (id) out[id] = true; });
  return out;
});

function isShiftSpent(staffId) { return !!spentStaff.value[staffId]; }

/* ---------- marking somebody done when their day actually ends ----------
 *
 * The out time stopped the board OFFERING them work; nothing marked them gone.
 * So the last half hour of a technician's day read as a technician standing
 * there with a blocked-off column, and the Done badge only appeared if somebody
 * at the desk remembered to right-click a name at exactly the wrong moment —
 * which is to say, while the floor was busiest.
 *
 * Now the board does it: their out time passes, their last square ends, and
 * they go out of the rotation as *Done for today*, the same state the menu sets
 * by hand. See `doneForDayDue` in js/rules/shift.js for the two conditions and
 * for why only a typed out time counts.
 *
 * WHAT IT WILL NOT DO is argue. Somebody already bypassed keeps whatever reason
 * the desk gave them, and — the case that matters — a technician the desk puts
 * BACK in the rotation stays back. That is the whole of `doneSweptAt`: the out
 * time already acted on, per technician. Without it, "Back in the rotation"
 * would be undone by the next tick of the clock and the button would look
 * broken.
 *
 * Keyed by the time rather than by a flag, so it expires by itself in the one
 * case it should: move the out time to six and it is a different time, not the
 * one already acted on, and six o'clock gets its own answer.
 *
 * On the day-staff row — `doneSweptAt` — rather than in this station's memory,
 * and the argument for keeping it here was wrong. It went: the effect is
 * written to the server, so a second station finds them already marked done and
 * stays quiet. True, and it misses the case the guard exists for. When the desk
 * puts somebody BACK in the rotation they are present, not busy and not
 * skipped, which is every condition `doneForDayDue` asks for — so the sweep on
 * any OTHER open station fires again and pushes its result, and the decision
 * made at the desk is undone for everybody by a screen nobody is standing at.
 */
function sweepDoneForDay() {
  // Nothing before the board has its data: `shiftOf` off a half-loaded day
  // would be reading defaults for people whose stored times have not arrived.
  if (ui.loading) return;
  // A shut book has already stopped everything and asked the day's questions.
  if (dayClosed.value) return;
  var t = minuteNow.value;
  var went = [];
  presentStaff.value.forEach(function (s) {
    var sh = shiftOf(s.id);
    if (!sh) return;
    var d = state.dayStaff[s.id];
    if (d && d.doneSweptAt === sh.endAt) return;    // already acted on this one
    // Not before the last service the book has on them, however late it runs.
    var home = Object.assign({}, sh, { endAt: workRules.homeAt(sh.endAt, bookedEndsFor(s.id)) });
    if (!shiftRules.doneForDayDue(home, t, {
      present: !!(d && d.present), busy: isBusy(s.id), skipped: isSkipped(s.id)
    })) return;
    // Written before the bypass, so the row that records the decision and the
    // row that records acting on it are one write — `setSkipped` pushes the
    // whole day-staff row, this included.
    if (d) d.doneSweptAt = sh.endAt;
    setSkipped(s.id, 'done', true);
    went.push({ name: s.name, endAt: sh.endAt });
  });
  if (went.length) toast(doneForDayNote(went), 'info');
}

/* One sentence for however many people just went.
 *
 * One, because `toast` has a single slot: a salon where the whole floor is out
 * at five would announce three technicians into the same corner of the screen
 * and the desk would read the last of them. A shared finishing time is said
 * once — which is the common case, since a floor that goes at five goes at five
 * — and a mixed sweep drops the time rather than listing three of them.
 *
 * It says WHY, not just what. An automatic change to the board is only
 * acceptable if the desk can tell what moved and what moved it; the way back is
 * one right-click on the name, so the sentence spends its room on the fact.
 */
function doneForDayNote(went) {
  var names = went.map(function (w) { return w.name; });
  var label = names.length > 2
    ? names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
    : names.join(' and ');
  var sameTime = went.every(function (w) { return w.endAt === went[0].endAt; });
  return label + (names.length > 1 ? ' are' : ' is') + ' done for today' +
    (sameTime ? ' — their ' + formatClock(went[0].endAt) + ' finish has passed'
              : ' — their finish times have passed') +
    ' and ' + (names.length > 1 ? 'their last services are' : 'their last service is') +
    ' over.';
}

/* Setting the times. `patch` is `{ in: 'HH:MM' }`, `{ out: 'HH:MM' }` or both,
 * the same shape `setDayHours` takes, because it is the same two fields being
 * edited one at a time by a pair of `<input type="time">`.
 *
 * An empty field is not a time of null, it is "go back to what the board
 * assumes" — which is exactly what a stored null already means, so clearing a
 * field and never having touched it come out in the same place.
 *
 * A finish at or before the start is the small hours of tomorrow, the same
 * reading `todayHours` takes of a closing time before the opening one. A salon
 * that shuts at 1 AM has technicians who finish at 1 AM.
 */
function setShiftTimes(staffId, patch) {
  var s = staffOf(staffId);
  var d = state.dayStaff[staffId];
  if (!s || !d) {
    toast('Mark them in today first.', 'warn');
    return false;
  }
  var cur = shiftOf(staffId);
  var startAt = 'in' in patch
    ? clockToMs(state.businessDate, cleanClock(patch.in))
    : d.shiftStartAt;
  var endAt = 'out' in patch
    ? clockToMs(state.businessDate, cleanClock(patch.out))
    : d.shiftEndAt;
  // Measured against whichever start is in force, stored or assumed: a finish
  // typed as 1 AM is the small hours whether or not the start was typed too.
  var from = startAt == null ? cur.startAt : startAt;
  if (endAt != null && from != null && endAt <= from) endAt += 86400000;

  d.shiftStartAt = startAt;
  d.shiftEndAt = endAt;
  // When, so a later change in Mangomint can take over again (work-hours.js).
  d.workHours = Object.assign(workHoursOf(d), { typedAt: Date.now() });
  pushDayStaff(staffId);

  // Read back rather than reasoned about: the sentence has to describe the day
  // the board is now working from, defaults and overnight roll included.
  var after = shiftOf(staffId);
  if (!after.customStart && !after.customEnd) {
    toast(s.name + ' is back on the usual hours for today.', 'ok');
  } else if (after.early) {
    toast(
      s.name + ' is on until ' + formatClock(after.early) +
      ' — nothing that would still be running then is offered to them.',
      'info'
    );
  } else {
    toast(s.name + ': ' + formatClock(after.startAt) + ' to ' +
      formatClock(after.endAt) + '.', 'ok');
  }
  return true;
}

function clearShiftTimes(staffId) {
  return setShiftTimes(staffId, { in: '', out: '' });
}

/* ---------- acting on Mangomint's day ---------- */

async function loadWorkHours() {
  var p = scheduling.provider.value;
  if (!scheduling.enabled.value || !sync.auth.user || !scheduling.can('workHours') ||
      !p || !p.fetchWorkHours) {
    workHours.byExternal = {};
    workHours.loadedFor = state.businessDate;
    return;
  }
  var forDate = state.businessDate;
  try {
    var byExternal = await p.fetchWorkHours(forDate);
    if (forDate !== state.businessDate) return;
    workHours.byExternal = byExternal || {};
  } catch (e) { /* offline, or part 17 not applied — keep what we have */ }
  // Asked, whatever the answer: the morning set-up waits for this, and a read
  // that failed must not hold the day up — it opens off the book as it did.
  if (forDate === state.businessDate) workHours.loadedFor = forDate;
}

// The time Mangomint had somebody out, for the idle total: before their start,
// and each break the desk did not bring them back from.
function mmOutSpansFor(staffId) {
  var mm = mmDayOf(staffId);
  if (!mm) return [];
  var released = workHoursOf(state.dayStaff[staffId]).released;
  var out = [];
  var sh = shiftOf(staffId);
  if (sh && sh.startAt != null && released.indexOf('in:' + sh.startAt) === -1) {
    out.push({ from: 0, to: sh.startAt });
  }
  mm.breaks.forEach(function (b) {
    if (released.indexOf(b.id) === -1) out.push({ from: b.from, to: b.to });
  });
  return out;
}

/* Everybody else on the board, as `floorTurnsAt` reads them: when they were
 * in, what they had already been charged, their squares, and when they were
 * out — bypassed at the desk, or held out by Mangomint. */
function floorMembersExcept(staffId) {
  return rotationStaff.value.filter(function (s) { return s.id !== staffId; }).map(function (s) {
    var d = state.dayStaff[s.id];
    var arrived = d.presentSince;
    var mm = mmDayOf(s.id);
    var sh = mm ? shiftOf(s.id) : null;
    var released = workHoursOf(d).released;
    if (sh && sh.startAt != null && sh.startAt > arrived &&
        released.indexOf('in:' + sh.startAt) === -1) arrived = sh.startAt;
    var outs = skipSpansFor(s.id).map(function (x) { return { from: x.from, to: x.to }; });
    if (mm) {
      mm.breaks.forEach(function (b) {
        if (released.indexOf(b.id) === -1) outs.push({ from: b.from, to: b.to });
      });
    }
    return {
      name: s.name,
      arrivedAt: arrived,
      missedTurns: d.missedTurns || 0,
      turns: turnsFor(s.id).map(function (t) {
        return { turnWeight: t.turnWeight, startedAt: t.startedAt, endedAt: t.endedAt };
      }),
      skips: skipsFor(s.id),
      outSpans: outs
    };
  });
}

/* "Back in the rotation" while Mangomint has them held out. Remembered on the
 * day, so the next minute — or another station — does not put them straight
 * back out. Somebody brought in before their start is here now: their not-in
 * count is what the floor has finished by now, and they arrived now. */
function releaseMmHold(staffId, d) {
  var o = mmOutOf(staffId);
  if (!o) return;
  var w = workHoursOf(d);
  if (w.released.indexOf(o.key) === -1) w.released = w.released.concat([o.key]);
  if (o.reason === 'notin') {
    var now = Date.now();
    var detail = workRules.floorTurnsDetail(floorMembersExcept(staffId), now, now);
    d.missedTurns = detail.count;
    d.presentSince = now;
    w.missedAt = o.until;
    w.missedWhy = missedRecord(now, detail, 'desk');
  }
  d.workHours = w;
}

/* The minute's work on Mangomint's day, in two halves.
 *
 * Somebody Mangomint has working who is not on the day at all — a shift added
 * after opening — goes on the board. Somebody the desk took off has a row, and
 * stays off.
 *
 * Somebody whose start has come, or moved, has their not-in count worked out
 * from the floor's turns as of that start (Kevin, 2026-10-22). It replaces
 * whatever they were marked in with rather than adding to it, so being on the
 * board since opening costs nothing extra.
 *
 * It moves nothing else (Kevin, 2026-10-25). After the day starts the desk's
 * column order is authoritative, and the arrival times follow it: the start no
 * longer resets their arrival to the start time, and no longer sends somebody
 * with missed turns to the last column.
 */
function sweepWorkHours() {
  if (ui.loading || dayClosed.value || !dayOpened.value) return;
  var days = mmDayByStaff.value;
  if (!Object.keys(days).length) return;
  rosterStaff.value.forEach(function (s) {
    if (!days[s.id] || state.dayStaff[s.id]) return;
    setPresent(s.id, true, true);
  });
  var t = Date.now();
  var came = [];
  presentStaff.value.forEach(function (s) {
    if (!days[s.id]) return;
    var d = state.dayStaff[s.id];
    var w = workHoursOf(d);
    var sh = shiftOf(s.id);
    if (!sh || !workRules.missedDue(sh.startAt, w.missedAt, t)) return;
    var detail = workRules.floorTurnsDetail(floorMembersExcept(s.id), sh.startAt, t);
    var missed = detail.count;
    // The count and its record only: their arrival time and their column are
    // the desk's from here on (work-hours.js `startArrived`).
    var next = workRules.startArrived(w, sh.startAt, missed,
      missedRecord(sh.startAt, detail, 'start'));
    d.missedTurns = next.missedTurns;
    d.workHours = next.workHours;
    pushDayStaff(s.id);
    if (missed > 0) came.push(s.name + ' (turn ' + (missed + 1) + ')');
  });
  if (came.length) {
    toast(came.join(', ') + (came.length > 1 ? ' are' : ' is') +
      ' in on Mangomint’s hours — the turns before they started count as missed.', 'info');
  }
}

// The first of that technician's bookings a walk-in would run into, with
// everything the warning needs to explain itself. Null when there is none —
// which includes every case the board cannot measure.
function walkInCollision(staffId, serviceId, startedAt) {
  if (!scheduling.enabled.value) return null;
  var from = startedAt || Date.now();
  var to = walkInEndsAt(serviceId, from);
  if (to == null) return null;
  // Sorted by start, so the first one that overlaps is the earliest. Anything
  // already over before the walk-in even starts is not in the way.
  var blocks = pinnedBlocksByStaff.value[staffId] || [];
  var hit = null;
  for (var i = 0; i < blocks.length; i++) {
    if (blocks[i].from >= to) break;
    if (blocks[i].to > from) { hit = blocks[i]; break; }
  }
  if (!hit) return null;
  var svc = serviceById.value[serviceId];
  return {
    staffId: staffId, serviceId: serviceId,
    serviceName: svc ? svc.name : 'That service',
    minutes: svc ? svc.defaultMinutes : null,
    endsAt: to,                       // when the walk-in would finish
    appt: hit.appt, svc: hit.svc,
    mapped: trackerServiceForBooked(hit.svc),
    startsAt: hit.from,
    // A promise to a person. Not something to offer to break.
    requested: isApptRequested(hit.svc, staffId),
    // Who else could take the appointment that is in the way — the answer when
    // the walk-in is the thing that has to happen.
    cover: coverForBooking(hit.appt, hit.svc, staffId)
  };
}

// ...and the other answer: who else could take the WALK-IN, which is what the
// desk needs when this technician is the one who cannot be moved. Rotation
// order, so the top of the list is who it would go to anyway, and each one
// carries its own collision so a swap out of one clash into another is
// visible rather than discovered a second time.
function walkInAlternatives(staffId, serviceId) {
  return availableFor(serviceId)
    .filter(function (s) { return s.id !== staffId; })
    .map(function (s) {
      return { staff: s, clash: walkInCollision(s.id, serviceId, null) };
    });
}

function closeBookClash() { ui.bookClash = null; }

/* The earlier line of the same visit, booked with this technician and not on
 * the board yet, that should go up before the one being seated — or null. */
function earlierUnseatedFor(staffId, link) {
  var a = apptById.value[link.appointmentId];
  var staff = staffOf(staffId);
  if (!a || a.cancelled || !staff) return null;
  var target = a.services.find(function (s) { return s.id === link.appointmentServiceId; });
  if (!target) return null;
  var lines = a.services.map(function (s) {
    return {
      id: s.id, startsAt: s.startsAt,
      mine: (s.techs || []).some(function (x) { return matchesStaff(staff, x); }),
      seated: !!turnByApptService.value[s.id],
      over: bookedLineOver(a, s) || !!addOnHostTurn(a, s)
    };
  });
  var id = earlierFirst(target.id, lines);
  if (id == null) return null;
  var svc = a.services.find(function (s) { return s.id === id; });
  return svc ? { appt: a, svc: svc, later: target } : null;
}

function closeOrderAsk() { ui.orderAsk = null; }

/* The desk's answer. 'earlier' puts up the visit's earlier service on the same
 * technician instead, with the book's times; 'later' puts up what was asked
 * for, as it was asked. Either way the sheet behind (assign, empty square) has
 * nothing left to do once a square is up. */
function answerOrderAsk(which) {
  var q = ui.orderAsk;
  if (!q) return false;
  ui.orderAsk = null;
  var ok;
  // Asked over the assign sheet, this is still the desk's tap on that sheet. Asked
  // over anything else, it is not.
  var fromSheet = !!ui.assign;
  var bookMove = null;
  if (which === 'earlier') {
    var mapped = trackerServiceForBooked(q.earlier);
    if (!mapped) { ui.assign = null; openAssign(q.appt, q.earlier); return false; }
    var earlierAt = assignOutcome(q.appt.id, q.earlier.id);
    if (fromSheet) bookMove = prepareBookMove(q.staffId, q.appt, q.earlier, earlierAt);
    ok = bookTurn(q.staffId, {
      serviceId: mapped.id, orderOk: true,
      at: earlierAt,
      link: {
        appointmentId: q.appt.id, appointmentServiceId: q.earlier.id,
        sourceServiceId: q.earlier.serviceId, clientId: q.appt.clientId,
        clientName: q.appt.clientName, sourceServiceName: q.earlier.name
      }
    });
  } else {
    var asked = fromSheet && q.opts.link ? bookedServiceById(q.opts.link.appointmentServiceId) : null;
    if (asked) bookMove = prepareBookMove(q.staffId, asked.appt, asked.svc, q.opts.at);
    ok = bookTurn(q.staffId, Object.assign({}, q.opts, { orderOk: true }));
  }
  if (ok) {
    ui.assign = null;
    ui.emptyPick = null;
    bringBookInLine(bookMove);
  }
  return ok;
}

// Book it anyway. Only reachable from the warning, and never from the block:
// the sheet does not draw the button when the booking was a request.
function confirmBookClash() {
  var c = ui.bookClash;
  if (!c || c.requested) return false;
  ui.bookClash = null;
  var ok = bookTurn(c.staffId, {
    serviceId: c.serviceId, requested: !!c.starWanted, force: true
  });
  // The empty-square sheet is one of the ways in here, and it stays open
  // behind the warning. Nothing left to pick once the square is up.
  if (ok) ui.emptyPick = null;
  return ok;
}

// Everything the header's next-booking popup shows, worked out in one place:
// what is coming, whether it is locked to them, and who else could take it if
// it is not. Null when nothing is coming, which is when the popup stays shut.
function nextBookingDetail(staffId) {
  var b = nextBookingFor(staffId);
  if (!b) return null;
  var mapped = b.mapped || null;
  var requested = b.why === 'requested';
  return {
    appt: b.appt, svc: b.svc, why: b.why, mapped: mapped, requested: requested,
    startsAt: b.svc.startsAt,
    // What the board would put on the countdown, so the popup and the square
    // it turns into never disagree about how long it is.
    minutes: b.svc.durationMinutes || (mapped && mapped.defaultMinutes) || null,
    endsAt: b.svc.startsAt + bookedHoldMs(b.svc, mapped),
    options: activeOptionsFor(b.svc),
    tech: (b.svc.techs || [])[0] || '',
    // A request is not up for grabs, so there is nobody else to list.
    cover: requested ? [] : coverForBooking(b.appt, b.svc, staffId)
  };
}

/* ---------- potential conflicts ----------
 *
 * Three different problems on one list above the book, because they share the
 * only property that matters at the desk: each is something that has to be
 * acted on before it happens, and none of them is visible from the grid alone
 * until it is too late.
 *
 *   mismatch   the board says one technician did the service and the book says
 *              another. Already flagged on the square; it is repeated here
 *              because the fix is in the booking system, not on the board —
 *              that is where the ticket and the commission are going.
 *   at risk    a booking whose technician will not be free when it starts, is
 *              booked twice over, is out of the rotation, is not in yet, is
 *              not on the board at all — or that nobody free can take. Everybody
 *              is taken to be in and ready at their In time.
 *   unlinked   a square with no booking behind it: a walk-in, or a booking
 *              nobody has attached.
 *
 * Whatever was written on the appointment rides along with every item that has
 * one behind it. "No acrylics, allergic" is exactly what the desk needs before
 * it moves that client to somebody else, and it is the one thing this list
 * cannot work out for itself.
 *
 * Empty whenever the scheduling plugin is off. Two of the three are questions
 * about the book, and the third — a turn with no appointment — is not a
 * question at all when there is no book for it to be missing from.
 */

// How far ahead the softer reasons are worth reporting. A square running now
// says something about the client due in twenty minutes and nothing at all
// about the four o'clock, and somebody on a break will very likely be back.
// A double booking carries no horizon: it is wrong whenever it is spotted.
var RISK_SOON_MIN = 60;

/* Mismatches with their opposite numbers beside them, so the two halves of a
 * swap read as one thing rather than as two items that happen to be about each
 * other. Everything unpaired keeps the order it came off the board in; a pair
 * goes in where its earlier half was, and the halves are ordered by the book's
 * clock, because the book's times are the record of who was due first. */
function orderMismatchPairs(list) {
  var byTurn = {};
  list.forEach(function (x) { byTurn[x.turnId] = x; });
  var out = [], placed = {};
  list.forEach(function (x) {
    if (placed[x.turnId]) return;
    placed[x.turnId] = true;
    var mate = x.swapWith ? byTurn[x.swapWith] : null;
    if (!mate) { out.push(x); return; }
    placed[mate.turnId] = true;
    var pair = x.bookedAt <= mate.bookedAt ? [x, mate] : [mate, x];
    pair[0].pairSide = 'a';
    pair[1].pairSide = 'b';
    pair[0].pairKey = pair[1].pairKey = 'pair-' + pair[0].turnId;
    out.push(pair[0], pair[1]);
  });
  return out;
}

var bookConflicts = computed(function () {
  var out = { mismatch: [], risk: [], unlinked: [], count: 0 };
  if (!scheduling.enabled.value) return out;

  var t = minuteNow.value;              // ticks, so bookings leave as they pass
  var soon = t + RISK_SOON_MIN * 60000;
  // A technician this far behind or less is not a conflict — see
  // js/rules/conflict.js.
  var buffer = conflictBufferMinutes.value * 60000;
  var index = bookedServiceIndex.value;
  var seated = turnByApptService.value;

  state.turns.forEach(function (turn) {
    var m = mismatchByTurn.value[turn.id];
    if (m) {
      var link = index[turn.appointmentServiceId];
      // The other half of a crossed pair, when there is exactly one. Its side
      // of the item is read off the book too — the whole offer is a statement
      // about what Mangomint says, so nothing in it comes off the board.
      var mateId = swapPartnerByTurn.value[turn.id] || null;
      var mate = mateId ? turnOf(mateId) : null;
      var mateM = mateId ? mismatchByTurn.value[mateId] : null;
      out.mismatch.push({
        key: 'mismatch-' + turn.id,
        turnId: turn.id,
        onBoard: m.onBoard,
        inBook: m.inBook,
        onSale: !!m.onSale,
        clientName: m.clientName,
        clientId: link ? link.appt.clientId : (turn.clientId == null ? null : turn.clientId),
        staffId: turn.staffId,
        serviceName: m.serviceName,
        running: turn.endedAt == null,
        startedAt: turn.startedAt,
        // The book's time for this one, which is what orders a pair.
        bookedAt: link ? link.svc.startsAt : turn.startedAt,
        swapWith: mate ? mate.id : null,
        swapStaff: mateM ? mateM.onBoard : '',
        swapStaffId: mate ? mate.staffId : null,
        swapClient: mateM ? mateM.clientName : '',
        swapClientId: mate ? mate.clientId == null ? null : mate.clientId : null,
        swapService: mateM ? mateM.serviceName : '',
        pairKey: '',
        pairSide: '',
        notes: (link && link.appt.notes) || ''
      });
      return;
    }
    if (turn.appointmentServiceId) return;
    var staff = staffOf(turn.staffId);
    out.unlinked.push({
      key: 'unlinked-' + turn.id,
      turnId: turn.id,
      staffName: turn.staffName || (staff && staff.name) || '',
      serviceName: turn.serviceName,
      letter: turn.serviceLetter,
      color: colorOfTurn(turn),
      running: turn.endedAt == null,
      startedAt: turn.startedAt
    });
  });

  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      if (seated[svc.id]) return;                 // the board has it: not a plan
      if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
      var mapped = trackerServiceForBooked(svc);
      var from = svc.startsAt;
      var to = from + bookedHoldMs(svc, mapped);
      if (to < t) return;                         // its window has been and gone

      function push(kind, staff, cover, extra) {
        var item = {
          key: 'risk-' + svc.id + '-' + kind + '-' + (staff ? staff.id : 'x'),
          kind: kind, staff: staff || null, cover: cover,
          appt: a, svc: svc, mapped: mapped,
          startsAt: from, endsAt: to, late: from < t,
          clientName: a.clientName, clientId: a.clientId, serviceName: svc.name,
          letter: mapped ? mapped.abbrev : '?',
          color: mapped ? mapped.color : null,
          requested: isApptRequested(svc),
          notes: a.notes || ''
        };
        if (extra) Object.keys(extra).forEach(function (k) { item[k] = extra[k]; });
        out.risk.push(item);
      }

      var labels = (svc.techs || []).filter(Boolean);
      var named = [], offBoard = [];
      // Somebody Mangomint has coming in today is not "not on the board" for
      // being due later — they are judged by their In time, like anybody else.
      var crew = presentStaff.value.concat(scheduledOffBoard.value);
      labels.forEach(function (label) {
        var s = null;
        crew.forEach(function (x) {
          if (!s && matchesStaff(x, label)) s = x;
        });
        if (s) named.push(s); else offBoard.push(label);
      });

      // The book has somebody on the board on it: the only question is whether
      // they will actually be free when the client arrives.
      if (named.length) {
        named.forEach(function (s) {
          var av = availabilityAt(s.id, from, to, svc.id, buffer);
          // Not in until after it starts. Assumed in and ready at their In
          // time (Kevin, 2026-11-02), so this is the only way being due later
          // is a conflict — and, like Gone by then, it is knowable at nine in
          // the morning, so it does not wait for the hour below. Two clients
          // in one chair is still the bigger fact.
          var inAt = readyFromFor(s.id);
          if (av.why !== 'pinned' && inAt != null &&
              conflictRules.readyTooLate(inAt, from, buffer)) {
            push('notin', s, conflictCover(a, svc, s.id, buffer), { until: inAt });
            return;
          }
          if (av.ok) return;
          if (av.why === 'pinned') {
            push('double', s, conflictCover(a, svc, s.id, buffer), {
              at: av.at,
              withRequested: !!av.requested,
              withClient: av.block.appt.clientName,
              withService: av.block.svc.name,
              withSvcId: av.block.svc.id
            });
          } else if (av.why === 'shift') {
            // Mangomint took the booking knowing their hours (work-hours.js).
            if (!workRules.goneByThen(av.until, to, av.endFrom, true)) return;
            /* Ahead of the horizon below, with the double booking rather than
             * with the soft reasons. A running square says nothing about the
             * four o'clock and somebody on a break will very likely be back,
             * which is what the hour is there for; a six o'clock booking on
             * somebody who goes at five is wrong at nine in the morning and
             * still wrong at half past five. Holding it back until an hour
             * before would be holding it back until it could not be moved. */
            push('gone', s, conflictCover(a, svc, s.id, buffer), { until: av.until });
          } else if (from > soon) {
            return;                               // too far off to mean anything
          } else if (av.why === 'busy') {
            push('late', s, conflictCover(a, svc, s.id, buffer), { until: av.until });
          } else if (av.why === 'skipped') {
            var r = skipReasonOf(s.id);
            push('skipped', s, conflictCover(a, svc, s.id, buffer),
              { reason: r ? r.label : 'Skip' });
          }
        });
        return;
      }

      // Nobody on the board is named on it. Either the book names somebody who
      // is not in today, or it names nobody at all and the work goes to
      // whoever is free — and there may be nobody.
      if (offBoard.length) {
        push('offboard', null, conflictCover(a, svc, null, buffer),
          { labels: offBoard.join(', ') });
        return;
      }
      if (!mapped) return;        // unmapped: there is nothing here to measure
      var cover = conflictCover(a, svc, null, buffer);
      if (!cover.some(function (x) { return x.ok; })) {
        push('nofree', null, cover, { none: !cover.length });
      }
    });
  });

  out.risk.sort(function (x, y) { return x.startsAt - y.startsAt; });
  if (out.mismatch.length > 1) out.mismatch = orderMismatchPairs(out.mismatch);
  out.count = out.mismatch.length + out.risk.length + out.unlinked.length;
  return out;
});

// The appointment behind a turn. Falls back through the booked service
// for a turn linked before appointment_id was being recorded.
function apptIdForTurn(t) {
  if (t.appointmentId) return t.appointmentId;
  var found = bookedServiceById(t.appointmentServiceId);
  return found ? found.appt.id : null;
}

/* ---------- keeping a square attached to its booking ----------
 *
 * Editing an appointment can hand every service on it a brand new id. The
 * booking system's webhooks carry no line ids at all, so the ingest rebuilds
 * the lines from scratch and the ids it gives them are its own; change the
 * pedicure on a two-service appointment and the untouched manicure next to it
 * comes back as a different row.
 *
 * A square linked to the old id would then read as a booking nobody had
 * seated — offered again in Due now, flagged as unlinked, and started again
 * by auto-start even though the service was finished an hour ago. So the link
 * is re-found by what does survive an edit: the appointment, and the service
 * it is a service OF. The line id is only ever a fast path.
 *
 * A service that genuinely changed — Classic Pedicure to Pamper Me Pedicure —
 * has a different service id, so it is not re-found by any of that. What
 * happens to it depends on whether anybody is doing it:
 *
 *   nobody has started it   the old square keeps its own record and the new
 *                           service is offered in Due now, because it is a
 *                           different service and it is still to be done.
 *   a square is running     the client is in the chair having the service
 *                           changed under them. Due now would be offering the
 *                           front desk a second turn for a client already in
 *                           somebody's chair, so the running square follows
 *                           the change instead: see swapCandidate.
 */

// How far after a square went up a booking may still be the thing it is on. A
// service swapped under a running turn keeps roughly its own start time; a
// service booked for later in the same visit is a different square, and must
// never be swallowed by the one already running.
var SWAP_GRACE_MS = 15 * 60000;

/* The line a running square has been changed onto, or null.
 *
 * A visit is usually more than one service, so "some other line on this
 * appointment" is not an answer — that is how a running pedicure would eat
 * the manicure booked after it. Three things have to hold at once:
 *
 *   - nothing else is on it. A line another square already holds is that
 *     square's, whether or not this one has lost its own.
 *   - the book has this square's technician on it. That is what separates the
 *     changed service from the rest of the visit: the other lines belong to
 *     whoever is booked for them.
 *   - it is not booked for later. A service due after this one started is the
 *     next thing in the visit, not the thing being done now.
 */
function swapCandidate(turn, byAppt, claimed) {
  var staff = staffOf(turn.staffId);
  if (!staff) return null;
  var mine = (byAppt[turn.appointmentId] || []).filter(function (x) {
    if (claimed[x.svc.id]) return false;
    if (x.svc.startsAt > turn.startedAt + SWAP_GRACE_MS) return false;
    return (x.svc.techs || []).some(function (label) { return matchesStaff(staff, label); });
  });
  if (!mine.length) return null;
  mine.sort(function (a, b) {
    return Math.abs(a.svc.startsAt - turn.startedAt) - Math.abs(b.svc.startsAt - turn.startedAt);
  });
  return mine[0];
}

// The line a finished square's corrected booking moved it to, or null. Lines
// held as another square's add-on count as taken; one folded onto this square
// does not — see js/rules/finished-swap.js.
function finishedSwapCandidate(turn, byAppt, claimed) {
  var staff = staffOf(turn.staffId);
  if (!staff) return null;
  var lines = byAppt[turn.appointmentId] || [];
  var held = turnByApptService.value;
  var own = serviceById.value[turn.serviceId];
  var id = finishedSwapLine(turn, own ? own.sameTime || null : null, lines.map(function (x) {
    var mapped = trackerServiceForBooked(x.svc);
    var holder = held[x.svc.id];
    return {
      id: x.svc.id,
      startsAt: x.svc.startsAt,
      side: mapped ? mapped.sameTime || null : null,
      onTech: (x.svc.techs || []).some(function (label) { return matchesStaff(staff, label); }),
      held: !!claimed[x.svc.id] || !!(holder && holder !== turn)
    };
  }));
  if (id == null) return null;
  return lines.find(function (x) { return x.svc.id === id; }) || null;
}

/* ---------- the booking was cancelled and re-made ----------
 *
 * The swap above moves a square between lines of ONE appointment. Some edits
 * do not stay inside one: correcting who a walk-in actually is deletes the
 * appointment and writes a new one. Seen live — a client seated as the house
 * "Walk In" account, the square started against that booking, and a minute
 * later the desk put the right name on it:
 *
 *   10827  Walk In       Acrylic Full Set  Yulisa  1:50  -> deleted
 *   10828  Evian Ibarra  Acrylic Fill      Yulisa  1:55  -> created 57s later
 *
 * Nothing in the payload joins those two. The square kept its link to a
 * booking that no longer exists, so it went on drawing "Walk In" and never
 * followed the service or the checkout — while the replacement, which nothing
 * was on, was offered to the desk in Due now for a client already in the
 * chair. This is the same event as a service swap by another route, so a
 * running square follows it the same way.
 *
 * The joins are circumstantial, so all of them have to hold at once:
 *
 *   - the square's own booking is CANCELLED. A live booking is the answer;
 *     this pass only ever runs for a square whose booking has gone.
 *   - the replacement is NEW to the board. What separates a re-made booking
 *     from the technician's next client is that the next client was already
 *     in the book. Sticky, and deliberately: the cancellation and the new
 *     booking arrive as two separate events and either can land first, so
 *     "new" has to still be true when the other half of the pair shows up.
 *   - nobody is on it. A visit with a square already on it is somebody else's.
 *   - the book has this square's technician on it, and
 *   - it is booked where the old one was. A booking written down again lands
 *     beside the one it replaces; anything further off is the next client.
 *
 * Like auto-start, the first sighting of the day's book only learns. Every
 * appointment is new to a console that has never looked at today, and none of
 * it is an event — but that is now once per DAY rather than once per station
 * per reload, which is the whole of what `tt_book_seen` bought. Reopening the
 * console mid-service still never re-homes a square; closing the tab and coming
 * back no longer makes the board forget the morning.
 */

// How far a replacement may sit from the booking it replaces.
var REMAKE_GRACE_MS = 15 * 60000;

/* ---------- what the board has already seen in the book ----------
 *
 * booked service id -> { appointmentId, seenStartAt, inService, lateArrival }.
 *
 * A mirror of `tt_book_seen`, loaded with the rest of the day and written back
 * only where something moved. Three module-scope objects used to do this —
 * `bookSeenAppts`, `freshAppts`, `bookLearned` for the re-made booking above,
 * and `seenInService`, `seenStart` for auto-start — and all five had the same
 * fault: they were emptied by a reload and private to one tab. Whether the
 * console noticed the front desk re-timing an appointment depended on which
 * screen happened to be awake, which is not something a salon can be asked to
 * reason about.
 *
 * Reactive because `missingBookedStaff` and the auto-start watcher read it and
 * have to redraw when it moves.
 */
var bookSeen = reactive({});

function replaceBookSeen(next) {
  Object.keys(bookSeen).forEach(function (k) { delete bookSeen[k]; });
  Object.keys(next || {}).forEach(function (k) { bookSeen[k] = next[k]; });
}

/* Only the one-poll snapshot. The mirror itself is not session state — it is a
 * copy of one date's rows, replaced by `applySnapshot` whenever the board
 * loads — and emptying it here would only make this station's copy wrong until
 * the next load, without unsaying anything in the table. Forgetting is what the
 * old maps did and what the table exists to stop. */
function resetBookMemory() {
  bookSeenBefore = {};
}

// Has the console ever looked at today's book? Not "is there anything in it":
// an empty answer means no station has observed today yet, and that is what
// makes the first sighting learn rather than act.
function bookEverSeen() {
  for (var k in bookSeen) if (bookSeen.hasOwnProperty(k)) return true;
  return false;
}

// The appointment-level questions, answered by walking that visit's lines. A
// visit is not one thing happening — see `attachableAppointments` — so "has
// this been in service" is true if any line of it has.
function apptSeen(apptId) {
  for (var k in bookSeen) {
    if (bookSeen.hasOwnProperty(k) && bookSeen[k].appointmentId === apptId) return true;
  }
  return false;
}

function apptWasLive(apptId) {
  for (var k in bookSeen) {
    if (bookSeen.hasOwnProperty(k) && bookSeen[k].appointmentId === apptId &&
        bookSeen[k].inService) return true;
  }
  return false;
}

function apptArrivedLate(apptId) {
  for (var k in bookSeen) {
    if (bookSeen.hasOwnProperty(k) && bookSeen[k].appointmentId === apptId &&
        bookSeen[k].lateArrival) return true;
  }
  return false;
}

/* The book as it stood BEFORE the copy now on screen, per line.
 *
 * Two things read this memory and they run at different moments: `reconcileTurns`
 * the instant a book arrives, and auto-start from a watcher after it. Whoever
 * looks second would find the memory already brought up to date and conclude
 * that nothing had happened — which is why the sighting is taken once, here,
 * and the previous values are kept for both of them to compare against.
 *
 * Not persisted, and it does not need to be: it is one poll old, and everything
 * durable about it is already in `bookSeen`.
 */
var bookSeenBefore = {};

/* Whether the console had ever looked at today's book BEFORE the sighting now
 * in hand. Not the same question as `bookEverSeen()` once `observeBook` has
 * run — by then the answer is always yes, because it has just written the rows
 * that make it so.
 *
 * It is what "the first sighting only learns" is measured against, and getting
 * it from the live mirror instead would mean a console opening on a brand new
 * day acted on the whole morning's book in the same breath as it first read it.
 */
var bookSeenPrimed = false;

/* Take one sighting of the book that is now on screen.
 *
 * Called once per book load, from the top of `reconcileTurns`, before anything
 * has looked at what changed. Writes down where each line now starts and
 * whether it was already known, and leaves the previous answers in
 * `bookSeenBefore`.
 *
 * `everSeen` is read once for the whole pass rather than per line, and that is
 * load-bearing: asked per line, the first line written would make the second
 * one look like a late arrival, and a console opening on a fresh day would
 * decide that all but one of the morning's bookings were brand new.
 */
function observeBook() {
  var everSeen = bookEverSeen();
  bookSeenPrimed = everSeen;
  appts.rows.forEach(function (a) {
    a.services.forEach(function (svc) {
      var was = bookSeen[svc.id] || null;
      bookSeenBefore[svc.id] = was
        ? { appointmentId: was.appointmentId, seenStartAt: was.seenStartAt,
            inService: was.inService, lateArrival: was.lateArrival,
            writtenUpAt: was.writtenUpAt || null }
        : null;
      if (was && was.seenStartAt === svc.startsAt && was.appointmentId === a.id) {
        return;                       // nothing moved, nothing to write
      }
      bookSeen[svc.id] = {
        appointmentId: a.id,
        seenStartAt: svc.startsAt,
        inService: !!(was && was.inService),
        // Decided once and never revisited: a booking that turned up
        // mid-afternoon is a new booking for the rest of the day, however many
        // times it is seen after that.
        lateArrival: was ? was.lateArrival : everSeen,
        writtenUpAt: was ? was.writtenUpAt || null : null
      };
      pushBookSeen(svc.id);
    });
  });
}

/* A booked service seen in service, which is the edge auto-start runs on.
 *
 * Separate from the sighting above because it moves on its own clock: the
 * in-service list is fetched by itself and changes without the book changing.
 * Sticky once true — a client who steps out and comes back is not a new client,
 * and the console must not seat them twice.
 */
function noteInService(apptId, svcId) {
  var m = bookSeen[svcId];
  if (!m) {
    m = { appointmentId: apptId, seenStartAt: null, inService: false, lateArrival: false };
    bookSeen[svcId] = m;
  }
  if (m.inService) return;
  m.inService = true;
  pushBookSeen(svcId);
}

// The line a running square's cancelled booking was re-made as, or null.
function remakeCandidate(turn, old, oldStart, claimed, seatedAppt) {
  var staff = staffOf(turn.staffId);
  if (!staff) return null;
  var best = null;
  appts.rows.forEach(function (a) {
    if (a.cancelled || a.id === old.id) return;
    if (!apptArrivedLate(a.id) || seatedAppt[a.id]) return;
    if (Math.abs(a.startsAt - old.startsAt) > REMAKE_GRACE_MS) return;
    a.services.forEach(function (svc) {
      if (claimed[svc.id]) return;
      if (!(svc.techs || []).some(function (l) { return matchesStaff(staff, l); })) return;
      var gap = Math.abs(svc.startsAt - oldStart);
      if (gap > REMAKE_GRACE_MS) return;
      if (!best || gap < best.gap) best = { appt: a, svc: svc, gap: gap };
    });
  });
  return best ? { appt: best.appt, svc: best.svc } : null;
}

// The board's own service follows the booking's, so a square that is now a
// Bubble Me Pedicure stops reading, and stops counting, as the Pamper Me it
// went up as. Nothing to follow when the new service is unmapped: the letter
// the square already has beats no letter at all.
/* What a square is worth when the type on it changes.
 *
 * Normally the type's own weight, which is the whole of the rule: a square is
 * an Acrylic Pedicure, an Acrylic Pedicure is a full turn.
 *
 * A merged square is the exception. Its weight is not any one type's — it is
 * what the desk said the combined sitting counts for, answered on the merge
 * sheet against the services in front of them, and it is the only weight on the
 * board that a type cannot reconstruct. Following the book here would undo that
 * silently, hours later, on a poll nobody is watching. The merge sheet is where
 * it is set and the only place it changes; see `setMergedWeight`.
 */
function applyServiceWeight(t, weight) {
  if (isMergedTurn(t)) return;
  t.turnWeight = weight;
}

function followBookedService(t, svc) {
  var mapped = trackerServiceForBooked(svc);
  if (!mapped || mapped.id === t.serviceId) return false;
  t.serviceId = mapped.id;
  t.serviceName = mapped.name;
  t.serviceLetter = mapped.abbrev;
  applyServiceWeight(t, mapped.turnWeight);
  return true;
}

/* ---------- saying which booking was edited, and how ----------
 * The book as it was before the read that replaced it, by visit id, kept for
 * exactly one pass of `reconcileTurns` so a toast can say what moved
 * (js/rules/booking-diff.js). Only for the same day: a book from yesterday is
 * not "before" anything on today's board. */
var bookBefore = {};

function rememberBookBefore(forDate) {
  var map = {};
  if (appts.bookDate === forDate) {
    (appts.rows || []).forEach(function (a) { map[a.id] = a; });
  }
  bookBefore = map;
}

// What happened to a visit's squares, said after what happened to the visit.
function squaresNote(e) {
  var who = e.turns.map(function (t) { return t.staffName; })
    .filter(function (n, i, all) { return n && all.indexOf(n) === i; });
  var whose = who.length ? who.join(' and ') + '’s ' + (e.turns.length > 1 ? 'squares' : 'square') : 'its square';
  if (e.how.remade) return whose + ' followed it to the new booking';
  if (e.how.retimed) {
    return whose + ' now ' + (e.turns.length > 1 ? 'start' : 'starts') + ' at ' + formatClock(e.turns[0].startedAt);
  }
  if (e.how.swapped) return whose + ' followed the new service';
  if (e.how.options) return whose + ' took the new options';
  return whose + ' kept';
}

/* One toast per edited visit: whose booking, what changed, what the board did.
 * Kevin, 2026-10-19 — the old line said only that "an edited booking" had been
 * matched back. More than three in one pass is a re-sync, not news, and gets
 * one line naming them. */
function sayEditedBookings(edited) {
  var list = Object.keys(edited).map(function (k) { return edited[k]; });
  if (!list.length) return;
  if (list.length > 3) {
    var names = list.map(function (e) {
      var a = apptById.value[e.apptId];
      return (a && a.clientName) || 'a client';
    });
    toast(list.length + ' bookings were edited in ' + scheduling.name.value + ' — ' +
      names.slice(0, 4).join(', ') + (names.length > 4 ? ' and others' : '') +
      '. Their squares followed.', 'info');
    bookBefore = {};
    return;
  }
  list.forEach(function (e) {
    var after = apptById.value[e.apptId];
    var before = bookBefore[e.oldApptId] || null;
    var client = (after && after.clientName) || (e.turns[0] && e.turns[0].clientName) || 'A client';
    var at = after && after.startsAt ? ' ' + formatClock(after.startsAt) : '';
    var changes = before && after ? describeVisitChange(before, after, formatClock) : [];
    if (e.how.remade && !changes.length) changes = ['cancelled and booked again'];
    toast(client + '’s' + at + ' booking was edited in ' + scheduling.name.value +
      (changes.length ? ' — ' + changes.join('; ') : '') + '. ' + squaresNote(e) + '.', 'info');
  });
  bookBefore = {};
}

function reconcileTurns() {
  if (!scheduling.enabled.value || !appts.rows.length) return;

  var byId = {};          // booked service id -> { appt, svc }
  var byAppt = {};        // appointment id -> [ { appt, svc } ]
  var apptById = {};      // appointment id -> the appointment itself
  appts.rows.forEach(function (a) {
    byAppt[a.id] = byAppt[a.id] || [];
    apptById[a.id] = a;
    a.services.forEach(function (svc) {
      byId[svc.id] = { appt: a, svc: svc };
      byAppt[a.id].push({ appt: a, svc: svc });
    });
  });

  /* One sighting of the book that has just arrived, before anything below looks
   * at what changed — see `observeBook`. It is what tells the re-made booking
   * pass which appointments turned up since the console last looked, and it
   * leaves auto-start the previous start times to compare against.
   *
   * Once per book, not once per station per reload: the answer is in
   * `tt_book_seen` now, so a console opened at two o'clock knows what the
   * morning already saw. */
  observeBook();

  var claimed = {};
  state.turns.forEach(function (t) {
    if (t.appointmentServiceId && byId[t.appointmentServiceId]) claimed[t.appointmentServiceId] = true;
  });

  // Visits the board already has somebody in a chair for. A square whose own
  // booking has been cancelled is not one of them — it is an orphan looking
  // for its booking, and the visit it lands on has to stay open to the rest
  // of them, so two squares off one re-made appointment both find a line.
  var seatedAppt = {};
  state.turns.forEach(function (t) {
    if (!t.appointmentId) return;
    var a = apptById[t.appointmentId];
    if (a && a.cancelled) return;
    seatedAppt[t.appointmentId] = true;
  });

  var relinked = 0, renamed = 0;
  // Visit id -> what happened to its squares this pass, for the toast that
  // says which booking was edited and how. See `sayEditedBookings`.
  var edited = {};
  function touched(t, how, oldApptId) {
    var id = t.appointmentId;
    var e = edited[id] = edited[id] || { apptId: id, oldApptId: oldApptId || id, turns: [], how: {} };
    if (oldApptId) e.oldApptId = oldApptId;
    if (e.turns.indexOf(t) === -1) e.turns.push(t);
    e.how[how] = true;
  }
  var swaps = [], remade = [], retimed = [];
  var pairs = [];         // [ { t, found } ] — found is null for the orphans

  // Pass one: match every square to its line by id, then by the same service
  // on the same appointment. Every square gets its strict match before the
  // swap below looks at anything, so a square that still has its own line can
  // never lose it to one that has not.
  state.turns.forEach(function (t) {
    if (!t.appointmentId) return;
    var changed = false;
    var found = t.appointmentServiceId ? byId[t.appointmentServiceId] : null;

    if (!found) {
      // Same appointment, same service, not already spoken for. Preferring the
      // nearest start time keeps two of the same service on one appointment
      // (two pedicures for a couple) in the order they were put up.
      //
      // Squares put up before the service id was being recorded have only the
      // name it was booked under to go on. That is a match on the SERVICE's
      // own name inside one appointment — never on the client's, which can be
      // a placeholder shared by half the day. It happens once: the id is
      // written back, and every match after this one is by id.
      var mine = (byAppt[t.appointmentId] || []).filter(function (x) {
        if (claimed[x.svc.id]) return false;
        return t.sourceServiceId != null
          ? x.svc.serviceId === t.sourceServiceId
          : !!t.sourceServiceName && x.svc.name === t.sourceServiceName;
      });
      if (mine.length) {
        mine.sort(function (a, b) {
          return Math.abs(a.svc.startsAt - t.startedAt) - Math.abs(b.svc.startsAt - t.startedAt);
        });
        found = mine[0];
        claimed[found.svc.id] = true;
        t.appointmentServiceId = found.svc.id;
        changed = true;
        relinked += 1;
        touched(t, 'relinked');
      }
    }

    pairs.push({ t: t, found: found, changed: changed });
  });

  // Pass two: the service was changed under a running square. Its own line is
  // gone from the book and the replacement is sitting there with this
  // technician's name on it, so the square moves onto it rather than the front
  // desk being offered a fresh turn for a client already in the chair.
  //
  // A finished square follows too, until the day is closed: a booking
  // corrected after the work is the desk correcting the record. See
  // js/rules/finished-swap.js for why its join is different.
  pairs.forEach(function (p) {
    if (p.found) return;
    var cand = p.t.endedAt == null ? swapCandidate(p.t, byAppt, claimed)
      : dayClosed.value ? null : finishedSwapCandidate(p.t, byAppt, claimed);
    if (!cand) return;
    claimed[cand.svc.id] = true;
    // Folded onto this very square as an add-on before it could follow — the
    // replacement line taken for something sold at the chair. It is the
    // square's own service now, not a second one on top.
    if (p.t.extras && p.t.extras.length) {
      p.t.extras = p.t.extras.filter(function (m) { return m.appointmentServiceId !== cand.svc.id; });
    }
    p.t.appointmentServiceId = cand.svc.id;
    p.found = cand;
    p.changed = true;
    p.swapped = true;
    swaps.push(p.t);
    touched(p.t, 'swapped');
  });

  // Pass two and a half: the booking itself was cancelled and written again.
  // Unlike the swap above this runs whether or not the square still has a
  // line, because a cancelled booking keeps its lines — they are simply no
  // longer anything anybody is going to do.
  pairs.forEach(function (p) {
    if (p.t.endedAt != null) return;
    var old = apptById[p.t.appointmentId];
    if (!old || !old.cancelled) return;
    var oldStart = p.found ? p.found.svc.startsAt : old.startsAt;
    var cand = remakeCandidate(p.t, old, oldStart, claimed, seatedAppt);
    if (!cand) return;
    claimed[cand.svc.id] = true;
    var wasAppt = p.t.appointmentId;
    p.t.appointmentId = cand.appt.id;
    p.t.appointmentServiceId = cand.svc.id;
    p.found = cand;
    p.changed = true;
    p.swapped = true;
    remade.push(p.t);
    touched(p.t, 'remade', wasAppt);
  });

  // Pass two and three quarters: the extras follow their lines the same way, once
  // every square has its own. See relinkExtras. Left alone once the day is closed,
  // like the finished squares above.
  if (!dayClosed.value) {
    var linesByAppt = {};
    appts.rows.forEach(function (a) {
      linesByAppt[a.id] = a.services.map(function (svc) {
        return { id: svc.id, serviceId: svc.serviceId, startsAt: svc.startsAt };
      });
    });
    pairs.forEach(function (p) {
      (p.t.extras || []).forEach(function (m) {
        if (byId[m.appointmentServiceId]) claimed[m.appointmentServiceId] = true;
      });
    });
    pairs.forEach(function (p) {
      if (!p.t.extras || !p.t.extras.length) return;
      var moved = relinkExtras(p.t.extras, linesByAppt, claimed);
      if (!moved.changed) return;
      p.t.extras = moved.extras;
      p.changed = true;
      noteWrittenUp(p.t);    // a fold taken back off stays off on the new line too
    });
  }

  // Pass three: whatever the book now says about a matched square.
  pairs.forEach(function (p) {
    var t = p.t;
    var found = p.found;
    var changed = p.changed;
    var swapped = !!p.swapped;

    // A walk-in gets a name later. Who the square is for is read off its own
    // appointment by id — never by name, and whether or not its line can still
    // be found: naming a walk-in usually rewrites the lines as well, after the
    // square has finished, and a square left drawing "Walk In" under somebody
    // else's walk-in is the confusion this prevents. See visit-key.js.
    var who = clientFromBooking(t, found ? found.appt : apptById[t.appointmentId]);
    if (who) {
      if (who.clientName !== t.clientName) renamed += 1;
      t.clientId = who.clientId;
      t.clientName = who.clientName;
      changed = true;
    }

    if (!found) {
      if (changed) pushTurn(t);
      return;
    }

    /* A start the desk chose on the assign sheet, and the book has since moved
     * the appointment underneath it.
     *
     * The book wins. The choice was an answer about one particular ten
     * o'clock — the technician was still with somebody at ten, so ten is not
     * when this began — and re-timing the appointment to half past is the
     * front desk saying the ten o'clock it was about is gone. Leaving the
     * chosen start there would mean a square whose reason no longer exists,
     * and one that quietly disagrees with the book for the rest of the day.
     *
     * `startBookedAt` is the comparison and `startedAt` cannot stand in for it:
     * a square deliberately started away from its booking is not evidence that
     * anything moved. It is also why both are columns rather than a note in
     * this station's memory — the desk that answered the question is often not
     * the screen that is open when the appointment is re-timed, and a reload
     * would lose it entirely.
     *
     * Before the countdown below, because the countdown is measured from the
     * start and has to be measured from the new one. Never into the future:
     * a booking pushed to half past while it is still ten past began when it
     * began, which is the same rule `bookedOutcome` seats them under.
     *
     * Running squares only. A finished one is the record of a service that was
     * actually performed, and the book changing afterwards does not change what
     * the technician did — the same line pass three draws everywhere else. The
     * answer stays on the row either way; it is a record of what was asked.
     */
    if (t.startBookedAt && t.endedAt == null && !dayClosed.value &&
        found.svc.startsAt && found.svc.startsAt !== t.startBookedAt) {
      t.startBookedAt = found.svc.startsAt;
      t.startedAt = Math.min(found.svc.startsAt, Date.now());
      changed = true;
      retimed.push(t);
      touched(t, 'retimed');
    }

    // The booking may have been re-timed or re-priced under a running square.
    // Through scheduleFor, so a booking that carries no length of its own
    // falls back to the type's default rather than dropping the countdown.
    var sched = scheduleFor(
      { appointmentServiceId: t.appointmentServiceId }, t.serviceId, t.startedAt);
    if (sched.minutes !== t.scheduledMinutes || sched.endsAt !== t.scheduledEndAt) {
      t.scheduledMinutes = sched.minutes;
      t.scheduledEndAt = sched.endsAt;
      changed = true;
    }

    if (found.svc.name && found.svc.name !== t.sourceServiceName) {
      t.sourceServiceName = found.svc.name;
      changed = true;
    }
    // What the line is a service OF, which is what the matching above runs on.
    // It follows the book for every square: it says which booking this is, not
    // what the technician did.
    if (t.sourceServiceId !== found.svc.serviceId) {
      // Changed in place — the line kept its id and the service on it moved.
      // The same event as the swap above by a different route, so a running
      // square follows it the same way. A square that never had a service id
      // recorded is not a change; it is one being filled in for the first time.
      if (t.sourceServiceId != null && t.endedAt == null && !swapped) {
        swapped = true;
        swaps.push(t);
      }
      t.sourceServiceId = found.svc.serviceId;
      changed = true;
    }

    /* The options the booking carries, and what they do to the square.
     *
     * An option is not a different service — it is the same service with more
     * on it, and it changes what the work is worth. A Classic Pedicure is half
     * a turn; add Acrylic halfway through and it is an Acrylic Pedicure and a
     * full one. So the square re-reads its type whenever the booking's options
     * move, not only when the service itself was swapped out.
     *
     * The stored list is what makes that safe. Following the book on every
     * pass would undo *Change service*: the desk sets a square to something by
     * hand, the next poll reads the booking and sets it straight back. Against
     * a snapshot, the board can tell the two apart — nothing happens until the
     * BOOKING changes, and a hand-set type stands until it does.
     *
     * Unlike a service swap, this reaches finished squares too. Options are
     * rung up at checkout as often as they are added mid-service, by which
     * time the technician has usually stopped the turn, and a half turn that
     * should have been a full one is wrong on the day's credit either way.
     * The books closing is what settles it: past `endDay` nothing moves, which
     * is what makes a closed day's numbers a record rather than a live figure.
     */
    var optionsMoved = optionsSignature(bookedOptionsOf(found.svc)) !==
      optionsSignature(t.options);
    if (optionsMoved) {
      t.options = bookedOptionsOf(found.svc);
      touched(t, 'options');
      changed = true;
    }

    // A finished square is otherwise the record of the service that was
    // actually performed, and the book changing afterwards does not change
    // what the technician did.
    var follow = swapped || (optionsMoved && !dayClosed.value);
    if (follow && followBookedService(t, found.svc)) {
      changed = true;
      // The countdown was worked out above against the type the square used
      // to be. A booking with a length of its own is unaffected — that always
      // wins — but one without falls back to the type's default, and the type
      // has just changed underneath it.
      var after = scheduleFor(
        { appointmentServiceId: t.appointmentServiceId }, t.serviceId, t.startedAt);
      t.scheduledMinutes = after.minutes;
      t.scheduledEndAt = after.endsAt;
    }

    if (changed) pushTurn(t);
  });

  sayEditedBookings(edited);
  foldAddOns();
  applyPriceWeights();
}

/* ---------- full turn or half, by price ----------
 *
 * Kevin, 2026-10-23: what the client pays for the sitting decides the weight —
 * the square's own booked line (options already in its price) plus the add-on
 * lines folded onto it, against the salon's cutoff. See js/rules/price-weight.js.
 *
 * Re-read on every pass, for finished squares as well as running ones, because
 * the price moves when it moves: Chrome goes on at checkout as often as at the
 * start, and Tam's 12:55 Gel Polish Change went from $30 to $45 a minute before
 * it was stopped. Like options, the day closing settles it.
 *
 * One technician's squares on one visit are counted as one sitting: each
 * service at the cutoff is a full turn, smaller ones ride along beside it, and
 * on their own their total decides (Kevin, 2026-10-23 — Thu's $40 pedicure and
 * $25 manicure are one full turn, drawn as two halves). See `visitWeights`.
 *
 * Left alone: a square with no booking, a visit with no price known, a visit
 * where the desk weighed a square on the merge sheet (that answer is the
 * desk's), and a type that counts for nothing.
 */
function priceSquare(t) {
  var lines = [t.appointmentServiceId].concat((t.extras || []).map(function (m) {
    return m.appointmentServiceId;
  }));
  var type = serviceById.value[t.serviceId];
  return {
    id: t.id,
    startedAt: t.startedAt,
    prices: lines.map(function (id) {
      var found = id ? bookedServiceById(id) : null;
      return found ? found.svc.price : null;
    }),
    typeWeight: type ? type.turnWeight : t.turnWeight,
    handWeighed: (t.extras || []).some(function (m) { return !m.addOn; })
  };
}

function applyPriceWeights() {
  if (!scheduling.enabled.value || dayClosed.value) return;
  if (appts.bookDate !== state.businessDate) return;
  var sittings = {};
  state.turns.forEach(function (t) {
    if (!t.appointmentId || !t.staffId) return;
    var key = t.appointmentId + '|' + t.staffId;
    (sittings[key] = sittings[key] || []).push(t);
  });
  Object.keys(sittings).forEach(function (key) {
    var turns = sittings[key];
    var weights = visitWeights(turns.map(priceSquare), fullTurnPrice.value);
    turns.forEach(function (t) {
      var w = weights[t.id];
      if (w == null || w === t.turnWeight) return;
      t.turnWeight = w;
      pushTurn(t);
    });
  });
}

/* Checking a client out ends whatever of that visit is still running. The turn
 * is stopped as of the ticket's close time rather than now, so one left
 * running until closing still reports the length it actually took.
 *
 * Only what is still running. A square stopped on the grid is the record of
 * when that technician finished, and the close — which is when the client got
 * round to paying, after the other services on the visit and however long
 * they sat — says nothing about it. See rules/checkout.js.
 *
 * Which is why the write is conditional rather than the usual whole row. This
 * screen deciding a square is still running is a reading of its own copy, and
 * another screen may have stopped it a second ago: an upsert from here would
 * put the close time over that stop. `ended_at is null` is asked of the
 * server instead, so a square somebody stopped stays stopped, and the reload
 * that follows brings their time down to this screen.
 */
function applyClosedTickets() {
  if (!ui.autoStop || !scheduling.can('checkout')) return;
  var stopped = [];
  state.turns.forEach(function (t) {
    var apptId = t.endedAt == null ? apptIdForTurn(t) : null;
    if (!apptId) return;
    var end = checkoutEnd(t, appts.ticketClosedAt[apptId]);
    if (end == null) return;
    t.endedAt = end;
    sync.enqueue({
      table: 'tt_turns', type: 'update',
      match: { id: t.id }, isNull: ['ended_at'],
      patch: { ended_at: new Date(end).toISOString() }
    });
    stopped.push(t);
  });
  if (!stopped.length) return;
  toast(
    stopped.length === 1
      ? 'Checked out — stopped ' + stopped[0].staffName + '’s ' + stopped[0].serviceName
      : 'Checked out — stopped ' + stopped.length + ' turns',
    'ok'
  );
}

/* ---------- starting turns off the book ----------
 *
 * The mirror image of applyClosedTickets: a checkout ends a turn, going in
 * service begins one. When a client is seated, every service on that
 * appointment that is starting *now* gets a turn on the tech the book has
 * against it. Services booked for later in the same visit are left alone
 * until their start time moves.
 *
 * WHAT COUNTS AS "IN SERVICE"
 * ---------------------------
 * A provider that can report it fills in fetchInService and that wins.
 * Mangomint cannot: its webhooks carry no status field of any kind — checked
 * against every payload it has ever sent us — and the ticket it opens when
 * the front desk hits Start is not published until the sale completes. So the
 * board reads the seating itself: an appointment is in service from the
 * moment its first service lands on the board, whoever put it there. Seat one
 * service of a visit and the rest of what is starting now follows.
 *
 * WHAT SETS IT OFF
 * ----------------
 * Two events, and deliberately nothing else:
 *   - an appointment goes in service    -> start everything of it due now
 *   - a booked service's start time moves, or a service is added to a visit
 *     already in service                -> start it if it is now due
 * The clock reaching a future service's start time is NOT one of them. A
 * booking is not a client in a chair, and a no-show would otherwise take a
 * turn off the rotation. The first pass after a load only learns the current
 * state; it never acts on it, so reopening the board mid-afternoon does not
 * start turns for everything the day has already been through.
 */

/* Where an auto-started square's clock begins: the book's time, not the moment
 * the board noticed.
 *
 * This is the same rule every other way of seating a booking already goes by —
 * the assign sheet, a card dragged onto a column, the empty-square sheet, all
 * of them through `bookedOutcome`. Auto-start was the one path that left it
 * out, and it is the path that puts up the squares nobody is watching: seat
 * one service of a two o'clock visit at twenty past and the rest of it went up
 * as though the client had walked in at twenty past, with the countdown twenty
 * minutes long in the wrong direction.
 *
 * Never ahead of the clock. A client seated early takes the rest of their
 * visit with them, and a square whose timer has not begun counting is a square
 * that cannot be read.
 *
 * The visit's first service is the exception: it starts when the booking
 * system started it, late or early — see js/rules/seated-start.js.
 *
 * Only the start, though — never `bookedOutcome`'s other answer, which puts a
 * booking whose whole window has passed up as finished. That one belongs to
 * somebody deliberately writing up work that is over. Auto-start fires because
 * a client has just gone in service, and a square nobody asked for should not
 * land already done.
 */
function autoStartAt(a, svc, now) {
  if (!svc.startsAt) return null;      // no time in the book; bookTurn uses its own
  return { startedAt: seatedStartAt(a, svc.startsAt, now), endedAt: null };
}

// Appointment id -> when the client went in service. The provider's answer
// first, then the board's own: the earliest turn standing against the visit.
var inServiceAt = computed(function () {
  var out = {};
  Object.keys(appts.inServiceAt).forEach(function (id) {
    if (appts.inServiceAt[id]) out[id] = appts.inServiceAt[id];
  });
  // Checked in at the booking system: the client has been taken back, whether
  // or not anybody has put a square up yet (Kevin, 2026-10-20). Which services
  // that starts is js/rules/auto-start.js, and a stale check-in starts none.
  Object.keys(bookStatus.byAppt).forEach(function (id) {
    var s = bookStatus.byAppt[id];
    if (!s || s.status !== 'CheckedIn' || !s.checkedInAt) return;
    if (!out[id] || s.checkedInAt < out[id]) out[id] = s.checkedInAt;
  });
  state.turns.forEach(function (t) {
    if (t.waiting) return;              // still in the waiting room
    var id = apptIdForTurn(t);
    if (id == null) return;
    if (!out[id] || t.startedAt < out[id]) out[id] = t.startedAt;
  });
  return out;
});

// Whether a due booked service can be turned into a turn, and on whom.
// Nobody is substituted in: the book names the tech, and putting a client
// with someone else is a decision for the front desk, not for this.
function autoStartTarget(svc) {
  var mapped = trackerServiceForBooked(svc);
  if (!mapped) {
    return { why: svc.name + ' is not mapped to a turn type — Settings → Appointments.' };
  }
  var busy = null, offBoard = false, named = false, barred = false;
  var staff = null;
  (svc.techs || []).forEach(function (label) {
    if (staff) return;
    var s = staffForTechLabel(label);
    if (!s) return;
    named = true;
    // An option can put the work beyond the technician the book named.
    if (!staffAllowedForBooked(svc, s.id)) { barred = true; return; }
    var day = state.dayStaff[s.id];
    if (!day || !day.present) { offBoard = true; return; }
    if (isBusy(s.id)) { busy = s; return; }
    staff = s;
  });
  if (staff) return { staff: staff, service: mapped };
  if (barred) {
    var opts = activeOptionsFor(svc).map(function (o) { return o.name; }).join(', ');
    return { why: svc.name + ' did not start — ' + (opts || 'its options') +
      ' is limited to certain technicians, and the one on the booking is not one of them.' };
  }
  if (busy) {
    return { busy: busy, service: mapped,
      why: busy.name + ' is still on a service, so ' + svc.name + ' did not start.' };
  }
  // Somebody not on the board already has a notice of its own offering to add
  // them, so saying it twice would only be noise.
  if (offBoard) return {};
  return {
    why: named
      ? svc.name + ' did not start — no tech on the board for it.'
      : svc.name + ' has nobody on it in the book, so it did not start.'
  };
}

/* What auto-start has already said out loud, per booked service.
 *
 * The one thing here that stays on the station, and the only one that should.
 * It is not a record of anything that happened — it is a note that this screen
 * has already put a sentence on this screen, so it does not put it there
 * sixty times while a booking sits unstartable. Shared, the person standing at
 * the second station would never be told why a client's service did not start,
 * because a station in the back room had already been told. A warning is for
 * whoever is looking at it.
 */
var autoStartNoted = {};
var autoStartRunning = false;

function resetAutoStart() {
  autoStartNoted = {};
  // The one-poll snapshot goes with it. What the console has SEEN is a row in
  // tt_book_seen and does not get forgotten here — see `resetBookMemory`.
  resetBookMemory();
}

function autoStartFromBook() {
  if (!scheduling.enabled.value) { resetAutoStart(); return; }
  if (ui.loading || !appts.rows.length) return;
  if (autoStartRunning) return;

  autoStartRunning = true;
  try {
    // Same reason as the write-up: an add-on is not a square to start.
    foldAddOns();
    var live = inServiceAt.value;
    /* The first sighting of the day's book only learns. That is now once per
     * DAY, across every station, rather than once per station per reload — the
     * answer lives in tt_book_seen. A console reopened at two o'clock therefore
     * goes on acting on the afternoon instead of sitting out a pass, and one
     * opened on a book nobody has looked at yet still learns it first. */
    var act = ui.autoStart && bookSeenPrimed;
    var now = Date.now();
    var started = [], blocked = [], busyLines = [];

    appts.rows.forEach(function (a) {
      var isLive = !a.cancelled && !!live[a.id];
      // Asked before it is written down, because the EDGE into service is the
      // event and not the state.
      var wasLive = apptWasLive(a.id);
      // Only what was booked at the same time as the seating; see
      // js/rules/auto-start.js. Asked once per visit, before anything goes up.
      var sameTime = isLive && act ? sameTimeFor(a, now) : null;

      var checkedInAt = checkedInAtFor(a.id);

      a.services.forEach(function (svc) {
        // A line checked in a few minutes ahead of its time is not in service
        // until that time comes, so the edge is still to happen then.
        if (isLive && earlyCheckIn(svc.startsAt, checkedInAt, now) !== 'waiting') {
          noteInService(a.id, svc.id);
        }

        /* What the book said last time, taken from the sighting `reconcileTurns`
         * made when this book arrived — not from the live memory, which that
         * sighting has already brought up to date.
         *
         * Consumed as it is read. This runs from a watcher that also fires when
         * only the in-service list has moved, and without this the same
         * re-timing would look like news on every one of those passes. */
        var before = bookSeenBefore[svc.id];
        var last = before ? before.seenStartAt : null;
        var moved = last != null && last !== svc.startsAt;
        // `null` is a sighting that found no row — genuinely a line nobody had
        // seen. `undefined` is no sighting yet, which is not the same claim and
        // must not be read as one: this runs from a watcher that can fire
        // before the first book has been observed, and treating that as "every
        // line is new" would seat the whole book on a reload.
        var isNew = before === null;
        if (before) before.seenStartAt = svc.startsAt;

        if (!act || !isLive) return;
        if (!(!wasLive || moved || isNew)) return;              // no event here
        if (turnByApptService.value[svc.id]) return;            // already on the board
        if (bookedLineOver(a, svc)) return;        // checked out, or dealt with
        if (!sameTime || !sameTime[svc.id]) return;      // not booked with the seating
        // Under turn order the check-in is only the client arriving: who takes
        // an unrequested booking is the desk's call. It stays on Due now, and
        // returning here keeps it out of the busy question and the notices.
        // The request is read from the booking whatever the display switch says.
        var asked = followsBook() ? null : requestInBook(svc);
        if (waitsForDesk({ followBook: followsBook(), requested: !!asked })) return;

        var target = autoStartTarget(svc);
        if (!target.staff && target.busy) {
          // Asked rather than noted: what happens to it is the desk's call.
          if (!autoStartNoted[svc.id]) {
            autoStartNoted[svc.id] = true;
            busyLines.push({ appt: a, svc: svc, staff: target.busy, service: target.service });
          }
          return;
        }
        if (!target.staff) {
          if (target.why && !autoStartNoted[svc.id]) {
            autoStartNoted[svc.id] = true;
            blocked.push(target.why);
          }
          return;
        }
        var ok = bookTurn(target.staff.id, {
          serviceId: target.service.id,
          requested: false,
          quiet: true,
          at: autoStartAt(a, svc, now),
          link: {
            appointmentId: a.id, appointmentServiceId: svc.id,
            sourceServiceId: svc.serviceId, clientId: a.clientId,
            clientName: a.clientName, sourceServiceName: svc.name
          }
        });
        if (ok) started.push(turnByApptService.value[svc.id]);
      });
    });

    if (started.length === 1) {
      var t = started[0];
      toast('In service — started ' + t.staffName + '’s ' + t.serviceName +
        (t.clientName ? ' for ' + t.clientName : ''), 'ok');
    } else if (started.length) {
      toast('In service — started ' + started.length + ' turns from the book.', 'ok');
    }
    // One at a time: a second line would replace the first before it is read.
    if (blocked.length && !started.length) toast(blocked[0], 'warn');
    if (busyLines.length) askAboutBusy(busyLines);
  } finally {
    autoStartRunning = false;
  }
}

/* Which of this visit's lines the seating carries with it, as a set of ids, or
 * null when the board cannot tell yet. That is a visit with a square standing
 * against a line the book no longer has: Mangomint reissues every line id on an
 * edit, and until the square is matched to its new line the visit looks empty,
 * which, checked in, would put that square's own service up a second time. */
function sameTimeFor(a, now) {
  var ids = {};
  a.services.forEach(function (svc) { ids[svc.id] = true; });
  var orphan = state.turns.some(function (t) {
    return t.appointmentId === a.id && t.appointmentServiceId && !ids[t.appointmentServiceId];
  });
  if (orphan) return null;
  var lines = a.services.map(function (svc) {
    var t = turnByApptService.value[svc.id];
    var seen = bookSeen[svc.id];
    return {
      id: svc.id, startsAt: svc.startsAt,
      onBoard: !!t, started: !!(t && !t.waiting),
      closed: bookedLineOver(a, svc) || !!(seen && seen.writtenUpAt)
    };
  });
  var out = {};
  sameTimeLines(lines, checkedInAtFor(a.id), now).forEach(function (id) { out[id] = true; });
  return out;
}

// The booking system's check-in instant for a visit it has checked in, or null.
function checkedInAtFor(apptId) {
  var st = bookStatus.byAppt[apptId];
  return st && st.status === 'CheckedIn' ? st.checkedInAt : null;
}

/* ---------- a technician on the visit is still busy ----------
 *
 * Kevin, 2026-10-20: when a service seated with the rest of its visit is booked
 * with somebody still on a service, the desk is asked: stop that square and
 * start this one, give it to somebody else, or leave it in Due now. A request
 * is part of the question. The client asked for that person, so waiting comes
 * first and moving says it drops the request (`busyChoices`). Per station,
 * like the toasts above: a question is for whoever is looking at it, and a
 * line answered at another station drops off this one because it is on the
 * board.
 */
function askAboutBusy(found) {
  var lines = ui.autoStartAsk ? ui.autoStartAsk.lines.slice() : [];
  found.forEach(function (f) {
    if (lines.some(function (l) { return l.svcId === f.svc.id; })) return;
    lines.push({ svcId: f.svc.id, staffId: f.staff.id, serviceId: f.service ? f.service.id : null });
  });
  ui.autoStartAsk = lines.length ? { lines: lines } : null;
}

// The open question with everything the sheet draws. Lines that have been
// dealt with, here or at another station, are left out.
var autoStartAskView = computed(function () {
  var q = ui.autoStartAsk;
  if (!q) return null;
  var lines = [];
  q.lines.forEach(function (l) {
    if (turnByApptService.value[l.svcId]) return;
    var found = bookedServiceById(l.svcId);
    if (!found || found.appt.cancelled || bookedLineOver(found.appt, found.svc)) return;
    var staff = staffOf(l.staffId);
    if (!staff) return;
    var requested = isApptRequested(found.svc, staff.id);
    lines.push({
      svcId: l.svcId, appt: found.appt, svc: found.svc, staff: staff,
      busyTurn: activeTurn(staff.id), requested: requested,
      choices: busyChoices(requested),
      visit: found.appt.services.map(function (s) {
        var t = turnByApptService.value[s.id];
        return {
          id: s.id, name: s.name, startsAt: s.startsAt,
          tech: (s.techs && s.techs[0]) || '',
          state: !t ? 'open' : t.waiting ? 'held' : t.endedAt != null ? 'done' : 'started'
        };
      })
    });
  });
  return lines.length ? lines : null;
});

function closeAutoStartAsk() { ui.autoStartAsk = null; }

function dropAutoStartLine(svcId) {
  var q = ui.autoStartAsk;
  if (!q) return;
  var rest = q.lines.filter(function (l) { return l.svcId !== svcId; });
  ui.autoStartAsk = rest.length ? { lines: rest } : null;
}

/* The desk's answer for one line.
 *   'wait'  nothing: the line stays in Due now, as any unseated booking does.
 *   'stop'  stop the technician's running square now and start this one on
 *           them from now, since they were busy until this moment.
 *   'move'  the assign sheet, which picks the next technician and drops the
 *           request star when it moves off the person who was asked for. */
function answerAutoStartAsk(svcId, choice) {
  var q = ui.autoStartAsk;
  var line = q && q.lines.find(function (l) { return l.svcId === svcId; });
  var found = bookedServiceById(svcId);
  dropAutoStartLine(svcId);
  if (!line || !found) return false;
  if (choice === 'move') { openAssign(found.appt, found.svc); return true; }
  if (choice !== 'stop') return true;
  var mapped = line.serviceId || (trackerServiceForBooked(found.svc) || {}).id;
  if (!mapped) { openAssign(found.appt, found.svc); return false; }
  var running = activeTurn(line.staffId);
  if (running) stopTurn(running.id);
  var a = found.appt, svc = found.svc;
  return !!bookTurn(line.staffId, {
    serviceId: mapped, orderOk: true,
    at: { startedAt: Date.now(), endedAt: null },
    link: {
      appointmentId: a.id, appointmentServiceId: svc.id,
      sourceServiceId: svc.serviceId, clientId: a.clientId,
      clientName: a.clientName, sourceServiceName: svc.name
    }
  });
}

function openEmptyPick(staffId) {
  if (refuseWhileClosed()) return;
  ui.emptyPick = { staffId: staffId };
}
function closeEmptyPick() { ui.emptyPick = null; }

// Manual override: start a turn on this tech without going through the queue.
function startTurnFor(staffId, serviceId, requested) {
  var ok = bookTurn(staffId, { serviceId: serviceId, requested: !!requested });
  if (ok) ui.emptyPick = null;
  return ok;
}

// Quick-assign a booking. An unmapped service has no turn type yet, so hand
// off to the full assign modal instead of guessing one.
function quickAssign(staffId, appt, svc, requested) {
  if (!staffAllowedForBooked(svc, staffId)) {
    var s = staffOf(staffId);
    toast((s ? s.name : 'That tech') + ' is not set up for ' +
      (activeOptionsFor(svc).map(function (o) { return o.name; }).join(', ') || 'that option') + '.', 'warn');
    return false;
  }
  var mapped = trackerServiceForBooked(svc);
  if (!mapped) {
    ui.emptyPick = null;
    openAssign(appt, svc);
    setAssignStaff(staffId);
    // Whatever was ticked on the way here survives the hand-off: the sheet
    // asked the question once already and should not ask it again.
    if (requested && ui.assign) ui.assign.requested = true;
    return false;
  }
  var ok = bookTurn(staffId, {
    serviceId: mapped.id,
    requested: !!requested,
    at: assignOutcome(appt.id, svc.id),
    link: {
      appointmentId: appt.id, appointmentServiceId: svc.id,
      sourceServiceId: svc.serviceId, clientId: appt.clientId,
      clientName: appt.clientName, sourceServiceName: svc.name
    }
  });
  if (ok) ui.emptyPick = null;
  return ok;
}

// Running turns a given booked service could be attached to.
function attachableTurns(serviceId) {
  return state.turns.filter(function (t) {
    return t.endedAt == null && !t.appointmentServiceId &&
      (!serviceId || t.serviceId === serviceId);
  });
}

function openMoveConfirm(turnId, toStaffId, anchorId) {
  ui.moveConfirm = {
    turnId: turnId, toStaffId: toStaffId,
    anchorId: anchorId == null ? null : anchorId
  };
}
function closeMoveConfirm() { ui.moveConfirm = null; }
function confirmMove(resetTimer) {
  if (!ui.moveConfirm) return;
  moveTurnTo(ui.moveConfirm.turnId, ui.moveConfirm.toStaffId,
    ui.moveConfirm.anchorId, resetTimer);
  ui.moveConfirm = null;
}

/* The drop, as the board's pointer handlers hand it over.
 *
 * Every landing that changes anything goes through the sheet. Not because the
 * move is dangerous — it is one field on one row, and it can be dragged back —
 * but because it is the only edit on this board that rewrites what the day says
 * happened, and a drag is the easiest gesture in the console to make by
 * accident. A square nudged with a sleeve must not quietly reassign an hour of
 * somebody's work.
 */
function askMoveTurn() {
  var plan = turnDropPlan.value;
  if (!plan) return false;
  if (plan.blocked) { toast(plan.blocked, 'warn'); return false; }
  if (plan.noop) return false;
  openMoveConfirm(plan.turnId, plan.staffId, plan.anchorId);
  return true;
}

/* ---------- what the move sheet reads ----------
 *
 * Worked out here rather than in the component, and read back off the board
 * every time rather than copied in when the sheet opened, for the reason the
 * swap sheet gives: a sheet can sit open across a webhook, an auto-stop or
 * another station's drag, and a sheet describing a board that has moved on is
 * a sheet that gets said yes to.
 *
 * Two halves, and the sheet keeps them apart on purpose:
 *
 *   facts       what the move does. Where it lands, what moves out of the way,
 *               what each column is worth afterwards.
 *   conflicts   what it runs over. Every one of these is a thing the board
 *               already believed that the move contradicts — so the button
 *               stops saying "Move" and starts saying "Move anyway".
 */
var MOVE_OVERLAP_SLACK = 60000;   // hand-clock noise, not two services at once

function turnLabel(t) {
  return t.clientName ? t.clientName + '’s ' + t.serviceName : t.serviceName;
}

function creditAfter(staffId, list) {
  return creditFrom(buildSlots(list), missedTurns(staffId), skipsFor(staffId).length).credit;
}

var moveConfirmDetail = computed(function () {
  var m = ui.moveConfirm;
  if (!m) return null;
  var plan = turnMovePlan(m.turnId, m.toStaffId, null, m.anchorId);
  if (!plan) return null;

  var t = plan.turn;
  var to = staffOf(plan.staffId);
  var from = staffOf(t.staffId);
  var running = t.endedAt == null;
  var missed = missedTurns(plan.staffId);
  var skips = skipsFor(plan.staffId);
  var wasRows = buildRows(buildSlots(plan.before), skips);
  var rows = buildRows(buildSlots(plan.after), skips);
  var at = moveRules.squareNumberOf(rows, t.id, missed);
  var below = moveRules.shiftedSquares(wasRows, rows, missed, t.id);

  var conflicts = [];
  var notes = [];

  /* Work the destination was already doing. The one conflict here that is a
   * claim about the room rather than about the board: two services on one pair
   * of hands at the same time is not something that happened, so one of the two
   * records is wrong and the board cannot know which. */
  var clashes = moveRules.overlapsWith(
    t, turnsFor(plan.staffId), now.value, MOVE_OVERLAP_SLACK);
  clashes.slice(0, 2).forEach(function (hit) {
    conflicts.push({
      key: 'overlap-' + hit.turn.id,
      text: to.name + ' was on ' + turnLabel(hit.turn) + ' from ' +
        formatClock(hit.turn.startedAt) +
        (hit.turn.endedAt == null ? ' and is still on it' : ' to ' + formatClock(hit.turn.endedAt)) +
        ' — the two overlap by ' + formatMins(hit.ms) + '.'
    });
  });
  if (clashes.length > 2) {
    conflicts.push({
      key: 'overlap-more',
      text: 'And ' + (clashes.length - 2) + ' more square' +
        (clashes.length - 2 === 1 ? '' : 's') + ' of theirs it runs across.'
    });
  }

  // Filed underneath something that has not finished. Legal — a record takes no
  // chair — but the column will read as though the running service were over,
  // so it is said rather than left to be noticed.
  var live = activeTurn(plan.staffId);
  if (live && live.id !== t.id) {
    var iMoved = plan.after.findIndex(function (x) { return x.id === t.id; });
    var iLive = plan.after.findIndex(function (x) { return x.id === live.id; });
    if (iMoved > iLive) {
      conflicts.push({
        key: 'under-live',
        text: 'It lands below ' + turnLabel(live) + ', which ' + to.name +
          ' still has running — the column will read as though that one had finished.'
      });
    }
  }

  /* Squares that re-form under it. A half turn pairs with whatever follows it,
   * so putting a square into the middle of a column can change what the squares
   * BELOW it are worth without anybody touching them — and what a square is
   * worth is where its technician sits in the rotation. */
  var repaired = moveRules.pairingChanges(
    buildSlots(plan.before), buildSlots(plan.after), t.id);
  // Named "below" in the sentence because that is where they are read, but the
  // count is of squares that actually change what they are worth.
  if (repaired.length) {
    conflicts.push({
      key: 'repair',
      text: 'Half turns re-pair around it, so ' + repaired.length +
        (repaired.length === 1 ? ' square below' : ' squares below') +
        ' change what they are worth — and with them ' + to.name +
        '’s place in the rotation.'
    });
  }

  // What the book says, when there is a book. Both ways round: a move can put
  // the board out of step with it, and a move is also how the desk puts the
  // board back in step, which is worth saying out loud on the way past.
  var labels = bookedTechLabels(t);
  if (labels.length && !bookNames(labels, to)) {
    conflicts.push({
      key: 'book',
      text: scheduling.name.value + ' has ' + labels.join(', ') + ' against ' +
        (t.clientName ? t.clientName + '’s booking' : 'that booking') +
        ', not ' + to.name + '. The square will carry the warning triangle.'
    });
  } else if (labels.length && !plan.same && !bookNames(labels, from)) {
    notes.push('This puts the square back in step with ' + scheduling.name.value + '.');
  }

  // Somebody's service list is what they do, not what they are allowed to have
  // done: this is a record being filed, so it is a question rather than a
  // refusal. It is still usually the sign of a drop on the wrong column.
  if (!plan.same && to.serviceIds.indexOf(t.serviceId) === -1) {
    conflicts.push({
      key: 'off-service',
      text: to.name + ' is not set up for ' + t.serviceName +
        ' — check it is the right column, or add it under Settings → Staff.'
    });
  }

  if (dayClosed.value) {
    conflicts.push({
      key: 'closed',
      text: 'The day is closed. This changes a record the day has been signed off on.'
    });
  }

  return {
    turn: t,
    letter: t.serviceLetter,
    color: colorOfTurn(t),
    label: turnLabel(t),
    from: from ? from.name : '?',
    to: to.name,
    same: plan.same,
    running: running,
    started: formatClock(t.startedAt),
    ended: t.endedAt == null ? null : formatClock(t.endedAt),
    elapsed: formatDuration((t.endedAt == null ? now.value : t.endedAt) - t.startedAt),
    at: at,
    below: below,
    // What each column is worth before and after. The whole point of the board
    // is that this number decides who gets the next walk-in, so a move that
    // changes it says by how much.
    credit: plan.same ? null : {
      from: from ? {
        name: from.name,
        was: turnCredit(from.id),
        now: creditAfter(from.id, turnsFor(from.id).filter(function (x) {
          return x.id !== t.id;
        }))
      } : null,
      to: { name: to.name, was: turnCredit(to.id), now: creditAfter(to.id, plan.after) }
    },
    conflicts: conflicts,
    notes: notes,
    blocked: plan.blocked,
    noop: plan.noop
  };
});

/* Two squares changing places, to put the board back in step with the book.
 *
 * Not two moves: the board cannot hold either one on its own, because each of
 * them is landing on somebody who is busy with the other. One write of both.
 *
 * Neither clock is touched. A client sat down when they sat down, and that is
 * as true after the swap as before it — what was wrong was the name over the
 * column, not the time under the square. The countdown is re-read off the
 * booking on the way past, because the book's times are the record and this is
 * the moment the two records are being reconciled. */
function swapTurns(turnId, otherId, kind) {
  var a = turnOf(turnId), b = turnOf(otherId);
  // Asked again here, of the board and the book as they stand now rather than
  // of whatever raised the offer. A sheet can sit open across a webhook.
  var why = swapReasonFor(a, b, kind);
  if (why) { toast(why, 'warn'); return false; }

  var sa = staffOf(a.staffId), sb = staffOf(b.staffId);
  var aSort = a.sortAt;
  a.staffId = sb.id; a.staffName = sb.name; a.sortAt = b.sortAt;
  b.staffId = sa.id; b.staffName = sa.name; b.sortAt = aSort;
  [a, b].forEach(function (t) {
    var sched = scheduleFor(
      { appointmentServiceId: t.appointmentServiceId }, t.serviceId, t.startedAt);
    t.scheduledMinutes = sched.minutes;
    t.scheduledEndAt = sched.endsAt;
    pushTurn(t);
  });
  toast((kind === 'book' ? 'Swapped to match the book — ' : 'Swapped — ') +
    (a.clientName || a.serviceName) + ' to ' + sb.name + ', ' +
    (b.clientName || b.serviceName) + ' to ' + sa.name + '.', 'ok');
  return true;
}

function openSwapConfirm(turnId, otherId, kind) {
  var k = kind === 'board' ? 'board' : 'book';
  var why = swapReasonFor(turnOf(turnId), turnOf(otherId), k);
  if (why) { toast(why, 'warn'); return; }
  ui.swapConfirm = { turnId: turnId, otherId: otherId, kind: k };
}
function closeSwapConfirm() { ui.swapConfirm = null; }
function confirmSwap() {
  if (!ui.swapConfirm) return;
  swapTurns(ui.swapConfirm.turnId, ui.swapConfirm.otherId, ui.swapConfirm.kind);
  ui.swapConfirm = null;
}

// What the swap sheet is about, read back off the board and the book every
// time rather than copied in when it opened — including whether the swap still
// resolves anything, so a book that moves under the sheet takes the button off
// it instead of leaving one that will fail when pressed.
var swapConfirmDetail = computed(function () {
  var s = ui.swapConfirm;
  if (!s) return null;
  var a = turnOf(s.turnId), b = turnOf(s.otherId);
  if (!a || !b) return null;
  var sa = staffOf(a.staffId), sb = staffOf(b.staffId);

  function side(turn, to) {
    var link = bookedServiceById(turn.appointmentServiceId);
    var from = staffOf(turn.staffId);
    return {
      id: turn.id,
      from: from ? from.name : '?',
      to: to ? to.name : '?',
      letter: turn.serviceLetter,
      color: colorOfTurn(turn),
      clientName: (link && link.appt.clientName) || turn.clientName || '',
      // For the note icon, and the warning when their notes name `to`.
      clientId: link && link.appt.clientId != null ? link.appt.clientId
        : turn.clientId == null ? null : turn.clientId,
      toId: to ? to.id : null,
      serviceName: (link && link.svc.name) || turn.sourceServiceName || turn.serviceName,
      bookedAt: link ? link.svc.startsAt : null,
      bookedTech: link ? (link.svc.techs || []).filter(Boolean).join(', ') : '',
      running: turn.endedAt == null,
      elapsed: formatDuration((turn.endedAt == null ? now.value : turn.endedAt) -
        turn.startedAt)
    };
  }

  /* A hand-made swap the book has an opinion about.
   *
   * Not a block — the desk is standing in the room and the booking system is
   * not, so what they say happened wins. But a square that currently agrees
   * with its booking and is about to stop agreeing is worth one line, because
   * it will come back as a flag on the board afterwards and it should not be a
   * surprise when it does.
   */
  var warn = null;
  if (s.kind === 'board' && scheduling.enabled.value) {
    var agrees = [[a, sa], [b, sb]].filter(function (pair) {
      return bookNames(bookedTechLabels(pair[0]), pair[1]);
    }).map(function (pair) { return pair[0].clientName || pair[0].serviceName; });
    if (agrees.length) {
      warn = 'The book has ' + agrees.join(' and ') + ' where they are now, so the ' +
        'swap will leave ' + (agrees.length > 1 ? 'those squares' : 'that square') +
        ' flagged against the booking.';
    }
  }

  return {
    a: side(a, sb),
    b: side(b, sa),
    kind: s.kind,
    warn: warn,
    blocked: swapReasonFor(a, b, s.kind)
  };
});

/* ---------- actions: staff ---------- */

function nextSortOrder(list) {
  return list.reduce(function (m, x) { return Math.max(m, x.sortOrder); }, 0) + 10;
}

function addStaff(name, serviceIds) {
  var clean = String(name || '').trim();
  if (!clean) return null;
  var s = {
    id: sync.uuid(), name: clean, serviceIds: (serviceIds || []).slice(),
    sortOrder: nextSortOrder(state.staff), active: true, ownClientsOnly: false
  };
  state.staff.push(s);
  pushStaff(s);
  setPresent(s.id, true);
  return s;
}

function updateStaff(staffId, patch) {
  var s = staffOf(staffId);
  if (!s) return;
  if (typeof patch.name === 'string' && patch.name.trim()) s.name = patch.name.trim();
  if (Array.isArray(patch.serviceIds)) s.serviceIds = patch.serviceIds.slice();
  if (typeof patch.ownClientsOnly === 'boolean') s.ownClientsOnly = patch.ownClientsOnly;
  pushStaff(s);
}

function toggleStaffService(staffId, serviceId) {
  var s = staffOf(staffId);
  if (!s) return;
  var i = s.serviceIds.indexOf(serviceId);
  if (i === -1) s.serviceIds.push(serviceId);
  else s.serviceIds.splice(i, 1);
  pushStaff(s);
}

// Full turns the floor has already finished — what a late arrival missed, and
// what a skipped tech is measured against. Skipped techs are excluded: they are
// not working the rotation, so holding the floor's number down with their own
// count would mean they never fall behind and never accrue a skip. Somebody
// whose day has run out is excluded for the first half of that reason: their
// count stopped when their hours did, and a technician marked in at half five
// should join on what the floor has actually done, not on what the person who
// went home at five had.
function floorTurnsCompleted() {
  var others = rotationStaff.value.filter(function (s) {
    return !isSkipped(s.id) && !isShiftSpent(s.id);
  });
  if (!others.length) return 0;
  var min = Infinity;
  others.forEach(function (s) { min = Math.min(min, turnCredit(s.id)); });
  return min === Infinity ? 0 : Math.max(0, Math.floor(min));
}

// Called as a turn is booked, before it lands. Walks the rotation as it would
// stand with nobody bypassed and charges every skipped tech the turn passed
// over on its way to whoever took it — the literal meaning of "skipped".
//
// It self-regulates: each skip costs a turn's credit, so a bypassed tech drops
// down the queue and is not charged again until the rotation comes back round
// to them. Booking five turns while one person is out does not cost them five.
function chargeSkips(toStaffId, serviceId) {
  var contenders = fullQueue.value.filter(function (s) {
    return !serviceId || s.serviceIds.indexOf(serviceId) !== -1;
  });
  for (var i = 0; i < contenders.length; i++) {
    var s = contenders[i];
    if (s.id === toStaffId) break;      // the turn reached its taker
    if (!isSkipped(s.id)) break;        // ...or someone who could have had it
    /* Gone home is not passing. A skip is a turn somebody was there for and did
     * not take — that is what the red mark in the column means and what the
     * day-end tally counts — and a technician who left at five was not offered
     * the six o'clock and did not decline it.
     *
     * It matters now because the board marks people done by itself when their
     * out time passes; without this, every technician who went home early would
     * collect skip marks all evening for turns taken after they had gone. It is
     * `done` alone, and not every bypass: a break and an "other" are somebody
     * standing in the room, which is exactly the pass this is here to charge.
     * They are still walked PAST rather than broken on, because the rotation
     * behind them has not changed — only the bill. */
    if (skipReasonIs(s.id, 'done')) continue;
    // Not in yet is charged too, but once, as the turns the floor had finished
    // by their start — see `sweepWorkHours`. A red square here would be twice.
    if (skipReasonIs(s.id, 'notin')) continue;
    var d = state.dayStaff[s.id];
    // Recorded with the square index so the red mark lands where the turn
    // would have gone, not at the bottom of the column — and with why, for the
    // sheet that takes one off (js/rules/skip-marks.js).
    var r = skipReasonOf(s.id);
    var taker = staffOf(toStaffId);
    var svc = serviceId ? serviceById.value[serviceId] : null;
    d.skipWhy = skipRules.withWhy(d.skipWhy, d.skips.length, {
      at: Date.now(), kind: 'passed',
      reason: r && r.auto ? 'block' : (r ? r.id : 'other'),
      label: r ? r.label.replace(/ until .*$/, '') : '',
      takerName: taker ? taker.name : '', serviceName: svc ? svc.name : ''
    });
    d.skips.push(slotsFor(s.id).length);
    pushDayStaff(s.id);
  }
}

/* ---------- taking a red square off ----------
 *
 * Kevin, 2026-10-25: right-click a red square and take it off, from a sheet
 * that says why it was charged. Taking it off gives back the turn's credit it
 * cost, which moves them up the queue — the sheet says that too. */
function openSkipRemove(staffId, ordinal) {
  var d = state.dayStaff[staffId];
  if (!d || skipRules.skipIndexForOrdinal(d.skips, ordinal) === -1) return;
  ui.skipRemove = { staffId: staffId, ordinal: ordinal };
}

function closeSkipRemove() { ui.skipRemove = null; }

var skipRemoveDetail = computed(function () {
  var q = ui.skipRemove;
  if (!q) return null;
  var d = state.dayStaff[q.staffId];
  var s = staffOf(q.staffId);
  if (!d || !s) return null;
  var index = skipRules.skipIndexForOrdinal(d.skips, q.ordinal);
  if (index === -1) return null;
  var record = (d.skipWhy || [])[index] || null;
  return {
    staffId: s.id, name: s.name, index: index,
    turn: missedTurns(s.id) + d.skips[index] + q.ordinal + 1,
    story: skipRules.skipStory(record, s.name, formatClock),
    recorded: !!record
  };
});

function confirmSkipRemove() {
  var q = ui.skipRemove;
  var det = skipRemoveDetail.value;
  ui.skipRemove = null;
  if (!q || !det) return;
  var d = state.dayStaff[q.staffId];
  var next = skipRules.withoutSkip(d.skips, d.skipWhy, det.index);
  d.skips = next.skips;
  d.skipWhy = next.why;
  pushDayStaff(q.staffId);
  toast('Red square taken off ' + det.name + '’s column.', 'ok');
}

// Bypass a tech without taking their column off the board: extended break,
// split shift, or done for the day. Their running turn, if any, is left alone.
// Why somebody is out of the rotation. Short keys because they are stored;
// the labels are what the menu and the column badge show.
var SKIP_REASONS = [
  { id: 'break', label: 'Break', badge: 'Break' },
  { id: 'done', label: 'Done for today', badge: 'Done' },
  { id: 'other', label: 'Other', badge: 'Skip' }
];

function skipReasonOf(staffId) {
  var d = state.dayStaff[staffId];
  if (d && d.skipped) {
    return SKIP_REASONS.find(function (r) { return r.id === d.skipReason; }) ||
      SKIP_REASONS[SKIP_REASONS.length - 1];
  }
  // Mangomint's day, when the desk has said nothing: `auto` marks it as the
  // board's reading rather than somebody's decision.
  var o = mmOutOf(staffId);
  if (!o) return null;
  if (o.reason === 'notin') {
    return { id: 'notin', label: 'Not in yet — starts at ' + formatClock(o.until),
             badge: 'In ' + formatClock(o.until), auto: true };
  }
  return { id: 'break', label: (o.label || 'Break') + ' until ' + formatClock(o.until),
           badge: 'Break', auto: true };
}

// Whether somebody is bypassed for one particular reason. Through
// `skipReasonOf` rather than off `d.skipReason` directly, so an unrecognised
// stored value falls through to Other in one place instead of two.
function skipReasonIs(staffId, id) {
  var r = skipReasonOf(staffId);
  return !!r && r.id === id;
}

/* ---- how long somebody has been out of the rotation ----
 *
 * `skips` counts the squares the floor took past a technician; this is the
 * time. They are different questions and the day-end idle total needs the
 * second one: a break is an hour nobody was idle for, but it is nought
 * squares if the floor was quiet through it.
 *
 * Kept as spans rather than a running total because a bypass is an interval
 * and the day is full of things that overlap it — most of all the technician's
 * own last service, which carries on running while "done for today" is being
 * ticked. Totals cannot be un-double-counted after the fact; intervals can be
 * merged. See `idleMsFor`.
 */
function skipSpansFor(staffId) {
  var d = state.dayStaff[staffId];
  return (d && d.skipSpans) || [];
}

function openSkipSpan(d) {
  var spans = d.skipSpans || (d.skipSpans = []);
  var last = spans[spans.length - 1];
  if (last && last.to == null) return;        // already open; nothing to do
  spans.push({ from: Date.now(), to: null });
}

function closeSkipSpan(d) {
  var spans = d.skipSpans || (d.skipSpans = []);
  var last = spans[spans.length - 1];
  if (!last || last.to != null) return;
  last.to = Date.now();
}

/* `reason` is required to skip somebody; passing none brings them back.
 *
 * `quiet` suppresses the toast for a caller that says it better itself — the
 * end-of-day sweep, which has a reason to give and would otherwise announce a
 * technician going home as though somebody had just clicked it.
 */
function setSkipped(staffId, reason, quiet) {
  var s = staffOf(staffId);
  var d = state.dayStaff[staffId];
  if (!s || !d || !d.present) {
    toast('Mark them in today first.', 'warn');
    return;
  }
  var pick = reason
    ? (SKIP_REASONS.find(function (r) { return r.id === reason; }) || SKIP_REASONS[2])
    : null;
  // The clock on the way out and off it on the way back, whatever the reason:
  // a technician out of the rotation is not a technician standing idle, and
  // the day-end total should not read as though they were.
  if (pick) openSkipSpan(d); else closeSkipSpan(d);
  if (!pick) releaseMmHold(staffId, d);
  d.skipped = !!pick;
  d.skipReason = pick ? pick.id : '';
  pushDayStaff(staffId);
  if (quiet) return;
  toast(
    pick
      ? s.name + ' is being skipped — ' + pick.label.toLowerCase() + '.'
      : s.name + ' is back in the rotation.',
    pick ? 'info' : 'ok'
  );
}

function toggleSkip(staffId) {
  setSkipped(staffId, isSkipped(staffId) ? null : 'other');
}

// Pass on one turn without going out of the rotation: the same red square and
// the same cost, but they stay in the queue for the next one.
function skipOneTurn(staffId) {
  var s = staffOf(staffId);
  var d = state.dayStaff[staffId];
  if (!s || !d || !d.present) {
    toast('Mark them in today first.', 'warn');
    return false;
  }
  d.skipWhy = skipRules.withWhy(d.skipWhy, d.skips.length, { at: Date.now(), kind: 'pass' });
  d.skips.push(slotsFor(staffId).length);
  pushDayStaff(staffId);
  ui.emptyPick = null;
  toast(s.name + ' passed on this turn.', 'ok');
  return true;
}

/* Own clients only, for today — the right-click on their name. Stored as null
 * when it lands back on their usual, so the roster's default holds for the rest
 * of the day (js/rules/own-clients.js). Nothing already on the board moves: a
 * square running on them keeps running, and only what the rotation offers next
 * changes. */
function setOwnClients(staffId, on) {
  var s = staffOf(staffId);
  var d = state.dayStaff[staffId];
  if (!s || !d || !d.present) {
    toast('Mark them in today first.', 'warn');
    return;
  }
  d.ownClientsOnly = ownRules.dayValueFor(s, on);
  pushDayStaff(staffId);
  toast(on
    ? s.name + ' is only taking their own clients today — out of the rotation.'
    : s.name + ' is back in the rotation.', on ? 'info' : 'ok');
}

function openStaffMenu(staffId, x, y) { ui.staffMenu = { staffId: staffId, x: x, y: y }; }
function closeStaffMenu() { ui.staffMenu = null; }

/* quiet:     suppress the per-tech toast so a bulk add can report once.
 * missedFor: what a first-time arrival joins on. Omitted, it is worked out
 *            here, which is right for one person walking in. A caller marking
 *            several people in at once has to read it once and pass it — see
 *            confirmAddStaff for what goes wrong otherwise.
 */
function setPresent(staffId, present, quiet, missedFor) {
  var s = staffOf(staffId);
  if (!s) return;
  if (!present && isBusy(staffId)) {
    toast('Stop ' + s.name + "'s service before marking them out.", 'warn');
    return;
  }
  var existing = state.dayStaff[staffId];
  // Carried across either way: the time somebody spent out of the rotation
  // this morning is still true this afternoon. Copied rather than shared, so
  // closing the open span below cannot reach back into the old row.
  var spans = existing && existing.skipSpans
    ? existing.skipSpans.map(function (s) { return { from: s.from, to: s.to }; })
    : [];

  if (present) {
    // Only charge missed turns the first time they are marked in today;
    // toggling back after a lunch break must not re-penalise them.
    var missed = existing ? existing.missedTurns
      : (missedFor == null ? floorTurnsCompleted() : missedFor);
    state.dayStaff[staffId] = {
      present: true,
      presentSince: existing && existing.present ? existing.presentSince : Date.now(),
      missedTurns: missed,
      // Marking someone in is putting them back on the floor, so it clears the
      // bypass and its reason. What they already sat out stays on the board.
      skipped: false,
      skipReason: '',
      skips: existing && existing.skips ? existing.skips : [],
      skipWhy: existing && existing.skipWhy ? existing.skipWhy : [],
      skipSpans: spans,
      // Their hours for the day survive being marked out and back in, the same
      // as the skip spans above: "off at five" was a fact about today when it
      // was typed and is still one after a lunch break. Null for somebody new
      // to the day, which is the board working them out again from scratch.
      shiftStartAt: existing ? existing.shiftStartAt : null,
      shiftEndAt: existing ? existing.shiftEndAt : null,
      // Carried, like the hours and the spans above: "already swept off at five"
      // is still true after a lunch break, and losing it here would let the
      // sweep mark somebody done a second time the minute they came back.
      doneSweptAt: existing ? existing.doneSweptAt : null,
      workHours: workHoursRow(existing ? existing.workHours : null),
      // Cleared, unlike the rest. The notice is "booked and not on the board",
      // and being marked in is that question answered rather than waved away —
      // so if they are marked out again later, it is worth saying once more.
      missingDismissed: false,
      // Carried: "only her own clients today" survives a lunch break.
      ownClientsOnly: existing && existing.ownClientsOnly != null ? existing.ownClientsOnly : null
    };
    // Back on the floor, so the idle clock starts again from here.
    closeSkipSpan(state.dayStaff[staffId]);
    // Late means the floor has started, not merely that someone else is in:
    // marking the opening crew in one at a time must leave the roster alone.
    if (!existing && state.turns.length) moveToLastColumn(staffId);
    if (!existing && missed > 0 && !quiet) {
      toast(s.name + ' starts on turn ' + (missed + 1) + ' — ' + missed + ' missed.', 'info');
    }
  } else {
    state.dayStaff[staffId] = {
      present: false,
      presentSince: existing ? existing.presentSince : Date.now(),
      missedTurns: existing ? existing.missedTurns : 0,
      skipped: false,
      skipReason: '',
      skips: existing && existing.skips ? existing.skips : [],
      skipWhy: existing && existing.skipWhy ? existing.skipWhy : [],
      skipSpans: spans,
      shiftStartAt: existing ? existing.shiftStartAt : null,
      shiftEndAt: existing ? existing.shiftEndAt : null,
      doneSweptAt: existing ? existing.doneSweptAt : null,
      workHours: workHoursRow(existing ? existing.workHours : null),
      // Kept: somebody marked out is back in the state the notice is about, and
      // an answer given ten minutes ago is still an answer.
      missingDismissed: !!(existing && existing.missingDismissed),
      ownClientsOnly: existing && existing.ownClientsOnly != null ? existing.ownClientsOnly : null
    };
    // Off the board is off the floor. The same span that a break opens, so
    // somebody marked out at three does not read as idle until closing —
    // and if they are marked back in, the span closes and the clock resumes.
    if (existing && existing.present) openSkipSpan(state.dayStaff[staffId]);
  }
  pushDayStaff(staffId);
}

/* ---------- adding staff to the day from the board ---------- */

// Everyone on the roster who is not on the board yet.
var addableStaff = computed(function () {
  return rosterStaff.value.filter(function (s) {
    var d = state.dayStaff[s.id];
    return !d || !d.present;
  });
});

// `ids` pre-ticks people — the missing-from-the-book prompt uses it. Guarded
// because the + button is wired straight to a click handler, which would
// otherwise hand this a DOM event.
function openAddStaff(ids) {
  ui.addStaff = { ids: Array.isArray(ids) ? ids.slice() : [] };
}
function closeAddStaff() { ui.addStaff = null; }

function toggleAddStaff(staffId) {
  if (!ui.addStaff) return;
  var i = ui.addStaff.ids.indexOf(staffId);
  if (i === -1) ui.addStaff.ids.push(staffId);
  else ui.addStaff.ids.splice(i, 1);
}

// Nobody has been on the board yet today, so they join on the turns the floor
// has already finished. Someone coming back from being marked out keeps the
// place they had, which is why the two are counted separately.
function isNewToday(staffId) { return !state.dayStaff[staffId]; }

/* Everyone goes in at once, and on the SAME number — read once, before the
 * first of them is added, and handed to every setPresent in the batch.
 *
 * The note that stood here said the reading could not move anyway, because a
 * new column joins on exactly the floor's number and so cannot lower the
 * minimum. That is true of a floor that has somebody on it. It is not true of
 * an empty one: floorTurnsCompleted() has no columns to take a minimum over and
 * answers 0, so the first person added anchors at 0 and every person after them
 * is measured against whatever turns that first person already has on the
 * board. Marking eight people in one at a time therefore gave the first of them
 * turn 1 and slashed the other seven back to turn 3.
 *
 * An empty floor is exactly what the morning's prompt is answered on, so this
 * was not a corner: it is what happens whenever a day is set up on a board that
 * already has turns — a station signing back in, or a roster that failed to
 * save. Snapshotting is also the right answer on its own terms. They all walked
 * in together; they should all join on the same turn.
 */
function confirmAddStaff() {
  if (!ui.addStaff || !ui.addStaff.ids.length) return 0;
  var ids = ui.addStaff.ids.slice();
  var missed = floorTurnsCompleted();
  var fresh = ids.filter(isNewToday).length;
  ids.forEach(function (id) { setPresent(id, true, true, missed); });
  // Several at once from the Add staff sheet: same as marking everybody in.
  // Anyone who is genuinely late was appended to the end by setPresent, and
  // being last in the columns is what keeps them last in the queue.
  reorderArrivals();
  ui.addStaff = null;
  var names = ids.map(function (id) {
    var s = staffOf(id);
    return s ? s.name : '';
  }).filter(Boolean);
  var note = '';
  if (fresh && missed > 0) {
    note = ' — ' + (fresh === ids.length ? 'starting' : fresh + ' starting') +
      ' on turn ' + (missed + 1);
  }
  toast(names.join(', ') + (names.length === 1 ? ' is' : ' are') + ' in' + note, 'ok');
  return ids.length;
}

// Soft delete: tt_turns cascades from tt_staff, so a hard delete would erase
// the tech's archived history along with them.
function deleteStaff(staffId) {
  var s = staffOf(staffId);
  if (!s) return;
  if (isBusy(staffId)) {
    toast('Stop ' + s.name + "'s service first.", 'warn');
    return;
  }
  s.active = false;
  pushStaff(s);
  if (state.dayStaff[staffId]) setPresent(staffId, false);
  toast(s.name + ' removed — past turns kept for reporting.', 'ok');
}

function restoreStaff(staffId) {
  var s = staffOf(staffId);
  if (!s) return;
  s.active = true;
  pushStaff(s);
}

function reorderStaff(fromIdx, toIdx) {
  var list = rosterStaff.value.slice();
  if (fromIdx === toIdx || !list[fromIdx] || !list[toIdx]) return;
  var moved = list.splice(fromIdx, 1)[0];
  list.splice(toIdx, 0, moved);
  list.forEach(function (s, i) {
    var order = (i + 1) * 10;
    if (s.sortOrder !== order) { s.sortOrder = order; pushStaff(s); }
  });
  // Every path that moves a column comes through here — the board drag and
  // appending a late arrival — so this is the one place the arrival order has
  // to be brought back in line with it.
  reorderArrivals();
}

// Somebody joining a day that is already under way goes on the right-hand end,
// where a name written at the bottom of the paper grid would have gone. It
// ignores the order lock on purpose: appending a column does not disturb the
// order of the ones already there, which is what the lock is protecting.
function moveToLastColumn(staffId) {
  var roster = rosterStaff.value;
  var from = roster.findIndex(function (s) { return s.id === staffId; });
  if (from === -1 || from === roster.length - 1) return;
  reorderStaff(from, roster.length - 1);
}

/* ---------- the columns are the arrival order ----------
 *
 * Whose turn it is comes down to turn credit first, and then — among people
 * on the same number — to who has been waiting longest. On a morning where
 * nobody has worked yet, that second rule is the whole rotation, and it reads
 * off the times people were marked in.
 *
 * Marking-in order and column order used to be able to disagree, and when
 * they did the board was quietly unarguable with: dragging a name to the
 * front of the grid moved the column and changed nothing about who was next,
 * because column position was only the last tie-break of three. The floor's
 * own model is simpler than that — the columns ARE the running order — so a
 * drag now says so, and the arrival times follow the grid.
 *
 * The times are PERMUTED, not invented. Whatever set of mark-in times today
 * has is kept intact and handed out in the new column order, so the earliest
 * one goes to whoever is now first. The salon really did have somebody in at
 * five past nine; the drag is a statement about who that was.
 *
 * The consequence to know: a header's "marked in at" follows the columns too.
 * That is not a side effect to be tidied away — it is the same fact, and if
 * the two could disagree the arrival order would not really have moved.
 *
 * Missed turns are untouched. What somebody sat out before they got here is
 * charged once, when they are marked in, and it is not a matter of order.
 */
function reorderArrivals() {
  var present = presentStaff.value;               // already in the new order
  if (present.length < 2) return;
  var times = present
    .map(function (s) { return presentSince(s.id); })
    .sort(function (a, b) { return a - b; });
  present.forEach(function (s, i) {
    var d = state.dayStaff[s.id];
    if (!d || d.presentSince === times[i]) return;
    d.presentSince = times[i];
    pushDayStaff(s.id);
  });
}

function movePresentStaff(fromPos, toPos) {
  if (ui.lockOrder) return;
  var present = presentStaff.value;
  var moving = present[fromPos];
  var target = present[toPos];
  if (!moving || !target) return;
  var roster = rosterStaff.value;
  reorderStaff(roster.indexOf(moving), roster.indexOf(target));
}

/* ---------- actions: services ---------- */

// Zero, half or full — anything else is read as a full turn.
var TURN_WEIGHTS = [0, 0.5, 1];

function cleanWeight(v) {
  var n = Number(v);
  return TURN_WEIGHTS.indexOf(n) === -1 ? 1 : n;
}

function weightLabel(w) {
  if (Number(w) === 0) return 'No turn';
  return Number(w) === 0.5 ? 'Half turn' : 'Full turn';
}

// The badge on a service chip. Blank for a full turn, which is the norm and
// does not need saying.
function weightBadge(w) {
  if (Number(w) === 0) return '0';
  return Number(w) === 0.5 ? '½' : '';
}

// What a service's usual length is allowed to be. Five minutes is the
// shortest thing anyone books; four hours is longer than the longest, and a
// ceiling is what stops a typed-in extra digit putting a square hours from
// being over. The two boxes in Settings are bounded by these same numbers, so
// the form and the store cannot disagree about what a valid length is.
var MIN_SERVICE_MINUTES = 5;
var MAX_SERVICE_MINUTES = 240;

// Blank, zero and nonsense all mean "no countdown on this one" — leaving it
// empty is a real answer. Anything else is held inside the range.
function cleanMinutes(value) {
  var n = Math.round(Number(value));
  if (!isFinite(n) || n <= 0) return null;
  return Math.min(MAX_SERVICE_MINUTES, Math.max(MIN_SERVICE_MINUTES, n));
}

/* A new service. The name is the only thing required of it.
 *
 * The key used to default to the name's first letter, and that default was the
 * whole reason a service could not be added without one: the box was there, but
 * leaving it empty took the letter anyway. A salon working entirely from the
 * mouse and the tablet got a keyboard full of bindings it never pressed, and two
 * services starting with the same letter got a silent collision out of it. So an
 * empty box is now a real answer here, the same as the Minutes box beside it —
 * see js/rules/services.js.
 */
function addService(name, shortcut, turnWeight, abbrev, defaultMinutes) {
  var clean = String(name || '').trim();
  if (!clean) return null;
  var key = cleanShortcut(shortcut);
  var s = {
    id: sync.uuid(), name: clean, shortcut: key,
    abbrev: serviceAbbrev(abbrev, clean, key),
    turnWeight: cleanWeight(turnWeight),
    defaultMinutes: cleanMinutes(defaultMinutes),
    quickAdd: true,
    // On for a new service too. A salon that has set the lobby screen up
    // expects something they just added to be on it; the switch beside it in
    // Settings is how a service is kept off, and that is a decision somebody
    // makes rather than one made for them at the moment of typing a name.
    selfCheckin: true,
    // Nothing to guess: the booking system's own id for this service is a fact
    // somebody looks up, not one derived from a name typed at the desk.
    bookingServiceId: '',
    color: PALETTE[state.services.length % PALETTE.length],
    sortOrder: nextSortOrder(state.services), active: true
  };
  state.services.push(s);
  pushService(s);
  return s;
}

// Two characters, as typed: an abbreviation is a label, so "GX" and "Gx" are
// both somebody's deliberate choice. The hotkey above it stays uppercased,
// because that one has to match a keystroke.
function cleanAbbrev(value) {
  return String(value == null ? '' : value).trim().slice(0, 2);
}

function updateService(serviceId, patch) {
  var s = serviceById.value[serviceId];
  if (!s) return;
  /* Whether the label on the square is one somebody chose or one this file
   * worked out, decided BEFORE anything moves — because both of the things it is
   * worked out from are editable on the same row, and after the fact there is no
   * telling a derived "P" from a typed one.
   *
   * A derived abbreviation follows its source: rename Pedicure to Manicure, or
   * take the key off it, and the square keeps up. One somebody typed is left
   * alone through both.
   */
  var wasDerived = s.abbrev === serviceAbbrev('', s.name, s.shortcut);

  if (typeof patch.name === 'string' && patch.name.trim()) s.name = patch.name.trim();
  // Emptying the key box is a real answer — it takes the service off the
  // keyboard — so this reads "was the field touched", not "is there something
  // in it". The board simply stops listening: see `shortcutMap`.
  if (typeof patch.shortcut === 'string') s.shortcut = cleanShortcut(patch.shortcut);
  // Emptying the abbreviation box hands the label back to the fallback instead
  // of blanking every square that service draws.
  if (typeof patch.abbrev === 'string') {
    s.abbrev = cleanAbbrev(patch.abbrev) || serviceAbbrev('', s.name, s.shortcut);
  } else if (wasDerived) {
    s.abbrev = serviceAbbrev('', s.name, s.shortcut);
  }
  if (patch.turnWeight != null) s.turnWeight = cleanWeight(patch.turnWeight);
  // An empty box is a real value here — it clears the countdown — so this
  // reads "was the field touched", not "is there something in it".
  if ('defaultMinutes' in patch) s.defaultMinutes = cleanMinutes(patch.defaultMinutes);
  // Off is a real answer too: anything that is not a side clears it.
  if ('sameTime' in patch) s.sameTime = sameTimeRules.cleanSide(patch.sameTime);
  if (patch.quickAdd != null) s.quickAdd = !!patch.quickAdd;
  if (patch.selfCheckin != null) s.selfCheckin = !!patch.selfCheckin;
  // An empty box is a real value here too — it is how a mistyped booking id is
  // taken back out — so this reads "was the field touched".
  if ('bookingServiceId' in patch) {
    s.bookingServiceId = booking.cleanServiceRef(patch.bookingServiceId);
  }
  if (typeof patch.color === 'string') s.color = patch.color;
  pushService(s);
}

function deleteService(serviceId) {
  var i = state.services.findIndex(function (x) { return x.id === serviceId; });
  if (i === -1) return;
  state.services.splice(i, 1);
  // Past turns keep their snapshot; only the staff mappings need cleaning.
  state.staff.forEach(function (s) {
    var j = s.serviceIds.indexOf(serviceId);
    if (j !== -1) { s.serviceIds.splice(j, 1); pushStaff(s); }
  });
  if (ui.armedServiceId === serviceId) ui.armedServiceId = null;
  sync.enqueue({ table: 'tt_services', type: 'delete', match: { id: serviceId } });
}

function reorderService(fromIdx, toIdx) {
  var list = activeServices.value.slice();
  if (fromIdx === toIdx || !list[fromIdx] || !list[toIdx]) return;
  var moved = list.splice(fromIdx, 1)[0];
  list.splice(toIdx, 0, moved);
  list.forEach(function (s, i) {
    var order = (i + 1) * 10;
    if (s.sortOrder !== order) { s.sortOrder = order; pushService(s); }
  });
}

/* ---------- actions: day ---------- */

function switchToToday() {
  state.businessDate = todayKey();
  ui.dateRolled = false;
  ui.armedServiceId = null;
  ui.cellMenuTurnId = null;
  ui.turnMenu = null;
  load(true);
}

function resetToday() {
  state.turns = [];
  // By date rather than by id: this clears the whole day by definition, and
  // enumerating the ids meant one DELETE round trip per turn on the board.
  sync.enqueue({
    table: 'tt_turns', type: 'delete', match: { business_date: state.businessDate }
  });
  Object.keys(state.dayStaff).forEach(function (id) {
    var d = state.dayStaff[id];
    d.missedTurns = 0;
    d.skipped = false;
    d.skips = [];
    d.skipWhy = [];
    d.presentSince = Date.now();
    // So the not-in count is worked out again over the empty board.
    d.workHours = Object.assign(workHoursOf(d), { missedAt: null });
    pushDayStaff(id);
  });
  ui.armedServiceId = null;
  ui.cellMenuTurnId = null;
  ui.turnMenu = null;
  toast("Today's board cleared.", 'ok');
}

/* ---------- ending the day ----------
 *
 * Closing the book is the end-of-day ritual, not a delete: every turn stays
 * exactly where it is and the day is still in History tomorrow. What it does
 * is stop the clocks — anything still running is stopped as of the close — and
 * shut the board to new turns, so a service added at ten to close does not
 * quietly land on a day somebody has already settled up.
 *
 * It is the salon's close, not a screen's: `closed_at` on the tt_days row for
 * the date, so every station in the building shuts together and a station that
 * signs out comes back to a day that is still shut. See tt_days in
 * server/schema/02-console-tables.sql.
 */

/* Keyed by date rather than by a bare flag, so the day rolling over opens the
 * board on its own — a closed Monday says nothing about Tuesday. `state.day` is
 * only ever the row for `businessDate`, so the date check is the load's job
 * rather than a comparison here.
 *
 * This used to be a localStorage marker, per device and deliberately so: the
 * note that stood here said a salon-wide close would need a row of its own in
 * the database, and called the smaller thing the honest one. It has a row now.
 * What tipped it was not the second screen in the back room but signing out:
 * a marker on a device is lost by the station that set it, and "have we shut up
 * shop?" turning out to be a fact about a browser tab is not something a front
 * desk should ever have to know.
 *
 * A station that closed the books under the old marker does not lose that on
 * the upgrade: the first load carries it into the row. See
 * adoptLegacyDayClosed.
 */
var dayClosed = computed(function () {
  return !!(state.day && state.day.closedAt);
});

// When, for the summary and for the clocks that stop at the close.
var dayClosedAt = computed(function () {
  return state.day ? state.day.closedAt : null;
});

/* Whether today's "who is in?" has been asked and answered.
 *
 * The answer is a row existing at all, not anything on it — including the
 * morning where the answer was "nobody else", which is a settled question and
 * not an empty one. See openDayFromBook.
 */
var dayOpened = computed(function () {
  return !!(state.day && state.day.startedAt);
});

// Opening and closing are the same upsert against the same row, so both go
// through here: whichever of the two happens first this morning creates it.
function setDay(startedAt, closedAt) {
  state.day = {
    businessDate: state.businessDate,
    startedAt: startedAt,
    closedAt: closedAt
  };
  pushDay();
}

/* ---- carrying the old marker across ----
 *
 * Before the close was a row, it was this key in localStorage. A station that
 * shut the books on the build before this one has the only record of it, and
 * the server has none — so without this, the first load after the upgrade
 * reopens a day somebody settled up, on the one evening of the year when the
 * takings are being counted.
 *
 * Runs at most once per station: the key is removed on sight, whatever is
 * decided about its contents. Wrong-date markers, unparseable ones and days the
 * server already knows are shut all take the same exit — there is nothing to
 * carry across, and leaving the key would mean asking again tomorrow.
 *
 * Only the tenant-namespaced key, never the bare one. `tenant.ns` falls back to
 * the un-namespaced base when it does not know the salon yet, and adopting that
 * is the exact confusion the namespacing exists to prevent: closing salon A's
 * Tuesday and then signing in as salon B must not hand B a board that says it
 * has already settled up. Hence the two guards below — a tenant, and a session
 * whose load actually returned this salon's day.
 */
var LEGACY_DAY_CLOSED_BASE = 'dreamspa.dash.dayClosed';

function adoptLegacyDayClosed() {
  if (!tenant.get() || !sync.auth.user) return;
  var key = tenant.ns(LEGACY_DAY_CLOSED_BASE);
  var raw = null;
  try {
    raw = window.localStorage.getItem(key);
    if (raw == null) return;
    window.localStorage.removeItem(key);
  } catch (e) { return; }

  var v = null;
  try { v = JSON.parse(raw); } catch (e) { return; }
  if (!v || !v.date) return;
  // A marker for another date says nothing about this one, and the row it
  // would have belonged to is a day nobody is looking at.
  if (v.date !== state.businessDate) return;
  // Already shut on the server — another station got here first, and its close
  // is the better record because it is the one every screen is reading.
  if (dayClosed.value) return;

  var at = v.at || Date.now();
  setDay(state.day ? state.day.startedAt : firstTurnAt() || at, at);
}

/* ---- the storey above `bookTurn`'s guard ----
 *
 * `bookTurn` refuses on a closed day, and that is the floor: every path that
 * puts a square up goes through it, so nothing gets past. What the floor
 * cannot do is stop the way in from being offered. The picker still opened and
 * filled itself in, the assign sheet still worked out who should take it, a
 * hotkey still armed a service, a dragged booking still lit up the column it
 * was aimed at — and then the click at the end of all that was refused. A
 * board that lets somebody do four things and then says no on the fifth reads
 * as broken rather than shut.
 *
 * So the ways in are closed too, and they say the same sentence when they are
 * asked, out of the same place — one wording for one rule, wherever it is met.
 */
var CLOSED_NOTE = 'The day is closed. Reopen it from End day to put another service up.';

function refuseWhileClosed() {
  if (!dayClosed.value) return false;
  toast(CLOSED_NOTE, 'warn');
  return true;
}

var runningTurns = computed(function () {
  return state.turns.filter(function (t) { return t.endedAt == null; });
});

// The earliest thing that happened on the board today, or null on an empty one.
// Only ever needed to date a day that was worked without being opened.
function firstTurnAt() {
  var first = null;
  state.turns.forEach(function (t) {
    if (first == null || t.startedAt < first) first = t.startedAt;
  });
  return first;
}

function endDay() {
  var at = Date.now();
  var stopped = 0;
  state.turns.forEach(function (t) {
    if (t.endedAt != null) return;
    t.endedAt = at;
    pushTurn(t);
    stopped += 1;
  });
  // Closing a day nobody opened — a Sunday the book knew nothing about, worked
  // by hand — still needs a row, and `startedAt` is the first thing that
  // happened on it rather than this moment.
  setDay(state.day ? state.day.startedAt : firstTurnAt() || at, at);
  ui.armedServiceId = null;
  ui.requestMode = false;
  ui.cellMenuTurnId = null;
  ui.turnMenu = null;
  ui.endDay = { step: 'summary' };
  toast(stopped
    ? 'Day closed — ' + stopped + (stopped === 1 ? ' service' : ' services') + ' stopped.'
    : 'Day closed.', 'ok');
}

function reopenDay() {
  // Only the door is reopened. The turns stopped by the close stay stopped:
  // they were stopped at a real time, and reopening is about being able to add
  // the client who has just walked in, not about undoing the evening.
  //
  // The row stays, with closed_at back to null. Dropping it instead would
  // reopen the day and un-ask the morning's question in one move, and the desk
  // would be greeted by "who else is in today?" at seven in the evening.
  if (state.day) setDay(state.day.startedAt, null);
  ui.endDay = null;
  toast('Day reopened — the board takes turns again.', 'ok');
}

function openEndDay() {
  ui.endDay = { step: dayClosed.value ? 'summary' : 'confirm' };
}

function closeEndDay() { ui.endDay = null; }

/* ---------- what the day was ----------
 *
 * Read off the board itself rather than stored anywhere: the turns, the day's
 * missed walk-ins and who was in are all already here, and a summary that is
 * derived cannot disagree with the grid it is summarising.
 */
function hourKey(ms) { return new Date(ms).getHours(); }

function hourLabel(h) {
  var d = new Date(2000, 0, 1, h, 0, 0);
  return d.toLocaleTimeString([], { hour: 'numeric' });
}

var daySummary = computed(function () {
  var turns = state.turns;
  var now = Date.now();
  var svcById = serviceById.value;

  var credit = 0, requests = 0, booked = 0, servedMs = 0, ended = 0;
  var longest = null;
  var clients = {};
  var byStaff = {};
  var byService = {};
  var byHour = {};
  var firstStart = null, lastEnd = null;

  turns.forEach(function (t) {
    credit += t.turnWeight;
    if (t.requested) requests += 1;
    if (t.appointmentServiceId || t.appointmentId) booked += 1;

    // A client is a visit, not a square: a pedicure and a manicure on one
    // appointment are one client seen, counted once. Never the client id —
    // every walk-in shares Mangomint's one "Walk In" record. See visit-key.js.
    clients[visitKey(t)] = true;

    if (firstStart == null || t.startedAt < firstStart) firstStart = t.startedAt;
    var end = t.endedAt == null ? now : t.endedAt;
    if (lastEnd == null || end > lastEnd) lastEnd = end;

    if (t.endedAt != null) {
      var ms = t.endedAt - t.startedAt;
      servedMs += ms;
      ended += 1;
      if (!longest || ms > longest.ms) longest = { ms: ms, turn: t };
    }

    var h = hourKey(t.startedAt);
    var slot = byHour[h] || (byHour[h] = { hour: h, turns: 0, missed: 0 });
    slot.turns += 1;

    var s = byStaff[t.staffId] || (byStaff[t.staffId] = {
      staffId: t.staffId, name: t.staffName || 'Removed tech',
      turns: 0, credit: 0, requests: 0, serviceMs: 0, lastEnd: null,
      idleMs: idleMsFor(t.staffId)
    });
    s.turns += 1;
    s.credit += t.turnWeight;
    if (t.requested) s.requests += 1;
    if (t.endedAt != null) {
      s.serviceMs += t.endedAt - t.startedAt;
      if (s.lastEnd == null || t.endedAt > s.lastEnd) s.lastEnd = t.endedAt;
    }

    /* The service mix counts SERVICES, so a merged square appears once under
     * each of the services it records. That is the one place on this summary
     * where the two questions come apart: "how many pedicures did we do today"
     * is a question about work performed, and a pedicure done alongside a
     * manicure was still a pedicure.
     *
     * The credit is not split with it. What the square counted for is one
     * number the desk set for the sitting as a whole, and there is no honest
     * way to divide it between the lines — so it is filed against the service
     * the square is, and the others add to the count without adding to the
     * credit. Every other total on this summary is per square and unaffected:
     * `credit` and `byStaff` above are what the rotation ran on. */
    servicesOfTurn(t).forEach(function (s, i) {
      var mix = byService[s.name] || (byService[s.name] = {
        name: s.name, letter: s.letter,
        color: (svcById[s.serviceId] || {}).color || DEFAULT_SERVICE_COLOR,
        turns: 0, credit: 0
      });
      mix.turns += 1;
      if (i === 0) mix.credit += t.turnWeight;
    });
  });

  // Everyone who was in counts, including whoever never got a turn — an empty
  // row is the most useful line on the table when the question is why.
  presentStaff.value.forEach(function (s) {
    if (byStaff[s.id]) return;
    byStaff[s.id] = {
      staffId: s.id, name: s.name, turns: 0, credit: 0,
      requests: 0, serviceMs: 0, lastEnd: null,
      // The row this matters most on: somebody who was in all day and never
      // got a square. "0 turns" says it happened; the idle figure says how
      // long it went on for.
      idleMs: idleMsFor(s.id)
    };
  });

  var missedRows = missed.rows.slice().sort(function (a, b) {
    return a.createdAt - b.createdAt;
  }).map(function (r) {
    return {
      id: r.id,
      at: r.createdAt,
      partySize: r.partySize || 0,
      services: (r.serviceIds || []).map(function (id) {
        var svc = svcById[id];
        return svc ? svc.name : 'Removed service';
      }),
      waitMinutes: r.waitMinutes == null ? null : r.waitMinutes
    };
  });

  var missedPeople = 0;
  missedRows.forEach(function (r) {
    missedPeople += r.partySize;
    var h = hourKey(r.at);
    var slot = byHour[h] || (byHour[h] = { hour: h, turns: 0, missed: 0 });
    slot.missed += r.partySize;
    if (firstStart == null || r.at < firstStart) firstStart = r.at;
    if (lastEnd == null || r.at > lastEnd) lastEnd = r.at;
  });

  // The gap between one turn round and the next: how often the floor was
  // starting a fresh row of services. The number the desk feels as "how busy
  // were we", and the one that says what a missed walk-in cost.
  var rowTimes = rowStartTimes.value.filter(function (v) { return v != null; })
    .slice().sort(function (a, b) { return a - b; });
  var gapMs = null;
  if (rowTimes.length > 1) {
    gapMs = (rowTimes[rowTimes.length - 1] - rowTimes[0]) / (rowTimes.length - 1);
  }

  var hours = Object.keys(byHour).map(function (k) { return byHour[k]; })
    .sort(function (a, b) { return a.hour - b.hour; });
  var busiest = null;
  hours.forEach(function (h) { if (!busiest || h.turns > busiest.turns) busiest = h; });
  var peak = hours.reduce(function (n, h) { return Math.max(n, h.turns + h.missed); }, 0);
  hours.forEach(function (h) {
    h.label = hourLabel(h.hour);
    h.share = peak ? h.turns / peak : 0;
    h.missedShare = peak ? h.missed / peak : 0;
  });

  var staff = Object.keys(byStaff).map(function (k) { return byStaff[k]; })
    .sort(function (a, b) { return b.credit - a.credit || a.name.localeCompare(b.name); });

  // Idle across the whole floor. Summed rather than measured off the day,
  // because it is person-time: two technicians standing about for an hour is
  // two hours of somebody being paid to wait, which is the thing worth seeing.
  var idleMs = null;
  staff.forEach(function (s) {
    if (s.idleMs == null) return;
    idleMs = (idleMs || 0) + s.idleMs;
  });

  // What the rotation is for: the distance between the busiest and the
  // quietest person who was in. Counted on the people who were actually on the
  // board — somebody who left at lunchtime is not an unfair rotation.
  var spread = null;
  var onBoard = staff.filter(function (s) {
    return presentStaff.value.some(function (p) { return p.id === s.staffId; });
  });
  if (onBoard.length > 1) {
    var lo = onBoard[0].credit, hi = onBoard[0].credit;
    onBoard.forEach(function (s) {
      if (s.credit < lo) lo = s.credit;
      if (s.credit > hi) hi = s.credit;
    });
    spread = { low: lo, high: hi, gap: hi - lo };
  }

  return {
    businessDate: state.businessDate,
    closedAt: dayClosedAt.value,
    served: turns.length,
    credit: credit,
    requests: requests,
    booked: booked,
    walkIns: turns.length - booked,
    clients: Object.keys(clients).length,
    running: runningTurns.value.length,
    avgServiceMs: ended ? servedMs / ended : null,
    servedMs: servedMs,
    longest: longest,
    gapMs: gapMs,
    rounds: rowTimes.length,
    firstAt: firstStart,
    lastAt: lastEnd,
    openMs: firstStart != null && lastEnd != null ? lastEnd - firstStart : null,
    hours: hours,
    busiest: busiest && busiest.turns ? busiest : null,
    missed: missedRows,
    missedPeople: missedPeople,
    staff: staff,
    idleMs: idleMs,
    onFloor: presentStaff.value.length,
    spread: spread,
    mix: Object.keys(byService).map(function (k) { return byService[k]; })
      .sort(function (a, b) { return b.turns - a.turns || a.name.localeCompare(b.name); })
  };
});

/* ---------- actions: the scheduling plugin ---------- */

/* Double bookings still to sort out, once each. The conflicts list has one
 * line per booking that runs into another, so a pair shows twice — one from
 * each side — and is counted here as the one problem it is: one technician,
 * two clients. Next availability carries a warning while any are open, because
 * every answer it gives about that technician leans on the book being right
 * (Kevin, 2026-10-19). */
var doubleBookings = computed(function () {
  var seen = {}, out = [];
  ((bookConflicts.value && bookConflicts.value.risk) || []).forEach(function (x) {
    if (x.kind !== 'double' || !x.staff) return;
    var pair = [x.svc.id, x.withSvcId].sort().join(':');
    var key = x.staff.id + '|' + pair;
    if (seen[key]) return;
    seen[key] = true;
    out.push({
      staff: x.staff, at: Math.min(x.startsAt, x.at || x.startsAt),
      line: x.staff.name + ': ' + x.clientName + ' (' + x.serviceName + ', ' +
        formatClock(x.startsAt) + ') and ' + x.withClient + ' (' + x.withService + ', ' +
        formatClock(x.at) + ')'
    });
  });
  return out.sort(function (a, b) { return a.at - b.at; });
});

// Open the appointments pane with its conflicts list showing, from the
// warning beside Next availability.
function showConflicts() {
  if (!appts.open) toggleApptPane();
  setApptRiskOpen(true);
}

function toggleApptPane() {
  appts.open = !appts.open;
  try {
    window.localStorage.setItem('dreamspa.dash.apptPane', appts.open ? 'open' : 'closed');
  } catch (e) { /* noop */ }
}

// On by default: the pane is a list of work still to be handed out, and a
// service already on the board is not that. Off, the whole book is there,
// assigned lines struck through, which is how you check what a client is in
// for rather than what to do next.
function setApptUnassignedOnly(on) {
  ui.apptUnassignedOnly = !!on;
  try {
    window.localStorage.setItem('dreamspa.dash.apptUnassigned',
      ui.apptUnassignedOnly ? 'on' : 'off');
  } catch (e) { /* noop */ }
}

// On by default as well, and for the same reason: a cancelled visit is not
// work to hand out, and on a busy morning it is several headings' worth of
// struck-through text between the lines that are. Off, they come back in
// place, struck through, which is how you check whether the client who has
// just walked in is the one who cancelled.
function setApptHideCancelled(on) {
  ui.apptHideCancelled = !!on;
  try {
    window.localStorage.setItem('dreamspa.dash.apptHideCancelled',
      ui.apptHideCancelled ? 'on' : 'off');
  } catch (e) { /* noop */ }
}

// Per device like the pane's own open state: a wall monitor and the desk
// tablet want different answers, and a station that has folded the list away
// should still find it folded away after a reload.
function setApptRiskOpen(on) {
  ui.apptRiskOpen = !!on;
  try {
    window.localStorage.setItem('dreamspa.dash.apptRisk',
      ui.apptRiskOpen ? 'open' : 'closed');
  } catch (e) { /* noop */ }
}

function toggleApptRisk() { setApptRiskOpen(!ui.apptRiskOpen); }

/* ---------- writing up a day that already happened ----------
 *
 * The board is meant to be kept as the day runs, and mostly it is. What it is
 * not kept through is the afternoon everyone was too busy to touch it — and
 * then the rotation is wrong, because the turns those services were worth were
 * never charged to anybody.
 *
 * This reads the book for services that have already finished with no square
 * against them, and writes each one up where it happened: started when the
 * booking started, stopped when it was due to end, in that technician's
 * column in time order. Same path as any other square — bookTurn with an `at`
 * — so a backfilled turn is a turn, weight and all.
 *
 * What it will not guess at: a service the map has no turn type for, one the
 * book has nobody against, and one whose technician is not on the board. Those
 * are handed back as a count rather than assumed.
 */
// A single-service visit that has been checked out has a real finish time on
// it, which beats the booked length. On a visit with several services the
// close is the end of the whole ticket and says nothing about where one
// service ended, so the book's own window is used instead.
function backfillEndOf(appt, svc) {
  return bookedServiceEndFromClose(appts.ticketClosedAt[appt.id], svc.startsAt,
    svc.durationMinutes, appt.services.length);
}

var backfillCandidates = computed(function () {
  var out = { ready: [], addOns: [], unmapped: 0, noTech: 0, offBoard: [] };
  if (!scheduling.enabled.value || !sync.auth.user) return out;
  // Both sides of this have to be the same day. A book against the wrong
  // board is the one case where every reading is wrong in the same direction:
  // yesterday's services have all finished and none of them are on today's
  // squares, so the whole of yesterday reads as work nobody wrote up.
  if (appts.bookDate !== state.businessDate) return out;
  var now = minuteNow.value;
  var byService = turnByApptService.value;

  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    a.services.forEach(function (svc) {
      if (byService[svc.id]) return;                       // already a square
      var endedAt = backfillEndOf(a, svc);
      if (endedAt == null || endedAt > now) return;        // not finished yet

      // Added at the chair onto a square already on the visit: part of that
      // turn, not one of its own (js/rules/add-on.js). Asked here as well as by
      // the fold, because the fold does it once per line and a square the desk
      // deleted — Vic McWherter's manicure, 2026-10-19 — has used that once up.
      var host = addOnHostTurn(a, svc);
      if (host) { out.addOns.push({ appt: a, svc: svc, host: host }); return; }

      var mapped = trackerServiceForBooked(svc);
      if (!mapped) { out.unmapped += 1; return; }

      // The sale's technician when the client has paid — the same person the
      // automatic write-up would have used — and the book's otherwise.
      var staff = staffForSaleLine(saleLineByService.value[svc.id]);
      if (!staff) {
        (svc.techs || []).some(function (label) {
          staff = staffForTechLabel(label);
          return !!staff;
        });
      }
      if (!staff) { out.noTech += 1; return; }

      var day = state.dayStaff[staff.id];
      if (!day || !day.present) {
        if (out.offBoard.indexOf(staff.name) === -1) out.offBoard.push(staff.name);
        return;
      }

      out.ready.push({
        appt: a, svc: svc, staff: staff, service: mapped,
        startedAt: svc.startsAt, endedAt: endedAt
      });
    });
  });

  // Oldest first, so each column fills in the order the day happened.
  out.ready.sort(function (x, y) { return x.startedAt - y.startedAt; });
  return out;
});

function backfillCompleted() {
  if (dayClosed.value) {
    toast('The day is closed. Reopen it before writing anything up.', 'warn');
    return 0;
  }
  var list = backfillCandidates.value.ready;
  var addOns = backfillCandidates.value.addOns;
  if (!list.length && !addOns.length) {
    toast('Nothing finished is missing from the board.', 'info');
    return 0;
  }
  // The add-ons first, onto the squares they belong to: no new turns.
  addOns.forEach(function (c) { foldInto(c.host, c.appt, c.svc); });
  var added = 0;
  list.forEach(function (c) {
    var ok = bookTurn(c.staff.id, {
      serviceId: c.service.id,
      requested: false,
      quiet: true,
      // sortAt is the same clock as a live turn's, so a written-up square
      // lands among that technician's turns in the order the day ran rather
      // than at the bottom of the column.
      sortAt: c.startedAt,
      at: { startedAt: c.startedAt, endedAt: c.endedAt },
      link: {
        appointmentId: c.appt.id, appointmentServiceId: c.svc.id,
        sourceServiceId: c.svc.serviceId, clientId: c.appt.clientId,
        clientName: c.appt.clientName, sourceServiceName: c.svc.name
      }
    });
    if (ok) added += 1;
  });
  var folded = addOns.length
    ? (addOns.length === 1
      ? addOns[0].appt.clientName + '’s ' + addOns[0].svc.name + ' went onto ' +
        addOns[0].host.staffName + '’s ' + addOns[0].host.serviceName + ' square — no new turn.'
      : addOns.length + ' services added at the chair went onto their squares — no new turns.')
    : '';
  toast((added ? (added === 1 ? 'Wrote up 1 finished service.' : 'Wrote up ' + added + ' finished services.') : '') +
    (added && folded ? ' ' : '') + folded, 'ok');
  return added + addOns.length;
}

/* ---------- writing up the day as it is checked out ----------
 *
 * The button above is for a day the board fell behind on. Most of what it
 * writes up, though, is a client who was seen and checked out with nobody
 * touching the board, and for those there is nothing to decide: the client has
 * paid, and the sale says who did each service. So the board does it on its
 * own, the moment the sale settles.
 *
 * On the technician the SALE names, not the booking. The sale is what the
 * commission is paid on, and it is what the desk corrects at checkout when the
 * pedicure booked with Ann was done by Bea — the booking is never touched
 * again. A board that wrote it up on Ann would charge Ann's turn for Bea's
 * work, and then flag the square as wrong for being exactly what the book said.
 *
 * Written up the same way as the button does it — finished, in the book's
 * window, in time order in that technician's column — except that the window
 * never runs past the checkout: a client who paid at 2:40 for a service booked
 * until 3:00 was not in the chair at 2:50.
 *
 * WHAT IT LEAVES FOR THE DESK, and says so once:
 *   - a service the sale has no line for (changed or dropped at checkout);
 *   - a line with nobody on it;
 *   - somebody on the sale who is not on the board today;
 *   - a service with no turn type mapped;
 *   - a technician with a square of their own over the same stretch that is
 *     not attached to any booking. That is very likely this client, put up by
 *     hand as a walk-in, and a second square would charge the turn twice. The
 *     conflicts list already shows it with "Attach an appointment…".
 * Everything it leaves is still offered by the button, on the book's
 * technician, for the desk to decide.
 *
 * ONCE, AND ONLY ONCE. A square the desk deletes stays deleted. Two things
 * remember that a booked service has been dealt with: `written_up_at` on its
 * `tt_book_seen` row, which every station loads, and `seatedEver` below, for a
 * station that watched a square come and go on realtime and so never reread
 * that row. The square's id is worked out from the booked line
 * (`stableTurnId`), so two stations acting on the same checkout write the same
 * row rather than racing to insert two.
 */

// Booked line id -> true once this station has seen a square on it. Not
// persisted: `written_up_at` is the durable half, this is the live one.
var seatedEver = {};

// What has already been said about a line it would not write up, so a sale
// that stays unwritable does not repeat itself on every refresh. Per station,
// for the reason `autoStartNoted` is.
var writeUpNoted = {};

function noteWrittenUp(turn) {
  if (!turn) return;
  var ids = [turn.appointmentServiceId].concat((turn.extras || []).map(function (m) {
    return m.appointmentServiceId;
  }));
  ids.forEach(function (svcId) {
    if (!svcId) return;
    seatedEver[svcId] = true;
    var m = bookSeen[svcId];
    if (!m || m.writtenUpAt) return;
    m.writtenUpAt = Date.now();
    pushBookSeen(svcId);
  });
}

// Booked line id -> the sale line for it, for every appointment that has
// settled. See rules/write-up.js for how a visit's lines are paired.
var saleLineByService = computed(function () {
  var out = {};
  if (!scheduling.can('saleStaff')) return out;
  appts.rows.forEach(function (a) {
    var lines = appts.saleStaff[a.id];
    if (!lines || !lines.length) return;
    var m = writeUpRules.matchSaleLines(a.services, lines);
    Object.keys(m).forEach(function (k) { out[k] = m[k]; });
  });
  return out;
});

// The board's technician behind a sale line: by the booking system's own id
// where somebody has linked it, and failing that by name, the way the book's
// tech labels have always been read.
function staffForSaleLine(line) {
  if (!line || !line.staffId) return null;
  var byId = rosterStaff.value.find(function (s) {
    return s.externalStaffId && s.externalStaffId === line.staffId;
  });
  return byId || staffForTechLabel(line.label);
}

// A square of theirs over the same stretch that no booking is attached to.
function unlinkedSquareOver(staffId, win, now) {
  return state.turns.some(function (t) {
    if (t.staffId !== staffId || t.appointmentServiceId) return false;
    var end = t.endedAt == null ? now : t.endedAt;
    return t.startedAt < win.endedAt && end > win.startedAt;
  });
}

function autoWriteUp() {
  if (!scheduling.enabled.value || !scheduling.can('saleStaff')) return;
  if (ui.loading || dayClosed.value || !sync.auth.user) return;
  if (appts.bookDate !== state.businessDate) return;
  // An add-on sold at the chair goes onto its square first, so it is not
  // written up as a turn of its own below.
  foldAddOns();

  var seated = turnByApptService.value;
  Object.keys(seated).forEach(function (k) { seatedEver[k] = true; });

  var now = Date.now();
  var byLine = saleLineByService.value;
  var done = [], held = [];

  function hold(svc, why) {
    if (writeUpNoted[svc.id]) return;
    writeUpNoted[svc.id] = true;
    held.push(why);
  }

  appts.rows.forEach(function (a) {
    if (a.cancelled) return;
    var closedAt = appts.ticketClosedAt[a.id];
    if (closedAt == null) return;                          // not settled
    a.services.forEach(function (svc) {
      if (seated[svc.id] || seatedEver[svc.id]) return;
      var seen = bookSeen[svc.id];
      if (seen && seen.writtenUpAt) return;                // dealt with once
      var line = byLine[svc.id];
      if (!line) return;                                   // the sale has no line for it

      var mapped = trackerServiceForBooked(svc);
      if (!mapped) {
        hold(svc, a.clientName + '’s ' + svc.name + ' was not written up — it is not ' +
          'mapped to a turn type (Settings → Appointments).');
        return;
      }
      var staff = staffForSaleLine(line);
      if (!staff) {
        hold(svc, a.clientName + '’s ' + svc.name + ' was not written up — ' +
          (line.staffId ? (line.label || 'the technician on the sale') +
            ' is not on the roster.' : 'nobody is on that line of the sale.'));
        return;
      }
      var day = state.dayStaff[staff.id];
      if (!day || !day.present) {
        hold(svc, staff.name + ' is on ' + a.clientName + '’s sale but not on the board ' +
          'today, so the ' + svc.name + ' was not written up.');
        return;
      }
      var win = writeUpRules.writeUpWindow(svc.startsAt, backfillEndOf(a, svc), closedAt);
      if (!win || win.endedAt > now) return;
      if (unlinkedSquareOver(staff.id, win, now)) {
        hold(svc, a.clientName + '’s ' + svc.name + ' was not written up — ' + staff.name +
          ' already has a square then with no appointment on it. Attach it instead.');
        return;
      }

      var turn = bookTurn(staff.id, {
        id: writeUpRules.stableTurnId(
          (tenant.get() || '') + ':' + state.businessDate + ':' + svc.id),
        serviceId: mapped.id,
        requested: false,
        quiet: true,
        sortAt: win.startedAt,
        at: win,
        link: {
          appointmentId: a.id, appointmentServiceId: svc.id,
          sourceServiceId: svc.serviceId, clientId: a.clientId,
          clientName: a.clientName, sourceServiceName: svc.name
        }
      });
      if (!turn) return;
      noteWrittenUp(turn);
      done.push(turn);
    });
  });

  if (done.length === 1) {
    var t = done[0];
    toast('Checked out — wrote up ' + t.staffName + '’s ' + t.serviceName +
      (t.clientName ? ' for ' + t.clientName : '') + '.', 'ok');
  } else if (done.length) {
    toast('Checked out — wrote up ' + done.length + ' finished services from the sale.', 'ok');
  }
  // One line at a time, and only on a pass that wrote nothing: a second toast
  // would replace the first before it was read.
  if (held.length && !done.length) toast(held[0], 'warn');
}

// Every read below returns quietly when the plugin is off or has no provider,
// so nothing downstream needs its own guard: an empty book is an empty book.
function provider() {
  return scheduling.enabled.value ? scheduling.provider.value : null;
}

function canRead() {
  var p = provider();
  if (!p) return null;
  if (p.needsAuth && !sync.auth.user) return null;
  return p;
}

async function loadAppointments() {
  var p = canRead();
  if (!p) return;
  // Which day this read is for, held from before the await: the date can roll
  // while it is in flight, and a book that arrives after the board has moved
  // on belongs to the day that asked for it, not the one it lands in. The
  // newer read is already running, so this one is simply dropped.
  var forDate = state.businessDate;
  appts.loading = true;
  appts.error = null;
  try {
    var rows = await p.fetchAppointments(forDate);
    if (forDate !== state.businessDate) return;
    rememberBookBefore(forDate);
    appts.rows = rows;
    appts.bookDate = forDate;
    appts.lastSync = Date.now();
    // Before anything downstream looks at the new book: a square that is
    // still the same service must not read as unbooked just because the
    // booking was edited.
    reconcileTurns();
    // A square put up since the requests were read, for a service Mangomint
    // has as one, gets its star now rather than at the next poll.
    starBookedRequests();
    reconcilePendingMoves();
  } catch (err) {
    if (forDate === state.businessDate) appts.error = String(err.message || err);
  } finally {
    // Only if this is still the read the pane is waiting on: a dropped one
    // must not put the spinner out from under the one that replaced it.
    if (forDate === state.businessDate) appts.loading = false;
  }
}

// Which of today's appointments have been checked out. Failure is not worth
// surfacing: the board simply keeps behaving as it did before, with turns
// stopped by hand.
async function loadTickets() {
  var p = canRead();
  if (!p || !scheduling.can('checkout')) return;
  var forDate = state.businessDate;
  try {
    var closed = await p.fetchClosedTickets(forDate);
    // Who did the work, read in the same breath so the two never describe two
    // different moments. A failure here costs only the automatic write-up, so
    // it keeps whatever it had rather than taking the stops down with it.
    var staff = appts.saleStaff;
    if (scheduling.can('saleStaff') && p.fetchSaleStaff) {
      try { staff = await p.fetchSaleStaff(forDate); } catch (e) { /* keep what we have */ }
    }
    if (forDate !== state.businessDate) return;   // see loadAppointments
    appts.ticketClosedAt = closed;
    appts.saleStaff = staff;
    ticketsVersion.value += 1;
    reconcilePendingMoves();
  } catch (e) { /* noop */ }
}

// Which of today's clients the outside system says are in a chair. No
// provider reports this yet — see the auto-start section — so this is the
// seam one drops into rather than dead weight: fill in fetchInService,
// declare the capability, and the board stops having to infer it.
async function loadInService() {
  var p = canRead();
  if (!p || !scheduling.can('inService')) return;
  var forDate = state.businessDate;
  try {
    var seated = await p.fetchInService(forDate);
    if (forDate !== state.businessDate) return;   // see loadAppointments
    appts.inServiceAt = seated;
  } catch (e) { /* keep what we have */ }
}

async function loadOptionRules() {
  var p = canRead();
  if (!p || !scheduling.can('options')) return;
  try { appts.optionRules = await p.fetchOptionRules(); } catch (e) { /* keep what we have */ }
}

async function loadOptionCatalogue() {
  var p = canRead();
  if (!p || !scheduling.can('options') || appts.optionCatalogueLoaded) return;
  try {
    appts.optionCatalogue = await p.fetchOptionCatalogue();
    appts.optionCatalogueLoaded = true;
  } catch (err) {
    appts.error = String(err.message || err);
  }
}

async function loadServiceMap() {
  var p = canRead();
  if (!p) return;
  try { appts.serviceMap = await p.fetchServiceMap(); } catch (e) { /* keep what we have */ }
}

async function loadCatalogue() {
  var p = canRead();
  if (!p || !scheduling.can('catalogue')) return;
  try {
    var data = await p.fetchCatalogue();
    appts.catalogue = data.catalogue;
    appts.serviceMap = data.map;
    appts.catalogueLoaded = true;
  } catch (err) {
    appts.error = String(err.message || err);
  }
}

function loadScheduling() {
  loadServiceMap();
  loadOptionRules();
  loadAppointments();
  loadTickets();
  loadInService();
  loadBookedRequests();
  loadBookStatus();
  loadWorkHours();
  loadPendingMoves();
  if (ui.configTab === 'scheduling') loadCatalogue();
}

// Back to knowing nothing about a book. The mapping goes too: it is the
// plugin's data, and leaving it behind would badge squares against a system
// the board is no longer talking to.
function clearSchedulingData() {
  appts.rows = [];
  pendingStore.rows = [];
  appts.bookDate = null;
  bookedRequests.byKey = {};
  bookStatus.byAppt = {};
  workHours.byExternal = {};
  appts.catalogue = [];
  appts.catalogueLoaded = false;
  appts.ticketClosedAt = {};
  appts.saleStaff = {};
  appts.inServiceAt = {};
  appts.serviceMap = {};
  appts.optionRules = {};
  appts.optionCatalogue = [];
  appts.optionCatalogueLoaded = false;
  appts.error = null;
  appts.lastSync = null;
  // Whatever the board learned about today's book goes with it, so switching
  // the plugin back on primes again instead of acting on a stale picture.
  resetAutoStart();
  ui.assign = null;
  ui.attachPick = null;
  ui.apptDrag = null;
}

var mappedServiceIds = computed(function () {
  return Object.keys(appts.serviceMap).filter(function (k) { return appts.serviceMap[k]; });
});

// Switching the slot off is the one settings change that can throw data away,
// so it asks first — but only when there is something to lose.
function requestScheduling(on) {
  if (on) { scheduling.setEnabled(true); return; }
  if (!mappedServiceIds.value.length) { scheduling.setEnabled(false); return; }
  ui.pluginPrompt = {
    name: scheduling.name.value,
    mapped: mappedServiceIds.value.length
  };
}

function closePluginPrompt() { ui.pluginPrompt = null; }

// `discard` deletes the stored mapping outright. Keeping it costs nothing and
// means switching the plugin back on restores every badge, which is why that
// is the default the modal offers.
function confirmDisableScheduling(discard) {
  var p = scheduling.provider.value;
  var n = mappedServiceIds.value.length;
  if (discard && p && p.clearServiceMap) p.clearServiceMap(mappedServiceIds.value.slice());
  ui.pluginPrompt = null;
  scheduling.setEnabled(false);
  toast(discard
    ? 'Appointments off — ' + n + ' service ' + (n === 1 ? 'mapping' : 'mappings') + ' discarded.'
    : 'Appointments off — the service mapping was kept.', 'ok');
}

function mapExternalService(externalServiceId, ttServiceId) {
  var p = provider();
  if (!p) return;
  if (ttServiceId) appts.serviceMap[externalServiceId] = ttServiceId;
  else delete appts.serviceMap[externalServiceId];
  p.saveServiceMapping(externalServiceId, ttServiceId || null);
}

// The tracker service an outside system's service rolls up to, if mapped.
function trackerServiceFor(externalServiceId) {
  var id = appts.serviceMap[externalServiceId];
  return id ? serviceById.value[id] || null : null;
}

/* ---------- what a booking's options do to it ----------
 * A booked service is not always the service it was booked as. A Classic
 * Pedicure with Gel on it is a Gel Pedicure — a full turn, not a half — and
 * anything with a custom design on it can only go to the technicians who do
 * custom designs. Both are properties of the OPTION, so both are read off the
 * option rather than off the base service, and both follow the booking
 * wherever it is put on the board.
 */

// The turn type this booking really is. The first option carrying a type wins;
// options are in the order the booking system lists them, which is the order
// they were added. What an option makes it can depend on what it was booked as
// — Gel Polish makes a pedicure a Gel Pedicure and a manicure a Gel Manicure —
// see js/rules/option-type.js.
function trackerServiceForBooked(svc) {
  if (!svc) return null;
  var base = trackerServiceFor(svc.serviceId);
  var id = optionTurnType(svc.options, appts.optionRules, base ? base.id : null,
    function (sid) { return !!serviceById.value[sid]; });
  return (id && serviceById.value[id]) || base;
}

/* ---------- two services on one visit at the same time ----------
 * See js/rules/same-time.js: a pedicure may be started alongside the visit's
 * manicure by a second technician, and a visit's two half turns are one
 * technician's job. Each turn type says which side of the client it is.
 */
var apptById = computed(function () {
  var map = {};
  (appts.rows || []).forEach(function (a) { map[a.id] = a; });
  return map;
});

function apptOfBooked(svc) {
  return svc && svc.appointmentId != null ? apptById.value[svc.appointmentId] || null : null;
}

// A booked service as the same-time rules see it.
function sameTimeShape(svc) {
  var m = trackerServiceForBooked(svc);
  return {
    id: svc.id, apptId: svc.appointmentId, from: svc.startsAt,
    to: svc.startsAt + bookedHoldMs(svc, m),
    side: m ? m.sameTime : null, weight: m ? m.turnWeight : null,
    techs: svc.techs || []
  };
}

// The earliest somebody other than whoever does the rest of the visit could
// start this booked service. Its booked start when nothing on the visit lets it
// run alongside.
function sameTimeEarliest(svc) {
  var a = apptOfBooked(svc);
  if (!a || a.cancelled || (a.services || []).length < 2) return svc.startsAt;
  return sameTimeRules.earliestStart(sameTimeShape(svc), a.services.map(sameTimeShape));
}

// Who is doing the other half of this booked service's half-turn pair, once
// that half is on the board — the pair is one technician's job, so from then on
// nobody else may take this half. Null when there is no pair, or when neither
// half has started (the pair can still go to anybody, together).
function halfPartnerStaffId(svc) {
  var a = apptOfBooked(svc);
  if (!a || a.cancelled) return null;
  var me = sameTimeShape(svc);
  var found = null;
  (a.services || []).some(function (o) {
    if (o.id === svc.id || !sameTimeRules.halfPair(me, sameTimeShape(o))) return false;
    var t = turnByApptService.value[o.id];
    if (t && t.staffId) { found = t.staffId; return true; }
    return false;
  });
  return found;
}

// Which technicians may take it, or null when nothing restricts it. Several
// restricting options have to be satisfied at once, so this is the
// intersection — a design in one option and a length in another means whoever
// can do both. The other half of a half-turn pair already under way narrows it
// to whoever is doing that half.
function allowedStaffForBooked(svc) {
  if (!svc) return null;
  var rules = appts.optionRules;
  var allowed = null;
  (svc.options || []).forEach(function (o) {
    var r = rules[o.id];
    if (!r || !r.staffIds || !r.staffIds.length) return;
    if (!allowed) { allowed = r.staffIds.slice(); return; }
    allowed = allowed.filter(function (id) { return r.staffIds.indexOf(id) !== -1; });
  });
  var partner = halfPartnerStaffId(svc);
  if (partner) {
    allowed = allowed ? allowed.filter(function (id) { return id === partner; }) : [partner];
  }
  return allowed;
}

function staffAllowedForBooked(svc, staffId) {
  var allowed = allowedStaffForBooked(svc);
  return !allowed || allowed.indexOf(staffId) !== -1;
}

// The options that are actually doing something, for the tooltips and sheets
// that have to explain why a booking is not what it was booked as.
function activeOptionsFor(svc) {
  var rules = appts.optionRules;
  return (svc && svc.options || []).filter(function (o) {
    var r = rules[o.id];
    return !!(r && (r.serviceId || (r.staffIds || []).length));
  });
}

/* What gets written onto the square, and what the square is compared against
 * when the book changes underneath it.
 *
 * ALL of the booking's options, not just the ones carrying a rule: the square
 * marks itself with a + when there is anything on the booking at all, and the
 * sheet lists the lot. An option with no rule is still something the desk
 * added, still something the technician has to do, and "Acrylic is on this
 * one" is worth reading whether or not it moves the turn weight.
 */
function bookedOptionsOf(svc) {
  return (svc && svc.options || []).map(function (o) {
    return { id: o.id, name: o.name };
  });
}

// Order matters and is the booking system's: options come back in the order
// they were added, so a signature over that order changes when they are
// re-ordered as well as when one comes or goes. Cheap enough to run over every
// matched square on every reconcile.
function optionsSignature(list) {
  return (list || []).map(function (o) { return o.id + ':' + o.name; }).join('|');
}

/* What the square's + is drawn from and what the sheet lists.
 *
 * The snapshot on the turn is the answer — it is the only one a finished
 * square, or a day in History, still has. A square whose snapshot is empty
 * falls back to the booking behind it, if the book still carries it: on the
 * day this shipped every square already on the board had an empty list, and
 * reading through to the booking marks them right away rather than leaving
 * half the board unmarked until the next reconcile writes the snapshot.
 */
function optionsOfTurn(t) {
  if (!t) return [];
  if (t.options && t.options.length) return t.options;
  var found = bookedServiceFor(t);
  return found ? bookedOptionsOf(found.svc) : [];
}

function optionRuleFor(optionId) { return appts.optionRules[optionId] || null; }

function setOptionRule(option, rule) {
  var p = provider();
  if (!p || !p.saveOptionRule) return;
  var byBase = {};
  Object.keys((rule && rule.byBase) || {}).forEach(function (k) {
    if (rule.byBase[k]) byBase[k] = rule.byBase[k];
  });
  var clean = {
    serviceId: rule && rule.serviceId ? rule.serviceId : null,
    byBase: byBase,
    staffIds: (rule && rule.staffIds ? rule.staffIds : []).slice()
  };
  if (!clean.serviceId && !clean.staffIds.length && !Object.keys(byBase).length) {
    delete appts.optionRules[option.id];
  } else {
    appts.optionRules[option.id] = {
      optionId: option.id, optionName: option.name,
      groupId: option.groupId, groupName: option.groupName,
      serviceId: clean.serviceId, byBase: clean.byBase, staffIds: clean.staffIds
    };
  }
  p.saveOptionRule(option, clean);
}

function setOptionRuleService(option, ttServiceId) {
  var cur = optionRuleFor(option.id) || {};
  setOptionRule(option, { serviceId: ttServiceId || null, byBase: cur.byBase || {},
                          staffIds: cur.staffIds || [] });
}

// What the option makes a booking that was booked as `baseId`. Null hands that
// base back to the plain answer above it.
function setOptionRuleBase(option, baseId, ttServiceId) {
  var cur = optionRuleFor(option.id) || {};
  var byBase = Object.assign({}, cur.byBase || {});
  if (ttServiceId) byBase[baseId] = ttServiceId; else delete byBase[baseId];
  setOptionRule(option, { serviceId: cur.serviceId || null, byBase: byBase,
                          staffIds: cur.staffIds || [] });
}

function toggleOptionRuleStaff(option, staffId) {
  var cur = optionRuleFor(option.id) || {};
  var list = (cur.staffIds || []).slice();
  var i = list.indexOf(staffId);
  if (i === -1) list.push(staffId); else list.splice(i, 1);
  setOptionRule(option, { serviceId: cur.serviceId || null, byBase: cur.byBase || {},
                          staffIds: list });
}

var optionRuleCount = computed(function () {
  return Object.keys(appts.optionRules).length;
});

// Grouped for the settings screen, and only the groups that are in use — the
// catalogue carries every option the salon has ever offered.
var optionGroups = computed(function () {
  var by = {};
  appts.optionCatalogue.forEach(function (o) {
    var key = o.groupName || 'Other options';
    if (!by[key]) by[key] = { name: key, options: [] };
    by[key].options.push(o);
  });
  return Object.keys(by).sort().map(function (k) { return by[k]; });
});

var apptStats = computed(function () {
  var total = 0, cancelled = 0, services = 0;
  appts.rows.forEach(function (a) {
    if (a.cancelled) { cancelled += 1; return; }
    total += 1;
    services += a.services.length;
  });
  return { total: total, cancelled: cancelled, services: services };
});

/* ---------- missed walk-ins ----------
 *
 * Somebody came in, nobody could take them, they left. Nothing on the turn
 * board records that — the day just looks quiet — so it is logged on its own:
 * how many people, what they were after, and when. That is the number that
 * answers "should we have had another tech on".
 */

var missed = reactive({ rows: [], loading: false, error: null });

async function loadMissedWalkins() {
  if (!sync.auth.user) return;
  missed.loading = true;
  try {
    missed.rows = await sync.fetchMissedWalkins(state.businessDate, state.businessDate);
    missed.error = null;
  } catch (err) {
    missed.error = String(err.message || err);
  } finally {
    missed.loading = false;
  }
}

// Total people turned away today, which is the number worth showing — three
// parties of two is six clients, not three.
var missedTodayPeople = computed(function () {
  return missed.rows.reduce(function (n, r) { return n + (r.partySize || 0); }, 0);
});

function openMissedWalkin() {
  ui.missedWalkin = { partySize: 1, serviceIds: [] };
}

function closeMissedWalkin() { ui.missedWalkin = null; }

function toggleMissedService(serviceId) {
  var m = ui.missedWalkin;
  if (!m) return;
  var i = m.serviceIds.indexOf(serviceId);
  if (i === -1) m.serviceIds.push(serviceId);
  else m.serviceIds.splice(i, 1);
}

function setMissedParty(n) {
  if (!ui.missedWalkin) return;
  ui.missedWalkin.partySize = Math.max(1, Math.min(20, Math.round(Number(n) || 1)));
}

/* What the party asked for, as services the board still knows about.
 *
 * Nothing named — plenty of people do not say — reads as the whole menu, on the
 * same reasoning History reads the floor by: nobody said, so anybody might have
 * been the answer. A list that resolves to nothing because the service has
 * since been deleted lands in the same place, which is the honest reading of a
 * question the board can no longer answer.
 */
function missedServiceIds(serviceIds) {
  var ids = (serviceIds || []).filter(function (id) { return serviceById.value[id]; });
  return ids.length ? ids : activeServices.value.map(function (s) { return s.id; });
}

/* How long the wait would have been for what this party asked for, in minutes.
 *
 * `nextUpDetail` is the same answer Next availability was showing while the
 * desk decided nobody could take them, so what gets logged is the number that
 * was on the screen at the time rather than one reconstructed from the turns a
 * fortnight later. Somebody free with room is a wait of nought, not an absent
 * answer: "they could have been seen there and then" is exactly the case worth
 * counting.
 *
 * For one person that is the soonest of what they named. For a party it is
 * the soonest they could all be seated together, each with their own
 * technician — one tech free is no answer to two people (rules/party-wait.js).
 * Each technician is measured the way Next availability measures them,
 * `earliestStartFor`, so a party of one logs what the card was showing.
 *
 * Null when the board cannot say — nobody in today performs it, or everyone who
 * does is on a break that ends when they say it does. An unknown wait is not a
 * long one, and logging a guess here is worse than logging nothing: this is a
 * number a salon puts another technician on the strength of.
 */
function missedWaitMinutes(serviceIds, partySize) {
  var t = minuteNow.value;
  var ids = missedServiceIds(serviceIds);
  var named = (serviceIds || []).filter(function (id) { return serviceById.value[id]; });
  var techs = rotationStaff.value.map(function (s) {
    var at = {};
    ids.forEach(function (id) {
      if (s.serviceIds.indexOf(id) === -1) return;
      var start = earliestStartFor(s.id, id, t);
      if (start != null) at[id] = start;
    });
    return { id: s.id, at: at };
  });
  var start = partyWaitRules.soonestPartyStart(
    partyWaitRules.partySeats(partySize, named, ids), techs);
  return start == null ? null : Math.max(0, Math.round((start - t) / 60000));
}

/* How many of the technicians in could have done the work.
 *
 * The count that turns "four in" into something worth acting on. Four in and
 * none of them does acrylics is a menu problem, or a rota problem, and it is
 * not answered by putting a fifth pedicurist on; four in and all four do it is
 * a floor that was simply full. Neither reading survives being asked later —
 * service lists are edited, and History can only ever ask them as they are now.
 *
 * A union, not a sum: a party after a fill and a pedicure counts somebody who
 * does either once, since one person is one person however many of the things
 * they can do.
 *
 * Marked in rather than free, for the same reason `staffPresent` is: how many
 * were free is the thing a missed walk-in is evidence about.
 */
function missedAbleCount(serviceIds) {
  var seen = {};
  missedServiceIds(serviceIds).forEach(function (id) {
    providersFor(id).forEach(function (s) { seen[s.id] = true; });
  });
  return Object.keys(seen).length;
}

/* ...and the same question asked of each service they named, on its own. A
 * party after an acrylic and a gel pedicure with six in who do acrylics and
 * four who do pedicures is a pedicure problem, and the union of the two reads
 * "6" and says nothing of the kind. Named services only — with nothing named
 * there is nothing to split. See js/rules/missed-floor.js. */
function missedAbleByService(serviceIds) {
  var named = (serviceIds || []).filter(function (id) { return serviceById.value[id]; });
  return missedFloorRules.ableByService(named, rotationStaff.value);
}

/* The wait in words, one wording for the sheet's live estimate and for every
 * logged row read back — so what the desk sees beside a missed party later is
 * the same sentence it was looking at when the party walked out. */
function missedWaitLabel(mins) {
  return mins == null ? 'no wait estimate'
    : mins < 1 ? 'no wait'
    : 'about ' + formatMins(mins * 60000) + ' wait';
}

/* What the sheet shows above its button: the facts it is about to record
 * alongside the party. Live, because all of them move while the sheet is open —
 * the wait counts down on the minute, and toggling a service asks a different
 * question of the floor. */
var missedWalkinEstimate = computed(function () {
  var m = ui.missedWalkin;
  if (!m) return null;
  var mins = missedWaitMinutes(m.serviceIds, m.partySize);
  return {
    staffPresent: presentStaff.value.length,
    // Said out loud only when they named something. With nothing named it is
    // everybody who does anything, which is not a second fact — it is the first
    // one again, and the desk reads a repeated number as a mistake.
    staffAble: missedAbleCount(m.serviceIds),
    ableByService: (function () {
      var by = missedAbleByService(m.serviceIds);
      return Object.keys(by).map(function (id) {
        return { serviceId: id, name: serviceById.value[id].name, able: by[id] };
      });
    })(),
    anyService: !m.serviceIds.length,
    waitMinutes: mins,
    waitLabel: missedWaitLabel(mins)
  };
});

/* The row itself, without any of the asking.
 *
 * Two things log a missed walk-in — the sheet the desk fills in, and the
 * question asked when somebody is taken off the waiting list (see
 * `logCheckinMissed`) — and they must produce the SAME row. A day's report
 * cannot tell "she was turned away at the door" from "she waited twenty minutes
 * and left", nor should it: both are a client who came in and was not seen, and
 * that is the whole of what this table counts.
 */
function writeMissedWalkin(partySize, serviceIds) {
  var ids = (serviceIds || []).slice();
  var row = {
    id: sync.uuid(),
    businessDate: state.businessDate,
    partySize: Math.max(1, partySize || 1),
    serviceIds: ids,
    /* The floor as it stood, stamped rather than worked out afterwards.
     *
     * "Four people left" is the number that says something went wrong; how many
     * were on and how long the wait was are the two that say what to do about
     * it, and neither survives being asked later. History reconstructs a floor
     * from the turns because that is all an old row leaves it — which misses
     * anybody who was in but never got a turn, and cannot recover a wait at all,
     * since the estimate lived in a running square that has long since stopped.
     * Both are known here, at the moment of the loss, so both are written down.
     *
     * `staffPresent` and `staffAble` are both who was marked in, not who was
     * free — free is what the missed walk-in itself is evidence about, and
     * counting it here would make the row argue its own case. `staffAble` is
     * the one that says which kind of problem this was: nobody in who does that
     * work is a different afternoon from everybody in who does it being busy. */
    staffPresent: presentStaff.value.length,
    staffAble: missedAbleCount(ids),
    staffAbleByService: missedAbleByService(ids),
    waitMinutes: missedWaitMinutes(ids, partySize),
    note: '',
    createdAt: Date.now()
  };
  missed.rows.push(row);
  sync.enqueue({
    table: 'tt_missed_walkins', type: 'upsert',
    row: {
      id: row.id, business_date: row.businessDate, party_size: row.partySize,
      service_ids: row.serviceIds, note: null,
      staff_present: row.staffPresent, staff_able: row.staffAble,
      staff_able_by_service: row.staffAbleByService,
      wait_minutes: row.waitMinutes,
      created_at: new Date(row.createdAt).toISOString()
    }
  });
  return row;
}

function logMissedWalkin() {
  var m = ui.missedWalkin;
  if (!m) return false;
  var row = writeMissedWalkin(m.partySize, m.serviceIds);
  ui.missedWalkin = null;
  toast(row.partySize === 1
    ? 'Logged a missed walk-in.'
    : 'Logged ' + row.partySize + ' missed walk-ins.', 'ok');
  return true;
}

function undoMissedWalkin(id) {
  var i = missed.rows.findIndex(function (r) { return r.id === id; });
  if (i === -1) return;
  missed.rows.splice(i, 1);
  sync.enqueue({ table: 'tt_missed_walkins', type: 'delete', match: { id: id } });
  toast('Missed walk-in removed.', 'ok');
}

/* ---------- self check-in ----------
 *
 * The lobby tablet — checkin.html, js/components/check-in.js — and the queue it
 * hands the front desk. It is the Quick add path with the client holding the
 * tablet instead of the desk: pick a service, and the board finds the
 * technician. What is different is what happens when it cannot.
 *
 * The desk, told nobody is free, decides something. A client cannot, so the
 * screen asks for a name and puts them in a queue, and the desk works the queue
 * in the order people arrived. That order is the whole of the promise, so it is
 * a tested pure function rather than a sort written twice — see
 * js/rules/waitlist.js.
 *
 * EVERY check-in is written down, not only the ones that had to wait. A client
 * who walked in and was seated at once is a row with `status: 'seated'` and the
 * turn it became; one who waited is `waiting` until the desk does something
 * about them. Two reasons for logging the easy case as well:
 *
 *   the desk    a square appearing on the board on its own, with a client name
 *               on it, is otherwise unattributable. The row is where "this came
 *               from the lobby, at 2:14" is written.
 *   the salon   how many people checked themselves in, and how long they stood
 *               there, is the number this feature exists to move. It cannot be
 *               reconstructed from the turns afterwards, because a turn records
 *               when the work started and not when the person arrived.
 *
 * Nothing is ever deleted. `left` is a status, the same way tt_missed_walkins is
 * a table: a day where four people waited twenty minutes and two of them walked
 * out is not a quiet day, and deleting the rows is how it would look like one.
 */

// Today's queue, oldest first — what the desk works from, and what the tablet
// counts a position off. Both read this same array through the same function.
var waitingCheckins = computed(function () {
  return waitlistRules.waitingRows(state.checkins);
});

// The number beside the label on the bar. Ticks with the minute rather than
// with `now`, like every other clock the desk reads.
var longestWaitMinutes = computed(function () {
  var first = waitingCheckins.value[0];
  return first ? waitlistRules.waitedMinutes(first, minuteNow.value) : 0;
});

function checkinRow(c) {
  return {
    id: c.id, business_date: c.businessDate, client_name: c.clientName,
    service_id: c.serviceId, service_name: c.serviceName,
    status: c.status,
    created_at: new Date(c.createdAt).toISOString(),
    resolved_at: c.resolvedAt ? new Date(c.resolvedAt).toISOString() : null,
    turn_id: c.turnId || null
  };
}

function pushCheckin(c) {
  sync.enqueue({ table: 'tt_checkins', type: 'upsert', row: checkinRow(c) });
}

function checkinOf(id) {
  return state.checkins.find(function (c) { return c.id === id; }) || null;
}

// The row itself, wherever a check-in came from and whatever became of it. The
// service name is snapshotted here rather than looked up later, so a service
// renamed at six o'clock does not rewrite what somebody asked for at two.
function recordCheckin(serviceId, name, status, turnId) {
  var svc = serviceById.value[serviceId];
  var entry = {
    id: sync.uuid(),
    businessDate: state.businessDate,
    clientName: waitlistRules.cleanName(name),
    serviceId: serviceId,
    serviceName: svc ? svc.name : 'Service',
    status: status,
    createdAt: Date.now(),
    resolvedAt: status === 'waiting' ? null : Date.now(),
    turnId: turnId || null
  };
  state.checkins.push(entry);
  pushCheckin(entry);
  return entry;
}

/* What the tablet may offer for a service, and the words for the wait.
 *
 * The decision itself is `checkinOffer` in the rules file, off the very answer
 * `armService` is gated on — so the lobby cannot offer a chair the board would
 * refuse to put a square in. What is added here is the language, which the
 * rules file has no business holding: a *client* is reading this, so the
 * technician's name is left out of it entirely. Who does the work is the
 * salon's business and the rotation's, and naming somebody on a lobby screen
 * invites being asked for them by name — which is a request, which is a
 * different thing with different rules.
 *
 * The wait is quoted only if the salon says so — see `setCheckinWaits`.
 */
function checkinOffer(serviceId) {
  var check = serviceAvailability(serviceId);
  /* How long the wait is, as a number, before anything is said about it.
   *
   * Deliberately NOT behind `ui.checkinWaits`. That switch governs whether a
   * client is TOLD a length of time; the cutoff below is about how long the wait
   * actually is. A salon that quotes no waits and asks for people to be sent to
   * book after an hour means both of those things, and reading the cutoff off the
   * wait-times switch would silently turn one of them off.
   */
  var waitMins = check.etaAt == null
    ? null
    : Math.round((check.etaAt - minuteNow.value) / 60000);

  // Where this service's booking link points, if the salon set one. Worked out
  // for every branch rather than only the one that uses it, because the answer
  // is what decides whether that branch exists at all.
  var link = checkinBookingLink(serviceId);

  // Free is not enough on its own for a screen acting unattended — see the note
  // over `checkinOffer` in the rules file for the whole of why.
  var offer = waitlistRules.checkinOffer(check, availableWithRoom(serviceId).length > 0, {
    url: link,
    afterMinutes: checkinBookAfter.value,
    waitMinutes: waitMins
  });
  /* The estimate off `check` rather than a second call to `nextUpDetail`.
   *
   * The sheet already asked it and carries the answer as `etaAt`, and this runs
   * for every service on the menu on every minute tick, so asking again would
   * walk the whole rotation twice per tile for a number it is already holding.
   * `etaAt` is only ever set on the two branches that can be joined, which is
   * also the only case a wait is quoted on.
   *
   * Spelled out rather than run through `formatMins`. Everywhere else in the
   * console a wait is read by somebody who reads forty of them a day and wants
   * "25m"; here it is read once, by a person deciding whether to stay, and the
   * two-letter version of a number reads as a machine talking. Past half an
   * hour it becomes a time to come back at, for the same reason the board's own
   * estimate does — nobody counts out forty minutes in their head. */
  var wait = '';
  if (ui.checkinWaits && offer.canJoin && waitMins != null) {
    wait = waitMins < 1 ? 'any minute now'
      : waitMins > ETA_CLOCK_AFTER_MIN ? 'from about ' + formatClock(check.etaAt)
      : waitMins === 1 ? 'about a minute'
      : 'about ' + waitMins + ' minutes';
  }
  return {
    serviceId: serviceId,
    kind: offer.kind,
    canJoin: offer.canJoin,
    // Only ever a length of time, never a name and never a reason. A client
    // does not need to know that the wait is Julian's four o'clock.
    //
    // Empty on the `book` branch, and not for want of a number: past the cutoff
    // the wait is not what the person is being asked to decide on any more, and
    // "the wait is from about 4:15 PM" beside "book an appointment instead" is
    // two answers to one question. The salon's own wording carries whatever it
    // wants said about the length — see `bookHead` in js/rules/checkin-look.js.
    wait: wait,
    // The closing case says its own sentence, because "see the front desk" with
    // no explanation reads as the machine being broken.
    closeClock: check.closeClock || '',
    // Only on the branch that sends them there, so no screen can put a booking
    // link in front of somebody it is not offering one to.
    bookUrl: offer.kind === 'book' ? link : ''
  };
}

/* Somebody is free: put the square up, exactly as pressing that service on the
 * board would, and log the check-in against it.
 *
 * `bookTurn` is the same call the board makes and refuses the same things —
 * a closed day above all — so a tablet left switched on after hours cannot put
 * work on a book the desk has settled. It answers with the turn, and a refusal
 * is a refusal: nothing is logged, and the screen says to see the desk rather
 * than claiming a chair that was never taken.
 */
function checkInNow(serviceId, name) {
  var check = serviceAvailability(serviceId);
  if (!check.ok) return null;
  /* Only somebody with room, and no falling back to whoever is merely free.
   *
   * The hotkey path DOES fall back — see `bookByShortcut` — because pressing a
   * key is a deliberate act by somebody who can see the floor, and the
   * collision warning is there to catch the rest. Neither is true here: the
   * screen decided, and `quiet` below means no warning would be shown to
   * anybody if it were wrong. So an afternoon where nobody has a clear run
   * returns null, the screen re-reads as the waiting-list branch, and the name
   * already typed is still in the box.
   */
  var who = availableWithRoom(serviceId)[0];
  if (!who) return null;
  var turn = bookTurn(who.id, {
    serviceId: serviceId,
    clientName: name,
    // No toast: nobody is standing at the board when this happens, and the
    // square arriving in the column is the whole of the news.
    quiet: true
  });
  if (!turn) return null;
  return {
    entry: recordCheckin(serviceId, name, 'seated', turn.id),
    turn: turn,
    staffName: who.name
  };
}

// Nobody is free, and they gave a name. A place in the queue, and the number to
// tell them — read off the same sorted array the desk's list is drawn from.
function joinWaitlist(serviceId, name) {
  var clean = waitlistRules.cleanName(name);
  if (!clean) return null;
  if (!serviceById.value[serviceId]) return null;
  if (dayClosed.value) return null;
  var entry = recordCheckin(serviceId, clean, 'waiting', null);
  return { entry: entry, position: waitlistRules.positionOf(state.checkins, entry.id) };
}

/* The desk seats somebody off the queue.
 *
 * The same two steps the tablet takes when a technician is free, in the same
 * order and through the same call — so a client seated from the queue and one
 * seated at the door produce the same square. Nobody free means the desk gets
 * the service warning sheet it would have got from the bar, which says who is
 * busy and when the first of them is expected; the entry stays in the queue,
 * because refusing to seat somebody is not the same as their having left.
 */
function seatCheckin(id) {
  var entry = checkinOf(id);
  if (!entry || entry.status !== 'waiting') return false;
  if (refuseWhileClosed()) return false;
  if (!entry.serviceId || !serviceById.value[entry.serviceId]) {
    toast(entry.serviceName + ' is no longer a service on the board.', 'warn');
    return false;
  }
  var check = serviceAvailability(entry.serviceId);
  if (!check.ok) { ui.serviceWarning = check; return false; }
  /* Room here too, and the reason is not the tablet's.
   *
   * `bookTurn` is called `quiet` below, because the news is the toast at the
   * end of this function rather than five of them — and quiet also suppresses
   * the walk-in collision sheet, which is what would otherwise catch seating
   * somebody on a technician whose own client is due in ten minutes. Falling
   * back to whoever is merely free would therefore skip the one warning that
   * case has. Requiring room means there is nothing to warn about.
   *
   * Nobody with room is not a refusal to the desk: the service is still on the
   * bar, and arming it puts every ordinary warning back in the way.
   */
  var who = availableWithRoom(entry.serviceId)[0];
  if (!who) {
    var d = nextUpDetail(entry.serviceId);
    toast(d.kind === 'eta'
      ? 'Nobody has room for ' + entry.clientName + '’s ' + entry.serviceName +
        ' yet — ' + d.name + ' should, about ' + formatClock(d.at) + '.'
      : 'Nobody has room for ' + entry.clientName + '’s ' + entry.serviceName +
        ' yet.', 'warn');
    return false;
  }
  var turn = bookTurn(who.id, {
    serviceId: entry.serviceId, clientName: entry.clientName, quiet: true
  });
  if (!turn) return false;
  entry.status = 'seated';
  entry.resolvedAt = Date.now();
  entry.turnId = turn.id;
  pushCheckin(entry);
  toast(entry.clientName + ' → ' + who.name + ' — off the waiting list.', 'ok');
  return true;
}

/* They left, or the desk dealt with them in person. Off the list, still in the
 * day: see the note at the top of this section for why this is a status rather
 * than a delete.
 *
 * Which of those two it was is the question `ui.checkinMissed` then asks, and it
 * is asked AFTER the write rather than instead of it. The desk has already
 * pressed twice to get here and the row is gone from the list either way — a
 * question that could take the removal back with it would make the two presses
 * mean less than they say. Answered or ignored, the person is off the list; the
 * only thing hanging on the answer is whether the day also records that the
 * salon lost them.
 */
function dropCheckin(id) {
  var entry = checkinOf(id);
  if (!entry || entry.status !== 'waiting') return false;
  entry.status = 'left';
  entry.resolvedAt = Date.now();
  pushCheckin(entry);
  toast(entry.clientName + ' taken off the waiting list.', 'ok');
  /* Snapshotted rather than held by id, because every one of these is a fact
   * about the moment of the removal: the wait stops counting when they go, and
   * the service name is the one they asked for even if Settings renames it
   * while the sheet is open. */
  ui.checkinMissed = {
    checkinId: entry.id,
    clientName: entry.clientName,
    serviceId: entry.serviceId || null,
    serviceName: entry.serviceName,
    waited: waitlistRules.waitedMinutes(entry, Date.now())
  };
  return true;
}

/* What that sheet draws. The colour and the letter are read live off the
 * service, the way every other sheet on the board reads its own — a service
 * recoloured in Settings with this open should not be the one square in the
 * console still wearing the old one. Deleted outright, there is no letter and
 * the sheet says so rather than showing an empty chip. */
var checkinMissedDetail = computed(function () {
  var m = ui.checkinMissed;
  if (!m) return null;
  var svc = m.serviceId ? serviceById.value[m.serviceId] : null;
  return {
    clientName: m.clientName,
    serviceName: m.serviceName,
    letter: svc ? svc.abbrev : '',
    color: svc ? svc.color : DEFAULT_SERVICE_COLOR,
    // The label rather than the number: "just now" is what a person who has been
    // standing there ninety seconds should read, not "1 min".
    waited: waitlistRules.waitLabel(m.waited)
  };
});

// No, the desk saw to them — or nobody answered, which is the same thing as far
// as the day's count goes. Nothing is written: a missed walk-in logged on a
// guess is worse than one not logged at all, because it is the number a salon
// puts another technician on the strength of.
function closeCheckinMissed() { ui.checkinMissed = null; }

/* Yes, they left without being seen.
 *
 * One person and the one service they asked for — both are known here, which is
 * the difference between this and the sheet the desk fills in by hand. Anybody
 * who came in WITH them was never on the list to begin with, so the party size
 * is one; a desk that knows better can still log the rest from the bar.
 */
function logCheckinMissed() {
  var m = ui.checkinMissed;
  if (!m) return false;
  ui.checkinMissed = null;
  writeMissedWalkin(1, m.serviceId ? [m.serviceId] : []);
  toast(m.clientName + ' logged as a missed walk-in.', 'ok');
  return true;
}

// The desk pressed it by mistake, or the client came back. Only ever the way
// back from `left` — a seated entry has a square on the board behind it, and
// putting it back on the list would leave the client in two places at once.
function unleaveCheckin(id) {
  var entry = checkinOf(id);
  if (!entry || entry.status !== 'left') return false;
  entry.status = 'waiting';
  entry.resolvedAt = null;
  pushCheckin(entry);
  return true;
}

function checkinWaited(entry) {
  return waitlistRules.waitedMinutes(entry, minuteNow.value);
}

async function loadHistory(from, to) {
  history.from = from;
  history.to = to;
  history.loading = true;
  history.error = null;
  try {
    var all = await Promise.all([
      sync.fetchHistory(from, to),
      // A day's missed walk-ins belong beside its turns: the two together are
      // what the day actually was.
      sync.fetchMissedWalkins(from, to).catch(function () { return []; }),
      // Who was marked in each day, so a miss can be read against the size of
      // the floor it happened on. Optional: without it the list still has the
      // times and what was wanted.
      sync.fetchDayStaffRange(from, to).catch(function () { return []; })
    ]);
    history.rows = all[0];
    history.missed = all[1];
    history.dayStaff = all[2];
    history.loaded = true;
  } catch (err) {
    history.error = String(err.message || err);
  } finally {
    history.loading = false;
  }
}

export const store = {
  state: state,
  ui: ui,
  history: history,
  appts: appts,
  now: now,
  // crosses a minute should read this rather than `now`.
  minuteNow: minuteNow,
  syncStatus: sync.status,
  // A sentence when this station has not heard from the server for a while.
  staleBoardNote: staleBoardNote,
  auth: sync.auth,
  // Whether this console is a platform admin standing in somebody else's salon.
  // Empty for every station, on every project — see js/rules/support.js.
  support: supportState,
  signIn: sync.signIn,
  signOut: sync.signOut,
  PALETTE: PALETTE,
  defaultServiceColor: DEFAULT_SERVICE_COLOR,
  colorOfTurn: colorOfTurn,
  // business hours
  hoursRows: hoursRows,
  hoursSet: hoursSet,
  todayHours: todayHours,
  dayStartDue: dayStartDue,
  openLeadMinutes: OPEN_LEAD_MIN,
  closingGraceMinutes: CLOSING_GRACE_MIN,
  setDayHours: setDayHours,
  copyHoursToOpenDays: copyHoursToOpenDays,
  clearBusinessHours: clearBusinessHours,
  closingOverrun: closingOverrun,
  closingNote: closingNote,
  // when the floor is expected to be clear — the top bar's last-two-hours stat
  lastFinish: lastFinish,
  lastFinishNote: lastFinishNote,
  lastFinishWindowMinutes: LAST_FINISH_WINDOW_MIN,
  // one technician's own hours for the day — see js/rules/shift.js
  shiftOf: shiftOf,
  setShiftTimes: setShiftTimes,
  clearShiftTimes: clearShiftTimes,
  isShiftSpent: isShiftSpent,
  shiftOutGraceMinutes: SHIFT_OUT_GRACE_MIN,
  // "17:30" as an instant today, so a stored time can be read back in the
  // same 12-hour clock as everything else on screen.
  clockAt: function (hhmm) { return clockToMs(state.businessDate, hhmm); },
  // derived
  boardColumns: boardColumns,
  dayClosed: dayClosed,
  dayClosedAt: dayClosedAt,
  dayOpened: dayOpened,
  refuseWhileClosed: refuseWhileClosed,
  daySummary: daySummary,
  visibleColumns: visibleColumns,
  // What the grid actually draws: visibleColumns, with the column under a drag
  // redrawn as it would read if the square landed there.
  previewColumns: previewColumns,
  turnDropPlan: turnDropPlan,
  visibleRows: visibleRows,
  doneRowCount: doneRowCount,
  doneStaffCount: doneStaffCount,
  canHideDoneRows: canHideDoneRows,
  // the phone board — see turn-list.js
  listColumns: listColumns,
  upNextStaffId: upNextStaffId,
  rotationRank: rotationRank,
  boardIsList: boardIsList,
  watchViewport: watchViewport,
  setBoardMode: setBoardMode,
  toggleBoardMode: toggleBoardMode,
  setBoardLayout: setBoardLayout,
  setTurnProgress: setTurnProgress,
  setListOrder: setListOrder,
  toggleListOrder: toggleListOrder,
  closePhoneExtras: closePhoneExtras,
  staffOf: staffOf,
  openSkipRemove: openSkipRemove,
  closeSkipRemove: closeSkipRemove,
  skipRemoveDetail: skipRemoveDetail,
  confirmSkipRemove: confirmSkipRemove,
  clientNotesFor: clientNotesFor,
  noteWarningFor: noteWarningFor,
  turnOf: turnOf,
  activeServices: activeServices,
  quickAddServices: quickAddServices,
  serviceById: serviceById,
  rosterStaff: rosterStaff,
  archivedStaff: archivedStaff,
  presentStaff: presentStaff,
  availableQueue: availableQueue,
  suggestedStaffId: suggestedStaffId,
  armedService: armedService,
  cellMenuTurn: cellMenuTurn,
  rowCount: rowCount,
  setViewportRows: setViewportRows,
  dueSoon: dueSoon,
  dueTakerFor: dueTakerFor,
  dueSoonMinutes: dueSoonMinutes,
  conflictBufferMinutes: conflictBufferMinutes,
  fullTurnPrice: fullTurnPrice,
  conflictBufferChoices: conflictRules.BUFFER_CHOICES,
  conflictBufferLimits: conflictRules.BUFFER_LIMITS,
  setConflictBufferMinutes: setConflictBufferMinutes,
  dueSoonChoices: DUE_SOON_CHOICES,
  dueSoonLimits: DUE_SOON_LIMITS,
  setDueSoonMinutes: setDueSoonMinutes,
  rowStartTimes: rowStartTimes,
  shortcutMap: shortcutMap,
  duplicateShortcuts: duplicateShortcuts,
  // lookups
  turnsFor: turnsFor,
  slotMatesOf: slotMatesOf,
  isSkipped: isSkipped,
  isOwnClients: isOwnClients,
  skipsFor: skipsFor,
  turnRowNumber: turnRowNumber,
  missedTurns: missedTurns,
  isBusy: isBusy,
  turnCredit: turnCredit,
  hasOpenHalf: hasOpenHalf,
  nextOpenRow: nextOpenRow,
  availableSince: availableSince,
  idleSince: idleSince,
  idleUntil: idleUntil,
  idleMsFor: idleMsFor,
  skipSpansFor: skipSpansFor,
  dayStartedAt: dayStartedAt,
  nextBookingFor: nextBookingFor,
  nextBookingDetail: nextBookingDetail,
  isApptRequested: isApptRequested,
  deskMarksRequests: deskMarksRequests,
  isBookedRequest: isBookedRequest,
  apptRequestFor: apptRequestFor,
  bookedStaffFor: bookedStaffFor,
  toggleApptRequest: toggleApptRequest,
  apptMenuTarget: apptMenuTarget,
  openApptMenu: openApptMenu,
  closeApptMenu: closeApptMenu,
  turnMenuTurn: turnMenuTurn,
  openTurnMenu: openTurnMenu,
  closeTurnMenu: closeTurnMenu,
  toggleTurnRequest: toggleTurnRequest,
  walkInEndsAt: walkInEndsAt,
  walkInCollision: walkInCollision,
  walkInAlternatives: walkInAlternatives,
  walkInMoves: walkInMoves,
  walkInMovesNote: walkInMovesNote,
  closeBookClash: closeBookClash,
  closeOrderAsk: closeOrderAsk,
  autoStartAskView: autoStartAskView,
  closeAutoStartAsk: closeAutoStartAsk,
  answerAutoStartAsk: answerAutoStartAsk,
  doubleBookings: doubleBookings,
  showConflicts: showConflicts,
  answerOrderAsk: answerOrderAsk,
  confirmBookClash: confirmBookClash,
  bookedServiceFor: bookedServiceFor,
  startOffsetOf: startOffsetOf,
  startOffsetMinutes: startOffsetMinutes,
  offsetPct: offsetRules.offsetPct,
  isTurnRequested: isTurnRequested,
  availabilityAt: availabilityAt,
  floorTurnsCompleted: floorTurnsCompleted,
  // actions
  init: init,
  armService: armService,
  disarm: disarm,
  nextUpFor: nextUpFor,
  nextUpDetail: nextUpDetail,
  nextUpList: nextUpList,
  partyDetail: partyDetail,
  setPartySize: setPartySize,
  partyMax: partyRules.PARTY_MAX,
  partyMin: partyRules.PARTY_MIN,
  partyWindowMin: partyRules.PARTY_WINDOW_MIN,
  dueNextList: dueNextList,
  earliestStartFor: earliestStartFor,
  serviceAvailability: serviceAvailability,
  closeServiceWarning: closeServiceWarning,
  setShowNextUp: setShowNextUp,
  setRequestsOn: setRequestsOn,
  followsBook: followsBook,
  pendingMoves: pendingMoves,
  pendingMoveLines: pendingMoveLines,
  runPendingMove: runPendingMove,
  assignNoteWarning: assignNoteWarning,
  setAutoStop: setAutoStop,
  setAutoStart: setAutoStart,
  toggleLockOrder: toggleLockOrder,
  toggleHideDoneRows: toggleHideDoneRows,
  toggleHideDoneStaff: toggleHideDoneStaff,
  setApptUnassignedOnly: setApptUnassignedOnly,
  setApptHideCancelled: setApptHideCancelled,
  bookConflicts: bookConflicts,
  toggleApptRisk: toggleApptRisk,
  backfillCandidates: backfillCandidates,
  backfillCompleted: backfillCompleted,
  openEndDay: openEndDay,
  closeEndDay: closeEndDay,
  endDay: endDay,
  reopenDay: reopenDay,
  theme: theme,
  themePresets: themeApi.PRESETS,
  themeFields: themeApi.FIELDS,
  themeValue: themeValue,
  setThemePreset: setThemePreset,
  setThemeColor: setThemeColor,
  clearThemeCustom: clearThemeCustom,
  logo: logo,
  logoLimits: themeApi.LOGO,
  setLogo: setLogo,
  clearLogo: clearLogo,
  fileSizeLabel: themeApi.fileSizeLabel,
  brand: brand,
  brandLimits: themeApi.BRAND,
  businessName: businessName,
  setBusinessName: setBusinessName,
  toggleRequestMode: toggleRequestMode,
  bookTurn: bookTurn,
  bookByShortcut: bookByShortcut,
  bookNextUp: bookNextUp,
  stopTurn: stopTurn,
  startWaitingNow: startWaitingNow,
  bookedLineOver: bookedLineOver,
  addOnHostTurn: addOnHostTurn,
  reopenTurn: reopenTurn,
  setTurnService: setTurnService,
  setTurnTimes: setTurnTimes,
  clockValue: clockValue,
  toggleTurnRequested: toggleTurnRequested,
  removeTurn: removeTurn,
  openCellMenu: openCellMenu,
  closeCellMenu: closeCellMenu,
  turnForApptService: turnForApptService,
  bookedServiceEnd: bookedServiceEnd,
  // The console's memory of today's book. Nothing draws it — it is exported for
  // the same reason `appts` is, so what the board believes about the book can be
  // looked at directly rather than inferred from what it did.
  bookSeen: bookSeen,
  openAssign: openAssign,
  setAssignStaff: setAssignStaff,
  closeAssign: closeAssign,
  assignLateStart: assignLateStart,
  confirmAssign: confirmAssign,
  attachToTurn: attachToTurn,
  attachableTurns: attachableTurns,
  canStartBooked: canStartBooked,
  moveTurnBlockedReason: moveTurnBlockedReason,
  bookedDropAt: bookedDropAt,
  startBookedOn: startBookedOn,
  insertBookedAt: insertBookedAt,
  // a finished booking landing on a square that is taken — see the merge sheet
  isFinishedDrag: isFinishedDrag,
  openDropChoice: openDropChoice,
  closeDropChoice: closeDropChoice,
  setDropChoiceWeight: setDropChoiceWeight,
  dropChoiceDetail: dropChoiceDetail,
  confirmDropChoice: confirmDropChoice,
  // squares carrying more than one service
  servicesOfTurn: servicesOfTurn,
  isMergedTurn: isMergedTurn,
  mergeWeightChoices: mergeWeightChoices,
  setMergedWeight: setMergedWeight,
  unmergeFromTurn: unmergeFromTurn,
  attachableAppointments: attachableAppointments,
  openAttachPick: openAttachPick,
  closeAttachPick: closeAttachPick,
  upcomingForStaff: upcomingForStaff,
  openEmptyPick: openEmptyPick,
  closeEmptyPick: closeEmptyPick,
  startTurnFor: startTurnFor,
  quickAssign: quickAssign,
  openMoveConfirm: openMoveConfirm,
  closeMoveConfirm: closeMoveConfirm,
  confirmMove: confirmMove,
  askMoveTurn: askMoveTurn,
  moveConfirmDetail: moveConfirmDetail,
  swapTargetFor: swapTargetFor,
  swapConfirmDetail: swapConfirmDetail,
  openSwapConfirm: openSwapConfirm,
  closeSwapConfirm: closeSwapConfirm,
  confirmSwap: confirmSwap,
  addStaff: addStaff,
  updateStaff: updateStaff,
  toggleStaffService: toggleStaffService,
  setPresent: setPresent,
  setSkipped: setSkipped,
  setOwnClients: setOwnClients,
  skipReasons: SKIP_REASONS,
  skipReasonOf: skipReasonOf,
  skipOneTurn: skipOneTurn,
  missingBookedStaff: missingBookedStaff,
  dismissMissingBooked: dismissMissingBooked,
  addMissingBooked: addMissingBooked,
  turnStaffMismatch: turnStaffMismatch,
  openStaffMenu: openStaffMenu,
  closeStaffMenu: closeStaffMenu,
  addableStaff: addableStaff,
  openAddStaff: openAddStaff,
  closeAddStaff: closeAddStaff,
  isNewToday: isNewToday,
  toggleAddStaff: toggleAddStaff,
  confirmAddStaff: confirmAddStaff,
  deleteStaff: deleteStaff,
  restoreStaff: restoreStaff,
  reorderStaff: reorderStaff,
  movePresentStaff: movePresentStaff,
  addService: addService,
  updateService: updateService,
  deleteService: deleteService,
  reorderService: reorderService,
  switchToToday: switchToToday,
  resetToday: resetToday,
  loadHistory: loadHistory,
  // self check-in — the lobby tablet and the desk's waiting list
  CHECKIN_FEATURE: CHECKIN_FEATURE,
  checkinLicensed: checkinLicensed,
  checkinServices: checkinServices,
  checkinLook: checkinLook,
  checkinLookRaw: checkinLookRaw,
  checkinCustomised: checkinCustomised,
  setCheckinLook: setCheckinLook,
  resetCheckinLook: resetCheckinLook,
  checkinColorFields: look.COLORS,
  checkinTextFields: look.TEXT,
  checkinTextMax: look.TEXT_MAX,
  checkinCssVars: look.cssVars,
  waitingCheckins: waitingCheckins,
  longestWaitMinutes: longestWaitMinutes,
  checkinOffer: checkinOffer,
  checkInNow: checkInNow,
  joinWaitlist: joinWaitlist,
  seatCheckin: seatCheckin,
  dropCheckin: dropCheckin,
  unleaveCheckin: unleaveCheckin,
  checkinMissedDetail: checkinMissedDetail,
  closeCheckinMissed: closeCheckinMissed,
  logCheckinMissed: logCheckinMissed,
  checkinWaited: checkinWaited,
  waitLabel: waitlistRules.waitLabel,
  setCheckinWaits: setCheckinWaits,
  // ...and where it sends somebody whose wait is longer than the salon will ask
  // them to sit through
  checkinBookingUrl: checkinBookingUrl,
  checkinBookingPerService: checkinBookingPerService,
  checkinBookingLink: checkinBookingLink,
  checkinBookAfter: checkinBookAfter,
  checkinBookLimits: booking.CUTOFF_LIMITS,
  checkinBookDefault: booking.CUTOFF_DEFAULT_MIN,
  checkinBookPlaceholder: booking.PLACEHOLDER,
  checkinBookingRefMax: booking.REF_MAX,
  setCheckinBookingUrl: setCheckinBookingUrl,
  setCheckinBookAfter: setCheckinBookAfter,
  deskNotes: deskNotes,
  postNote: postNote,
  dismissNote: dismissNote,
  minuteNow: minuteNow,
  missed: missed,
  missedTodayPeople: missedTodayPeople,
  missedWalkinEstimate: missedWalkinEstimate,
  missedWaitLabel: missedWaitLabel,
  openMissedWalkin: openMissedWalkin,
  closeMissedWalkin: closeMissedWalkin,
  toggleMissedService: toggleMissedService,
  setMissedParty: setMissedParty,
  logMissedWalkin: logMissedWalkin,
  undoMissedWalkin: undoMissedWalkin,
  toggleApptPane: toggleApptPane,
  loadAppointments: loadAppointments,
  loadCatalogue: loadCatalogue,
  mapExternalService: mapExternalService,
  trackerServiceForBooked: trackerServiceForBooked,
  allowedStaffForBooked: allowedStaffForBooked,
  optionsOfTurn: optionsOfTurn,
  optionCountByTurn: optionCountByTurn,
  optionRuleFor: optionRuleFor,
  setOptionRuleService: setOptionRuleService,
  setOptionRuleBase: setOptionRuleBase,
  toggleOptionRuleStaff: toggleOptionRuleStaff,
  optionRuleCount: optionRuleCount,
  optionGroups: optionGroups,
  loadOptionCatalogue: loadOptionCatalogue,
  // the scheduling plugin slot
  scheduling: scheduling,
  schedulingOn: scheduling.enabled,
  schedulingName: scheduling.name,
  schedulingPlugins: scheduling.plugins,
  schedulingPlugin: scheduling.plugin,
  schedulingCan: scheduling.can,
  setSchedulingPlugin: scheduling.setPlugin,
  mappedServiceIds: mappedServiceIds,
  requestScheduling: requestScheduling,
  closePluginPrompt: closePluginPrompt,
  confirmDisableScheduling: confirmDisableScheduling,
  toast: toast,
  // utils
  formatDuration: formatDuration,
  formatMins: formatMins,
  formatClock: formatClock,
  formatCredit: formatCredit,
  turnWeights: TURN_WEIGHTS,
  MIN_SERVICE_MINUTES: MIN_SERVICE_MINUTES,
  MAX_SERVICE_MINUTES: MAX_SERVICE_MINUTES,
  weightLabel: weightLabel,
  weightBadge: weightBadge,
  pluralTurns: pluralTurns,
  todayKey: todayKey
};
